import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import {
  VIDEO_ACCEPT, PART_SIZE, MAX_VIDEO_BYTES, MAX_TOTAL_VIDEO_BYTES,
  isVideoFile, validateVideoFile, checkVideoBudget, deriveProfileVideo, setProfileVideo,
  fitsManualSend, partByteRanges, fmtMB,
} from "./videoLib.mjs";
import { isChartSlot, shouldBindZone, chartCellArt, pairGroupArt } from "./renderRouting.mjs";
import { mergeTemplatesWithTombstones } from "./mergeTemplates.mjs";
import { diffTemplates, sameDeep, chunkBySize } from "./syncDiff.mjs";
import { byteLen, templateSizeInfo } from "./oversize.mjs";
import { createCoalescingQueue } from "./saveQueue.mjs";
import { parseTemplatesResponse, detectColorPropertyKey, optionsForProperty, makeColorLinks, pruneColorLinks, hasAssignments, colorLinksForPayload, buildSetPickerRows } from "./colorLinks.mjs";
import { matchZone, applyTransform, SKIP_REASONS } from "./linkedDelta.mjs";

/* ============================================================
   Mockup Studio v6 – Creative Seller Suite
   Global groups, sort order, no-design toggle, flood-fill mask
   ============================================================ */

const ThemeContext = createContext({ isDark: true, toggle: () => {} });
function useTheme() { return useContext(ThemeContext); }

// Global dark mode: when embedded in the suite iframe the suite's master toggle owns the
// theme (follow ?theme boot + SUITE_THEME_STATE broadcast, never persist locally, hide the
// local toggle). Standalone keeps its own toggle + preference (default dark — the studio's
// historical look). The light palette in t(d) has existed all along; this enables it.
const IS_EMBEDDED = (() => { try { return window.self !== window.top; } catch { return true; } })();
const THEME_KEY = "cs_studio_theme";
function getTheme() {
  try {
    if (IS_EMBEDDED) return new URLSearchParams(window.location.search).get("theme") === "dark" ? "dark" : "light";
    return localStorage.getItem(THEME_KEY) || "dark";
  } catch { return "dark"; }
}
function saveTheme(v) { if (!IS_EMBEDDED) { try { localStorage.setItem(THEME_KEY, v); } catch { /* storage unavailable */ } } }

function t(d) {
  return {
    bg:d?"#0a0a14":"#f8fafc",bgCard:d?"#1a1a2e":"#fff",bgHeader:d?"rgba(10,10,20,0.8)":"rgba(255,255,255,0.85)",
    border:d?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)",borderActive:d?"rgba(99,102,241,0.3)":"rgba(99,102,241,0.4)",
    text:d?"#e2e8f0":"#1e293b",textSub:d?"#94a3b8":"#64748b",textMuted:d?"#64748b":"#94a3b8",textDim:d?"#475569":"#cbd5e1",
    overlay:d?"rgba(0,0,0,0.85)":"rgba(255,255,255,0.85)",modalBg:d?"#12121f":"#fff",
    savedBg:d?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",savedBorder:d?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",
    uploadBg:d?"rgba(99,102,241,0.04)":"rgba(99,102,241,0.06)",uploadBgHover:d?"rgba(99,102,241,0.08)":"rgba(99,102,241,0.12)",
    uploadBorder:d?"rgba(99,102,241,0.3)":"rgba(99,102,241,0.35)",uploadBorderHover:d?"rgba(99,102,241,0.6)":"rgba(99,102,241,0.7)",
    designUploadBg:d?"rgba(236,72,153,0.04)":"rgba(236,72,153,0.06)",designUploadBorder:d?"rgba(236,72,153,0.3)":"rgba(236,72,153,0.35)",
    statusBarBg:d?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",scrollThumb:d?"#2a2a3e":"#cbd5e1",btnSecBg:d?"#2a2a3e":"#e2e8f0",
  };
}

const GC=["#6366f1","#ec4899","#f59e0b","#10b981","#ef4444","#8b5cf6","#06b6d4","#f97316","#84cc16","#e879f9"];
function gc(i){return GC[i%GC.length]}

/* ── DB v5 — dropped the old per-set video store PVID (v4). Listing videos now
   live server-side in the mockup-studio-videos Blobs store, so the local store
   is removed on upgrade. Members on v4 upgrade cleanly (deleteObjectStore in the
   versionchange txn); the old bytes were browser-only and unannounced, nothing
   to migrate. ── */
const DB="MockupStudioDB",DBV=5,TPL="mockupTemplates",PROF="mockupProfiles",SESS="mockupSession";
function openDB(){return new Promise((r,j)=>{const q=indexedDB.open(DB,DBV);q.onupgradeneeded=e=>{const db=e.target.result;if(!db.objectStoreNames.contains(TPL))db.createObjectStore(TPL,{keyPath:"id"});if(!db.objectStoreNames.contains(PROF))db.createObjectStore(PROF,{keyPath:"id"});if(!db.objectStoreNames.contains(SESS))db.createObjectStore(SESS,{keyPath:"id"});if(db.objectStoreNames.contains("mockupProfileVideos"))db.deleteObjectStore("mockupProfileVideos")};q.onblocked=()=>j(new Error("DB blocked"));q.onsuccess=()=>r(q.result);q.onerror=()=>j(q.error)})}
async function dbPut(store,obj){const db=await openDB();return new Promise((r,j)=>{const tx=db.transaction(store,"readwrite");tx.objectStore(store).put(obj);tx.oncomplete=r;tx.onerror=()=>j(tx.error)})}
async function dbDel(store,id){const db=await openDB();return new Promise((r,j)=>{const tx=db.transaction(store,"readwrite");tx.objectStore(store).delete(id);tx.oncomplete=r;tx.onerror=()=>j(tx.error)})}
async function dbAll(store){const db=await openDB();return new Promise((r,j)=>{const tx=db.transaction(store,"readonly");const q=tx.objectStore(store).getAll();q.onsuccess=()=>r(q.result||[]);q.onerror=()=>j(q.error)})}
async function dbGet(store,id){const db=await openDB();return new Promise((r,j)=>{const tx=db.transaction(store,"readonly");const q=tx.objectStore(store).get(id);q.onsuccess=()=>r(q.result||null);q.onerror=()=>j(q.error)})}

/* Session helpers — persist the user's working state so reopening the
   studio picks up where they left off instead of forcing them to re-select
   templates and re-assign designs every time.

   Storage layout: TWO records in the SESS store.
     - "current"        → metadata: selSavedIds, designs, step, setNames,
                          showRes, ts (small, always saves)
     - "current_genout" → genOut: {[k]: data} (big, can fail to save)
   Splitting them lets a too-big genOut not block the metadata save. The
   loader pulls both and reconstructs the merged session. The user's setup
   is preserved even when generated mockups can't be persisted. */
const SESSION_ID="current";
const GENOUT_ID="current_genout";
const SESSION_TTL_MS=24*60*60*1000; // 24h — older sessions are stale

async function loadSession(){
  try{
    const meta=await dbGet(SESS,SESSION_ID);
    if(!meta)return null;
    if(Date.now()-(meta.ts||0)>SESSION_TTL_MS)return null;
    const gen=await dbGet(SESS,GENOUT_ID).catch(()=>null);
    // Back-compat: older session blobs stored genOut inside the metadata
    // record. If the new dedicated genOut record is missing but the old
    // metadata has genOut, use that. Avoids data loss on first load with
    // the new code.
    const resolvedGenOut=gen?.genOut||meta.genOut||{};
    return{...meta,genOut:resolvedGenOut};
  }catch(e){console.warn("Session load error:",e);return null}
}
async function saveSessionMeta(state){
  try{
    await dbPut(SESS,{id:SESSION_ID,ts:Date.now(),...state});
  }catch(e){console.warn("Session metadata save failed:",e)}
}
async function saveSessionGenOut(genOut){
  try{
    await dbPut(SESS,{id:GENOUT_ID,ts:Date.now(),genOut});
  }catch(e){console.warn("Session genOut save failed (likely exceeds IndexedDB quota):",e)}
}
async function clearSession(){
  try{await dbDel(SESS,SESSION_ID)}catch(e){}
  try{await dbDel(SESS,GENOUT_ID)}catch(e){}
}

/* Global groups stored in localStorage (no DB version bump needed) */
function loadGlobalGroups(){try{const s=localStorage.getItem("ms_globalGroups");return s?JSON.parse(s):[]}catch(e){return[]}}
function saveGlobalGroups(grps){try{localStorage.setItem("ms_globalGroups",JSON.stringify(grps))}catch(e){}}
/* Deletion tombstones (brief 5.14 §3): remember templates deleted locally so the
   server-merge on reload can't RESURRECT them if the server delete didn't take — e.g. the
   studio was opened standalone with no suite token, so serverDeleteTemplate was skipped
   (guarded by `if(!_suiteToken)return`), leaving the copy on the server blob → the merge
   re-adds it as "server-only". A tombstone survives the reload and blocks the re-add;
   it's pruned once the server confirms the id is gone (see the load merge). */
function loadTombstones(){try{const s=localStorage.getItem("ms_deletedTemplates");const a=s?JSON.parse(s):[];return Array.isArray(a)?a:[]}catch(e){return[]}}
function saveTombstones(ids){try{localStorage.setItem("ms_deletedTemplates",JSON.stringify(ids))}catch(e){}}
function addTombstone(id){if(!id)return;const s=new Set(loadTombstones());s.add(id);saveTombstones([...s])}
function removeTombstone(id){if(!id)return;saveTombstones(loadTombstones().filter(x=>x!==id))}
/* Profile (set) tombstones — brief 5.17 §2. Profiles previously had NO server-delete
   authority and NO tombstones, so a deleted set survived on the server blob and the
   non-destructive load merge re-added it on the next sync (and the Suite's appendStudioSet
   re-uploaded the whole read-back array). Mirrors the template tombstone discipline above. */
function loadProfileTombstones(){try{const s=localStorage.getItem("ms_deletedProfiles");const a=s?JSON.parse(s):[];return Array.isArray(a)?a:[]}catch(e){return[]}}
function saveProfileTombstones(ids){try{localStorage.setItem("ms_deletedProfiles",JSON.stringify(ids))}catch(e){}}
function addProfileTombstone(id){if(!id)return;const s=new Set(loadProfileTombstones());s.add(id);saveProfileTombstones([...s])}
function removeProfileTombstone(id){if(!id)return;saveProfileTombstones(loadProfileTombstones().filter(x=>x!==id))}
function loadTpls(){return dbAll(TPL)}
function loadProfs(){return dbAll(PROF)}
function saveTplSync(tpl){removeTombstone(tpl&&tpl.id);dbPut(TPL,tpl).catch(()=>{});scheduleTemplateSync(tpl)} // (re)saving un-deletes; local-first, server sync coalesced+batched (scheduleTemplateSync)
function delTplSync(id){addTombstone(id);dbDel(TPL,id).catch(()=>{});serverDeleteTemplate(id)}
function saveProfsSync(profs){for(const p of profs){dbPut(PROF,p).catch(()=>{})};serverSaveProfiles(profs)}

/* ── Server Sync via Netlify Blobs ── */
const API_BASE="/api/mockup-store";
let _suiteToken=null; // set from App props
// Suite-session expiry signalling. The Suite token has a finite lifetime; once it
// expires (or the Suite session dies and stops pushing SUITE_TOKEN_REFRESH), every
// authed call here 401s. Previously those failures returned null and were treated as
// "no server data" → templates silently didn't load → an EMPTY grid and a BLANK
// pipeline handoff, with no hint why (member report: "blank box", "logged out",
// "can't find my templates" — all one cause). We now surface expiry explicitly.
let _authExpired=false;   // latched true on a 401/403 or a past-exp token; reset only on reopen (fresh module load)
let _onAuthExpired=null;  // App registers a state setter so the UI can show a "session expired" card
function setSyncToken(token){_suiteToken=token}
function setAuthExpiredHandler(fn){_onAuthExpired=fn}
function markAuthExpired(){if(_authExpired)return;_authExpired=true;try{_onAuthExpired&&_onAuthExpired()}catch(e){}}
function isAuthExpired(){return _authExpired}
// Client-side expiry read — decode the JWT `exp` with no server round-trip, so a
// token that ages out MID-SESSION (member editing for hours) is caught before the
// next server call fails silently. Absent token is the gate's "no_token" case, not expiry.
function suiteTokenExpired(){
  if(!_suiteToken)return false;
  try{const p=JSON.parse(atob(_suiteToken.split(".")[1].replace(/-/g,"+").replace(/_/g,"/")));return !!(p&&p.exp&&p.exp<Math.floor(Date.now()/1000))}catch{return false}
}

// Server-save REJECTION signalling (mirrors the auth-expiry pattern above). `save-template`
// can return 400 (library full) or 413 (image too large) — a SUCCESSFUL fetch promise with
// res.ok=false, which the old fire-and-forget `.catch(...)` swallowed entirely. The template
// then lived in THIS browser only (IndexedDB) while the server never stored it, and a set
// could be saved referencing it — the silent data-loss that cost a member ~2 weeks (Sep 2026:
// a set that showed 18 mockups locally only offered 10 downstream, the other 8 never persisted).
// We now (a) track the rejected ids so saveProfi refuses to write a set that points at an
// unsaved mockup, and (b) surface interactive rejections to the member.
// template id (String) → REASON its last server save didn't land:
//   'oversize'  — 413, the body exceeded the server's single-payload cap; PERMANENT until shrunk
//   'cap'       — 400, a real per-user storage / template-count limit was hit
//   'transient' — 5xx / network / write-contention; NOT a rejection, retried by the load-time resync
// This was a plain Set<id>. A Set can only say "failed", so the set-save gate hard-coded
// "your mockup library is full" for EVERY flagged mockup — mis-reporting a transient 502 as a
// full library (the wrong string that cost two diagnoses). Keyed by String(id) so number/string
// ids (IndexedDB vs server) compare equal.
const _failedServerSaves=new Map();
let _onSaveError=null;              // App registers a surfacer (e.g. alert)
function setSaveErrorHandler(fn){_onSaveError=fn}
// The saved-mockup grid shows a permanent "Not backed up" badge on any template
// whose last server save was rejected. That badge is DERIVED from _failedServerSaves
// (single source of truth) — the App registers a listener here and we ping it after
// every mutation of the set, so the badge reflects reality without a second store to
// keep in sync. Replaces the old once-per-session oversize alert() (a blocking modal
// that re-fired on every reload because the dedup Set lived in page memory).
let _onFailedSavesChange=null;
function setFailedSavesHandler(fn){_onFailedSavesChange=fn}
function _notifyFailedSaves(){if(_onFailedSavesChange){try{_onFailedSavesChange([..._failedServerSaves.entries()])}catch(e){}}} // [id,reason] pairs so the badge can word itself per reason
// ONE consolidated, reason-accurate message for a flush's GENUINE rejections (oversize / cap).
// Transient contention is never surfaced here — those retry silently on the next load. Replaces
// the old per-template _emitSaveError storm (one alert per reordered card) + its 400="full"
// assumption. `rejects` = [{tpl, reason}].
function _emitBatchSaveError(rejects){
  const over=rejects.filter(r=>r.reason==="oversize").map(r=>(r.tpl&&r.tpl.name)||"mockup");
  const nCap=rejects.filter(r=>r.reason==="cap").length;
  const bits=[];
  if(over.length)bits.push(`${over.length} mockup${over.length>1?"s are":" is"} too large to back up (${over.slice(0,3).join(", ")}${over.length>3?", …":""}) — re-save ${over.length>1?"them":"it"} at a smaller size and upload again.`);
  if(nCap)bits.push(`Your mockup library is full — delete some unused mockups to make room.`);
  if(!bits.length)return;
  const msg=`${bits.join(" ")} Affected mockups are saved on this device only until that's fixed.`;
  if(_onSaveError){try{_onSaveError(msg)}catch(e){}}else console.warn("Server save rejections:",msg);
}
// HONEST 413 (payload too large). This is silent data-loss on the sync path: the
// mockup saved to THIS device only, never reached the server, so it won't appear
// on other devices or in sets. LOG it always (id + measured size + which field
// blew the budget — for diagnosis). The MEMBER-facing signal is NOT an alert() here
// anymore: an oversized mockup is a PERMANENT state, not an event, so it's surfaced
// as a persistent "Not backed up" badge on the mockup's card (derived from
// _failedServerSaves, which the caller has already updated). A blocking modal that
// re-fired on every reload was the wrong form; the badge is quieter but never gone.
function _reportOversize(tpl,maxMB){
  if(!tpl||tpl.id==null)return;
  const info=templateSizeInfo(tpl);
  console.warn(`[save 413] "${tpl.name||tpl.id}" id=${tpl.id} — ${info.totalMB}MB over ${maxMB}MB cap; biggest field "${info.field}" (${info.fieldMB}MB). Saved on this device only.`);
}
// ── Interactive template sync — COALESCED + BATCHED ─────────────────────────
// The load-time resync was batched in Sep 2026; the INTERACTIVE per-edit path was NOT — it
// fired one immediate whole-document write per edit (saveTplSync → serverSaveTemplate →
// save-template). Reordering N cards = N overlapping read-modify-writes on the single per-user
// blob → contention → 502/500, plus one rejection alert PER card. This routes interactive saves
// through the SAME batched, sequential, contention-safe path as the load resync: collect dirty
// templates (latest-wins by id) over a short window, then flush via _pushTemplatesBatched.
// saveTplSync still writes IndexedDB immediately, so nothing is lost if a flush is deferred —
// the load-time resync re-pushes anything still dirty (and a still-failing item keeps its badge).
// Coalescing queue (see saveQueue.mjs). flush pushes the collected dirty templates through the
// batched, contention-safe path; surface defaults ON for interactive flushes (one consolidated
// alert), OFF when a caller reports rejections itself (set-save). Latest-wins per id guarantees
// the LAST edit of a card always ships — the drop this could otherwise cause is covered in
// saveQueue.test.mjs with real timers.
const _tplQueue=createCoalescingQueue({delayMs:600,flush:(batch,opts)=>_pushTemplatesBatched(batch,{surface:(opts&&opts.surface)!==false})});
function scheduleTemplateSync(tpl){
  if(!_suiteToken||!tpl||tpl.id==null)return;
  _tplQueue.schedule(tpl.id,tpl);
}
function flushTemplateSync(opts){return _tplQueue.flush(opts);} // awaitable; opts={surface}

// Phase-1 sharding read-compat. A v2 payload (data.version===2) carries metadata-only
// templates: srcRef/fgRef instead of the inline image. We hydrate each back to the v1
// in-memory shape (populate src/foregroundMask) HERE, before returning — so every
// downstream reader (the merge, migrate(), the dirty-diff, serverSaveAll) is byte-
// identical to the v1 path and never meets a template without its image; the diff also
// stays consistent (serverData carries the same hydrated src the merged copy does, so
// nothing spuriously re-pushes). Local-first: an image already in IndexedDB is reused,
// so only images THIS device lacks are fetched. Entirely dormant until a member is
// migrated (no data.v2 exists in Phase 1).
// Phase-3 sharding WRITE state. `_isV2` is (re)set by _hydrateV2 on every load and gates the
// v2 write path (a v1 member never touches it). `_imgSig` records the last-known SERVER
// signature of each image, so an unchanged image (a pure reorder, a zone edit) is NOT
// re-uploaded — only genuinely new/edited images are POSTed. `_sig` is a cheap change-detector.
let _isV2=false;
const _imgSig={};
const _sig=s=>s?(s.length+":"+s.slice(0,24)+s.slice(-24)):null;
async function _fetchImage(id,field){
  try{
    const r=await fetch(`${API_BASE}?action=image&id=${encodeURIComponent(id)}&field=${field}`,{headers:{Authorization:`Bearer ${_suiteToken}`}});
    if(!r.ok)return null;
    return await r.text(); // the original data: URL string, stored opaquely server-side
  }catch(e){return null}
}
// Back up ONE image blob for a v2 member (raw data: URL string body). Returns a STATUS
// object, not a bare bool, because the caller must distinguish a 413 (image over the
// server's single-payload cap — PERMANENT until the member shrinks it) from a transient
// 5xx/network blip (retry-able). The old bare-bool collapsed both into "failed", so a v2
// member's oversized image was mislabeled "transient" and retried forever (see _toV2Metadata).
// { ok:true } on success · { ok:false, oversize:true, maxMB } on 413 · { ok:false } otherwise.
async function _uploadImage(id,field,val){
  try{
    const r=await fetch(`${API_BASE}?action=image&id=${encodeURIComponent(id)}&field=${field}`,{method:"POST",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"text/plain"},body:val||""});
    if(r.status===401||r.status===403){markAuthExpired();return {ok:false}}
    if(r.status===413){let maxMB=5;try{maxMB=(await r.json())?.maxMB||5}catch{}return {ok:false,oversize:true,maxMB}}
    return {ok:r.ok};
  }catch(e){return {ok:false}}
}
async function _hydrateV2(data){
  _isV2=!!(data&&data.version===2); // remember the doc format so the write path targets data.v2
  if(!data||data.version!==2||!Array.isArray(data.templates))return data;
  const templates=await Promise.all(data.templates.map(async t=>{
    if(!t||t.id==null)return t;
    if(t.src)return t; // already carries an image (defensive; v2 metadata shouldn't)
    const local=await dbGet(TPL,t.id).catch(()=>null); // local-first: reuse this device's copy
    const src=t.srcRef?((local&&local.src)||await _fetchImage(t.id,"src")):(local?local.src:null);
    const foregroundMask=t.fgRef?((local&&local.foregroundMask)||await _fetchImage(t.id,"fg")):null;
    _imgSig[t.id]={s:_sig(src),f:_sig(foregroundMask)}; // this image IS on the server → don't re-upload unless it changes
    const {srcRef,fgRef,...rest}=t;
    return {...rest,src:src||undefined,foregroundMask:foregroundMask||null};
  }));
  const {version,...rest}=data;
  return {...rest,templates};
}
async function serverLoad(){
  if(!_suiteToken)return null;
  try{
    const r=await fetch(`${API_BASE}?action=load`,{headers:{Authorization:`Bearer ${_suiteToken}`}});
    // 401/403 = the Suite token is expired/rejected. Latch expiry so the UI can say
    // "session expired, reopen" instead of returning null → silently-empty templates.
    if(r.status===401||r.status===403){markAuthExpired();return null;}
    if(!r.ok)return null;
    return await _hydrateV2(await r.json()); // v1 payload passes through untouched
  }catch(e){console.warn("Server load failed:",e);return null}
}

// Load-time sync push — DIRTY-CHECKED + BATCHED (see syncDiff.mjs for the why).
// `serverData` is the snapshot serverLoad just returned. We push ONLY what differs
// from it: a clean load (nothing changed) writes NOTHING. This replaces the old
// "re-push the whole library on every load as ~52 concurrent whole-doc writes"
// storm, which starved reads (blank Studio) and — being unserialized load-modify-
// write on one blob — could silently drop updates (strong consistency does not
// serialize concurrent RMW; a stale copy can clobber a concurrent one).
function serverSaveAll(templates,profiles,globalGroups,serverData){
  if(!_suiteToken)return;
  // If the server read FAILED (serverData==null) we can't diff — do NOT write
  // blind over an unread doc (that risks clobbering server-only data we never
  // saw). The next successful load retries.
  if(!serverData)return;
  const srvT=Array.isArray(serverData.templates)?serverData.templates:[];
  const srvP=Array.isArray(serverData.profiles)?serverData.profiles:[];
  const srvGG=Array.isArray(serverData.globalGroups)?serverData.globalGroups:[];
  // Profiles + groups are single small whole-array writes (no images) — send only
  // when they actually changed. Order-sensitive so a member's reorder still persists.
  if(!sameDeep(profiles,srvP))serverSaveProfiles(profiles);
  if(!sameDeep(globalGroups,srvGG))serverSaveGroups(globalGroups);
  // Templates: only the missing/changed ones, sent as ONE (or a few size-bounded,
  // SEQUENTIAL) batch writes — never N concurrent whole-doc writes.
  const dirty=diffTemplates(templates,srvT);
  if(dirty.length)_pushTemplatesBatched(dirty);
}

// Push the dirty templates via the batch endpoint: one whole-doc load-modify-write
// per chunk, chunks bounded under the server payload cap and sent SEQUENTIALLY (no
// concurrency = no self-collision, and a far smaller window for an external writer
// — Speedrun/another tab — to be clobbered). The bulk path tracks rejections into
// _failedServerSaves (which drives the per-card "Not backed up" badge + the interactive
// path) but does NOT pop any modal on load — the badge is the load-time signal.
// v2 pre-step: turn dirty templates (v1 shape, with inline src/foregroundMask) into
// METADATA templates (srcRef/fgRef, no image bytes) after backing up any NEW/CHANGED image
// to its own blob. An image whose signature still matches the server is left alone (a reorder
// re-uploads nothing). If an image upload FAILS we do NOT emit its metadata — writing a
// srcRef to a blob that isn't there would orphan the image; instead we flag the template
// transient so the load-time resync retries it, image included. Returns metadata to send.
async function _toV2Metadata(dirty,rejects){
  const out=[];
  for(const t of dirty){
    if(!t||t.id==null)continue;
    const id=String(t.id),cur=_imgSig[id]||{};let okImg=true,over=null;
    if(t.src&&_sig(t.src)!==cur.s){ const r=await _uploadImage(id,"src",t.src); if(r.ok)_imgSig[id]={...(_imgSig[id]||{}),s:_sig(t.src)}; else{okImg=false;if(r.oversize)over=r} }
    if(okImg&&t.foregroundMask&&_sig(t.foregroundMask)!==cur.f){ const r=await _uploadImage(id,"fg",t.foregroundMask); if(r.ok)_imgSig[id]={...(_imgSig[id]||{}),f:_sig(t.foregroundMask)}; else{okImg=false;if(r.oversize)over=r} }
    if(!okImg){
      // A 413 on the image blob is PERMANENT (the image exceeds the server's single-payload
      // cap) — classify it 'oversize', exactly as the v1 save-templates 413 handler below does,
      // so the member gets the honest "too large, re-export smaller" signal + an oversize-coloured
      // badge instead of a forever-retried "transient". This was THE bug for migrated (v2) members:
      // every image-upload failure was blanket-marked "transient", so an oversized mockup looped
      // silently and re-uploading the SAME file never cleared it. Non-413 failures stay transient
      // (genuine 5xx/contention → retried by the next load-time resync).
      if(over){_failedServerSaves.set(id,"oversize");_reportOversize(t,over.maxMB||5);if(rejects)rejects.push({tpl:t,reason:"oversize"})}
      else _failedServerSaves.set(id,"transient");
      continue;
    }
    const {src,foregroundMask,...meta}=t;
    out.push({...meta,srcRef:t.src?`img/${id}/src`:undefined,fgRef:t.foregroundMask?`img/${id}/fg`:null});
  }
  return out;
}
async function _pushTemplatesBatched(dirty,{surface=false}={}){
  if(!_suiteToken||!dirty||!dirty.length)return;
  // v2 members: back up images to blobs + reduce to metadata BEFORE the save-templates
  // write, so the doc the edge parses carries no image bytes. v1 members are untouched.
  const rejects=[]; // GENUINE rejections {tpl,reason:'oversize'|'cap'} for one consolidated interactive alert — v2 image-blob 413s collect here too (via _toV2Metadata)
  // v2: an all-oversize dirty set reduces to zero metadata rows; still surface the honest
  // "too large" message (and the badges are already set) before the early return.
  if(_isV2){dirty=await _toV2Metadata(dirty,rejects);_notifyFailedSaves();if(!dirty.length){if(surface&&rejects.length)_emitBatchSaveError(rejects);return}}
  const MAX_CHUNK_BYTES=4*1024*1024; // stay under the server's MAX_SINGLE_PAYLOAD_MB (5)
  const chunks=chunkBySize(dirty,MAX_CHUNK_BYTES,t=>byteLen(JSON.stringify(t))+1);
  for(const templates of chunks){
    try{
      const res=await fetch(`${API_BASE}?action=save-templates`,{method:"POST",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify({templates})});
      if(res.status===401||res.status===403){markAuthExpired();return} // auth, not a cap rejection
      if(res.status===413){ // payload too large — PERMANENT until the mockup is shrunk. Was silent on this path (data-loss); make it honest + logged, naming the culprit(s). The card badge shows it too.
        let maxMB=5;try{maxMB=(await res.json())?.maxMB||5}catch{}
        templates.forEach(t=>{if(!t||t.id==null)return;const over=templateSizeInfo(t).totalBytes>maxMB*1048576;_failedServerSaves.set(String(t.id),over?"oversize":"transient");if(over){_reportOversize(t,maxMB);rejects.push({tpl:t,reason:"oversize"})}}); // the true culprit(s) → oversize; the rest of the chunk → transient (they re-chunk + retry)
        _notifyFailedSaves();
        continue;
      }
      if(!res.ok){templates.forEach(t=>{if(t&&t.id!=null)_failedServerSaves.set(String(t.id),"transient")});_notifyFailedSaves();continue} // transient (contention/5xx) → keep for retry, no alert
      let data={};try{data=await res.json()}catch{}
      const skipped=new Map((Array.isArray(data.skipped)?data.skipped:[]).filter(s=>s&&s.reason!=="deleted").map(s=>[String(s.id),s.reason])); // server skips: reason 'cap' (limit) — 'deleted' filtered out (not a failure)
      templates.forEach(t=>{if(!t||t.id==null)return;const k=String(t.id);if(skipped.has(k)){const reason=skipped.get(k)==="cap"?"cap":"transient";_failedServerSaves.set(k,reason);rejects.push({tpl:t,reason})}else _failedServerSaves.delete(k)});
      _notifyFailedSaves();
    }catch(e){console.warn("Server batch save failed (network):",e)} // network blip ≠ rejection; keep state, retried next load
  }
  if(surface&&rejects.length)_emitBatchSaveError(rejects); // ONE message for the whole flush, never per-card
}

function serverDeleteTemplate(id){
  if(!_suiteToken)return;
  fetch(`${API_BASE}?action=delete-template&id=${encodeURIComponent(id)}`,{method:"DELETE",headers:{Authorization:`Bearer ${_suiteToken}`}}).catch(e=>console.warn("Server delete failed:",e));
}

// Batch delete — remove MANY template ids in ONE server-side load-modify-write.
// Replaces the per-id delete fan-out (N concurrent whole-doc deletes), which not
// only 502'd under contention but — being unserialized read-modify-write — could
// clobber each other's deletedIds tombstone set and RESURRECT deleted mockups (a
// stale copy writing back a deletedIds without the just-added id). One request =
// one write = every tombstone recorded + every id filtered atomically. Resolves
// true only on ACK; on failure the local tombstones stay and the load-time retry
// re-issues the batch, so a deleted mockup can never come back. Single
// delete-template above is kept for single-item clicks + older/cached clients.
function serverDeleteTemplatesMany(ids){
  if(!_suiteToken||!ids||!ids.length)return Promise.resolve(false);
  return fetch(`${API_BASE}?action=delete-templates`,{method:"DELETE",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify({ids})}).then(r=>r.ok).catch(e=>{console.warn("Server batch delete failed:",e);return false});
}

function serverSaveProfiles(profiles){
  if(!_suiteToken)return;
  fetch(`${API_BASE}?action=save-profiles`,{method:"POST",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify({profiles})}).catch(e=>console.warn("Server save profiles failed:",e));
}

// Coalesced profiles sync — same rationale as scheduleTemplateSync. The whole-profiles-array
// write is small (no images), but rapid set edits (attach video, colour links, video-delete
// cleanup) each fired one whole-doc write that contended with the template writes above. Each
// caller passes the COMPLETE next array, so latest-wins is correct: the last write in a burst
// carries the freshest state. The load-time resync (serverSaveAll) stays on the direct call —
// it's diffed and fires at most once per load.
// Single-slot coalescing (one key): each caller passes the COMPLETE next array, so the last
// write in a burst carries the freshest state. batch is [latestArray]; take the last.
const _profQueue=createCoalescingQueue({delayMs:600,flush:(batch)=>{const p=batch[batch.length-1];if(p)serverSaveProfiles(p);}});
function scheduleProfilesSync(profiles){
  if(!_suiteToken)return;
  _profQueue.schedule("profiles",profiles);
}
function flushProfilesSync(){return _profQueue.flush();}

// Single-profile upsert (brief 5.17 §2e belt) — add ONE profile without re-uploading the
// whole array. Used by the Suite's appendStudioSet so it never re-persists profiles it
// merely read back (which resurrected locally-deleted sets).
function serverSaveProfile(profile){
  if(!_suiteToken)return;
  fetch(`${API_BASE}?action=save-profile`,{method:"POST",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify(profile)}).catch(e=>console.warn("Server save profile failed:",e));
}

// Authoritative server-side profile delete (brief 5.17 §2). Resolves true only when the
// server ACKs the removal; false on any failure/no-token so the caller can stay honest
// (the tombstone keeps the id and the load merge retries the delete on the next authed load).
function serverDeleteProfile(id){
  if(!_suiteToken)return Promise.resolve(false);
  return fetch(`${API_BASE}?action=delete-profile&id=${encodeURIComponent(id)}`,{method:"DELETE",headers:{Authorization:`Bearer ${_suiteToken}`}}).then(r=>r.ok).catch(e=>{console.warn("Server delete profile failed:",e);return false});
}
// Batch profile delete (mirrors serverDeleteTemplatesMany) — one write for many
// sets. Used by the load-time retry so a backlog of un-synced set deletes drains
// in a single request instead of one-per-id.
function serverDeleteProfilesMany(ids){
  if(!_suiteToken||!ids||!ids.length)return Promise.resolve(false);
  return fetch(`${API_BASE}?action=delete-profiles`,{method:"DELETE",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify({ids})}).then(r=>r.ok).catch(e=>{console.warn("Server batch delete profiles failed:",e);return false});
}
function hasSyncToken(){return !!_suiteToken}

function serverSaveGroups(globalGroups){
  if(!_suiteToken)return;
  fetch(`${API_BASE}?action=save-groups`,{method:"POST",headers:{Authorization:`Bearer ${_suiteToken}`,"Content-Type":"application/json"},body:JSON.stringify({globalGroups})}).catch(e=>console.warn("Server save groups failed:",e));
}

/* ── Mockup Bank API (community proven-mockup bank) ──
   Separate edge function + Blobs stores from the per-user mockup-store.
   Reuses the same suite-JWT bearer auth. Images are served by a gated
   endpoint; <img> can't send Authorization headers, so the image URL
   carries the token as a query param (same token already in the iframe). */
const BANK_API="/api/mockup-bank";
async function bankFetch(qs,opts={}){
  const headers={...(opts.headers||{})};
  if(_suiteToken)headers.Authorization=`Bearer ${_suiteToken}`;
  const r=await fetch(`${BANK_API}?${qs}`,{...opts,headers});
  let data=null;try{data=await r.json()}catch{}
  if(!r.ok)throw new Error((data&&data.error)||`Request failed (${r.status})`);
  return data;
}
function bankSubmit(payload){return bankFetch("action=submit",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)})}
function bankListPending(){return bankFetch("action=listPending")}
function bankListPublished(){return bankFetch("action=listPublished")}
function bankListBrowse(){return bankFetch("action=listBrowse")}
function bankApprove(id){return bankFetch(`action=approve&id=${encodeURIComponent(id)}`,{method:"POST"})}
function bankReject(id){return bankFetch(`action=reject&id=${encodeURIComponent(id)}`,{method:"POST"})}
function bankProvenDelete(id,scope){return bankFetch(`action=provenDelete&id=${encodeURIComponent(id)}${scope?`&scope=${scope}`:""}`,{method:"POST"})}
function bankImageUrl(scope,id,kind,i){return `${BANK_API}?action=image&scope=${scope}&id=${encodeURIComponent(id)}&kind=${kind}&i=${i}&token=${encodeURIComponent(_suiteToken||"")}`}
/* Personal collections */
function bankPersonalCreate(payload){return bankFetch("action=personalCreate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)})}
function bankPersonalListMine(){return bankFetch("action=personalListMine")}
function bankPersonalListPublic(){return bankFetch("action=personalListPublic")}
function bankPersonalSetVisibility(id,visibility){return bankFetch(`action=personalSetVisibility&id=${encodeURIComponent(id)}&visibility=${visibility}`,{method:"POST"})}
function bankPersonalDelete(id){return bankFetch(`action=personalDelete&id=${encodeURIComponent(id)}`,{method:"POST"})}
function bankPersonalPromote(id){return bankFetch(`action=personalPromote&id=${encodeURIComponent(id)}`,{method:"POST"})}
function bankPersonalImageUrl(id,kind){return `${BANK_API}?action=personalImage&id=${encodeURIComponent(id)}&kind=${kind}&token=${encodeURIComponent(_suiteToken||"")}`}
/* Community archive (shared, admin-curated) */
function bankArchiveCreate(payload){return bankFetch("action=archiveCreate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)})}
function bankArchiveList(){return bankFetch("action=archiveList")}
function bankArchiveUpdate(id,payload){return bankFetch(`action=archiveUpdate&id=${encodeURIComponent(id)}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)})}
function bankArchiveDelete(id){return bankFetch(`action=archiveDelete&id=${encodeURIComponent(id)}`,{method:"POST"})}
function bankArchiveImageUrl(id,i,kind){return `${BANK_API}?action=archiveImage&id=${encodeURIComponent(id)}&i=${i}&kind=${kind}&token=${encodeURIComponent(_suiteToken||"")}`}

/* ── Listing Video LIBRARY API (mockup-studio-videos Blobs store) ──
   Chunked upload: begin (budget pre-check) → upload-part × N (raw bytes) →
   finalize (manifest + index). Read: list (manifests incl. thumbs), part (raw
   bytes for reassembly). Same suite-JWT bearer as mockup-store. */
const VIDEO_API="/api/mockup-videos";
function videoAuthHeaders(extra){const h={...(extra||{})};if(_suiteToken)h.Authorization=`Bearer ${_suiteToken}`;return h}
async function videoList(){const r=await fetch(`${VIDEO_API}?action=list`,{headers:videoAuthHeaders()});if(!r.ok)throw new Error(`list failed (${r.status})`);return r.json()}
async function videoBegin(meta){const r=await fetch(`${VIDEO_API}?action=begin`,{method:"POST",headers:videoAuthHeaders({"Content-Type":"application/json"}),body:JSON.stringify(meta)});const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`begin failed (${r.status})`);return d}
async function videoUploadPart(videoId,part,bytes){const r=await fetch(`${VIDEO_API}?action=upload-part&videoId=${encodeURIComponent(videoId)}&part=${part}`,{method:"POST",headers:videoAuthHeaders({"Content-Type":"application/octet-stream"}),body:bytes});const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`part ${part} failed (${r.status})`);return d}
async function videoFinalize(manifest){const r=await fetch(`${VIDEO_API}?action=finalize`,{method:"POST",headers:videoAuthHeaders({"Content-Type":"application/json"}),body:JSON.stringify(manifest)});const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`finalize failed (${r.status})`);return d}
async function videoDelete(videoId){const r=await fetch(`${VIDEO_API}?action=delete&videoId=${encodeURIComponent(videoId)}`,{method:"DELETE",headers:videoAuthHeaders()});const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`delete failed (${r.status})`);return d}
async function videoDeleteMany(ids){const r=await fetch(`${VIDEO_API}?action=delete-many`,{method:"DELETE",headers:videoAuthHeaders({"Content-Type":"application/json"}),body:JSON.stringify({ids})});const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`delete-many failed (${r.status})`);return d}
async function videoFetchPart(videoId,part){const r=await fetch(`${VIDEO_API}?action=part&videoId=${encodeURIComponent(videoId)}&part=${part}`,{headers:videoAuthHeaders()});if(!r.ok)throw new Error(`part ${part} fetch failed (${r.status})`);return r.arrayBuffer()}
// Reassemble a library video's bytes from its server parts into one Blob.
async function videoReassembleBlob(manifest){
  const parts=[];
  for(let i=0;i<(manifest.parts||0);i++)parts.push(await videoFetchPart(manifest.videoId,i));
  // MIME from extension so <video> and the Etsy leg get a sensible type.
  const ext=(manifest.file_name||"").toLowerCase().match(/\.([a-z0-9]+)$/);
  const mime=ext?({mp4:"video/mp4",mov:"video/quicktime",avi:"video/x-msvideo",webm:"video/webm",mpeg:"video/mpeg",mpg:"video/mpeg"}[ext[1]]||"video/mp4"):"video/mp4";
  return new Blob(parts,{type:mime});
}
function blobToDataUrl(blob){return new Promise((res,rej)=>{const rd=new FileReader();rd.onload=()=>res(rd.result);rd.onerror=()=>rej(rd.error);rd.readAsDataURL(blob)})}

/* Migrate old templates */
function migrate(tpl){
  if(tpl.smartObjects)return{...tpl,noDesign:tpl.noDesign||false,sortOrder:tpl.sortOrder??999};
  if(!tpl.smartObject)return{...tpl,smartObjects:[],groups:[{id:"g1",name:"Group 1",color:gc(0)}],noDesign:tpl.noDesign||false,sortOrder:tpl.sortOrder??999};
  return{...tpl,smartObjects:[{...tpl.smartObject,id:`so_${Date.now()}`,group:"g1"}],groups:[{id:"g1",name:"Group 1",color:gc(0)}],noDesign:false,sortOrder:999};
}

const STEPS=[{id:"upload",label:"Upload",icon:"①"},{id:"configure",label:"Smart Objects",icon:"②"},{id:"generate",label:"Generate",icon:"③"}];

/* Example design shown inside SO zones when no designPreview is available */
const EXAMPLE_DESIGN_SVG=`data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4500 5400"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="%236366f1"/><stop offset="100%" stop-color="%23ec4899"/></linearGradient></defs><rect width="4500" height="5400" fill="url(%23bg)"/><text x="2250" y="2400" text-anchor="middle" fill="white" font-size="420" font-weight="bold" font-family="sans-serif">DESIGN</text><text x="2250" y="3000" text-anchor="middle" fill="rgba(255,255,255,0.6)" font-size="200" font-family="sans-serif">Example Preview</text><rect x="800" y="3400" width="2900" height="400" rx="200" fill="rgba(255,255,255,0.15)"/><text x="2250" y="3650" text-anchor="middle" fill="white" font-size="160" font-family="sans-serif">4500 × 5400</text></svg>')}`;

function ThemeToggle(){const{isDark,toggle}=useTheme();const c=t(isDark);return<button onClick={toggle} style={{width:34,height:34,borderRadius:9,border:`1px solid ${c.border}`,background:c.bgCard,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14}}>{isDark?"☀️":"🌙"}</button>}

/* Tooltip */
function Tip({id,text,openTip,setOpenTip,placement="bottom",align="right"}){
  const{isDark}=useTheme();const isOpen=openTip===id;
  // Horizontal anchor: "right" (default, popup extends LEFT — used near
  // page-right edges) vs "left" (popup extends RIGHT — used near page-left
  // edges where the right-anchored variant would clip off the iframe).
  const horiz=align==="left"?{left:0}:{right:0};
  return<span style={{position:"relative",display:"inline-flex",alignItems:"center",marginLeft:4}}>
    <button onClick={e=>{e.stopPropagation();setOpenTip(isOpen?null:id)}} className="mg-tip-trigger" style={{width:16,height:16,borderRadius:"50%",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:`1px solid ${isDark?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.25)"}`,background:"transparent",color:isDark?"rgba(255,255,255,0.5)":"rgba(0,0,0,0.5)",opacity:isOpen?1:0.6,fontWeight:700}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=isOpen?"1":"0.6"}>?</button>
    {isOpen&&<div style={{position:"absolute",zIndex:50,...horiz,...(placement==="bottom"?{top:"calc(100% + 8px)"}:{bottom:"calc(100% + 8px)"}),width:260,maxWidth:"80vw",borderRadius:12,background:"#1e293b",color:"#f1f5f9",fontSize:11,lineHeight:1.5,padding:"10px 14px",boxShadow:"0 8px 30px rgba(0,0,0,0.4)",pointerEvents:"none",whiteSpace:"normal",textAlign:"left"}}>{text}</div>}
  </span>;
}

/* MockupCard */
function MockupCard({mockup,onRemove,onConfigure,isConfigured,selectable,selected,onToggleSelect,extraButtons,sortOrder,onSortChange,syncFailed}){
  const{isDark}=useTheme();const c=t(isDark);
  const m=migrate(mockup);const sos=m.smartObjects;const grps=m.groups||[];
  return<div style={{position:"relative",borderRadius:12,overflow:"hidden",background:c.bgCard,border:`2px solid ${selected?"#6366f1":isConfigured?"#34d399":c.border}`,transition:"all 0.25s",cursor:onConfigure||selectable?"pointer":"default",transform:selected?"scale(0.97)":"scale(1)"}} onClick={selectable?onToggleSelect:onConfigure}>
    <div style={{position:"relative",paddingTop:"100%",background:isDark?"#0f0f1a":"#e2e8f0"}}>
      <img className="pb-img" src={mockup.src} alt={mockup.name} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}} />
      {m.noDesign&&<div style={{position:"absolute",top:7,left:7,background:"#f59e0b",color:"#0f0f1a",borderRadius:14,padding:"2px 8px",fontSize:9,fontWeight:700,zIndex:2}}>No Design</div>}
      {isConfigured&&!selectable&&!m.noDesign&&<div style={{position:"absolute",top:7,right:7,background:"#34d399",color:"#0f0f1a",borderRadius:14,padding:"2px 8px",fontSize:9,fontWeight:700,zIndex:2}}>{sos.length>1?`${sos.length} zones`:"✓ Ready"}</div>}
      {selectable&&<div style={{position:"absolute",top:7,right:7,width:22,height:22,borderRadius:6,background:selected?"#6366f1":"rgba(255,255,255,0.1)",border:`2px solid ${selected?"#6366f1":"rgba(255,255,255,0.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>{selected&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}</div>}
      {sos.map((so,i)=>{const g=grps.find(x=>x.id===so.group);return<div key={so.id||i} style={{position:"absolute",left:`${so.x}%`,top:`${so.y}%`,width:`${so.w}%`,height:`${so.h}%`,border:`2px dashed ${g?.color||"#6366f1"}`,background:`${g?.color||"#6366f1"}22`,borderRadius:2,pointerEvents:"none",transform:so.rotation?`rotate(${so.rotation}deg)`:undefined,transformOrigin:"center"}}/>})}
      {syncFailed&&(()=>{
        // Reason-accurate badge (syncFailed carries the reason, not just a bool). The three states
        // are genuinely different actions for the member, so they must NOT share one "usually too
        // large" string: oversize = re-export smaller (re-uploading the same file is futile); cap =
        // delete to free room; transient = nothing to do, it retries itself. Collapsing them was the
        // repeat-offender defect (a wrong message sending people down the wrong path).
        const over=syncFailed==="oversize",cap=syncFailed==="cap";
        const label=over?"⚠ Too large to back up":cap?"⚠ Library full":"⟳ Not backed up yet";
        const tip=over
          // It's the FILE SIZE that matters (~3.5 MB), not the pixel dimensions — the studio stores
          // mockup photos at full resolution (only DESIGNS get downsampled), so a very detailed image
          // can still fail even at 3000 px. Lead with the size; give 3000 px as the usual-case guide.
          ?"This mockup's image file is too large to back up (the limit is about 3.5 MB per file), so it lives only on THIS device and won't appear on your other devices or in sets. Re-export it SMALLER and upload the smaller file — save as JPEG (not PNG), around 3000 px, and if it's still over ~3.5 MB drop the size a bit more. Re-uploading the same file won't help."
          :cap
          ?"Your mockup library is full, so this couldn't be backed up — delete some unused mockups to make room, then it'll back up on its own."
          :"Couldn't reach the cloud on the last try, so this is saved on THIS device for now. The studio retries automatically each time it loads — nothing you need to do.";
        return <div title={tip} style={{position:"absolute",bottom:7,left:7,background:over||cap?"#ef4444":"#f59e0b",color:"white",borderRadius:14,padding:"2px 8px",fontSize:9,fontWeight:700,zIndex:2,display:"flex",alignItems:"center",gap:3,pointerEvents:"auto"}}>{label}</div>;
      })()}
    </div>
    <div style={{padding:"9px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",minHeight:40}}>
      <div style={{display:"flex",alignItems:"center",gap:5,overflow:"hidden",flex:1}}>
        {onSortChange!=null&&<select value={sortOrder??999} onChange={e=>{e.stopPropagation();onSortChange(mockup.id,parseInt(e.target.value))}} onClick={e=>e.stopPropagation()} style={{width:38,padding:"3px 2px",borderRadius:5,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:11,textAlign:"center",outline:"none",cursor:"pointer",fontWeight:600}} title="Listing order">{Array.from({length:20},(_,i)=>i+1).map(n=><option key={n} value={n}>{n}</option>)}<option value={999}>—</option></select>}
        <span className="pb-text" style={{color:c.text,fontSize:11,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{mockup.name}</span>
      </div>
      <div style={{display:"flex",gap:3,alignItems:"center"}}>
        {extraButtons}
        {onRemove&&<button onClick={e=>{e.stopPropagation();onRemove(mockup.id)}} style={{background:"rgba(239,68,68,0.12)",border:"none",color:"#f87171",borderRadius:5,padding:"2px 7px",cursor:"pointer",fontSize:9,fontWeight:600}}>Remove</button>}
      </div>
    </div>
  </div>;
}

/* Library listing-video card. A video is a first-class library item: it shows a
   first-frame thumbnail, a 🎥 badge, duration + size, and the explicit "your
   design is NOT applied — it publishes as-is" note. Selectable INTO a set like a
   template (single-select: one video per set). Bytes load on demand for preview. */
function VideoCard({v,selected,onToggle,onDelete,getUrl}){
  const{isDark}=useTheme();const c=t(isDark);
  const[url,setUrl]=useState(null);const[loading,setLoading]=useState(false);
  const mb=(Number(v.sizeBytes||0)/1048576).toFixed(1);
  const dur=v.durationSec!=null?`${Number(v.durationSec).toFixed(1)}s`:"length unverified";
  const preview=async()=>{if(url||loading)return;setLoading(true);try{const u=await getUrl(v.videoId);setUrl(u)}catch(e){}finally{setLoading(false)}};
  // Body click toggles SET-MEMBERSHIP selection (indigo ✓ top-right). Deletion is
  // a SEPARATE per-card control (🗑, bottom-right) — intentionally NOT the same
  // selection, since deleting a video is permanent and no-backup; the handler
  // confirms and warns about any set using it first.
  const borderColor=selected?"#6366f1":"#8b5cf6";
  return<div style={{position:"relative",borderRadius:12,overflow:"hidden",background:c.bgCard,border:`2px solid ${borderColor}`,transition:"all 0.25s",cursor:"pointer",transform:selected?"scale(0.97)":"scale(1)"}} onClick={onToggle}>
    <div style={{position:"relative",paddingTop:"100%",background:"#000"}}>
      {url
        ? <video className="pb-img" src={url} controls muted preload="metadata" onClick={e=>e.stopPropagation()} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>
        : v.thumb
          ? <img className="pb-img" src={v.thumb} alt={v.file_name} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain",opacity:0.9}}/>
          : <div style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:34}}>🎥</div>}
      <div style={{position:"absolute",top:7,left:7,background:"#8b5cf6",color:"white",borderRadius:14,padding:"2px 8px",fontSize:9,fontWeight:700,zIndex:2}}>🎥 Video</div>
      <div style={{position:"absolute",top:7,right:7,width:22,height:22,borderRadius:6,background:selected?"#6366f1":"rgba(255,255,255,0.15)",border:`2px solid ${selected?"#6366f1":"rgba(255,255,255,0.35)"}`,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>{selected&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}</div>
      {!url&&<button onClick={e=>{e.stopPropagation();preview()}} title="Preview" style={{position:"absolute",bottom:7,left:7,background:"rgba(0,0,0,0.6)",border:"none",color:"white",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:10,fontWeight:700,zIndex:2}}>{loading?"…":"▶ Preview"}</button>}
      {onDelete&&<button onClick={e=>{e.stopPropagation();onDelete()}} title="Delete this video permanently" style={{position:"absolute",bottom:7,right:7,background:"rgba(239,68,68,0.85)",border:"none",color:"white",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:10,fontWeight:700,zIndex:2}}>🗑</button>}
    </div>
    <div style={{padding:"9px 10px"}}>
      <span className="pb-text" style={{display:"block",color:c.text,fontSize:11,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.file_name}</span>
      <div style={{color:c.textMuted,fontSize:9,marginTop:3}}>{mb} MB · {dur}</div>
      <div style={{color:c.textMuted,fontSize:8,marginTop:4,lineHeight:1.4}}>Your design is <strong>not</strong> applied — it uploads to Etsy as-is. Add it to a set to publish it with every listing.</div>
    </div>
  </div>;
}

/* ── F9/F10: honest zone dimensions ─────────────────────────────────────
   Zones store x/y/w/h as PERCENT of the template image (see the bake:
   (so.w/100)*W). The SO editor's bottom metrics bar already exposes those
   X/Y/W/H values as editable % number fields for the selected zone — that %
   bar (NOT any cm field; there is no cm anywhere in this app) is what the
   member was mis-reading. F10 adds W/H **px** fields to that same bar, two-way
   synced with the % fields (px = % ÷100 × template natural px). Storage stays
   percent; px is a view/input layer only.
   The renderer CONTAINS the design (getContainedRect) — the design keeps its
   own aspect and is centered/letterboxed inside the zone, never stretched — so
   there is NO distortion to fear. What matters is the ASPECT: a 7:8 zone
   matches Printify print files (4200×4800) so the design fills it with no
   empty margins. */
const PRINT_AR=7/8; // Comfort Colors / Printify print area = 4200×4800 = 7:8
function gcdInt(a,b){a=Math.round(a);b=Math.round(b);while(b){const tmp=a%b;a=b;b=tmp}return a||1}
function trimNum(n){return String(Math.round(n*100)/100)} // 1.10→"1.1", 1.333→"1.33"
function prettyAspect(w,h){
  const r=w/h;
  for(const[a,b]of[[1,1],[7,8],[8,7],[4,5],[5,4],[3,4],[4,3],[2,3],[3,2],[16,9],[9,16]])if(Math.abs(r-a/b)<0.02)return`${a}:${b}`;
  const g=gcdInt(w,h),A=Math.round(w)/g,B=Math.round(h)/g;
  if(A<=40&&B<=40)return`${A}:${B}`;
  return r>=1?`${trimNum(r)}:1`:`1:${trimNum(1/r)}`;
}
function matchesPrintAR(w,h){return Math.abs(w/h-PRINT_AR)<0.02}
/* F12: aspect-lock helper. Given w/h (percent) that already satisfy the locked
   ratio, cap them uniformly so both fit within availW/availH — NEVER breaks the
   ratio to satisfy the clamp, it just scales the pair down. */
function fitRatio(w,h,availW,availH){
  let s=1;
  if(w>availW&&availW>0)s=Math.min(s,availW/w);
  if(h>availH&&availH>0)s=Math.min(s,availH/h);
  return{w:w*s,h:h*s};
}

/* ═══════ LINKED PLACEMENT EDITOR — position a design across a whole set at once ═══════
   Model B (linked, shared delta): the member drags / resizes / rotates the design on
   ANY one mockup; every participating mockup's matching-group zone receives the SAME
   transform from its OWN baseline, so each design moves the same distance, scales by
   the same factor and rotates by the same amount — staying in the same place relative
   to its own shirt. All previews are live CSS overlays (no canvas), so every mockup
   in the grid updates together as you drag. Per-mockup opt-out; anything that can't
   take a transform (no zone for the group, or a perspective zone for rotation) is
   skipped and reported, never mangled. See linkedDelta.mjs for the pure engine. */
function LinkedPlacementEditor({mockups,globalGroups,designPreview,onApply,onCancel,scope,onScopeChange,regenCountFor,allowPermanent=true}){
  const{isDark}=useTheme();const c=t(isDark);
  const migd=mockups.map(migrate);
  // Groups that actually have a zone on at least one mockup, name-resolved.
  const groupOpts=(()=>{
    const counts={};
    migd.forEach(m=>(m.smartObjects||[]).forEach(s=>{if(s.group)counts[s.group]=(counts[s.group]||0)+1}));
    return Object.keys(counts)
      .map(id=>({id,count:counts[id],name:(globalGroups.find(g=>g.id===id)||{}).name||id,color:(globalGroups.find(g=>g.id===id)||{}).color||"#6366f1"}))
      .sort((a,b)=>b.count-a.count);
  })();
  // "All groups" (ALL) applies the shared delta to every group's zone at once —
  // the default when a set spans more than one group, since members running both
  // light and dark usually want to shift everything together. Single-group sets
  // just select that one group (no picker shown).
  const ALL="__all__";
  const[group,setGroup]=useState(groupOpts.length>1?ALL:(groupOpts[0]?groupOpts[0].id:null));
  // The group ids this gesture touches: every present group in "All groups" mode,
  // otherwise just the selected one.
  const activeGroupIds=group===ALL?groupOpts.map(g=>g.id):(group?[group]:[]);
  // Working geometry (x/y/w/h/corners) per mockup — pointer gestures mutate this.
  const[work,setWork]=useState(()=>{const o={};migd.forEach(m=>{o[m.id]=(m.smartObjects||[]).map(s=>({...s,corners:s.corners?{tl:{...s.corners.tl},tr:{...s.corners.tr},br:{...s.corners.br},bl:{...s.corners.bl}}:undefined}))});return o;});
  // Rotation is a single shared delta (degrees) applied on top of each zone's
  // ORIGINAL rotation — kept separate from geometry so the slider and the drag
  // gestures never fight over the same fields. Baked into zones only at Apply.
  const origRot=useRef({});if(Object.keys(origRot.current).length===0){migd.forEach(m=>{(m.smartObjects||[]).forEach(s=>{origRot.current[`${m.id}:${s.id}`]=s.rotation||0})})}
  const[rotDelta,setRotDelta]=useState(0);
  const[optOut,setOptOut]=useState(new Set());
  const[aspects,setAspects]=useState({}); // id → height/width*100
  const panelRefs=useRef({});
  const gRef=useRef(null); // live gesture {kind,driverId,base:{id→zone},start:{x,y},center,halfDiag}

  useEffect(()=>{let live=true;migd.forEach(m=>{const img=new Image();img.onload=()=>{if(live)setAspects(p=>({...p,[m.id]:img.naturalHeight/img.naturalWidth*100}))};img.src=m.src});return()=>{live=false}},[]);// eslint-disable-line

  // Design preview for a group's zone (each group shows its own artwork).
  const dsgFor=(gid)=>(gid&&designPreview&&designPreview[gid])||EXAMPLE_DESIGN_SVG;
  // The zone(s) this gesture targets on a mockup: one per active group (matchZone
  // picks the first zone of a group, as in single-group mode). "All groups"
  // returns every present group's zone; single mode returns the one (or none).
  const zonesOf=(id)=>activeGroupIds.map(gid=>matchZone({smartObjects:work[id]},gid)).filter(Boolean);
  // A mockup participates if it isn't opted out AND has at least one active zone.
  const participating=(id)=>!optOut.has(id)&&zonesOf(id).length>0;
  // Skip-and-report, evaluated across ALL active groups. In "All groups" mode a
  // mockup with a zone in one group but not another is NOT falsely flagged "no
  // zone" — it's only "no zone" when it has none in any active group (the skip
  // is per-group, not per-mockup). Perspective zones still can't be rotated.
  const skips=migd.map(m=>{
    const zs=zonesOf(m.id);
    if(zs.length===0)return{id:m.id,name:m.name,reason:"no-zone"};
    if(rotDelta!==0&&!optOut.has(m.id)&&zs.some(z=>z.corners))return{id:m.id,name:m.name,reason:"perspective-rotate"};
    return null;
  }).filter(Boolean);

  const pct=(id,e)=>{const el=panelRefs.current[id];if(!el)return{x:0,y:0};const r=el.getBoundingClientRect();return{x:((e.clientX-r.left)/r.width)*100,y:((e.clientY-r.top)/r.height)*100}};
  const zoneCenter=(z)=>z.corners?(()=>{const xs=[z.corners.tl.x,z.corners.tr.x,z.corners.br.x,z.corners.bl.x],ys=[z.corners.tl.y,z.corners.tr.y,z.corners.br.y,z.corners.bl.y];return{x:(Math.min(...xs)+Math.max(...xs))/2,y:(Math.min(...ys)+Math.max(...ys))/2}})():{x:z.x+z.w/2,y:z.y+z.h/2};

  // Snapshot every participating mockup's active zone(s) as the gesture baseline
  // (an array per mockup — one entry per active group).
  const snapshotBase=()=>{const b={};migd.forEach(m=>{if(!participating(m.id))return;const zs=zonesOf(m.id).map(z=>({...z,corners:z.corners?JSON.parse(JSON.stringify(z.corners)):undefined}));if(zs.length)b[m.id]=zs});return b;};
  const putZone=(sos,z)=>sos.map(s=>s.id===z.id?z:s);

  const beginMove=(driverId,e)=>{e.preventDefault();e.stopPropagation();if(zonesOf(driverId).length===0)return;const base=snapshotBase();const p=pct(driverId,e);gRef.current={kind:"move",driverId,base,start:p};};
  // Resize scales every active zone by the same factor; the factor is measured
  // from the grabbed zone's centre, so pass that specific zone in.
  const beginResize=(driverId,z,e)=>{e.preventDefault();e.stopPropagation();if(!z)return;const base=snapshotBase();const center=zoneCenter(z);const p=pct(driverId,e);const halfDiag=Math.max(0.5,Math.hypot(p.x-center.x,p.y-center.y));gRef.current={kind:"resize",driverId,base,center,halfDiag};};

  useEffect(()=>{
    const onMove=(e)=>{
      const g=gRef.current;if(!g)return;e.preventDefault();
      const p=pct(g.driverId,e);
      let tf;
      if(g.kind==="move")tf={type:"move",dx:p.x-g.start.x,dy:p.y-g.start.y};
      else{const d=Math.hypot(p.x-g.center.x,p.y-g.center.y);tf={type:"scale",factor:Math.max(0.1,Math.min(8,d/g.halfDiag))};}
      setWork(prev=>{const next={...prev};Object.keys(g.base).forEach(id=>{let sos=prev[id];g.base[id].forEach(bz=>{const r=applyTransform(bz,tf);if(r.zone)sos=putZone(sos,r.zone)});next[id]=sos});return next;});
    };
    const onUp=()=>{gRef.current=null;};
    window.addEventListener("mousemove",onMove);window.addEventListener("mouseup",onUp);
    const tm=e=>{if(e.touches.length===1){const t0=e.touches[0];onMove({clientX:t0.clientX,clientY:t0.clientY,preventDefault:()=>e.preventDefault()})}};
    window.addEventListener("touchmove",tm,{passive:false});window.addEventListener("touchend",onUp);
    return()=>{window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);window.removeEventListener("touchmove",tm);window.removeEventListener("touchend",onUp)};
  },[group,optOut]);// eslint-disable-line

  // Effective rotation for display/apply = the zone's original rotation + the
  // shared slider delta, but ONLY for participating mockups — an opted-out panel
  // keeps its own rotation so the preview matches what Apply will (not) write.
  const effRot=(id,z)=>{if(!z||z.corners)return 0;const d=participating(id)?rotDelta:0;return Math.max(-45,Math.min(45,(origRot.current[`${id}:${z.id}`]||0)+d))};
  const toggleOpt=(id)=>setOptOut(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});

  // The templates that would actually change, with their final (delta-applied,
  // rotation-baked) zones. Computed ONCE so the apply, the enabled-state, and the
  // regenerate-count all read the same truth.
  const pendingUpdates=(()=>{
    const u={};
    const normZone=z=>JSON.stringify({...z,rotation:z.rotation||0});
    migd.forEach(m=>{
      if(!participating(m.id))return;
      const activeIds=new Set(zonesOf(m.id).map(z=>z.id));
      if(activeIds.size===0)return;
      const origById=new Map((m.smartObjects||[]).map(s=>[s.id,s]));
      // Bake each active zone's final rotation; non-active zones pass through.
      // "Changed" is decided per active zone vs its own original (like single-zone
      // mode, generalized to N groups) — untouched zones are never compared, so a
      // set's unrelated zones can't trip a false "changed".
      let changed=false;
      const finalSos=work[m.id].map(s=>{
        if(!activeIds.has(s.id))return s;
        const fz=s.corners?s:{...s,rotation:effRot(m.id,s)};
        const o=origById.get(s.id);
        if(!o||normZone(o)!==normZone(fz))changed=true;
        return fz;
      });
      if(!changed)return;
      u[m.id]=finalSos;
    });
    return u;
  })();
  const changedIds=Object.keys(pendingUpdates);
  const changed=changedIds.length>0;
  const doApply=()=>onApply(pendingUpdates,scope);
  // Post-gen only: how many already-generated mockups will re-render on apply.
  const regenN=(regenCountFor&&changed)?regenCountFor(changedIds):null;

  const participatingCount=migd.filter(m=>participating(m.id)).length;
  const btn={padding:"7px 16px",borderRadius:9,border:"none",fontSize:12,fontWeight:700,cursor:"pointer"};

  return<div style={{position:"fixed",inset:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,padding:22,maxWidth:1180,width:"100%",maxHeight:"94vh",overflow:"auto",border:`1px solid ${c.border}`,display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700}}>Bulk edit zones</h2>
          <p style={{color:c.textSub,margin:0,fontSize:12,maxWidth:620,lineHeight:1.5}}>Drag the design on <strong>any</strong> mockup — resize from a corner, rotate with the slider. Every mockup below moves the same amount from its own starting spot, so the print lands at the same place on each shirt.</p>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <button onClick={onCancel} style={{...btn,background:c.btnSecBg,color:c.textSub}}>Cancel</button>
          <button onClick={doApply} disabled={!changed} style={{...btn,background:changed?"linear-gradient(135deg,#6366f1,#8b5cf6)":c.btnSecBg,color:changed?"#fff":c.textDim,cursor:changed?"pointer":"not-allowed"}}>{regenCountFor?(changed?`Apply · regenerate ${regenN}`:"Apply"):`Apply to ${participatingCount} mockup${participatingCount===1?"":"s"}`}</button>
        </div>
      </div>

      {/* Controls strip: which design (group) + rotation */}
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",padding:"9px 12px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
        {groupOpts.length>1&&<label style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{color:c.textSub,fontSize:11,fontWeight:600}}>Design area</span>
          <select value={group||""} onChange={e=>{setGroup(e.target.value);setRotDelta(0)}} style={{padding:"5px 8px",borderRadius:7,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:11,fontWeight:600,outline:"none"}}>
            <option value={ALL}>All groups</option>
            {groupOpts.map(g=><option key={g.id} value={g.id}>{g.name} ({g.count})</option>)}
          </select>
        </label>}
        <div style={{display:"flex",alignItems:"center",gap:8,flex:"1 1 220px",maxWidth:340}}>
          <span style={{color:c.textSub,fontSize:11,fontWeight:600}}>↻ Rotate all</span>
          <input type="range" min="-45" max="45" value={rotDelta} onChange={e=>setRotDelta(Number(e.target.value))} style={{flex:1,minWidth:0,accentColor:"#6366f1"}}/>
          <span style={{color:c.textMuted,fontSize:11,minWidth:34,textAlign:"right"}}>{rotDelta>0?"+":""}{rotDelta}°</span>
          {rotDelta!==0&&<button onClick={()=>setRotDelta(0)} style={{padding:"2px 7px",borderRadius:5,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,cursor:"pointer"}}>Reset</button>}
        </div>
        {onScopeChange&&<div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto"}} title="Permanent writes the new placement onto your saved templates (sticks for future projects). This session only keeps it to the current generated results.">
          <span style={{color:c.textSub,fontSize:11,fontWeight:600}}>Save to</span>
          {(allowPermanent?[{v:"permanent",label:"Templates"},{v:"session",label:"This session"}]:[{v:"session",label:"This session"}]).map(o=>
            <button key={o.v} onClick={()=>onScopeChange(o.v)} style={{padding:"4px 9px",borderRadius:6,border:scope===o.v?"1.5px solid #6366f1":`1px solid ${c.border}`,background:scope===o.v?"rgba(99,102,241,0.15)":"transparent",color:scope===o.v?"#a5b4fc":c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>{o.label}</button>)}
        </div>}
      </div>

      {onScopeChange&&!allowPermanent&&<div style={{color:c.textMuted,fontSize:10,lineHeight:1.5}}>Templates are shared across all your sets, so a permanent write can’t be limited to one set — this edit applies to <strong style={{color:c.textSub}}>this session only</strong>.</div>}

      {regenCountFor&&changed&&<div style={{padding:"8px 12px",borderRadius:9,background:"rgba(99,102,241,0.1)",border:"1px solid rgba(99,102,241,0.3)",color:"#a5b4fc",fontSize:11,fontWeight:600,lineHeight:1.5}}>
        ↻ Applying re-renders <strong>{regenN}</strong> already-generated mockup{regenN===1?"":"s"} with the new placement{scope==="permanent"?", and saves it onto your templates":" (this session only)"}.
      </div>}

      {skips.length>0&&<div style={{padding:"8px 12px",borderRadius:9,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#fbbf24",fontSize:11,fontWeight:600,lineHeight:1.5}}>
        ⚠️ Not changed: {skips.map(s=>`${s.name} (${SKIP_REASONS[s.reason]})`).join(" · ")}
      </div>}

      {group?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(258px,1fr))",gap:12}}>
        {migd.map(m=>{
          const zs=zonesOf(m.id);const hasZone=zs.length>0;const isOut=optOut.has(m.id);const part=participating(m.id);
          const asp=aspects[m.id]||100;
          return<div key={m.id} style={{borderRadius:11,overflow:"hidden",border:`1px solid ${part?"rgba(99,102,241,0.4)":c.border}`,background:c.bgCard,opacity:isOut?0.5:1}}>
            <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 9px",borderBottom:`1px solid ${c.border}`}}>
              <label style={{display:"flex",alignItems:"center",gap:5,cursor:hasZone?"pointer":"default",flex:1,minWidth:0}}>
                {hasZone&&<input type="checkbox" checked={!isOut} onChange={()=>toggleOpt(m.id)} style={{accentColor:"#6366f1",width:13,height:13,cursor:"pointer",flexShrink:0}}/>}
                <span className="pb-text" style={{color:c.text,fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</span>
              </label>
              {!hasZone&&<span style={{fontSize:9,fontWeight:700,color:"#fbbf24",flexShrink:0}}>no zone</span>}
            </div>
            <div ref={el=>{panelRefs.current[m.id]=el}} style={{position:"relative",paddingTop:`${asp}%`,userSelect:"none",touchAction:"none",background:isDark?"#0a0a14":"#e2e8f0"}}>
              <img src={m.src} alt={m.name} draggable={false} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",display:"block"}}/>
              {zs.map(z=>{
                if(z.corners){
                  const cn=z.corners;const xs=[cn.tl.x,cn.tr.x,cn.br.x,cn.bl.x],ys=[cn.tl.y,cn.tr.y,cn.br.y,cn.bl.y];
                  const bx=Math.min(...xs),by=Math.min(...ys),bw=Math.max(...xs)-bx,bh=Math.max(...ys)-by;
                  return<React.Fragment key={z.id}>
                    <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:part?"auto":"none",cursor:part?"move":"default"}} viewBox="0 0 100 100" preserveAspectRatio="none" onMouseDown={part?e=>beginMove(m.id,e):undefined} onTouchStart={part?e=>{const t0=e.touches[0];beginMove(m.id,{clientX:t0.clientX,clientY:t0.clientY,preventDefault:()=>e.preventDefault(),stopPropagation:()=>e.stopPropagation()})}:undefined}>
                      <polygon points={`${cn.tl.x},${cn.tl.y} ${cn.tr.x},${cn.tr.y} ${cn.br.x},${cn.br.y} ${cn.bl.x},${cn.bl.y}`} fill="rgba(99,102,241,0.08)" stroke="#6366f1" strokeWidth="0.5" vectorEffect="non-scaling-stroke"/>
                    </svg>
                    <img src={dsgFor(z.group)} alt="" draggable={false} style={{position:"absolute",left:`${bx}%`,top:`${by}%`,width:`${bw}%`,height:`${bh}%`,objectFit:"contain",opacity:0.55,pointerEvents:"none"}}/>
                  </React.Fragment>;
                }
                return<div key={z.id} style={{position:"absolute",left:`${z.x}%`,top:`${z.y}%`,width:`${z.w}%`,height:`${z.h}%`,transform:`rotate(${effRot(m.id,z)}deg)`,transformOrigin:"center",border:`2px solid ${part?"#6366f1":"rgba(148,163,184,0.6)"}`,borderRadius:2,cursor:part?"move":"default",boxShadow:part?"0 0 10px rgba(99,102,241,0.5)":"none"}} onMouseDown={part?e=>beginMove(m.id,e):undefined} onTouchStart={part?e=>{const t0=e.touches[0];beginMove(m.id,{clientX:t0.clientX,clientY:t0.clientY,preventDefault:()=>e.preventDefault(),stopPropagation:()=>e.stopPropagation()})}:undefined}>
                  <img src={dsgFor(z.group)} alt="" draggable={false} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",opacity:0.8,pointerEvents:"none"}}/>
                  {part&&["nw","ne","sw","se"].map(k=><div key={k} onMouseDown={e=>beginResize(m.id,z,e)} onTouchStart={e=>{const t0=e.touches[0];beginResize(m.id,z,{clientX:t0.clientX,clientY:t0.clientY,preventDefault:()=>e.preventDefault(),stopPropagation:()=>e.stopPropagation()})}} style={{position:"absolute",width:12,height:12,background:"#fff",border:"2px solid #6366f1",borderRadius:3,cursor:`${k}-resize`,zIndex:2,...(k==="nw"?{top:-6,left:-6}:k==="ne"?{top:-6,right:-6}:k==="sw"?{bottom:-6,left:-6}:{bottom:-6,right:-6})}}/>)}
                </div>;
              })}
            </div>
          </div>;
        })}
      </div>:<div style={{textAlign:"center",padding:"40px 20px",color:c.textMuted,fontSize:13}}>None of these mockups have a design area (zone) yet. Add a smart-object zone first, then come back to position the design across the set.</div>}
    </div>
  </div>;
}

/* ═══════ MULTI-SO EDITOR with "No Design" toggle, global groups, backspace, zoom <100% ═══════ */
function MultiSOEditor({mockup,onSave,onCancel,globalGroups,onUpdateGlobalGroups,designPreview,extraFooter,onEditForeground}){
  const{isDark}=useTheme();const c=t(isDark);
  const ref=useRef(null);const wrapRef=useRef(null);
  const m=migrate(mockup);
  const[sos,setSos]=useState(m.smartObjects);
  const[noDesign,setNoDesign]=useState(m.noDesign||false);
  // Use global groups, but also keep local groups for this template's zone assignments
  const[grps,setGrps]=useState(globalGroups.length?globalGroups:[{id:"g1",name:"Group 1",color:gc(0)}]);
  const[sel,setSel]=useState(null);
  const[drawing,setDrawing]=useState(false);
  const[sp,setSp]=useState(null);const[tmp,setTmp]=useState(null);
  const[dm,setDm]=useState(null);const[ds,setDs]=useState(null);const[drs,setDrs]=useState(null);
  const[openTip,setOpenTip]=useState(null);
  const[ngn,setNgn]=useState("");
  const[bulkSel,setBulkSel]=useState(new Set());
  const[imgAspect,setImgAspect]=useState(null);
  const[imgDims,setImgDims]=useState(null); // F9: template natural {w,h} in px
  const[zoom,setZoom]=useState(1);
  const clipRef=useRef(null);
  const soHistRef=useRef([]);
  const soRedoRef=useRef([]);
  const perspCanvasRef=useRef(null);
  const perspDesignImgs=useRef({}); // cached Image objects for design previews
  // Clip-mask preview: over the region the clip TRIMS (outside the garment) we
  // redraw the base photo — the exact layer the bake draws over the design, so
  // the design shows trimmed to the garment — AND wash it with a visible pink
  // tint so the member can plainly SEE what the clip removes (base-photo-only
  // was accurate but invisible against the background). Garment area stays clear
  // so the design shows through. Only built when the template carries a mask.
  const[showFg,setShowFg]=useState(true);
  const[fgOverlay,setFgOverlay]=useState(null);
  useEffect(()=>{
    if(!mockup.foregroundMask){setFgOverlay(null);return}
    let cancelled=false;const base=new Image(),mask=new Image();let n=0;
    const done=()=>{ if(cancelled||++n<2)return;
      const W=base.naturalWidth,H=base.naturalHeight;if(!W||!H)return;
      const cv=document.createElement("canvas");cv.width=W;cv.height=H;const x=cv.getContext("2d");
      x.drawImage(base,0,0);
      x.globalCompositeOperation="destination-in";x.drawImage(mask,0,0);     // base ∩ mask = clipped (trimmed) region only
      x.globalCompositeOperation="source-atop";x.fillStyle="rgba(236,72,153,0.4)";x.fillRect(0,0,W,H); // pink wash, only where the base now shows
      setFgOverlay(cv.toDataURL("image/png"));
    };
    base.onload=done;mask.onload=done;base.src=mockup.src;mask.src=mockup.foregroundMask;
    return()=>{cancelled=true};
  },[mockup.src,mockup.foregroundMask]);

  // Cache design preview images for perspective rendering
  useEffect(()=>{
    if(!designPreview)return;
    Object.entries(designPreview).forEach(([gid,src])=>{
      if(perspDesignImgs.current[src])return;
      const img=new Image();img.crossOrigin="anonymous";img.onload=()=>{perspDesignImgs.current[src]=img};img.src=src;
    });
  },[designPreview]);

  // Real-time perspective preview: redraw canvas whenever corners change
  useEffect(()=>{
    const cv=perspCanvasRef.current;const inner=ref.current;
    if(!cv||!inner)return;
    const perspZones=sos.filter(s=>s.corners);
    if(perspZones.length===0){cv.width=0;cv.height=0;return}
    // Match canvas to the image container size
    const rect=inner.getBoundingClientRect();
    const W=rect.width;const H=rect.height;
    if(W===0||H===0)return;
    cv.width=W;cv.height=H;
    const ctx=cv.getContext("2d");
    ctx.clearRect(0,0,W,H);
    perspZones.forEach(so=>{
      const dpSrc=designPreview?designPreview[so.group]:null;
      const di=dpSrc?perspDesignImgs.current[dpSrc]:null;
      if(!di)return;
      const cn=so.corners;
      const corners=[{x:(cn.tl.x/100)*W,y:(cn.tl.y/100)*H},{x:(cn.tr.x/100)*W,y:(cn.tr.y/100)*H},{x:(cn.br.x/100)*W,y:(cn.br.y/100)*H},{x:(cn.bl.x/100)*W,y:(cn.bl.y/100)*H}];
      ctx.save();ctx.globalAlpha=0.7;drawPerspective(ctx,di,corners);ctx.restore();
    });
  },[sos,designPreview]);

  // Save SO state for undo
  const soSnap=()=>{soHistRef.current.push(JSON.stringify(sos));if(soHistRef.current.length>40)soHistRef.current.shift();soRedoRef.current=[]};
  const soUndo=()=>{if(soHistRef.current.length===0)return;soRedoRef.current.push(JSON.stringify(sos));const prev=JSON.parse(soHistRef.current.pop());setSos(prev)};
  const soRedo=()=>{if(soRedoRef.current.length===0)return;soHistRef.current.push(JSON.stringify(sos));const next=JSON.parse(soRedoRef.current.pop());setSos(next)};

  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);
  useEffect(()=>{const img=new Image();img.onload=()=>{setImgAspect(img.naturalHeight/img.naturalWidth*100);setImgDims({w:img.naturalWidth,h:img.naturalHeight})};img.src=mockup.src},[mockup.src]);

  // Keyboard: copy/paste/backspace/undo/redo
  useEffect(()=>{
    const h=e=>{
      if((e.metaKey||e.ctrlKey)&&e.key==="z"&&!e.shiftKey){e.preventDefault();soUndo();return}
      if((e.metaKey||e.ctrlKey)&&e.key==="z"&&e.shiftKey){e.preventDefault();soRedo();return}
      if((e.metaKey||e.ctrlKey)&&e.key==="c"&&sel){e.preventDefault();const so=sos.find(s=>s.id===sel);if(so)clipRef.current={...so}}
      if((e.metaKey||e.ctrlKey)&&e.key==="v"&&clipRef.current){e.preventDefault();soSnap();const s=clipRef.current;const p={...s,id:`so_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,x:s.x+3,y:s.y+3};setSos(pr=>[...pr,p]);setSel(p.id)}
      if((e.key==="Backspace"||e.key==="Delete")&&sel&&!e.target.closest("input,select,textarea")){e.preventDefault();soSnap();setSos(pr=>pr.filter(s=>s.id!==sel));setSel(null)}
    };
    window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);
  },[sel,sos]);

  // Ctrl+scroll zoom
  useEffect(()=>{const w=wrapRef.current;if(!w)return;const h=e=>{if(!e.ctrlKey&&!e.metaKey)return;e.preventDefault();const d=e.deltaY>0?-0.08:0.08;setZoom(z=>Math.max(0.25,Math.min(6,z+d*z)))};w.addEventListener("wheel",h,{passive:false});return()=>w.removeEventListener("wheel",h)},[]);

  const coords=e=>{const inner=ref.current;if(!inner)return{x:0,y:0};const r=inner.getBoundingClientRect();return{x:((e.clientX-r.left)/r.width)*100,y:((e.clientY-r.top)/r.height)*100}};
  const selObj=sos.find(s=>s.id===sel);
  // F10: selected-zone pixel view. px = percent ÷100 × template natural px.
  // Rectangular zones only (perspective zones warp onto a quad). Storage stays
  // percent — these are derived for display/input; imgDims null until img loads.
  const selPx=(selObj&&imgDims&&!selObj.corners)?{w:Math.round((selObj.w/100)*imgDims.w),h:Math.round((selObj.h/100)*imgDims.h)}:null;
  const selOkAR=!!(selPx&&selPx.w>0&&selPx.h>0&&matchesPrintAR(selPx.w,selPx.h));
  // F12: aspect-ratio lock — editor-session state (NOT persisted to the zone
  // schema; per-selection). Stores the locked ratio in template PIXELS (w/h),
  // because templates aren't square so a percent ratio would drift. null = off.
  const[arLock,setArLock]=useState(null);
  useEffect(()=>{setArLock(null)},[sel]); // selecting a different zone resets the lock
  const canLock=!!(selPx&&imgDims&&selObj&&!selObj.corners&&selPx.w>0&&selPx.h>0);
  const toggleLock=()=>{
    if(!canLock){return}
    if(arLock!=null){setArLock(null);return}
    // Capture the current px ratio; if it's within tolerance of 7:8 canonicalize
    // the lock to EXACTLY print (matching the green ✓ logic — "close" locks to print).
    const r=selPx.w/selPx.h;
    setArLock(matchesPrintAR(selPx.w,selPx.h)?PRINT_AR:r);
  };
  // Set a w/h dimension (percent). When the lock is on, the partner dim is
  // recomputed through imgDims to hold the pixel ratio, then the pair is capped
  // to stay inside the template (anchored at the zone's current top-left).
  const applyDim=(k,vPct)=>{
    setSos(pr=>pr.map(s=>{
      if(s.id!==sel)return s;
      let w=k==="w"?vPct:s.w,h=k==="h"?vPct:s.h;
      if(arLock!=null&&imgDims){
        if(k==="w"){const wpx=vPct/100*imgDims.w;h=(wpx/arLock)/imgDims.h*100}
        else{const hpx=vPct/100*imgDims.h;w=(hpx*arLock)/imgDims.w*100}
        const fit=fitRatio(w,h,100-s.x,100-s.y);w=fit.w;h=fit.h;
      }
      return{...s,w,h};
    }));
  };
  // Commit a px entry back as percent, clamped to [1, natural dim] so the zone
  // stays inside the template (the % fields today clamp nothing; px does).
  // Routes through applyDim so the aspect lock (if on) recomputes the partner.
  const setDimPx=(k,val)=>{if(!imgDims)return;const dim=k==="w"?imgDims.w:imgDims.h;const px=Math.max(1,Math.min(dim,parseFloat(val)||0));applyDim(k,(px/dim)*100)};
  // F11b: cluster bracket styling — a faint fill + a stronger dark-mode border so
  // the metrics groups read as distinct blocks in both themes (c.border alone
  // nearly vanished on dark). Applied to all four bordered clusters.
  const clBg=isDark?"rgba(255,255,255,0.045)":"rgba(0,0,0,0.03)";
  const clBorder=`1px solid ${isDark?"rgba(255,255,255,0.14)":c.border}`;
  // F12c: shared cluster shell so every bracketed group reads as one designed
  // strip — identical fill/border, radius, horizontal padding, and a fixed
  // height for uniform vertical rhythm. Bar rows use alignItems:stretch.
  const clBase={display:"inline-flex",alignItems:"center",height:30,padding:"0 8px",borderRadius:6,background:clBg,border:clBorder,boxSizing:"border-box"};
  const clRow={display:"flex",alignItems:"stretch",gap:8,flexWrap:"wrap"};
  const unitChip={padding:"1px 5px",borderRadius:4,background:isDark?"rgba(255,255,255,0.09)":"rgba(0,0,0,0.07)",color:c.textSub,fontSize:8,fontWeight:800,letterSpacing:"0.4px"};
  // In-bar interactive controls (inputs/buttons) share one height for alignment.
  const barBtn={height:22,display:"inline-flex",alignItems:"center",justifyContent:"center",padding:"0 9px",borderRadius:5,fontSize:9,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",boxSizing:"border-box"};
  const barInput={height:22,padding:"0 3px",borderRadius:4,fontSize:9,textAlign:"center",outline:"none",boxSizing:"border-box"};
  const inside=(p,so)=>p.x>=so.x&&p.x<=so.x+so.w&&p.y>=so.y&&p.y<=so.y+so.h;
  const handle=(p,so)=>{const hs=3;const cs={nw:{x:so.x,y:so.y},ne:{x:so.x+so.w,y:so.y},sw:{x:so.x,y:so.y+so.h},se:{x:so.x+so.w,y:so.y+so.h}};for(const[k,v]of Object.entries(cs)){if(Math.abs(p.x-v.x)<hs&&Math.abs(p.y-v.y)<hs)return k}return null};

  // Use refs for drag state so window-level handlers always see latest values
  const drawingRef=useRef(false);const spRef=useRef(null);const tmpRef=useRef(null);
  const dmRef=useRef(null);const dsRef=useRef(null);const drsRef=useRef(null);
  // One-step undo: true once we've snapshotted the pre-gesture state for the
  // current drag/resize/move. Reset at mouseup so the next gesture snaps again.
  const soGestureSnapRef=useRef(false);
  const selRef=useRef(sel);selRef.current=sel;
  const sosRef=useRef(sos);sosRef.current=sos;

  const lockScroll=()=>{const w=wrapRef.current;if(w)w.style.overflow="hidden"};
  const unlockScroll=()=>{const w=wrapRef.current;if(w)w.style.overflow="auto"};

  const onDown=e=>{e.preventDefault();const p=coords(e);
    if(selObj){const h=handle(p,selObj);if(h){lockScroll();dmRef.current=h;dsRef.current=p;drsRef.current={...selObj};setDm(h);setDs(p);setDrs({...selObj});return}if(inside(p,selObj)){lockScroll();dmRef.current='move';dsRef.current=p;drsRef.current={...selObj};setDm('move');setDs(p);setDrs({...selObj});return}}
    for(const so of[...sos].reverse()){if(inside(p,so)){setSel(so.id);return}}
    setSel(null);lockScroll();drawingRef.current=true;spRef.current=p;tmpRef.current=null;setDrawing(true);setSp(p);setTmp(null);
  };

  // Window-level mousemove/mouseup for reliable drag outside the image area
  useEffect(()=>{
    const onWindowMove=e=>{
      const p=coords(e);
      // Capture the pre-gesture SO state on the FIRST move of a drag/resize/move —
      // the scattered mousedown handlers all pass through here before any setSos
      // mutation, so this is the one chokepoint. (Snapping at mouseup would record
      // the POST-drag state; the pre-drag state would be lost and ⌘Z couldn't step
      // back the drag. Draw/paste/delete snap their own pre-mutation state.)
      if(dmRef.current&&!soGestureSnapRef.current){soGestureSnapRef.current=true;soSnap();}
      if(drawingRef.current&&spRef.current){
        const sp=spRef.current;
        const t={x:Math.min(sp.x,p.x),y:Math.min(sp.y,p.y),w:Math.abs(p.x-sp.x),h:Math.abs(p.y-sp.y)};
        tmpRef.current=t;setTmp(t);return;
      }
      const curDm=dmRef.current;const curDs=dsRef.current;const curDrs=drsRef.current;
      if(!curDm||!curDs||!curDrs)return;
      if(curDm==='move'){
        const dx=p.x-curDs.x,dy=p.y-curDs.y;
        setSos(pr=>pr.map(s=>{
          if(s.id!==selRef.current)return s;
          const moved={...s,x:curDrs.x+dx,y:curDrs.y+dy};
          if(s.corners&&curDrs.corners){
            moved.corners={tl:{x:curDrs.corners.tl.x+dx,y:curDrs.corners.tl.y+dy},tr:{x:curDrs.corners.tr.x+dx,y:curDrs.corners.tr.y+dy},br:{x:curDrs.corners.br.x+dx,y:curDrs.corners.br.y+dy},bl:{x:curDrs.corners.bl.x+dx,y:curDrs.corners.bl.y+dy}};
          }
          return moved;
        }));return;
      }
      if(['p_tl','p_tr','p_bl','p_br'].includes(curDm)){
        const corner=curDm.slice(2);
        const dx=p.x-curDs.x,dy=p.y-curDs.y;
        setSos(pr=>pr.map(s=>{
          if(s.id!==selRef.current||!curDrs.corners)return s;
          const c={...curDrs.corners};
          c[corner]={x:curDrs.corners[corner].x+dx,y:curDrs.corners[corner].y+dy};
          return{...s,corners:c};
        }));return;
      }
      if(['nw','ne','sw','se'].includes(curDm)){
        const dx=p.x-curDs.x,dy=p.y-curDs.y;let nr={...curDrs};
        // F12: when the aspect lock is on, corner drags scale BOTH dims to the
        // locked pixel ratio (curDrs is the immutable start rect → no feedback
        // loop with the lock recompute). The dragged corner moves; the opposite
        // corner stays fixed. Ratio is capped, never broken, to fit the template.
        if(arLock!=null&&imgDims){
          const rp=arLock*imgDims.h/imgDims.w; // percent-space w/h ratio
          const east=curDm==='se'||curDm==='ne',south=curDm==='se'||curDm==='sw';
          const cw=Math.max(3,curDrs.w+(east?dx:-dx));   // width intent from horizontal drag
          const ch=Math.max(3,curDrs.h+(south?dy:-dy));  // height intent from vertical drag
          let w2=Math.max(cw,ch*rp),h2=w2/rp;            // follow the larger pull, hold ratio
          const availW=east?(100-curDrs.x):(curDrs.x+curDrs.w); // room from the fixed anchor
          const availH=south?(100-curDrs.y):(curDrs.y+curDrs.h);
          const fit=fitRatio(w2,h2,availW,availH);w2=fit.w;h2=fit.h;
          nr.w=w2;nr.h=h2;
          nr.x=east?curDrs.x:(curDrs.x+curDrs.w-w2); // anchor opposite corner
          nr.y=south?curDrs.y:(curDrs.y+curDrs.h-h2);
        }
        else if(curDm==='se'){nr.w=Math.max(3,curDrs.w+dx);nr.h=Math.max(3,curDrs.h+dy)}
        else if(curDm==='sw'){nr.x=curDrs.x+dx;nr.w=Math.max(3,curDrs.w-dx);nr.h=Math.max(3,curDrs.h+dy)}
        else if(curDm==='ne'){nr.w=Math.max(3,curDrs.w+dx);nr.y=curDrs.y+dy;nr.h=Math.max(3,curDrs.h-dy)}
        else if(curDm==='nw'){nr.x=curDrs.x+dx;nr.y=curDrs.y+dy;nr.w=Math.max(3,curDrs.w-dx);nr.h=Math.max(3,curDrs.h-dy)}
        setSos(pr=>pr.map(s=>s.id===selRef.current?{...s,...nr}:s));
      }
    };
    const onWindowUp=()=>{
      if(drawingRef.current&&tmpRef.current&&tmpRef.current.w>1&&tmpRef.current.h>1){
        soSnap();const dg=grps[0]?.id||"g1";
        const n={...tmpRef.current,id:`so_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,group:dg,rotation:0};
        setSos(pr=>[...pr,n]);setSel(n.id);
      }
      soGestureSnapRef.current=false; // gesture ended — the pre-drag snap (if any) is already recorded
      drawingRef.current=false;spRef.current=null;tmpRef.current=null;
      dmRef.current=null;dsRef.current=null;drsRef.current=null;
      setDrawing(false);setSp(null);setTmp(null);setDm(null);setDs(null);setDrs(null);
      unlockScroll();
    };
    window.addEventListener("mousemove",onWindowMove);
    window.addEventListener("mouseup",onWindowUp);
    const onTouchMove=e=>{if(e.touches.length===1){const tt=e.touches[0];onWindowMove({clientX:tt.clientX,clientY:tt.clientY})}};
    const onTouchEnd=()=>onWindowUp();
    window.addEventListener("touchmove",onTouchMove,{passive:false});
    window.addEventListener("touchend",onTouchEnd);
    return()=>{window.removeEventListener("mousemove",onWindowMove);window.removeEventListener("mouseup",onWindowUp);window.removeEventListener("touchmove",onTouchMove);window.removeEventListener("touchend",onTouchEnd)};
  });
  const delSO=id=>{setSos(pr=>pr.filter(s=>s.id!==id));if(sel===id)setSel(null)};
  const addGroup=()=>{const nm=ngn.trim()||`Group ${grps.length+1}`;const g={id:`g_${Date.now()}`,name:nm,color:gc(grps.length)};setGrps(pr=>[...pr,g]);setNgn("");return g};
  // Create a group with an EXACT preset name (name-steering). Salted id so two
  // quick preset clicks can't collide on Date.now().
  const addGroupNamed=(nm)=>{const g={id:`g_${Date.now()}${Math.round(Math.random()*1e4)}`,name:nm,color:gc(grps.length)};setGrps(pr=>[...pr,g]);return g};
  const hst=pos=>({position:"absolute",width:12,height:12,background:"white",border:"2px solid #6366f1",borderRadius:3,cursor:`${pos}-resize`,zIndex:2,pointerEvents:"auto",...(pos==='nw'?{top:-6,left:-6}:pos==='ne'?{top:-6,right:-6}:pos==='sw'?{bottom:-6,left:-6}:{bottom:-6,right:-6})});
  const tch=fn=>e=>{if(e.touches.length===1){const tt=e.touches[0];fn({clientX:tt.clientX,clientY:tt.clientY,preventDefault:()=>e.preventDefault()})}};

  const handleSave=()=>{
    // Persist groups globally
    onUpdateGlobalGroups(grps);
    // Strip internal editor state from zones before saving
    const cleanSos=sos.map(({_origRect,...rest})=>rest);
    onSave({smartObjects:cleanSos,groups:grps,noDesign});
  };

  return<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,padding:22,maxWidth:960,width:"100%",maxHeight:"92vh",overflow:"auto",border:`1px solid ${c.border}`}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div>
          <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700,display:"inline-flex",alignItems:"center"}}>Smart Object Editor<Tip id="mso-ed" text="Draw zones, assign to groups. Groups are shared across all templates. Use ⌘C/⌘V to copy/paste zones. Backspace deletes selected zone. ⌘+Scroll to zoom." openTip={openTip} setOpenTip={setOpenTip}/></h2>
          <p style={{color:c.textSub,margin:0,fontSize:12}}>⌘C copy • ⌘V paste • ⌘Z undo • ⌘⇧Z redo • Backspace delete • ⌘+Scroll zoom</p>
        </div>
        {/* No Design toggle */}
        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"6px 12px",borderRadius:8,background:noDesign?"rgba(245,158,11,0.15)":"transparent",border:`1px solid ${noDesign?"rgba(245,158,11,0.4)":c.border}`}} onClick={e=>e.stopPropagation()}>
          <input type="checkbox" checked={noDesign} onChange={e=>setNoDesign(e.target.checked)} style={{accentColor:"#f59e0b",width:14,height:14,cursor:"pointer"}}/>
          <span style={{color:noDesign?"#f59e0b":c.textSub,fontSize:11,fontWeight:600}}>No Design (standalone)</span>
          <Tip id="no-design" text="Check this for templates that should be included in the output as-is, without any design applied — like size charts, care instructions, or info cards. The original image will be passed through the pipeline untouched." openTip={openTip} setOpenTip={setOpenTip}/>
        </label>
      </div>

      {noDesign?
        <div style={{textAlign:"center",padding:"40px 20px",borderRadius:12,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
          <div style={{fontSize:32,marginBottom:8}}>📋</div>
          <div style={{color:c.text,fontSize:14,fontWeight:600,marginBottom:4}}>Standalone Template</div>
          <div style={{color:c.textMuted,fontSize:12}}>This image will be included as-is in your generated output without any design overlay.</div>
        </div>
      :
      <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:14}}>
        <div>
          <div style={{display:"flex",gap:5,marginBottom:8,alignItems:"center"}}>
            <span style={{color:c.textSub,fontSize:10,fontWeight:600}}>🔍</span>
            <button onClick={()=>setZoom(z=>Math.max(0.25,z-0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
            <span style={{color:c.textMuted,fontSize:9,minWidth:28}}>{Math.round(zoom*100)}%</span>
            <button onClick={()=>setZoom(z=>Math.min(6,z+0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
            {zoom!==1&&<button onClick={()=>setZoom(1)} style={{padding:"2px 7px",borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,cursor:"pointer"}}>Reset</button>}
            {mockup.foregroundMask&&<button onClick={()=>setShowFg(v=>!v)} title="Shows the foreground over the design: pink shading = where the mask re-shows the base over the design (clip trim, hair, etc.). Turn off to edit zone handles unobstructed." style={{marginLeft:"auto",padding:"2px 9px",borderRadius:4,border:"none",background:showFg?"rgba(236,72,153,0.18)":c.btnSecBg,color:showFg?"#f9a8d4":c.textMuted,fontSize:9,fontWeight:600,cursor:"pointer"}}>🎭 Foreground: {showFg?"shown":"hidden"}</button>}
            {mockup.foregroundMask&&onEditForeground&&<button onClick={()=>onEditForeground(mockup)} title="Open the foreground editor to refine the mask (clip trim, hair, etc.)." style={{padding:"2px 9px",borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,fontWeight:600,cursor:"pointer"}}>✂ Foreground</button>}
          </div>
          <div ref={wrapRef} style={{borderRadius:9,overflow:"auto",background:isDark?"#0a0a14":"#e2e8f0",maxHeight:"55vh"}}>
          <div style={{width:zoom===1?"100%":`${zoom*100}%`}}>
          <div ref={ref} style={{position:"relative",cursor:"crosshair",userSelect:"none",paddingTop:imgAspect?`${imgAspect}%`:"75%"}} onMouseDown={onDown} onTouchStart={tch(onDown)}>
            <img className="pb-img" src={mockup.src} alt={mockup.name} draggable={false} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",display:"block"}}/>
            <canvas ref={perspCanvasRef} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none"}}/>
            {sos.map(so=>{const g=grps.find(x=>x.id===so.group);const isSel=sel===so.id;const dpSrc=designPreview?designPreview[so.group]:EXAMPLE_DESIGN_SVG;
              if(so.corners){
                const cn=so.corners;
                return<React.Fragment key={so.id}>
                  <svg style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none",overflow:"visible"}} viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polygon points={`${cn.tl.x},${cn.tl.y} ${cn.tr.x},${cn.tr.y} ${cn.br.x},${cn.br.y} ${cn.bl.x},${cn.bl.y}`} fill="transparent" stroke={g?.color||"#6366f1"} strokeWidth={isSel?"0.5":"0.3"} strokeDasharray={isSel?undefined:"0.8 0.4"} vectorEffect="non-scaling-stroke" style={{pointerEvents:"all",cursor:isSel?"move":"pointer"}} onMouseDown={e=>{e.stopPropagation();e.preventDefault();if(isSel){lockScroll();const p=coords(e);dmRef.current='move';dsRef.current=p;drsRef.current={...so,corners:{tl:{...cn.tl},tr:{...cn.tr},br:{...cn.br},bl:{...cn.bl}}};setDm('move');setDs(p);setDrs({...so,corners:{tl:{...cn.tl},tr:{...cn.tr},br:{...cn.br},bl:{...cn.bl}}})}else{setSel(so.id)}}}/>
                    {isSel&&["tl","tr","bl","br"].map(ck=>{const pt=cn[ck];return<circle key={ck} cx={pt.x} cy={pt.y} r="1" fill="white" stroke="#6366f1" strokeWidth="0.35" vectorEffect="non-scaling-stroke" style={{pointerEvents:"all",cursor:"crosshair",filter:"drop-shadow(0 0 2px rgba(0,0,0,0.5))"}} onMouseDown={e=>{e.stopPropagation();e.preventDefault();lockScroll();const p=coords(e);dmRef.current=`p_${ck}`;dsRef.current=p;drsRef.current={...so,corners:{tl:{...cn.tl},tr:{...cn.tr},br:{...cn.br},bl:{...cn.bl}}};setDm(`p_${ck}`);setDs(p);setDrs({...so,corners:{tl:{...cn.tl},tr:{...cn.tr},br:{...cn.br},bl:{...cn.bl}}})}}/>})}
                  </svg>
                  <div className="pb-text" style={{position:"absolute",left:`${Math.min(cn.tl.x,cn.bl.x)}%`,top:`${Math.min(cn.tl.y,cn.tr.y)-3}%`,background:g?.color||"#6366f1",color:"white",padding:"1px 6px",borderRadius:4,fontSize:8,fontWeight:700,whiteSpace:"nowrap",pointerEvents:"none"}}>{g?.name||"—"}</div>
                </React.Fragment>;
              }
              return<div key={so.id} style={{position:"absolute",left:`${so.x}%`,top:`${so.y}%`,width:`${so.w}%`,height:`${so.h}%`,border:`2.5px solid ${g?.color||"#6366f1"}`,background:"transparent",borderRadius:2,transform:so.rotation?`rotate(${so.rotation}deg)`:undefined,transformOrigin:"center",cursor:isSel?"move":"pointer",boxShadow:isSel?`0 0 0 2px white, 0 0 12px ${g?.color||"#6366f1"}80`:"none",overflow:"hidden"}}><img className="pb-img" src={dpSrc} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain",opacity:designPreview?0.7:0.35,pointerEvents:"none"}}/><div className="pb-text" style={{position:"absolute",top:-20,left:0,background:g?.color||"#6366f1",color:"white",padding:"1px 6px",borderRadius:4,fontSize:8,fontWeight:700,whiteSpace:"nowrap",pointerEvents:"none"}}>{g?.name||"—"}</div>{isSel&&<><div style={hst('nw')} onMouseDown={e=>{e.stopPropagation();e.preventDefault();lockScroll();const p=coords(e);dmRef.current='nw';dsRef.current=p;drsRef.current={...so};setDm('nw');setDs(p);setDrs({...so})}}/><div style={hst('ne')} onMouseDown={e=>{e.stopPropagation();e.preventDefault();lockScroll();const p=coords(e);dmRef.current='ne';dsRef.current=p;drsRef.current={...so};setDm('ne');setDs(p);setDrs({...so})}}/><div style={hst('sw')} onMouseDown={e=>{e.stopPropagation();e.preventDefault();lockScroll();const p=coords(e);dmRef.current='sw';dsRef.current=p;drsRef.current={...so};setDm('sw');setDs(p);setDrs({...so})}}/><div style={hst('se')} onMouseDown={e=>{e.stopPropagation();e.preventDefault();lockScroll();const p=coords(e);dmRef.current='se';dsRef.current=p;drsRef.current={...so};setDm('se');setDs(p);setDrs({...so})}}/></>}</div>})}
            {tmp&&<div style={{position:"absolute",left:`${tmp.x}%`,top:`${tmp.y}%`,width:`${tmp.w}%`,height:`${tmp.h}%`,border:"2.5px dashed #6366f1",background:"rgba(99,102,241,0.15)",borderRadius:2,pointerEvents:"none"}}/>}
            {/* Foreground-mask preview overlay: base photo re-covers the design
                outside the garment — exactly what the bake draws over the design.
                pointerEvents:none so zone handles underneath stay grabbable; the
                toolbar toggle hides it for unobstructed handle editing. */}
            {fgOverlay&&showFg&&<img className="pb-img" src={fgOverlay} alt="" draggable={false} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none"}}/>}
          </div></div></div>
          {selObj&&<div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8,padding:"7px 10px",borderRadius:8,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
            {/* F12c: the metrics bar is designed as ONE control strip — every item
               (%, px+lock, rotation, actions, aspect+ⓘ) is an identically bracketed
               cluster (clBase) laid out in deliberate rows, uniform height, no naked
               text, no bleeding. Presentation only; all handlers unchanged.
               Row 1 = [%] [px + ⛓ Lock] · Row 2 = [↻ rotation] [actions] [aspect + ⓘ]. */}
            {!selObj.corners&&<div style={clRow}>
              <div style={{...clBase,gap:4}}>
                <span title="Zone size as a percentage of the mockup image" style={unitChip}>%</span>
                {[{k:"x",label:"X"},{k:"y",label:"Y"},{k:"w",label:"W"},{k:"h",label:"H"}].map(f=><label key={f.k} style={{display:"flex",alignItems:"center",gap:2}}><span style={{color:c.textMuted,fontSize:8,fontWeight:700}}>{f.label}</span><input type="number" value={Math.round((selObj[f.k]||0)*10)/10} onChange={e=>{const v=parseFloat(e.target.value)||0;if(f.k==="w"||f.k==="h")applyDim(f.k,v);else setSos(pr=>pr.map(s=>s.id===sel?{...s,[f.k]:v}:s))}} style={{...barInput,width:44,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text}} step="0.5"/></label>)}
              </div>
              <div style={{...clBase,gap:4}}>
                <span title="Zone size in the mockup image's pixels" style={unitChip}>px</span>
                {[{k:"w",label:"W"},{k:"h",label:"H"}].map(f=><label key={f.k} style={{display:"flex",alignItems:"center",gap:2}}><span style={{color:c.textMuted,fontSize:8,fontWeight:700}}>{f.label}</span><input type="number" value={selPx?selPx[f.k]:""} disabled={!imgDims} placeholder={imgDims?"":"…"} onChange={e=>setDimPx(f.k,e.target.value)} title={imgDims?"Exact pixels of the template image":"Loading template size…"} style={{...barInput,width:52,border:`1px solid ${c.border}`,background:imgDims?c.bgCard:"transparent",color:imgDims?c.text:c.textMuted,opacity:imgDims?1:0.5,cursor:imgDims?"text":"not-allowed"}} step="10" min="1"/></label>)}
                {/* F12b: aspect-lock lives INSIDE the px cluster, right after H — it
                   locks the W↔H relationship, so this is its semantic home. */}
                {(()=>{const lockLabel=selPx?prettyAspect(selPx.w,selPx.h):prettyAspect(arLock||1,1);return<button onClick={toggleLock} disabled={!canLock} title={arLock!=null?`Proportions locked at ${lockLabel} — click to unlock`:"Lock the zone's proportions while resizing"} style={{...barBtn,gap:3,marginLeft:2,borderRadius:4,fontWeight:700,border:arLock!=null?"1.5px solid #6366f1":`1px solid ${c.border}`,background:arLock!=null?"rgba(99,102,241,0.15)":c.bgCard,color:arLock!=null?"#a5b4fc":(canLock?c.textSub:c.textDim),cursor:canLock?"pointer":"not-allowed",opacity:canLock?1:0.5}}>{arLock!=null?`⛓ ${lockLabel}`:"⛓ Lock"}</button>})()}
              </div>
            </div>}
            <div style={clRow}>
              {!selObj.corners&&<div style={{...clBase,gap:7,flex:"1 1 160px",maxWidth:240}}>
                <span style={{color:c.textSub,fontSize:10,fontWeight:600}}>↻</span>
                <input type="range" min="-45" max="45" value={selObj.rotation||0} onChange={e=>setSos(pr=>pr.map(s=>s.id===sel?{...s,rotation:Number(e.target.value)}:s))} style={{flex:1,minWidth:0,accentColor:"#6366f1"}}/>
                <span style={{color:c.textMuted,fontSize:10,minWidth:28,textAlign:"right"}}>{selObj.rotation||0}°</span>
              </div>}
              <div style={{...clBase,gap:6}}>
                <button onClick={()=>{soSnap();if(selObj.corners){
                  // Exit perspective: convert back to rectangle from bounding box
                  const cn=selObj.corners;const xs=[cn.tl.x,cn.tr.x,cn.br.x,cn.bl.x];const ys=[cn.tl.y,cn.tr.y,cn.br.y,cn.bl.y];
                  setSos(pr=>pr.map(s=>s.id===sel?{...s,x:Math.min(...xs),y:Math.min(...ys),w:Math.max(...xs)-Math.min(...xs),h:Math.max(...ys)-Math.min(...ys),corners:undefined,_origRect:undefined,rotation:0}:s));
                }else{
                  // Enter perspective: store original rect and create corners from it
                  const s=selObj;setSos(pr=>pr.map(so=>so.id===sel?{...so,_origRect:{x:s.x,y:s.y,w:s.w,h:s.h},corners:{tl:{x:s.x,y:s.y},tr:{x:s.x+s.w,y:s.y},br:{x:s.x+s.w,y:s.y+s.h},bl:{x:s.x,y:s.y+s.h}}}:so));
                }}} style={{...barBtn,border:selObj.corners?`1.5px solid #6366f1`:`1px solid ${c.border}`,background:selObj.corners?"rgba(99,102,241,0.15)":"transparent",color:selObj.corners?"#a5b4fc":c.textMuted}}>◇ Perspective</button>
                {selObj.corners&&<button onClick={()=>{soSnap();const orig=selObj._origRect||{x:selObj.x,y:selObj.y,w:selObj.w,h:selObj.h};setSos(pr=>pr.map(so=>so.id===sel?{...so,corners:{tl:{x:orig.x,y:orig.y},tr:{x:orig.x+orig.w,y:orig.y},br:{x:orig.x+orig.w,y:orig.y+orig.h},bl:{x:orig.x,y:orig.y+orig.h}}}:so))}} style={{...barBtn,border:`1px solid ${c.border}`,background:"transparent",color:c.textMuted}}>↺ Reset</button>}
                <button onClick={()=>delSO(sel)} style={{...barBtn,border:"none",background:"rgba(239,68,68,0.15)",color:"#f87171"}}>Delete</button>
              </div>
              {!selObj.corners&&selPx&&<div style={{...clBase,gap:3}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9,color:selOkAR?"#34d399":c.textMuted,fontWeight:selOkAR?700:500,whiteSpace:"nowrap"}}>{selOkAR?`${prettyAspect(selPx.w,selPx.h)} ✓`:`${prettyAspect(selPx.w,selPx.h)} · centers with margins`}<Tip id="zone-aspect" text="Zone size in the template image's pixels — type % or px, they stay in sync. Zones are proportional, not fixed-pixel: the design is fit INSIDE the zone keeping its own shape (never stretched), so you don't need an exact pixel size. What matters is the ASPECT: a 7:8 zone matches Printify print files (4200×4800 px) so the design fills it edge-to-edge. Any other aspect still looks correct — the design just centers with a little empty space around it. Use the ⛓ lock to hold the zone's proportions while you resize by typing or dragging." openTip={openTip} setOpenTip={setOpenTip}/></span>
              </div>}
            </div>
          </div>}
        </div>

        {/* Sidebar */}
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{padding:"10px 12px",borderRadius:9,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:c.text,marginBottom:6,display:"flex",alignItems:"center",justifyContent:"space-between"}}><span>Groups (shared)</span><Tip id="mso-grps" text="Groups are shared across ALL templates. Create once, reuse everywhere. Each group gets one design in the generate step." openTip={openTip} setOpenTip={setOpenTip}/></div>
            {grps.map(g=><div key={g.id} style={{display:"flex",alignItems:"center",gap:5,marginBottom:4,padding:"4px 7px",borderRadius:6,background:isDark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",border:`1px solid ${g.color}33`}}>
              <div style={{width:10,height:10,borderRadius:3,background:g.color,flexShrink:0}}/>
              <input value={g.name} onChange={e=>setGrps(pr=>pr.map(x=>x.id===g.id?{...x,name:e.target.value}:x))} style={{flex:1,background:"transparent",border:"none",color:c.text,fontSize:10,fontWeight:600,outline:"none",padding:0,minWidth:0}}/>
              <span style={{color:c.textMuted,fontSize:9,flexShrink:0}}>{sos.filter(s=>s.group===g.id).length}</span>
              {grps.length>1&&<button onClick={()=>{setGrps(pr=>pr.filter(x=>x.id!==g.id));const fb=grps.find(x=>x.id!==g.id)?.id;setSos(pr=>pr.map(s=>s.group===g.id?{...s,group:fb}:s))}} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:10,padding:0}}>✕</button>}
            </div>)}
            {/* Name-steering presets: create a group with EXACTLY one of the three
                names that already route perfectly on every path (lexicon + /design/i
                in manual, /light|dark/i in 1-click, "All Groups" → neutral everywhere).
                Disabled when a group with that exact name already exists. Free-text
                input stays for custom names. */}
            <div style={{display:"flex",gap:4,marginTop:6}}>
              {["Light Design","Dark Design","All Groups"].map(nm=>{
                const exists=grps.some(x=>(x.name||"").trim().toLowerCase()===nm.toLowerCase());
                return<button key={nm} disabled={exists} onClick={()=>addGroupNamed(nm)} title={exists?"already exists":`Create a “${nm}” group`} style={{flex:1,padding:"5px 4px",borderRadius:5,border:"none",fontSize:9,fontWeight:700,cursor:exists?"not-allowed":"pointer",background:exists?c.btnSecBg:"rgba(99,102,241,0.15)",color:exists?c.textDim:"#a5b4fc",opacity:exists?0.55:1}}>+ {nm}</button>;
              })}
            </div>
            <div style={{display:"flex",gap:4,marginTop:5}}>
              <input value={ngn} onChange={e=>setNgn(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addGroup()} placeholder="New group…" style={{flex:1,padding:"4px 7px",borderRadius:5,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:10,outline:"none"}}/>
              <button onClick={addGroup} style={{padding:"4px 9px",borderRadius:5,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:9,fontWeight:700,cursor:"pointer"}}>+</button>
            </div>
            <div style={{marginTop:7,padding:"6px 8px",borderRadius:6,background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.22)",color:c.textSub,fontSize:9,lineHeight:1.5}}>
              💡 Name a group after the DESIGN that goes there: <strong>Black</strong>/<strong>Dark Design</strong> groups get your dark designs, <strong>White</strong>/<strong>Light Design</strong> groups get your light designs, <strong>All Groups</strong> gets every design. Use the buttons or type your own name — either way it just works.
            </div>
          </div>

          <div style={{padding:"10px 12px",borderRadius:9,background:c.statusBarBg,border:`1px solid ${c.border}`,flex:1,overflow:"auto",maxHeight:200}}>
            <div style={{fontSize:11,fontWeight:700,color:c.text,marginBottom:6}}>Zones ({sos.length})</div>
            {sos.length===0&&<p style={{color:c.textMuted,fontSize:10,margin:0}}>Draw rectangles on the image.</p>}
            {sos.map((so,i)=>{const g=grps.find(x=>x.id===so.group);return<div key={so.id} style={{display:"flex",alignItems:"center",gap:4,marginBottom:3,padding:"3px 6px",borderRadius:5,background:sel===so.id?`${g?.color||"#6366f1"}20`:"transparent",border:`1px solid ${sel===so.id?(g?.color||"#6366f1"):"transparent"}`,cursor:"pointer"}} onClick={()=>setSel(so.id)}>
              <input type="checkbox" checked={bulkSel.has(so.id)} onChange={e=>{e.stopPropagation();setBulkSel(pr=>{const n=new Set(pr);if(n.has(so.id))n.delete(so.id);else n.add(so.id);return n})}} style={{accentColor:g?.color,width:11,height:11,cursor:"pointer"}}/>
              <div style={{width:8,height:8,borderRadius:2,background:g?.color||"#6366f1",flexShrink:0}}/>
              <span style={{color:c.text,fontSize:10,fontWeight:sel===so.id?700:500,flex:1}}>Zone {i+1}</span>
              <select className="pb-text" value={so.group} onChange={e=>{e.stopPropagation();setSos(pr=>pr.map(s=>s.id===so.id?{...s,group:e.target.value}:s))}} style={{padding:"2px 3px",borderRadius:4,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:9,outline:"none",cursor:"pointer"}}>{grps.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}</select>
              <button onClick={e=>{e.stopPropagation();delSO(so.id)}} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:10,padding:0}}>✕</button>
            </div>})}
            {bulkSel.size>0&&<div style={{marginTop:6,padding:"5px 7px",borderRadius:6,background:isDark?"rgba(99,102,241,0.08)":"rgba(99,102,241,0.1)",border:"1px solid rgba(99,102,241,0.2)",display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
              <span style={{color:"#a5b4fc",fontSize:9,fontWeight:600}}>{bulkSel.size} →</span>
              {grps.map(g=><button className="pb-text" key={g.id} onClick={()=>{setSos(pr=>pr.map(s=>bulkSel.has(s.id)?{...s,group:g.id}:s));setBulkSel(new Set())}} style={{padding:"2px 7px",borderRadius:4,border:"none",background:g.color,color:"white",fontSize:8,fontWeight:700,cursor:"pointer"}}>{g.name}</button>)}
            </div>}
          </div>
        </div>
      </div>}

      <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end",alignItems:"center"}}>
        {extraFooter}
        <button onClick={onCancel} style={{padding:"9px 22px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        <button onClick={handleSave} style={{padding:"9px 22px",borderRadius:9,border:"none",background:noDesign||sos.length>0?"linear-gradient(135deg,#6366f1,#8b5cf6)":c.btnSecBg,color:noDesign||sos.length>0?"white":c.textDim,fontSize:12,fontWeight:700,cursor:noDesign||sos.length>0?"pointer":"not-allowed"}}>{noDesign?"Save (No Design)":sos.length>0?`Save (${sos.length} zone${sos.length!==1?"s":""})`:""}</button>
      </div>
    </div>
  </div>;
}

/* ── FG Editor with flood-fill tool ── */
// ONE foreground editor, ONE behavior: full product photo + a RED cover-mask
// wash + Draw / Erase / Magic Fill. Used for hand-drawn masks AND imported
// foregrounds alike — a PSD clip or imported hair layer arrives as a standard
// foregroundMask (opaque = re-show base over the design) and edits identically.
// onSave(dataURL) writes foregroundMask.
function FGEditor({mockup,onSave,onCancel}){
  const{isDark}=useTheme();const c=t(isDark);
  const cvRef=useRef(null);const wRef=useRef(null);
  const[bs,setBs]=useState(20);const[tool,setTool]=useState("draw");const[painting,setPainting]=useState(false);
  const[zoom,setZoom]=useState(1);const[isPan,setIsPan]=useState(false);const[panS,setPanS]=useState(null);const[pan,setPan]=useState({x:0,y:0});
  const[openTip,setOpenTip]=useState(null);const[tolerance,setTolerance]=useState(30);
  const imgR=useRef(null);const maskR=useRef(null);const histR=useRef([]);const redoR=useRef([]);
  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);
  // An imported mask (PSD clip, or hair-as-mask) is stored with black RGB — the
  // bake reads ALPHA only, but the editor shows RGB, so recolor opaque pixels to
  // the red convention (255,50,50) on load; ALPHA is untouched so the render is
  // unchanged and it edits exactly like a hand-drawn mask.
  useEffect(()=>{histR.current=[];redoR.current=[];const img=new Image();img.onload=()=>{imgR.current=img;const cv=cvRef.current;cv.width=img.naturalWidth;cv.height=img.naturalHeight;const m=document.createElement("canvas");m.width=img.naturalWidth;m.height=img.naturalHeight;maskR.current=m;if(mockup.foregroundMask){const mi=new Image();mi.onload=()=>{const mx=m.getContext("2d");mx.drawImage(mi,0,0);const md=mx.getImageData(0,0,m.width,m.height);const dd=md.data;for(let k=0;k<dd.length;k+=4){if(dd[k+3]>0){dd[k]=255;dd[k+1]=50;dd[k+2]=50;}}mx.putImageData(md,0,0);snap();redraw()};mi.src=mockup.foregroundMask}else{snap();redraw()}};img.src=mockup.src},[mockup.src,mockup.foregroundMask]);
  useEffect(()=>{const h=e=>{if((e.metaKey||e.ctrlKey)&&e.key==="z"&&!e.shiftKey){e.preventDefault();undo()}if((e.metaKey||e.ctrlKey)&&e.key==="z"&&e.shiftKey){e.preventDefault();redo()}};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h)},[]);
  useEffect(()=>{const w=wRef.current;if(!w)return;const h=e=>{if(!e.ctrlKey&&!e.metaKey)return;e.preventDefault();setZoom(z=>Math.max(0.5,Math.min(6,z+(e.deltaY>0?-0.08:0.08)*z)))};w.addEventListener("wheel",h,{passive:false});return()=>w.removeEventListener("wheel",h)},[]);
  useEffect(()=>{const d=e=>{if(e.code==="Space"){e.preventDefault();e.stopPropagation();if(document.activeElement?.tagName==="BUTTON")document.activeElement.blur();if(!e.repeat)setIsPan(true)}};const u=e=>{if(e.code==="Space"){e.preventDefault();e.stopPropagation();setIsPan(false);setPanS(null)}};window.addEventListener("keydown",d,true);window.addEventListener("keyup",u,true);return()=>{window.removeEventListener("keydown",d,true);window.removeEventListener("keyup",u,true)}},[]);

  const snap=()=>{const m=maskR.current;if(!m)return;histR.current.push(m.getContext("2d").getImageData(0,0,m.width,m.height));if(histR.current.length>50)histR.current.shift();redoR.current=[]};
  // snap() stores the mask state BEFORE each action, so the top of histR is the
  // pre-last-action state — restore the POPPED snapshot itself. (The old code
  // popped then restored the NEW top, i.e. the state before the PREVIOUS action,
  // so from the 2nd action on a single ⌘Z reverted two steps.)
  const undo=()=>{const m=maskR.current;if(!m||histR.current.length<=1)return;redoR.current.push(m.getContext("2d").getImageData(0,0,m.width,m.height));const prev=histR.current.pop();m.getContext("2d").putImageData(prev,0,0);redraw()};
  const redo=()=>{const m=maskR.current;if(!m||redoR.current.length===0)return;const next=redoR.current.pop();histR.current.push(m.getContext("2d").getImageData(0,0,m.width,m.height));m.getContext("2d").putImageData(next,0,0);redraw()};
  // Full-clarity product photo + a translucent red wash (0.4) over the masked
  // region — the standard foreground look for every mask.
  const redraw=()=>{const cv=cvRef.current;const ctx=cv.getContext("2d");const img=imgR.current;const m=maskR.current;if(!img||!m)return;ctx.clearRect(0,0,cv.width,cv.height);ctx.drawImage(img,0,0);ctx.save();ctx.globalAlpha=0.4;ctx.drawImage(m,0,0);ctx.restore()};

  const getXY=(cx,cy)=>{const cv=cvRef.current;const r=cv.getBoundingClientRect();return{x:Math.round((cx-r.left)*(cv.width/r.width)),y:Math.round((cy-r.top)*(cv.height/r.height))}};
  const paint=(cx,cy)=>{const m=maskR.current;if(!m)return;const{x,y}=getXY(cx,cy);const cv=cvRef.current;const sx=cv.width/cv.getBoundingClientRect().width;const sb=bs*sx;const ctx=m.getContext("2d");ctx.globalCompositeOperation=tool==="erase"?"destination-out":"source-over";if(tool!=="erase")ctx.fillStyle="rgba(255,50,50,1)";ctx.beginPath();ctx.arc(x,y,sb/2,0,Math.PI*2);ctx.fill();ctx.globalCompositeOperation="source-over";redraw()};

  // Flood fill for "magic wand" style selection
  const floodFill=(cx,cy)=>{
    const img=imgR.current;const m=maskR.current;if(!img||!m)return;
    snap();
    const{x:sx,y:sy}=getXY(cx,cy);
    // Read source image pixels
    const tmpCv=document.createElement("canvas");tmpCv.width=img.naturalWidth;tmpCv.height=img.naturalHeight;
    const tmpCtx=tmpCv.getContext("2d");tmpCtx.drawImage(img,0,0);
    const srcData=tmpCtx.getImageData(0,0,tmpCv.width,tmpCv.height);const src=srcData.data;
    const w=tmpCv.width,h=tmpCv.height;
    if(sx<0||sy<0||sx>=w||sy>=h)return;
    const idx=(sy*w+sx)*4;const tr=src[idx],tg=src[idx+1],tb=src[idx+2];
    const visited=new Uint8Array(w*h);const stack=[[sx,sy]];const tol=tolerance;
    const match=(i)=>{const dr=src[i]-tr,dg=src[i+1]-tg,db=src[i+2]-tb;return Math.sqrt(dr*dr+dg*dg+db*db)<=tol};
    while(stack.length>0){const[px,py]=stack.pop();const pi=py*w+px;if(px<0||py<0||px>=w||py>=h||visited[pi])continue;if(!match(pi*4))continue;visited[pi]=1;stack.push([px+1,py],[px-1,py],[px,py+1],[px,py-1])}
    // Paint matched pixels onto mask
    const mCtx=m.getContext("2d");const mData=mCtx.getImageData(0,0,w,h);const md=mData.data;
    for(let i=0;i<w*h;i++){if(visited[i]){md[i*4]=255;md[i*4+1]=50;md[i*4+2]=50;md[i*4+3]=255}}
    mCtx.putImageData(mData,0,0);redraw();
  };

  const onDown=e=>{e.preventDefault();
    if(e.button===1||isPan){setPanS({x:e.clientX-pan.x,y:e.clientY-pan.y});setIsPan(true);return}
    const p=e.touches?e.touches[0]:e;
    if(tool==="fill"){floodFill(p.clientX,p.clientY);return}
    setPainting(true);snap();paint(p.clientX,p.clientY);
  };
  const onMove=e=>{if(isPan&&panS){setPan({x:e.clientX-panS.x,y:e.clientY-panS.y});return}if(!painting)return;const p=e.touches?e.touches[0]:e;paint(p.clientX,p.clientY)};
  const onUp=()=>{setPainting(false);setIsPan(false);setPanS(null)};

  return<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,padding:22,maxWidth:900,width:"100%",maxHeight:"90vh",overflow:"auto",border:`1px solid ${c.border}`}}>
      <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700,display:"inline-flex",alignItems:"center"}}>Foreground Layer<Tip id="fg-ed" text="Red marks the areas that appear IN FRONT of the design (hair, straps, a clip edge). An imported PSD foreground arrives here as red, ready to refine. Draw to add, Erase to remove, or Magic Fill to click an area and auto-select similar colors." openTip={openTip} setOpenTip={setOpenTip}/></h2>
      <p style={{color:c.textSub,margin:"0 0 10px",fontSize:12}}><strong>⌘Z</strong> undo • <strong>⌘⇧Z</strong> redo • <strong>⌘+Scroll</strong> zoom • <strong>Space+drag</strong> pan</p>
      <div style={{display:"flex",gap:5,marginBottom:8,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={()=>setTool("draw")} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:tool==="draw"?"#6366f1":c.btnSecBg,color:tool==="draw"?"white":c.textSub}}>✏️ Draw</button>
        <button onClick={()=>setTool("erase")} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:tool==="erase"?"#6366f1":c.btnSecBg,color:tool==="erase"?"white":c.textSub}}>🧹 Erase</button>
        <button onClick={()=>setTool("fill")} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:tool==="fill"?"#ec4899":c.btnSecBg,color:tool==="fill"?"white":c.textSub}}>🪄 Magic Fill<Tip id="fg-fill" text="Click on an area to auto-select all similar-colored pixels. Great for selecting hair, straps, or handles in one click. Adjust tolerance to be more or less selective." openTip={openTip} setOpenTip={setOpenTip}/></button>
        <button onClick={undo} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:c.btnSecBg,color:c.textSub}}>↩</button>
        <button onClick={redo} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:c.btnSecBg,color:c.textSub}}>↪</button>
        {tool!=="fill"&&<div style={{display:"flex",alignItems:"center",gap:4}}><span style={{color:c.textSub,fontSize:9}}>Size:</span><input type="range" min="5" max="80" value={bs} onChange={e=>setBs(Number(e.target.value))} style={{width:60,accentColor:"#6366f1"}}/><span style={{color:c.textMuted,fontSize:9}}>{bs}</span></div>}
        {tool==="fill"&&<div style={{display:"flex",alignItems:"center",gap:4}}><span style={{color:c.textSub,fontSize:9}}>Tolerance:</span><input type="range" min="5" max="80" value={tolerance} onChange={e=>setTolerance(Number(e.target.value))} style={{width:60,accentColor:"#ec4899"}}/><span style={{color:c.textMuted,fontSize:9}}>{tolerance}</span></div>}
        <button onClick={()=>{const m=maskR.current;if(m){m.getContext("2d").clearRect(0,0,m.width,m.height);snap();redraw()}}} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:"rgba(239,68,68,0.15)",color:"#f87171"}}>Clear</button>
        <div style={{display:"flex",gap:4,alignItems:"center",marginLeft:4}}>
          <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
          <span style={{color:c.textMuted,fontSize:9,minWidth:28}}>{Math.round(zoom*100)}%</span>
          <button onClick={()=>setZoom(z=>Math.min(6,z+0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
          {zoom!==1&&<button onClick={()=>{setZoom(1);setPan({x:0,y:0})}} style={{padding:"2px 7px",borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,cursor:"pointer"}}>Reset</button>}
        </div>
      </div>
      <div ref={wRef} style={{borderRadius:9,overflow:"auto",background:isDark?"#0a0a14":"#e2e8f0",cursor:isPan?"grab":tool==="fill"?"crosshair":"crosshair",maxHeight:"50vh"}}>
        <canvas ref={cvRef} style={{display:"block",width:zoom===1?"100%":`${zoom*100}%`}} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}/>
      </div>
      <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{padding:"9px 22px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        <button onClick={()=>{const m=maskR.current;if(m)onSave(m.toDataURL("image/png"))}} style={{padding:"9px 22px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save</button>
      </div>
    </div>
  </div>;
}

/* ─── Crop editor (O25b — Smart-Object-style scoped crop) ─────────────────
   Reframe a design by dragging a normalized crop rect {sx,sy,sw,sh} on the
   BARE design (flat coords — correct geometry for rotated/perspective zones
   because the warp happens in the live preview, not on the drag surface).

   Left: bare-design drag surface (salvaged from the shipped CropEditor).
   Right: LIVE mockup composite — debounced one-tile regenerateLayersStandalone
   with the in-progress crop, routed through paintDesignSO, so you see the
   crop on the actual mockup. Preview-resolution, ONE tile, never a grid
   re-bake (same cost as the SO/DC modals' per-subject regen).

   Footer: three SESSION scopes mirroring the SO editor — This mockup (a) /
   All instances of this template (c) / All mockups in batch (b). The parent
   writes to the matching crop store and runs the matching flush. These are
   CROP stores, fully separate from SO-placement stores. Phase-2 (permanent,
   combo-keyed) is intentionally NOT here. */
const FULL_CROP={sx:0,sy:0,sw:1,sh:1};
function isFullCrop(cr){return!cr||(cr.sx<=0.001&&cr.sy<=0.001&&cr.sw>=0.999&&cr.sh>=0.999)}
const CROP_SCOPES=[
  {v:"thisonly",label:"This mockup",color:"#60a5fa"},
  {v:"template",label:"All instances of this template",color:"#f59e0b"},
  {v:"batch",label:"All mockups in batch",color:"#34d399"},
];
function CropScopeModal({tpl,gdm,resolveCrop,onSave,onCancel}){
  const{isDark}=useTheme();const c=t(isDark);
  const m=migrate(tpl);
  const sos=m.smartObjects||[];
  // Distinct design groups present on this template that actually have a
  // design assigned (in gdm). Only these can be cropped.
  const groupsInUse=[...new Set(sos.map(s=>s.group).filter(g=>gdm&&gdm[g]))];
  const[activeGroup,setActiveGroup]=useState(groupsInUse[0]||null);
  // Seed each group's crop from the currently-resolved effective crop (so
  // re-opening shows the active crop), else full-frame.
  const[cropByGroup,setCropByGroup]=useState(()=>{
    const init={};
    for(const g of groupsInUse){const cur=resolveCrop?resolveCrop(g):null;init[g]=cur&&!isFullCrop(cur)?{...cur}:{...FULL_CROP};}
    return init;
  });
  const[scope,setScope]=useState("thisonly");
  const[preview,setPreview]=useState(null); // {baseOut,designOut,fgOut}
  const[previewing,setPreviewing]=useState(false);
  // Fixed crop-surface size in px, frozen to the design's aspect (fit within a
  // bound). Container is design-aspect → objectFit:contain has NO letterbox →
  // crop% maps 1:1 to design%. Recomputed ONLY when the design (src) changes,
  // never by the crop — this is what keeps the surface static by construction.
  const[box,setBox]=useState(null); // {w,h}
  const imgWrapRef=useRef(null);
  const dragRef=useRef(null); // {mode, startN:{x,y}, startCrop}
  const src=activeGroup?gdm[activeGroup]:null;
  const crop=activeGroup?(cropByGroup[activeGroup]||FULL_CROP):FULL_CROP;

  // Freeze the crop surface size to the design's aspect (fit within a bound),
  // once per design. Deps [src] only → a crop change never re-runs this.
  useEffect(()=>{
    if(!src){setBox(null);return}
    let cancelled=false;
    const img=new Image();
    img.onload=()=>{
      if(cancelled||!img.naturalWidth||!img.naturalHeight)return;
      const ar=img.naturalWidth/img.naturalHeight;
      const MAXW=420,MAXH=340;
      let w=MAXW,h=MAXW/ar;
      if(h>MAXH){h=MAXH;w=MAXH*ar;}
      setBox({w:Math.round(w),h:Math.round(h)});
    };
    img.src=src;
    return()=>{cancelled=true};
  },[src]);

  // LIVE composite preview — debounced ~220ms, one tile, preview resolution.
  const cropKey=JSON.stringify(cropByGroup);
  useEffect(()=>{
    if(groupsInUse.length===0)return;
    let cancelled=false;setPreviewing(true);
    const handle=setTimeout(()=>{
      regenerateLayersStandalone(tpl,gdm,cropByGroup)
        .then(r=>{if(!cancelled){setPreview(r);setPreviewing(false)}})
        .catch(()=>{if(!cancelled)setPreviewing(false)});
    },220);
    return()=>{cancelled=true;clearTimeout(handle)};
  },[cropKey]);

  const setCrop=(next)=>setCropByGroup(p=>({...p,[activeGroup]:next}));
  const clamp01=v=>Math.max(0,Math.min(1,v));
  const MINS=0.05; // minimum crop size (normalized)
  const ptrN=(e)=>{const el=imgWrapRef.current;if(!el)return{x:0,y:0};const r=el.getBoundingClientRect();const p=e.touches?e.touches[0]:e;return{x:clamp01((p.clientX-r.left)/r.width),y:clamp01((p.clientY-r.top)/r.height)}};

  const onDown=(mode)=>(e)=>{e.preventDefault();e.stopPropagation();dragRef.current={mode,startN:ptrN(e),startCrop:{...crop}};
    window.addEventListener("mousemove",onMove);window.addEventListener("mouseup",onUp);
    window.addEventListener("touchmove",onMove,{passive:false});window.addEventListener("touchend",onUp);};
  const onMove=(e)=>{
    const d=dragRef.current;if(!d)return;e.preventDefault();
    const n=ptrN(e);const dx=n.x-d.startN.x,dy=n.y-d.startN.y;const s=d.startCrop;
    let{sx,sy,sw,sh}=s;
    if(d.mode==="move"){sx=clamp01(s.sx+dx);sy=clamp01(s.sy+dy);if(sx+sw>1)sx=1-sw;if(sy+sh>1)sy=1-sh;}
    else{
      let left=s.sx,top=s.sy,right=s.sx+s.sw,bottom=s.sy+s.sh;
      if(d.mode.includes("w"))left=clamp01(Math.min(s.sx+dx,right-MINS));
      if(d.mode.includes("e"))right=clamp01(Math.max(s.sx+s.sw+dx,left+MINS));
      if(d.mode.includes("n"))top=clamp01(Math.min(s.sy+dy,bottom-MINS));
      if(d.mode.includes("s"))bottom=clamp01(Math.max(s.sy+s.sh+dy,top+MINS));
      sx=left;sy=top;sw=right-left;sh=bottom-top;
    }
    setCrop({sx,sy,sw,sh});
  };
  const onUp=()=>{dragRef.current=null;window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);window.removeEventListener("touchmove",onMove);window.removeEventListener("touchend",onUp);};

  const doSave=()=>onSave(cropByGroup,scope);

  const pct=v=>`${v*100}%`;
  const handle=(mode,style)=>{
    const base={position:"absolute",width:14,height:14,background:"#6366f1",border:"2px solid white",borderRadius:3,boxShadow:"0 1px 4px rgba(0,0,0,0.5)",zIndex:3};
    const cur={nw:"nwse-resize",ne:"nesw-resize",se:"nwse-resize",sw:"nesw-resize",n:"ns-resize",s:"ns-resize",e:"ew-resize",w:"ew-resize"}[mode];
    return<div onMouseDown={onDown(mode)} onTouchStart={onDown(mode)} style={{...base,cursor:cur,...style}}/>;
  };

  return<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,padding:22,maxWidth:920,width:"100%",maxHeight:"92vh",overflow:"auto",border:`1px solid ${c.border}`}}>
      <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700}}>⛶ Crop Design</h2>
      <p style={{color:c.textSub,margin:"0 0 12px",fontSize:12}}>Drag the box to reframe the design — the live preview shows it on the mockup. Pick a scope below, then Save.</p>
      {!src?
        <p style={{color:c.textMuted,fontSize:13,padding:"30px 0",textAlign:"center"}}>No design on this mockup to crop.</p>
      :<>
        {groupsInUse.length>1&&<div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{color:c.textMuted,fontSize:10,fontWeight:600}}>Design:</span>
          {groupsInUse.map((g,i)=>{const gr=(m.groups||[]).find(x=>x.id===g);return<button className="pb-text" key={g} onClick={()=>setActiveGroup(g)} style={{padding:"4px 10px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:activeGroup===g?"#6366f1":c.btnSecBg,color:activeGroup===g?"white":c.textSub}}>{gr?.name||`Design ${i+1}`}</button>})}
        </div>}
        <div style={{display:"flex",gap:5,marginBottom:8,alignItems:"center"}}>
          <button onClick={()=>setCrop({...FULL_CROP})} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",background:c.btnSecBg,color:c.textSub}}>Reset crop</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {/* Left — bare design drag surface */}
          <div>
            <div style={{color:c.textMuted,fontSize:9,fontWeight:600,marginBottom:4}}>DRAG TO CROP</div>
            <div style={{borderRadius:9,overflow:"auto",background:isDark?"#0a0a14":"#e2e8f0",maxHeight:"56vh",display:"flex",justifyContent:"center",alignItems:"flex-start",padding:14,boxSizing:"border-box"}}>
              {box?
                /* DEAD-SIMPLE crop surface (o25f): a FIXED-size container whose
                   px size is frozen to the design's aspect (see `box`). The
                   design <img> is objectFit:contain inside it — painted once,
                   centered, STATIC: its size/position/src reference NOTHING
                   about the crop. The crop rect + dim bands are absolutely-
                   positioned overlays in the SAME fixed coord system (container
                   == design aspect, so crop% == design%). Dragging the box
                   moves ONLY the overlay; the image cannot move. */
                <div ref={imgWrapRef} style={{position:"relative",width:box.w,height:box.h,flexShrink:0,userSelect:"none",touchAction:"none",overflow:"hidden",borderRadius:6,
                  backgroundColor:"#ffffff",
                  backgroundImage:"linear-gradient(45deg,#cbd5e1 25%,transparent 25%,transparent 75%,#cbd5e1 75%,#cbd5e1),linear-gradient(45deg,#cbd5e1 25%,transparent 25%,transparent 75%,#cbd5e1 75%,#cbd5e1)",
                  backgroundSize:"20px 20px",backgroundPosition:"0 0,10px 10px"}}>
                  <img className="pb-img" src={src} alt="" draggable={false} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",display:"block",pointerEvents:"none"}}/>
                  {/* Dim everything outside the crop rect (4 bands) */}
                  <div style={{position:"absolute",top:0,left:0,right:0,height:pct(crop.sy),background:"rgba(0,0,0,0.55)",pointerEvents:"none"}}/>
                  <div style={{position:"absolute",left:0,right:0,top:pct(crop.sy+crop.sh),bottom:0,background:"rgba(0,0,0,0.55)",pointerEvents:"none"}}/>
                  <div style={{position:"absolute",left:0,top:pct(crop.sy),width:pct(crop.sx),height:pct(crop.sh),background:"rgba(0,0,0,0.55)",pointerEvents:"none"}}/>
                  <div style={{position:"absolute",left:pct(crop.sx+crop.sw),top:pct(crop.sy),right:0,height:pct(crop.sh),background:"rgba(0,0,0,0.55)",pointerEvents:"none"}}/>
                  {/* Crop rect + handles */}
                  <div onMouseDown={onDown("move")} onTouchStart={onDown("move")} style={{position:"absolute",left:pct(crop.sx),top:pct(crop.sy),width:pct(crop.sw),height:pct(crop.sh),border:"1.5px solid #818cf8",boxShadow:"0 0 0 1px rgba(0,0,0,0.4)",cursor:"move",zIndex:2}}>
                    {/* Edge strips: dragging a BORDER resizes that edge (not move).
                        Inset from the corners so the corner handles win there. */}
                    <div onMouseDown={onDown("w")} onTouchStart={onDown("w")} style={{position:"absolute",top:9,bottom:9,left:-4,width:8,cursor:"ew-resize",zIndex:1}}/>
                    <div onMouseDown={onDown("e")} onTouchStart={onDown("e")} style={{position:"absolute",top:9,bottom:9,right:-4,width:8,cursor:"ew-resize",zIndex:1}}/>
                    <div onMouseDown={onDown("n")} onTouchStart={onDown("n")} style={{position:"absolute",left:9,right:9,top:-4,height:8,cursor:"ns-resize",zIndex:1}}/>
                    <div onMouseDown={onDown("s")} onTouchStart={onDown("s")} style={{position:"absolute",left:9,right:9,bottom:-4,height:8,cursor:"ns-resize",zIndex:1}}/>
                    {handle("nw",{left:-7,top:-7})}
                    {handle("ne",{right:-7,top:-7})}
                    {handle("sw",{left:-7,bottom:-7})}
                    {handle("se",{right:-7,bottom:-7})}
                    {handle("n",{left:"calc(50% - 7px)",top:-7})}
                    {handle("s",{left:"calc(50% - 7px)",bottom:-7})}
                    {handle("w",{left:-7,top:"calc(50% - 7px)"})}
                    {handle("e",{right:-7,top:"calc(50% - 7px)"})}
                  </div>
                </div>
              :<div style={{height:200,display:"flex",alignItems:"center",justifyContent:"center",color:c.textMuted,fontSize:11}}>Loading…</div>}
            </div>
          </div>
          {/* Right — live mockup composite */}
          <div>
            <div style={{color:c.textMuted,fontSize:9,fontWeight:600,marginBottom:4,display:"flex",alignItems:"center",gap:6}}>ON THE MOCKUP {previewing&&<span style={{color:"#a5b4fc"}}>updating…</span>}</div>
            <div style={{borderRadius:9,overflow:"hidden",background:isDark?"#0a0a14":"#e2e8f0",maxHeight:"52vh"}}>
              <div style={{position:"relative",width:"100%",paddingTop:"75%"}}>
                {(!preview||!preview.baseOut)?
                  <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:c.textMuted,fontSize:11}}>{previewing?"Rendering…":"Preview"}</div>
                :<>
                  <img className="pb-img" src={preview.baseOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>
                  {preview.designOut&&<img className="pb-img" src={preview.designOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>}
                  {preview.fgOut&&<img className="pb-img" src={preview.fgOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>}
                </>}
              </div>
            </div>
          </div>
        </div>
        {/* Scope selector */}
        <div style={{marginTop:12,padding:"10px 12px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
          <div style={{color:c.textSub,fontSize:10,fontWeight:600,marginBottom:6}}>Apply this crop to:</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {CROP_SCOPES.map(o=><button key={o.v} onClick={()=>setScope(o.v)} style={{padding:"4px 10px",borderRadius:6,border:scope===o.v?`1.5px solid ${o.color}`:`1px solid rgba(255,255,255,0.1)`,background:scope===o.v?`${o.color}15`:"transparent",color:scope===o.v?o.color:"#94a3b8",fontSize:10,fontWeight:600,cursor:"pointer",transition:"all 0.15s"}}>{o.label}</button>)}
          </div>
        </div>
      </>}
      <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{padding:"9px 22px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        <button onClick={doSave} disabled={!src} style={{padding:"9px 22px",borderRadius:9,border:"none",background:src?"linear-gradient(135deg,#6366f1,#8b5cf6)":c.btnSecBg,color:"white",fontSize:12,fontWeight:700,cursor:src?"pointer":"not-allowed",opacity:src?1:0.5}}>Save crop</button>
      </div>
    </div>
  </div>;
}

/* Design Controls Panel — reusable for global, per-set, per-mockup */
const DEFAULT_DC={brightness:100,contrast:100,saturation:100,shadow:0,shadowAngle:135,shadowOffset:5,shadowBlurAmt:8,opacity:89,shadowLift:0};
function DCPanel({dc,onChange,label,c,onReset,onApply,onEdit,compact,tipId,tipText,openTip,setOpenTip}){
  const isDef=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&(dc.opacity||89)===89&&(dc.shadowLift||0)===0;
  const sliders=[{k:"opacity",label:"Opacity",min:10,max:100},{k:"brightness",label:"Brightness",min:50,max:150},{k:"contrast",label:"Contrast",min:50,max:150},{k:"saturation",label:"Saturation",min:0,max:200},{k:"shadowLift",label:"Lift Shadows",min:0,max:100},{k:"shadow",label:"Drop Shadow",min:0,max:100}];
  // When an Edit-with-Preview modal is wired (the global + per-set bulk
  // scopes), the inline panel is a READ-ONLY summary, not an editor. The
  // sliders are disabled — they show current values at a glance — and ALL
  // adjustment + the (heavy) re-bake happens inside the preview modal via
  // its Apply. This keeps the expensive regeneration behind a single,
  // preview-gated action, and removes the old dead-end where the inline
  // sliders looked editable but had no commit path (no inline onChange,
  // Apply, or Reset in this mode → one fewer way to touch state, zero new
  // ways). Without onEdit the panel stays fully interactive.
  const readOnly=!!onEdit;
  // accent: brightness/contrast/etc use indigo; shadow sub-sliders use violet
  const sliderRow=(k,labelTxt,min,max,def,accent,suffix)=>{
    const val=dc[k]||def;
    if(readOnly)return<div key={k} onClick={onEdit} title="Open Edit with Preview to adjust" style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
      <span style={{color:c.textSub,fontSize:9,fontWeight:600,minWidth:58}}>{labelTxt}</span>
      <input type="range" disabled readOnly min={min} max={max} value={val} onChange={()=>{}} style={{flex:1,accentColor:accent,opacity:0.5,pointerEvents:"none"}}/>
      <span style={{color:c.textSub,fontSize:9,minWidth:24,fontWeight:600}}>{val}{suffix}</span>
    </div>;
    return<div key={k} style={{display:"flex",alignItems:"center",gap:5}}>
      <span style={{color:c.textSub,fontSize:9,fontWeight:600,minWidth:58}}>{labelTxt}</span>
      <input type="range" min={min} max={max} value={val} onChange={e=>onChange({...dc,[k]:Number(e.target.value)})} style={{flex:1,accentColor:accent}}/>
      <span style={{color:c.textMuted,fontSize:9,minWidth:24}}>{val}{suffix}</span>
    </div>;
  };
  return<div style={{padding:compact?"8px 10px":"10px 14px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`,marginBottom:compact?0:12}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:readOnly?4:8,gap:6}}>
      <span style={{color:c.text,fontSize:compact?10:11,fontWeight:700,display:"inline-flex",alignItems:"center"}}>{label||"🎛 Design Controls"}{tipId&&tipText&&<Tip id={tipId} text={tipText} openTip={openTip} setOpenTip={setOpenTip}/>}</span>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        {/* Edit — opens a modal-based editor with full-size live preview.
            For the bulk (global/set) scopes this is the ONLY path to adjust
            AND commit: its Apply previews the change then triggers the
            re-bake in one step. So it's the prominent primary action here. */}
        {onEdit&&<button onClick={onEdit} style={{padding:"5px 12px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(99,102,241,0.35)"}} title="Open the live-preview editor to adjust and apply">✎ Edit with Preview</button>}
        {/* Apply — only when there is NO preview modal: commits the panel's
            current values directly. The heavy re-bake stays an explicit
            click either way (never auto-applied on slider drag). */}
        {onApply&&!onEdit&&<button onClick={onApply} style={{padding:"3px 10px",borderRadius:5,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:9,fontWeight:700,cursor:"pointer"}} title="Regenerate affected mockups with current DC values baked in">Apply</button>}
        {!readOnly&&!isDef&&onReset&&<button onClick={onReset} style={{padding:"3px 10px",borderRadius:5,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,fontWeight:600,cursor:"pointer"}}>Reset</button>}
      </div>
    </div>
    {readOnly&&<div onClick={onEdit} style={{color:c.textMuted,fontSize:9,marginBottom:8,cursor:"pointer"}} title="Open Edit with Preview to adjust">Current settings — adjust in <span style={{color:"#a5b4fc",fontWeight:600}}>Edit with Preview</span></div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 14px"}}>
      {sliders.map(s=>sliderRow(s.k,s.label,s.min,s.max,0,"#6366f1",""))}
    </div>
    {dc.shadow>0&&<div style={{marginTop:6,paddingTop:6,borderTop:`1px solid ${c.border}`,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 14px"}}>
      {sliderRow("shadowAngle","Direction",0,360,135,"#8b5cf6","°")}
      {sliderRow("shadowOffset","Offset",1,30,5,"#8b5cf6","")}
      {sliderRow("shadowBlurAmt","Blur",0,40,8,"#8b5cf6","")}
    </div>}
  </div>;
}

/* DC Modal — full-size with zoom controls matching FG/SO editors */
function DCModal({mockup,baseOut,designOut,fgOut,dc,onChange,onSave,onCancel}){
  const{isDark}=useTheme();const c=t(isDark);
  const wrapRef=useRef(null);
  const[zoom,setZoom]=useState(1);
  const[openTip,setOpenTip]=useState(null);
  const[liftedDesign,setLiftedDesign]=useState(null);
  const[pixelCache,setPixelCache]=useState(null); // {data: ImageData, w, h}
  const cssFilter=dcToCss(dc);
  const isDefault=cssFilter==="none"&&(dc.shadowLift||0)===0;
  const sliders=[{k:"opacity",label:"Opacity",min:10,max:100},{k:"brightness",label:"Brightness",min:50,max:150},{k:"contrast",label:"Contrast",min:50,max:150},{k:"saturation",label:"Saturation",min:0,max:200},{k:"shadowLift",label:"Lift Shadows",min:0,max:100},{k:"shadow",label:"Drop Shadow",min:0,max:100}];

  // Load and cache design image pixel data at preview resolution
  useEffect(()=>{
    if(!designOut){setPixelCache(null);return}
    const img=new Image();img.onload=()=>{
      const scale=Math.min(1,800/img.naturalWidth);
      const w=Math.round(img.naturalWidth*scale);const h=Math.round(img.naturalHeight*scale);
      const cv=document.createElement("canvas");cv.width=w;cv.height=h;
      cv.getContext("2d").drawImage(img,0,0,w,h);
      setPixelCache({data:cv.getContext("2d").getImageData(0,0,w,h),w,h});
    };img.src=designOut;
  },[designOut]);

  // Apply shadow lift in real-time using cached pixel data
  useEffect(()=>{
    const lift=dc.shadowLift||0;
    if(!pixelCache||lift===0){setLiftedDesign(null);return}
    const{data,w,h}=pixelCache;
    const cv=document.createElement("canvas");cv.width=w;cv.height=h;
    const ctx=cv.getContext("2d");
    const copy=new ImageData(new Uint8ClampedArray(data.data),w,h);
    ctx.putImageData(copy,0,0);
    applyShadowLift(cv,lift);
    setLiftedDesign(cv.toDataURL("image/png"));
  },[dc.shadowLift,pixelCache]);
  const previewDesign=liftedDesign||designOut;

  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);
  // Ctrl/Cmd+scroll zoom
  useEffect(()=>{const w=wrapRef.current;if(!w)return;const h=e=>{if(!e.ctrlKey&&!e.metaKey)return;e.preventDefault();setZoom(z=>Math.max(0.25,Math.min(6,z+(e.deltaY>0?-0.08:0.08)*z)))};w.addEventListener("wheel",h,{passive:false});return()=>w.removeEventListener("wheel",h)},[]);

  return<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,maxWidth:960,width:"100%",maxHeight:"92vh",border:`1px solid ${c.border}`,display:"flex",flexDirection:"column",overflow:"visible"}}>
      {/* Header — not scrollable, tooltip can overflow */}
      <div style={{padding:"22px 22px 0",flexShrink:0,position:"relative",zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div>
            <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700,display:"inline-flex",alignItems:"center"}}>🎛 Design Controls<Tip id="dc-individual" text="These controls apply to this specific mockup only, overriding both the global and set-level settings. Adjust brightness, contrast, and saturation to fine-tune how the design looks on this particular mockup. Use Lift Shadows to brighten only the dark areas of the design (like the shadows adjustment on a phone). Use the drop shadow slider to add depth beneath the design. Click Apply to save, or Cancel to discard changes." openTip={openTip} setOpenTip={setOpenTip}/></h2>
            <p style={{color:c.textSub,margin:0,fontSize:12}}>Adjust design appearance — changes only affect the design overlay, not the mockup photo. <strong>⌘+Scroll</strong> (Mac) or <strong>Ctrl+Scroll</strong> (Windows) to zoom.</p>
          </div>
          {!isDefault&&<button onClick={()=>onChange({...DEFAULT_DC})} style={{padding:"5px 14px",borderRadius:7,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>Reset all</button>}
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{padding:"0 22px 22px",overflow:"auto",flex:1}}>
        {/* Zoom controls */}
        <div style={{display:"flex",gap:5,marginBottom:8,alignItems:"center"}}>
          <span style={{color:c.textSub,fontSize:10,fontWeight:600}}>🔍</span>
          <button onClick={()=>setZoom(z=>Math.max(0.25,z-0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
          <span style={{color:c.textMuted,fontSize:9,minWidth:28}}>{Math.round(zoom*100)}%</span>
          <button onClick={()=>setZoom(z=>Math.min(6,z+0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
          {zoom!==1&&<button onClick={()=>setZoom(1)} style={{padding:"2px 7px",borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,cursor:"pointer"}}>Reset</button>}
        </div>

        {/* Preview with zoom — 3 layers */}
        <div ref={wrapRef} style={{borderRadius:10,overflow:"auto",background:isDark?"#0a0a14":"#e2e8f0",maxHeight:"55vh"}}>
          <div style={{width:zoom===1?"100%":`${zoom*100}%`}}>
            <div style={{position:"relative",width:"100%",paddingTop:"75%"}}>
              <img className="pb-img" src={baseOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>
              {previewDesign&&<img className="pb-img" src={previewDesign} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain",filter:cssFilter,opacity:(dc.opacity||89)/100,transition:"filter 0.1s, opacity 0.1s"}}/>}
              {fgOut&&<img className="pb-img" src={fgOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>}
            </div>
          </div>
        </div>

        {/* Sliders */}
        <div style={{marginTop:14,padding:"14px 16px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px"}}>
            {sliders.map(s=><div key={s.k} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>{s.label}</span>
              <input type="range" min={s.min} max={s.max} value={dc[s.k]} onChange={e=>onChange({...dc,[s.k]:Number(e.target.value)})} style={{flex:1,accentColor:"#6366f1",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc[s.k]||0}{s.k!=="shadow"&&s.k!=="shadowLift"?"%":""}</span>
            </div>)}
          </div>
          {dc.shadow>0&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px",marginTop:10,paddingTop:10,borderTop:`1px solid ${c.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Direction</span>
              <input type="range" min={0} max={360} value={dc.shadowAngle||135} onChange={e=>onChange({...dc,shadowAngle:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowAngle||135}°</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Offset</span>
              <input type="range" min={1} max={30} value={dc.shadowOffset||5} onChange={e=>onChange({...dc,shadowOffset:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowOffset||5}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Blur</span>
              <input type="range" min={0} max={40} value={dc.shadowBlurAmt||8} onChange={e=>onChange({...dc,shadowBlurAmt:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowBlurAmt||8}</span>
            </div>
          </div>}
        </div>

        <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
          <button onClick={onCancel} style={{padding:"9px 22px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={onSave} style={{padding:"9px 22px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Apply</button>
        </div>
      </div>
    </div>
  </div>;
}

/* BulkDCEditor — modal for adjusting DC at global or set scope.

   Same UX as DCModal but operates on a user-pickable subject from the
   batch rather than one fixed mockup. When the user clicks "Edit with
   preview" on a bulk DC panel, this modal opens. Live preview updates
   in real-time as sliders move (CSS overlay on design layer + canvas
   shadow lift). Clicking "Apply to all" closes the modal AND fires the
   parent's onApply callback to regenerate all affected mockups with
   the new values baked in.

   Subject can be changed via dropdown — useful for testing how an
   adjustment looks on different template types (mug vs t-shirt).
   Layers are regenerated once per subject change, not per slider tick. */
function BulkDCEditor({title,scopeDescription,dc,onChange,subjects,onApply,onCancel}){
  const{isDark}=useTheme();const c=t(isDark);
  const wrapRef=useRef(null);
  const[zoom,setZoom]=useState(1);
  const[openTip,setOpenTip]=useState(null);
  const[liftedDesign,setLiftedDesign]=useState(null);
  const[pixelCache,setPixelCache]=useState(null);
  const[selectedKey,setSelectedKey]=useState(subjects[0]?.key||null);
  const[layers,setLayers]=useState(null);
  const[loading,setLoading]=useState(false);
  // Snapshot of the dc value when the modal opened. Cancel restores
  // this — the user shouldn't have their slider tweaks persist if
  // they change their mind. The parent's dc state has been mutating
  // throughout the slider drags so we need to be able to roll back.
  const initialDcRef=useRef(dc);

  const subject=subjects.find(s=>s.key===selectedKey)||null;

  // Keep selection in bounds if subjects list changes
  useEffect(()=>{
    if(subjects.length===0){setSelectedKey(null);return}
    if(!subjects.some(s=>s.key===selectedKey))setSelectedKey(subjects[0].key);
  },[subjects,selectedKey]);

  // Regenerate editing layers when subject changes
  useEffect(()=>{
    if(!subject){setLayers(null);return}
    let cancelled=false;
    setLoading(true);
    regenerateLayersStandalone(subject.tpl,subject.gdm).then(r=>{
      if(cancelled)return;
      setLayers(r);setLoading(false);
    }).catch(()=>{if(!cancelled){setLayers(null);setLoading(false)}});
    return()=>{cancelled=true};
  },[selectedKey,subject?.tpl?.id]);

  // Cache pixel data for shadow-lift performance
  useEffect(()=>{
    if(!layers?.designOut){setPixelCache(null);return}
    const img=new Image();img.onload=()=>{
      const scale=Math.min(1,800/img.naturalWidth);
      const w=Math.round(img.naturalWidth*scale);
      const h=Math.round(img.naturalHeight*scale);
      const cv=document.createElement("canvas");cv.width=w;cv.height=h;
      cv.getContext("2d").drawImage(img,0,0,w,h);
      setPixelCache({data:cv.getContext("2d").getImageData(0,0,w,h),w,h});
    };img.src=layers.designOut;
  },[layers?.designOut]);

  // Live shadow-lift
  useEffect(()=>{
    const lift=dc.shadowLift||0;
    if(!pixelCache||lift===0){setLiftedDesign(null);return}
    const{data,w,h}=pixelCache;
    const cv=document.createElement("canvas");cv.width=w;cv.height=h;
    const ctx=cv.getContext("2d");
    const copy=new ImageData(new Uint8ClampedArray(data.data),w,h);
    ctx.putImageData(copy,0,0);
    applyShadowLift(cv,lift);
    setLiftedDesign(cv.toDataURL("image/png"));
  },[dc.shadowLift,pixelCache]);

  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);
  // Ctrl/Cmd+scroll zoom
  useEffect(()=>{const w=wrapRef.current;if(!w)return;const h=e=>{if(!e.ctrlKey&&!e.metaKey)return;e.preventDefault();setZoom(z=>Math.max(0.25,Math.min(6,z+(e.deltaY>0?-0.08:0.08)*z)))};w.addEventListener("wheel",h,{passive:false});return()=>w.removeEventListener("wheel",h)},[]);

  const cssFilter=dcToCss(dc);
  const isDef=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&(dc.opacity||89)===89&&(dc.shadowLift||0)===0;
  const previewDesign=liftedDesign||layers?.designOut;
  const sliders=[{k:"opacity",label:"Opacity",min:10,max:100},{k:"brightness",label:"Brightness",min:50,max:150},{k:"contrast",label:"Contrast",min:50,max:150},{k:"saturation",label:"Saturation",min:0,max:200},{k:"shadowLift",label:"Lift Shadows",min:0,max:100},{k:"shadow",label:"Drop Shadow",min:0,max:100}];

  return<div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:c.overlay,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(12px)"}}>
    <div style={{background:c.modalBg,borderRadius:16,maxWidth:960,width:"100%",maxHeight:"92vh",border:`1px solid ${c.border}`,display:"flex",flexDirection:"column",overflow:"visible"}}>
      <div style={{padding:"22px 22px 0",flexShrink:0,position:"relative",zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:14,flexWrap:"wrap"}}>
          <div style={{flex:"1 1 280px",minWidth:0}}>
            <h2 style={{color:c.text,margin:"0 0 3px",fontSize:18,fontWeight:700,display:"inline-flex",alignItems:"center"}}>{title}</h2>
            <p style={{color:c.textSub,margin:0,fontSize:12}}>{scopeDescription} <strong>⌘+Scroll</strong> (Mac) or <strong>Ctrl+Scroll</strong> (Windows) to zoom.</p>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
            <span style={{color:c.textMuted,fontSize:11,fontWeight:600}}>Preview:</span>
            <select
              value={selectedKey||""}
              onChange={e=>setSelectedKey(e.target.value)}
              style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:11,outline:"none",cursor:"pointer",maxWidth:240}}
              title="Pick which mockup to preview adjustments on"
            >
              {subjects.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {!isDef&&<button onClick={()=>onChange({...DEFAULT_DC})} style={{padding:"5px 14px",borderRadius:7,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>Reset</button>}
          </div>
        </div>
      </div>

      <div style={{padding:"0 22px 22px",overflow:"auto",flex:1}}>
        {/* Zoom controls */}
        <div style={{display:"flex",gap:5,marginBottom:8,alignItems:"center"}}>
          <span style={{color:c.textSub,fontSize:10,fontWeight:600}}>🔍</span>
          <button onClick={()=>setZoom(z=>Math.max(0.25,z-0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
          <span style={{color:c.textMuted,fontSize:9,minWidth:28}}>{Math.round(zoom*100)}%</span>
          <button onClick={()=>setZoom(z=>Math.min(6,z+0.25))} style={{width:22,height:22,borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
          {zoom!==1&&<button onClick={()=>setZoom(1)} style={{padding:"2px 7px",borderRadius:4,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:9,cursor:"pointer"}}>Reset zoom</button>}
        </div>

        {/* Preview with zoom */}
        <div ref={wrapRef} style={{borderRadius:10,overflow:"auto",background:isDark?"#0a0a14":"#e2e8f0",maxHeight:"55vh"}}>
          <div style={{width:zoom===1?"100%":`${zoom*100}%`}}>
            <div style={{position:"relative",width:"100%",paddingTop:"75%"}}>
              {loading||!layers?<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}><div className="mg-spinner"/></div>:<>
                <img className="pb-img" src={layers.baseOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>
                {previewDesign&&<img className="pb-img" src={previewDesign} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain",filter:cssFilter,opacity:(dc.opacity||89)/100,transition:"filter 0.05s, opacity 0.05s"}}/>}
                {layers.fgOut&&<img className="pb-img" src={layers.fgOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"contain"}}/>}
              </>}
            </div>
          </div>
        </div>

        {/* Sliders */}
        <div style={{marginTop:14,padding:"14px 16px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px"}}>
            {sliders.map(s=><div key={s.k} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>{s.label}</span>
              <input type="range" min={s.min} max={s.max} value={dc[s.k]} onChange={e=>onChange({...dc,[s.k]:Number(e.target.value)})} style={{flex:1,accentColor:"#6366f1",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc[s.k]||0}{s.k!=="shadow"&&s.k!=="shadowLift"?"%":""}</span>
            </div>)}
          </div>
          {dc.shadow>0&&<div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${c.border}`,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Direction</span>
              <input type="range" min={0} max={360} value={dc.shadowAngle||135} onChange={e=>onChange({...dc,shadowAngle:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowAngle||135}°</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Offset</span>
              <input type="range" min={1} max={30} value={dc.shadowOffset||5} onChange={e=>onChange({...dc,shadowOffset:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowOffset||5}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:c.textSub,fontSize:11,fontWeight:600,minWidth:70}}>Blur</span>
              <input type="range" min={0} max={40} value={dc.shadowBlurAmt||8} onChange={e=>onChange({...dc,shadowBlurAmt:Number(e.target.value)})} style={{flex:1,accentColor:"#8b5cf6",height:6}}/>
              <span style={{color:c.textMuted,fontSize:11,minWidth:30,textAlign:"right"}}>{dc.shadowBlurAmt||8}</span>
            </div>
          </div>}
        </div>

        <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
          <button onClick={()=>{onChange(initialDcRef.current);onCancel()}} style={{padding:"9px 22px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={onApply} style={{padding:"9px 22px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>Apply to all</button>
        </div>
      </div>
    </div>
  </div>;
}

// arOverride: optional design aspect ratio (w/h) to fit by. Defaults to the
// image's intrinsic aspect. Passed when a per-mockup crop is active so the
// destination is fitted to the CROPPED region's aspect, not the full design.
function getContainedRect(di,dx,dy,dw,dh,arOverride){const ir=arOverride||(di.naturalWidth/di.naturalHeight);const br=dw/dh;let w,h,x,y;if(ir>br){w=dw;h=dw/ir;x=dx;y=dy+(dh-h)/2}else{h=dh;w=dh*ir;x=dx+(dw-w)/2;y=dy}return{drawX:x,drawY:y,drawW:w,drawH:h}}

/* Compress a source canvas directly to a JPEG data URL, scaled to fit
   within MAX_DIM on the longest edge. Used to keep the `full` mockup
   layer at JPEG q=0.92 from the moment of generation, matching what gets
   uploaded to Etsy anyway (Suite re-compresses to q=0.85 at upload time
   but starting from q=0.92 JPEG instead of full PNG saves ~75% memory
   and IndexedDB footprint with no visible quality loss).

   Synchronous — no Image roundtrip needed since we already have a canvas
   in hand from the generation pipeline. White background fill because
   JPEG can't represent transparency, and most mockups have full-coverage
   backgrounds anyway. */
function canvasToCompressedJpeg(srcCanvas,{maxDim=2000,quality=0.92}={}){
  const sw=srcCanvas.width,sh=srcCanvas.height;
  let w=sw,h=sh;
  if(w>maxDim||h>maxDim){
    const r=Math.min(maxDim/w,maxDim/h);
    w=Math.round(w*r);h=Math.round(h*r);
  }
  const cv=document.createElement("canvas");
  cv.width=w;cv.height=h;
  const ctx=cv.getContext("2d");
  ctx.fillStyle="#ffffff";ctx.fillRect(0,0,w,h);
  if(w===sw&&h===sh){
    ctx.drawImage(srcCanvas,0,0);
  }else{
    ctx.drawImage(srcCanvas,0,0,w,h);
  }
  return cv.toDataURL("image/jpeg",quality);
}

/* Downsample an uploaded file to a working data URL. Designs come in at
   3000-6000px native (5-20MB raw) — keeping that in memory across 50
   designs eats 700MB-1GB just sitting in React state, which crashes the
   tab before the user even clicks Generate.

   Output is PNG when transparency is detected (designs frequently have
   alpha for proper compositing onto template smart-object areas) and
   JPEG otherwise. maxDim=2000 matches Etsy's recommended upload size,
   and is what eventually ships anyway after the suite's re-compression.

   Returns a data URL string. Falls back to the original FileReader output
   if anything goes wrong (corrupt image, decode failure) — better to keep
   the user's design at original size than lose it. */
function readAndDownsample(file,{maxDim=2000,jpegQuality=0.92}={}){
  return new Promise((resolve)=>{
    const r=new FileReader();
    r.onerror=()=>resolve(null);
    r.onload=(ev)=>{
      const orig=ev.target.result;
      downsampleDataUrl(orig,{maxDim,jpegQuality}).then(resolve);
    };
    r.readAsDataURL(file);
  });
}

/* Same downsampling logic but takes a data URL string directly. Used by
   the pipeline postMessage path where the design data already arrived as
   a data URL from the suite. */
function downsampleDataUrl(dataUrl,{maxDim=2000,jpegQuality=0.92}={}){
  return new Promise((resolve)=>{
    if(!dataUrl||typeof dataUrl!=="string"){resolve(dataUrl);return}
    const img=new Image();
    img.onerror=()=>resolve(dataUrl); // keep original on decode failure
    img.onload=()=>{
      try{
        const sw=img.naturalWidth,sh=img.naturalHeight;
        if(sw<=maxDim&&sh<=maxDim){resolve(dataUrl);return}
        const r2=Math.min(maxDim/sw,maxDim/sh);
        const w=Math.round(sw*r2),h=Math.round(sh*r2);
        const cv=document.createElement("canvas");
        cv.width=w;cv.height=h;
        const ctx=cv.getContext("2d");
        ctx.drawImage(img,0,0,w,h);
        let hasAlpha=false;
        try{
          const checks=[[0,0],[w-1,0],[0,h-1],[w-1,h-1],[w>>1,h>>1]];
          for(const[x,y]of checks){
            const px=ctx.getImageData(x,y,1,1).data;
            if(px[3]<255){hasAlpha=true;break}
          }
        }catch(e){hasAlpha=true}
        const out=hasAlpha
          ?cv.toDataURL("image/png")
          :cv.toDataURL("image/jpeg",jpegQuality);
        resolve(out);
      }catch(e){resolve(dataUrl)}
    };
    img.src=dataUrl;
  });
}

/* Prepare a proof screenshot for the Mockup Bank: a reasonably-sized full
   image (for the detail view) plus a small thumbnail (for grids/lists).
   Both are compressed client-side so submissions stay well under the
   request-size cap and images are served as discrete blobs. */
async function prepProofImages(file){
  const full=await readAndDownsample(file,{maxDim:1280,jpegQuality:0.8});
  if(!full)return null;
  const thumb=await downsampleDataUrl(full,{maxDim:360,jpegQuality:0.7});
  return {full,thumb};
}

/* Draw an image warped into a quad defined by 4 corner points using triangle mesh subdivision */
function drawPerspective(ctx,img,corners,subdivisions=12){
  const[tl,tr,br,bl]=corners;
  const sw=img.naturalWidth||img.width;const sh=img.naturalHeight||img.height;
  const n=subdivisions;
  function lerp(a,b,t){return{x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t}}
  function getPoint(u,v){return lerp(lerp(tl,tr,u),lerp(bl,br,u),v)}
  for(let i=0;i<n;i++){for(let j=0;j<n;j++){
    const u0=i/n,u1=(i+1)/n,v0=j/n,v1=(j+1)/n;
    const p00=getPoint(u0,v0),p10=getPoint(u1,v0),p01=getPoint(u0,v1),p11=getPoint(u1,v1);
    const sx=u0*sw,sy=v0*sh,sw2=(u1-u0)*sw,sh2=(v1-v0)*sh;
    drawTriangle(ctx,img,sx,sy,sx+sw2,sy,sx,sy+sh2,p00,p10,p01);
    drawTriangle(ctx,img,sx+sw2,sy,sx+sw2,sy+sh2,sx,sy+sh2,p10,p11,p01);
  }}
}
function drawTriangle(ctx,img,sx0,sy0,sx1,sy1,sx2,sy2,d0,d1,d2){
  ctx.save();ctx.beginPath();ctx.moveTo(d0.x,d0.y);ctx.lineTo(d1.x,d1.y);ctx.lineTo(d2.x,d2.y);ctx.closePath();ctx.clip();
  const denom=sx0*(sy1-sy2)+sx1*(sy2-sy0)+sx2*(sy0-sy1);
  if(Math.abs(denom)<1e-6){ctx.restore();return}
  const a=(d0.x*(sy1-sy2)+d1.x*(sy2-sy0)+d2.x*(sy0-sy1))/denom;
  const b=(d0.x*(sx1-sx2)+d1.x*(sx2-sx0)+d2.x*(sx0-sx1))/(-denom);
  const cc=(d0.x*(sx1*sy2-sx2*sy1)+d1.x*(sx2*sy0-sx0*sy2)+d2.x*(sx0*sy1-sx1*sy0))/denom;
  const d2y=(d0.y*(sy1-sy2)+d1.y*(sy2-sy0)+d2.y*(sy0-sy1))/denom;
  const e=(d0.y*(sx1-sx2)+d1.y*(sx2-sx0)+d2.y*(sx0-sx1))/(-denom);
  const f=(d0.y*(sx1*sy2-sx2*sy1)+d1.y*(sx2*sy0-sx0*sy2)+d2.y*(sx0*sy1-sx1*sy0))/denom;
  ctx.setTransform(a,d2y,b,e,cc,f);ctx.drawImage(img,0,0);ctx.restore();
}

/* ─── Per-mockup design crop (O25) ───────────────────────────────────────
   A crop is a normalized vector rect {sx,sy,sw,sh} (each 0–1, relative to
   the design's intrinsic dimensions) stored on a smart object as `so.crop`.
   Normalized so it's resolution-independent and reusable across any use of
   the design (the future "apply to all" / Option-A follow-up just copies
   the rect). Absent/null crop = full design = current behavior. */

// Pixel-space source rect for a crop on a decoded image (or canvas).
function cropSrcRect(di,crop){const W=di.naturalWidth||di.width,H=di.naturalHeight||di.height;return{sx:crop.sx*W,sy:crop.sy*H,sw:crop.sw*W,sh:crop.sh*H}}
// Full-design-sized canvas with ONLY the crop sub-rect painted (the rest left
// transparent), at the region's OWN position. Used for the perspective path:
// warping this masks the design in place — drawPerspective maps the full
// canvas onto the corner quad, so the crop region lands on its corresponding
// sub-area of the surface and the cropped-away area is transparent. (NOT a
// tight pre-crop, which would re-fit the region to fill the whole quad.)
function maskedDesignCanvas(di,crop){const W=di.naturalWidth||di.width,H=di.naturalHeight||di.height;const cv=document.createElement("canvas");cv.width=W;cv.height=H;const sx=crop.sx*W,sy=crop.sy*H,sw=crop.sw*W,sh=crop.sh*H;cv.getContext("2d").drawImage(di,sx,sy,sw,sh,sx,sy,sw,sh);return cv}
// Paint one smart object's design onto a context. Behavior with no `so.crop`
// is byte-identical to the previous inline draw at all three bake sites.
// CROP = MASK, not re-fit. A crop NEVER changes where/how big the design sits
// in its zone — it only hides the parts outside the crop rect, in place.
function paintDesignSO(ctx,di,so,W,H){
  if(so.corners){
    const cn=so.corners;
    const corners=[{x:(cn.tl.x/100)*W,y:(cn.tl.y/100)*H},{x:(cn.tr.x/100)*W,y:(cn.tr.y/100)*H},{x:(cn.br.x/100)*W,y:(cn.br.y/100)*H},{x:(cn.bl.x/100)*W,y:(cn.bl.y/100)*H}];
    // Mask in place: warp a full-design canvas with only the crop region
    // painted, so the visible part holds its position on the warped surface
    // and the rest vanishes (instead of stretching the region to fill the quad).
    const src=so.crop?maskedDesignCanvas(di,so.crop):di;
    ctx.save();ctx.globalAlpha=1;drawPerspective(ctx,src,corners);ctx.restore();
  }else{
    const dx=(so.x/100)*W,dy=(so.y/100)*H,dw=(so.w/100)*W,dh=(so.h/100)*H;
    // Natural placed rect — where the FULL design sits in the zone. Computed
    // with NO arOverride, so a crop does NOT change the design's position/size.
    const{drawX:DX,drawY:DY,drawW:DW,drawH:DH}=getContainedRect(di,dx,dy,dw,dh);
    const rot=(so.rotation||0)*Math.PI/180;
    ctx.save();ctx.globalAlpha=1;
    if(rot!==0){const cx=dx+dw/2,cy=dy+dh/2;ctx.translate(cx,cy);ctx.rotate(rot);ctx.translate(-cx,-cy)}
    if(so.crop){
      // Draw ONLY the crop's sub-rect of the source, at its corresponding
      // sub-position WITHIN the natural placed rect. The visible region stays
      // exactly where the uncropped design was; the cropped-away area is not
      // drawn (vanishes). Source & dest sub-rects share the design aspect, so
      // no distortion. DW/DH == design aspect, so DX+sx*DW etc. land precisely.
      const sr=cropSrcRect(di,so.crop);
      ctx.drawImage(di,sr.sx,sr.sy,sr.sw,sr.sh,DX+so.crop.sx*DW,DY+so.crop.sy*DH,so.crop.sw*DW,so.crop.sh*DH);
    }else{
      ctx.drawImage(di,DX,DY,DW,DH);
    }
    ctx.restore();
  }
}

/* Dual-design chart (pairing arc, part D): compose two artworks into ONE
   side-by-side image so a paired listing's single chart slot can show BOTH
   variants. Each artwork keeps its own aspect ratio, contained in its own
   column at a shared working height, with a small transparent gutter between
   them. The result is a normal design image (transparent background) that the
   existing zone painter contain-fits into the chart's design zone — nothing
   downstream changes. Resolves to a PNG data URL, or falls back to the primary
   src alone if either image fails to load (a chart is never rendered blank). */
function buildPairCombinedImage(primarySrc,partnerSrc){
  return new Promise(resolve=>{
    if(!primarySrc){resolve(partnerSrc||null);return}
    if(!partnerSrc){resolve(primarySrc);return}
    const a=new Image(),b=new Image();
    let done=0,settled=false;
    const bail=()=>{if(!settled){settled=true;resolve(primarySrc)}};
    const go=()=>{
      done++;if(done<2||settled)return;
      try{
        const H=1000;                       // shared working height
        const gap=Math.round(H*0.04);       // transparent gutter between the two
        const wA=Math.max(1,Math.round(H*(a.naturalWidth/a.naturalHeight)));
        const wB=Math.max(1,Math.round(H*(b.naturalWidth/b.naturalHeight)));
        const cv=document.createElement("canvas");
        cv.width=wA+gap+wB;cv.height=H;
        const ctx=cv.getContext("2d");
        ctx.clearRect(0,0,cv.width,cv.height);   // transparent background
        ctx.drawImage(a,0,0,wA,H);
        ctx.drawImage(b,wA+gap,0,wB,H);
        const out=cv.toDataURL("image/png");
        cv.width=0;cv.height=0;
        settled=true;resolve(out);
      }catch(ex){settled=true;resolve(primarySrc)}
    };
    a.onload=go;a.onerror=bail;b.onload=go;b.onerror=bail;
    a.src=primarySrc;b.src=partnerSrc;
  });
}

/* Chart / size-guide slot detector — MIRRORS the suite's once-per-listing
   dedup regex (/chart|size|sizing|guide/i on templateName). Read-only: the
   studio only uses it to decide whether a design's pair partner should be
   composed into that template's zone. templateName itself is never changed,
   so the suite's dedup and the studio-set ordering keep working untouched. */
// CHART_SLOT_RE / isChartSlot now live in ./renderRouting.mjs (imported above),
// aligned VERBATIM with the suite's ONCE_PER_LISTING_SLOT so a "…Guide" banner is
// not mis-detected as a chart (which would give it F8 inverse/combined binding).

/* Garment-tone lexicon (pairing arc D fix). A color-chart template's per-cell
   zones are grouped by garment-color NAME (the SO-editor group name — a
   garment-colour word). A paired listing must mirror the print-file
   inversion law — DARK garments carry the LIGHT design, LIGHT garments the DARK
   design — so on a paired chart each cell needs its garment classed light/dark
   from that group name. The studio carried NO color vocabulary, so this adds
   one approximating the suite's garment lexicon (Comfort Colors / Bella+Canvas
   family + generic tone words). Dark is matched first so a name containing a
   light-sounding word but naming a dark garment resolves dark, not light.
   Unknown name → null, and the caller then
   keeps the never-blank both-designs fallback. Pure string logic; a single
   (unpaired) design never reaches it, so singles stay byte-identical. */
const GARMENT_TONE_DARK=["black","pepper","espresso","moss","blue jean","bluejean","denim","navy","forest","charcoal","graphite","midnight","coal","ebony","hunter","maroon","burgundy","chocolate","brown","indigo","slate","gunmetal","army","olive","carbon","onyx","obsidian","true navy","dark","deep"];
const GARMENT_TONE_LIGHT=["white","ivory","natural","sand","ash","blossom","cream","bone","chalk","butter","banana","cornsilk","powder","mint","seafoam","sky","baby","pastel","light","pale","peachy","citrus","flo blue"];
function classifyGarmentTone(name){
  if(!name)return null;
  const n=String(name).toLowerCase();
  for(const w of GARMENT_TONE_DARK){if(n.includes(w))return "dark"}
  for(const w of GARMENT_TONE_LIGHT){if(n.includes(w))return "light"}
  return null;
}

// Re-hydrate suite pairing onto a design list. `map` is id → {pairId,pairPrimary}
// from the pipeline. For each design whose id matches, merge the pair fields on —
// never duplicating, never touching src / group / name. Non-clearing (a design not
// in the map is left as-is) and identity-stable when nothing changes (returns the
// SAME array ref → no needless re-render/autosave). Used by BOTH the PIPELINE_STATE
// handler (live designs) and the Resume path (a session restored from a PRE-pairing
// build, which would otherwise re-clobber pairId with its stale copy).
function healPairMeta(list,map){
  if(!Array.isArray(list)||!map||map.size===0)return list;
  let changed=false;
  const out=list.map(x=>{const pr=map.get(x.id);if(pr&&(x.pairId!==pr.pairId||x.pairPrimary!==pr.pairPrimary)){changed=true;return{...x,pairId:pr.pairId,pairPrimary:pr.pairPrimary}}return x;});
  return changed?out:list;
}

// GROUP TONE TAG (owner, 2026) — DORMANT plumbing. A group MAY carry an explicit
// `tone` tag ("dark"/"light"/"all"); routing still READS it (shouldBindZone.groupTone,
// the pairSets tone branch, groupIsNeutral, the MOCKUP_PROFILES broadcast field) so any
// group saved with a tag during the brief chip-UI window keeps working. But NO UI writes
// `tone` anymore — the owner replaced the chips with name-steering (three preset buttons
// that create "Light Design"/"Dark Design"/"All Groups" groups, whose NAMES already route
// perfectly via classifyGarmentTone + /design/i here and /light|dark/i in the suite). For
// every untagged group (i.e. everyone now) the tag paths are inert and name inference wins.

/* Mean luminance of an artwork's opaque pixels (0–255), on a downscaled sample.
   Used to tell the pair's LIGHT design (light-inked, belongs on DARK garments)
   from its DARK design (dark-inked, belongs on LIGHT garments) with no suite
   role flag. Transparent pixels are skipped so the design's own ink — not its
   empty background — drives the class. Falls back to 128 (neutral) on any load
   or canvas failure so classification degrades to a stable default. */
function artLuminance(src){
  return new Promise(resolve=>{
    if(!src){resolve(128);return}
    const img=new Image();
    img.onerror=()=>resolve(128);
    img.onload=()=>{
      try{
        const S=64;
        const r=Math.min(1,S/Math.max(1,img.naturalWidth),S/Math.max(1,img.naturalHeight));
        const w=Math.max(1,Math.round(img.naturalWidth*r)),h=Math.max(1,Math.round(img.naturalHeight*r));
        const cv=document.createElement("canvas");cv.width=w;cv.height=h;
        const ctx=cv.getContext("2d");
        ctx.clearRect(0,0,w,h);
        ctx.drawImage(img,0,0,w,h);
        const data=ctx.getImageData(0,0,w,h).data;
        let sum=0,n=0;
        for(let i=0;i<data.length;i+=4){
          if(data[i+3]<16)continue;                // ignore transparent background
          sum+=0.2126*data[i]+0.7152*data[i+1]+0.0722*data[i+2];n++;
        }
        cv.width=0;cv.height=0;
        resolve(n?sum/n:128);
      }catch(ex){resolve(128)}
    };
    img.src=src;
  });
}

/* Global concurrency limiter for mockup generation. Without this, mounting
   N GenMockup components at once means N parallel canvas pipelines, each
   holding 4 full-resolution PNG dataURLs in memory — easily 4-8GB of
   pressure for a 50-design batch (50 × 12 = 600 simultaneous renders),
   which crashes Chrome with STATUS_OUT_OF_MEMORY (Error code 5).

   Cap of 6 matches typical CPU cores — fully uses the machine without
   blowing the memory budget. Each slot represents one in-flight canvas
   pipeline; new mockups wait until a slot frees up.

   The waiters queue is intentionally simple — first-come-first-served.
   The order doesn't matter much because the user looks at the whole grid
   at once; whichever finishes first appears first. */
const MAX_CONCURRENT_GEN=2;
let _genActive=0;
const _genWaiters=[];
function acquireGenSlot(){
  return new Promise(resolve=>{
    if(_genActive<MAX_CONCURRENT_GEN){_genActive++;resolve();return}
    _genWaiters.push(resolve);
  });
}
function releaseGenSlot(){
  if(_genWaiters.length>0){
    const next=_genWaiters.shift();
    next(); // _genActive stays the same — pass the slot directly to the next waiter
  }else{
    _genActive=Math.max(0,_genActive-1);
  }
}

function dcToCss(dc){
  const isDefault=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&(dc.shadowLift||0)===0;
  if(isDefault)return"none";
  const ang=(dc.shadowAngle||135)*Math.PI/180;
  const offset=dc.shadowOffset||5;
  const blur=dc.shadowBlurAmt||8;
  const sx=Math.round(Math.cos(ang)*offset);const sy=Math.round(Math.sin(ang)*offset);
  return`brightness(${dc.brightness}%) contrast(${dc.contrast}%) saturate(${dc.saturation}%)${dc.shadow>0?` drop-shadow(${sx}px ${sy}px ${blur}px rgba(0,0,0,${dc.shadow/100*0.6}))`:""}`
}

/* Apply shadow lift — brightens dark areas of the design without affecting highlights.
   amount: 0-100 (0 = no change, 100 = max lift).
   Works like phone/Canva shadow adjustment: only the dark tones get lifted. */
function applyShadowLift(canvas,amount){
  if(!amount||amount<=0)return canvas;
  const ctx=canvas.getContext("2d");
  const imgData=ctx.getImageData(0,0,canvas.width,canvas.height);
  const d=imgData.data;
  const str=amount/100; // 0..1
  // Smooth shadow lift: affects all pixels below threshold with gradual falloff.
  // Higher threshold = more of the tonal range is lifted.
  // The lift is strongest for the darkest pixels and tapers to zero at threshold.
  const threshold=200;
  const maxLift=140; // max +140 levels for deepest shadows at full strength
  for(let i=0;i<d.length;i+=4){
    if(d[i+3]===0)continue; // skip fully transparent
    const r=d[i],g=d[i+1],b=d[i+2];
    const lum=0.299*r+0.587*g+0.114*b;
    if(lum>=threshold)continue;
    // Smooth curve: darker pixels get more lift, tapering to 0 at threshold
    const t=1-lum/threshold; // 1 at black, 0 at threshold
    const lift=t*t*str*maxLift; // quadratic curve for natural-looking lift
    d[i]=Math.min(255,r+lift);
    d[i+1]=Math.min(255,g+lift);
    d[i+2]=Math.min(255,b+lift);
  }
  ctx.putImageData(imgData,0,0);
  return canvas;
}

/* GenMockup — two-layer, memoized to prevent lag */
const GenMockup=React.memo(function GenMockup({tpl,gdm,onReady,onEditSO,onEditFG,onEditCrop,designCtrl,onSortChange,onEditDC,cachedResult}){
  const{isDark}=useTheme();const c=t(isDark);
  const cvBaseRef=useRef(null);const cvDesignRef=useRef(null);const cvFgRef=useRef(null);const containerRef=useRef(null);
  const[baseOut,setBaseOut]=useState(null);const[designOut,setDesignOut]=useState(null);const[fgOut,setFgOut]=useState(null);const[fullOut,setFullOut]=useState(null);
  const[loading,setLoading]=useState(true);
  // Always generate eagerly. The previous IntersectionObserver-based lazy
  // generation meant the user had to scroll to the bottom of a 70-mockup
  // list before everything rendered — and unrendered mockups never made it
  // into the pipeline payload. Eager generation is a few seconds slower on
  // initial paint but actually completes the work the user asked for.
  const visible=true;
  const m=migrate(tpl);
  const dc=designCtrl||{...DEFAULT_DC};
  const key=tpl.id+JSON.stringify(gdm)+JSON.stringify(m.smartObjects)+(m.noDesign?"nd":"")+(m.foregroundMask?m.foregroundMask.length:"0");

  // If we already have a cached result for this mockup (from a restored
  // session), hydrate from cache and skip the regeneration pipeline.
  //
  // CRITICAL: hydration must run AT MOST ONCE per component instance and
  // ONLY on initial mount. The cachedResult prop tracks the parent's
  // genOut[mk] entry which changes whenever onReady runs — including
  // when this component itself fires onReady after generating. If the
  // hydration effect re-runs on cachedResult change, it sees the
  // just-pushed cache (which only has `full`, no base/design/fg) and
  // overwrites the locally-generated full layers with nulls, breaking
  // the design-controls slider which needs the source layers.
  //
  // Lock the cached value at mount time using a ref, and only ever
  // hydrate from it once. After mount, this component drives its own
  // state via the generation pipeline.
  const initialCachedResultRef=useRef(cachedResult);
  const hydratedFromCacheRef=useRef(false);
  useEffect(()=>{
    if(hydratedFromCacheRef.current)return;
    const initial=initialCachedResultRef.current;
    if(!initial)return;
    if(typeof initial==="string"){
      // Legacy noDesign result was stored as a single dataURL string
      setBaseOut(initial);setDesignOut(null);setFgOut(null);setFullOut(initial);
    }else if(initial.full){
      // Note: only `full` is in cachedResult after the layer-storage
      // optimization that drops base/design/fg from genOut. The other
      // layers stay null until regenerateLayers() runs on demand (when
      // the user opens the DC slider modal).
      setBaseOut(initial.base||null);
      setDesignOut(initial.design||null);
      setFgOut(initial.fg||null);
      setFullOut(initial.full);
    }
    setLoading(false);
    hydratedFromCacheRef.current=true;
  },[]);

  // On-demand regeneration of the editing layers. Used when the user
  // opens the DC slider modal on a mockup that was hydrated from cache —
  // the cache only has the composited `full`, not the base/design/fg
  // sources needed to drive the live filter/shadow previews. Re-runs the
  // canvas pipeline to produce the missing layers without disturbing the
  // already-displayed full output.
  //
  // Returns a Promise that resolves to {baseOut, designOut, fgOut} so the
  // Edit DC click handler can wait for layers before launching the modal.
  const[regenInFlight,setRegenInFlight]=useState(false);
  const regenerateLayers=()=>new Promise((resolve)=>{
    if(m.noDesign){resolve({baseOut:m.src,designOut:null,fgOut:null});return}
    const sos=m.smartObjects;if(!sos.length){resolve({baseOut,designOut,fgOut});return}
    setRegenInFlight(true);
    const cvB=document.createElement("canvas");const ctxB=cvB.getContext("2d");
    const cvD=document.createElement("canvas");const ctxD=cvD.getContext("2d");
    const mi=new Image();const fi=m.foregroundMask?new Image():null;
    const dis={};let ld=0;
    const uds=[...new Set(sos.map(s=>gdm[s.group]).filter(Boolean))];
    const tot=1+(fi?1:0)+uds.length;
    function go(){ld++;if(ld<tot)return;
      const W=mi.naturalWidth,H=mi.naturalHeight;
      cvB.width=W;cvB.height=H;cvD.width=W;cvD.height=H;
      ctxB.drawImage(mi,0,0);
      ctxD.clearRect(0,0,W,H);
      for(const so of sos){const ds=gdm[so.group];const di=dis[ds];if(!di)continue;
        paintDesignSO(ctxD,di,so,W,H);
      }
      const baseDu=cvB.toDataURL("image/png");
      const desDu=cvD.toDataURL("image/png");
      // Foreground layer (drawn OVER the design) = base re-covered where the
      // foregroundMask is opaque (clip trim and/or imported hair-as-mask).
      let fgDu=null;
      if(fi){
        const cvFg=document.createElement("canvas");cvFg.width=W;cvFg.height=H;
        const ctxFg=cvFg.getContext("2d");
        ctxFg.clearRect(0,0,W,H);ctxFg.drawImage(mi,0,0);ctxFg.globalCompositeOperation="destination-in";ctxFg.drawImage(fi,0,0);
        fgDu=cvFg.toDataURL("image/png");
      }
      setBaseOut(baseDu);setDesignOut(desDu);setFgOut(fgDu);setRegenInFlight(false);
      resolve({baseOut:baseDu,designOut:desDu,fgOut:fgDu});
    }
    mi.onload=go;mi.src=m.src;if(fi){fi.onload=go;fi.src=m.foregroundMask}
    uds.forEach(src=>{const img=new Image();img.onload=go;img.src=src;dis[src]=img});
  });

  useEffect(()=>{
    if(!visible)return;
    // Skip regeneration if we already hydrated from cache. The user
    // explicitly asked to resume a session — re-rendering the same
    // images defeats the purpose. If they want a fresh render they can
    // click Generate which clears genOut.
    if(hydratedFromCacheRef.current)return;
    if(m.noDesign){setBaseOut(m.src);setDesignOut(null);setFgOut(null);setFullOut(m.src);setLoading(false);if(onReady)onReady(tpl.id,m.src);return}
    const sos=m.smartObjects;if(!sos.length)return;
    setLoading(true);setBaseOut(null);setDesignOut(null);setFgOut(null);setFullOut(null);

    // Cancellation flag: if the component unmounts (e.g. user navigates
    // away) before generation completes, we must NOT hold the slot
    // forever. The cleanup function flips this and the go() callback
    // bails out + releases.
    let cancelled=false;
    let slotHeld=false;

    acquireGenSlot().then(()=>{
      if(cancelled){releaseGenSlot();return}
      slotHeld=true;
      const cvB=cvBaseRef.current;
      const cvD=cvDesignRef.current;
      // Refs may be null if the component unmounted between acquire and
      // resolve — defensive check.
      if(!cvB||!cvD){slotHeld=false;releaseGenSlot();return}
      const ctxB=cvB.getContext("2d");
      const ctxD=cvD.getContext("2d");
      const mi=new Image();const fi=m.foregroundMask?new Image():null;
      const dis={};let ld=0;
      const uds=[...new Set(sos.map(s=>gdm[s.group]).filter(Boolean))];
      const tot=1+(fi?1:0)+uds.length;
      function finish(){
        if(slotHeld){slotHeld=false;releaseGenSlot()}
      }
      function go(){
        if(cancelled){finish();return}
        ld++;if(ld<tot)return;
        const W=mi.naturalWidth,H=mi.naturalHeight;
        cvB.width=W;cvB.height=H;cvD.width=W;cvD.height=H;
        ctxB.drawImage(mi,0,0);
        ctxD.clearRect(0,0,W,H);
        for(const so of sos){const ds=gdm[so.group];const di=dis[ds];if(!di)continue;
          paintDesignSO(ctxD,di,so,W,H);
        }
        const cvFull=document.createElement("canvas");cvFull.width=W;cvFull.height=H;const ctxF=cvFull.getContext("2d");
        ctxF.drawImage(mi,0,0);
        // Bake design controls (DC) into the design layer before
        // compositing onto the base. This is what makes global/set/
        // per-mockup DC visible in the bulk results view — without it,
        // changing DC sliders had no effect because the static tile is
        // just an <img> of the baked composite.
        //
        // The bake mirrors compositeWithDC's logic:
        //   1. Filter (brightness/contrast/saturation) applied to design canvas
        //   2. Opacity applied while drawing
        //   3. Shadow lift (canvas pixel manipulation) on the filtered design
        //   4. Drop-shadow during composite onto base
        //
        // All effects are scoped to the design layer only — the template
        // (base) and foreground are NOT affected by DC, matching what the
        // user gets via per-mockup 🎛 modal downloads.
        const opac=(dc.opacity||89)/100;
        const sLift=dc.shadowLift||0;
        const dcIsDefault=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&opac===0.89&&sLift===0;
        if(dcIsDefault){
          // Fast path — no DC effects, simple composite at fixed 0.89 alpha
          ctxF.globalAlpha=0.89;ctxF.drawImage(cvD,0,0);ctxF.globalAlpha=1;
        }else{
          // Slow path — bake DC. Build a separate filtered design canvas
          // so we can apply shadow-lift + drop-shadow to it cleanly.
          const cvFiltered=document.createElement("canvas");cvFiltered.width=W;cvFiltered.height=H;
          const ctxFil=cvFiltered.getContext("2d");
          ctxFil.filter=`brightness(${dc.brightness}%) contrast(${dc.contrast}%) saturate(${dc.saturation}%)`;
          ctxFil.globalAlpha=opac;
          ctxFil.drawImage(cvD,0,0);
          ctxFil.filter="none";
          if(sLift>0)applyShadowLift(cvFiltered,sLift);
          if(dc.shadow>0){
            const ang=(dc.shadowAngle||135)*Math.PI/180;
            const offset=dc.shadowOffset||5;
            const blur=dc.shadowBlurAmt||8;
            const scale=W/800;
            ctxF.save();
            ctxF.shadowColor=`rgba(0,0,0,${Math.min(1,dc.shadow/100*0.6)})`;
            ctxF.shadowBlur=blur*scale;
            ctxF.shadowOffsetX=Math.round(Math.cos(ang)*offset*scale);
            ctxF.shadowOffsetY=Math.round(Math.sin(ang)*offset*scale);
            ctxF.drawImage(cvFiltered,0,0);
            ctxF.restore();
          }else{
            ctxF.drawImage(cvFiltered,0,0);
          }
          cvFiltered.width=0;cvFiltered.height=0;
        }
        // Foreground layer: base re-covered where the foregroundMask is opaque
        // (clip trim and/or imported hair-as-mask), drawn ON TOP of the design.
        let fullDu;
        if(fi){
          const cvFg=cvFgRef.current;cvFg.width=W;cvFg.height=H;const ctxFg=cvFg.getContext("2d");
          ctxFg.clearRect(0,0,W,H);ctxFg.drawImage(mi,0,0);ctxFg.globalCompositeOperation="destination-in";ctxFg.drawImage(fi,0,0);
          ctxF.drawImage(cvFg,0,0);
          // `full` ships everywhere (display, pipeline, save) so compress
          // once here at q=0.92. CRITICAL: we do NOT keep base/design/fg
          // PNG dataURLs in local React state. Each is 5-15MB and 600
          // mounted GenMockups holding all four would consume 9-24GB,
          // crashing Chrome with STATUS_OUT_OF_MEMORY at 50-design
          // batches. The DC slider 🎛 button's onClick already calls
          // regenerateLayers() when these are missing, so the user can
          // still edit — there's just a brief regen wait when they open
          // the slider modal on a finished mockup.
          fullDu=canvasToCompressedJpeg(cvFull);
          // Release the foreground canvas buffer once we've drawn it
          // into cvFull and extracted the JPEG. Setting w/h to 0 frees
          // the W×H×4 byte pixel buffer immediately.
          cvFg.width=0;cvFg.height=0;
        }else{
          fullDu=canvasToCompressedJpeg(cvFull);
        }
        // Release ALL canvas + Image buffers now that we've extracted the
        // JPEG. Without this, every finished GenMockup retains 4 canvas
        // pixel buffers (~64MB each for 4000×4000 templates) and decoded
        // image buffers (template + each design) until garbage collected.
        // For 600 finished components that's 100s of GB of unused memory
        // sitting around waiting for GC. Manual release here keeps
        // finished-mockup memory footprint at just the `fullOut` JPEG
        // (~1-2MB).
        cvB.width=0;cvB.height=0;
        cvD.width=0;cvD.height=0;
        cvFull.width=0;cvFull.height=0;
        // Image objects don't have a direct release API but clearing src
        // and dropping references lets the decoded pixel buffer GC.
        try{mi.src="";}catch(e){}
        if(fi){try{fi.src="";}catch(e){}}
        for(const k in dis){try{dis[k].src=""}catch(e){};delete dis[k]}

        setBaseOut(null);setDesignOut(null);setFgOut(null);setFullOut(fullDu);setLoading(false);
        if(onReady)onReady(tpl.id,{full:fullDu});
        finish();
      }
      mi.onload=go;
      mi.onerror=()=>{finish()};
      mi.src=m.src;
      if(fi){fi.onload=go;fi.onerror=()=>{finish()};fi.src=m.foregroundMask}
      uds.forEach(src=>{const img=new Image();img.onload=go;img.onerror=()=>{finish()};img.src=src;dis[src]=img});
    });

    return()=>{
      cancelled=true;
      // If the slot was acquired but generation hadn't completed yet,
      // release it now. If the slot was never acquired (still waiting),
      // the .then handler will check cancelled and release directly.
      if(slotHeld){slotHeld=false;releaseGenSlot()}
    };
  },[key,visible]);

  const cssFilter=dcToCss(dc);const isDefault=cssFilter==="none"&&(dc.shadowLift||0)===0;

  // Shadow lift live preview: process design layer at preview resolution
  const[liftedDesign,setLiftedDesign]=useState(null);
  const[pixelCache,setPixelCache]=useState(null);
  const prevLiftRef=useRef(0);
  useEffect(()=>{
    const lift=dc.shadowLift||0;
    if(!designOut||lift===0){setPixelCache(null);setLiftedDesign(null);prevLiftRef.current=0;return}
    // Only rebuild cache if designOut changed (not on every lift change)
    if(pixelCache&&prevLiftRef.current>0)return;
    prevLiftRef.current=lift;
    const img=new Image();img.onload=()=>{
      // Use display-appropriate resolution: max 800px keeps quality good without hogging memory
      const scale=Math.min(1,800/img.naturalWidth);
      const w=Math.round(img.naturalWidth*scale);const h=Math.round(img.naturalHeight*scale);
      const cv=document.createElement("canvas");cv.width=w;cv.height=h;
      cv.getContext("2d").drawImage(img,0,0,w,h);
      setPixelCache({data:cv.getContext("2d").getImageData(0,0,w,h),w,h});
    };img.src=designOut;
  },[designOut,dc.shadowLift]);
  useEffect(()=>{
    const lift=dc.shadowLift||0;
    if(!pixelCache||lift===0){setLiftedDesign(null);return}
    const{data,w,h}=pixelCache;
    const cv=document.createElement("canvas");cv.width=w;cv.height=h;
    const ctx=cv.getContext("2d");
    const copy=new ImageData(new Uint8ClampedArray(data.data),w,h);
    ctx.putImageData(copy,0,0);
    applyShadowLift(cv,lift);
    setLiftedDesign(cv.toDataURL("image/png"));
  },[dc.shadowLift,pixelCache]);
  const previewDesign=liftedDesign||designOut;

  return<div ref={containerRef} style={{borderRadius:10,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`}}>
    <canvas ref={cvBaseRef} style={{display:"none"}}/><canvas ref={cvDesignRef} style={{display:"none"}}/><canvas ref={cvFgRef} style={{display:"none"}}/>
    {(!visible||loading)?<div style={{paddingTop:"100%",position:"relative"}}><div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>{visible&&<div className="mg-spinner"/>}<span style={{color:c.textMuted,fontSize:10}}>{visible?"Generating…":""}</span></div></div>
    :<div>
      <div style={{position:"relative",paddingTop:"100%",background:isDark?"#0f0f1a":"#e2e8f0"}}>
        <img className="pb-img" src={baseOut||fullOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}}/>
        {previewDesign&&<img className="pb-img" src={previewDesign} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover",filter:cssFilter,opacity:(dc.opacity||89)/100,transition:"filter 0.1s, opacity 0.1s"}}/>}
        {fgOut&&<img className="pb-img" src={fgOut} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}}/>}
      </div>
      <div style={{padding:"7px 10px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:4,overflow:"hidden",flex:1}}>
          {onSortChange&&<select value={m.sortOrder??999} onChange={e=>onSortChange(tpl.id,parseInt(e.target.value))} style={{width:34,padding:"2px 1px",borderRadius:4,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:10,textAlign:"center",outline:"none",cursor:"pointer",fontWeight:600,flexShrink:0}} title="Sort order">{Array.from({length:20},(_,i)=>i+1).map(n=><option key={n} value={n}>{n}</option>)}<option value={999}>—</option></select>}
          {m.noDesign&&<span style={{background:"#f59e0b",color:"#0f0f1a",borderRadius:4,padding:"1px 5px",fontSize:7,fontWeight:700,flexShrink:0}}>Standalone</span>}
          <span className="pb-text" style={{color:c.text,fontSize:9,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tpl.name}</span>
        </div>
        <div style={{display:"flex",gap:3,alignItems:"center",flexShrink:0}}>
          {onEditDC&&!m.noDesign&&<button
            onClick={async()=>{
              // Editing layers may be missing if this mockup was restored
              // from a saved session — base/design/fg aren't persisted in
              // genOut, only `full` is. Regenerate them on demand before
              // opening the slider modal so previews work correctly.
              let bo=baseOut,dout=designOut,fg=fgOut;
              if(!bo||!dout){
                const r=await regenerateLayers();
                bo=r.baseOut;dout=r.designOut;fg=r.fgOut;
              }
              onEditDC({tpl,baseOut:bo,designOut:dout,fgOut:fg});
            }}
            disabled={regenInFlight}
            style={{background:!isDefault?"rgba(99,102,241,0.2)":"rgba(99,102,241,0.08)",border:"none",color:"#a5b4fc",borderRadius:5,padding:"3px 6px",cursor:regenInFlight?"wait":"pointer",fontSize:8,fontWeight:600,opacity:regenInFlight?0.6:1}}
            title={regenInFlight?"Preparing edit layers…":"Design controls"}
          >{regenInFlight?"…":"🎛"}</button>}
          {onEditSO&&!m.noDesign&&<button onClick={()=>onEditSO(tpl)} style={{background:"rgba(99,102,241,0.15)",border:"none",color:"#a5b4fc",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Edit smart objects">✎</button>}
          {onEditFG&&!m.noDesign&&<button onClick={()=>onEditFG(tpl)} style={{background:"rgba(251,146,60,0.15)",border:"none",color:"#fb923c",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Edit foreground">✂</button>}
          {onEditCrop&&!m.noDesign&&<button onClick={()=>onEditCrop(tpl)} style={{background:"rgba(45,212,191,0.15)",border:"none",color:"#2dd4bf",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Crop design">⛶</button>}
          <button onClick={async()=>{const layers={full:fullOut,base:baseOut,design:designOut,fg:fgOut};const dataUrl=await compositeWithDC(layers,dc);const blob=dataUrlToBlob(dataUrl);const blobUrl=URL.createObjectURL(blob);const a=document.createElement("a");a.href=blobUrl;a.download=`mockup-${tpl.name.replace(/\.[^.]+$/,"")}.png`;document.body.appendChild(a);a.click();document.body.removeChild(a);setTimeout(()=>URL.revokeObjectURL(blobUrl),1000)}} style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",borderRadius:6,padding:"3px 8px",fontSize:9,fontWeight:700,border:"none",cursor:"pointer"}}>↓</button>
        </div>
      </div>
    </div>}
  </div>;
});

/* ─────────────────────────────────────────────────────────────────────────
   StaticMockupTile

   Lightweight replacement for GenMockup that renders a finished mockup
   without any canvas/Image/state machinery. Used for mockups that already
   have entries in genOut — either from the current generation that just
   completed, or hydrated from a saved session.

   600 of these mounted simultaneously costs almost nothing because each
   is just a <div> with one <img> tag. By contrast, mounting 600
   GenMockup components in one render pass exceeds the tab's memory
   budget at MOUNT time (refs, hidden canvases, useState, useEffect
   slots, slot-acquire promises, etc), crashing Chrome with
   STATUS_OUT_OF_MEMORY before any canvas work even begins. This split
   is what makes 50-design batches viable.

   Editing buttons (✎ smart objects, ✂ foreground, 🎛 design controls,
   ↓ download) still work — DC takes a regenerate-on-demand path since
   the editing layers aren't held in state. */
const StaticMockupTile=React.memo(function StaticMockupTile({tpl,cached,dc,c,isDark,onSortChange,onEditSO,onEditFG,onEditCrop,onEditDC}){
  const fullSrc=typeof cached==="string"?cached:cached?.full;
  const[busy,setBusy]=useState(false);
  // DC is baked into `full` at generation time now — no display-time
  // CSS filter needed. The cached JPEG already contains all DC effects
  // scoped correctly to the design layer. The dc prop is kept for the
  // 🎛 button's modal context but not applied as overlay here.
  if(!fullSrc){
    return <div style={{borderRadius:10,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`,paddingTop:"100%"}}/>;
  }
  return <div style={{borderRadius:10,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`}}>
    <div style={{position:"relative",paddingTop:"100%",background:isDark?"#0f0f1a":"#e2e8f0"}}>
      <img className="pb-img" src={fullSrc} alt="" loading="lazy" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}}/>
    </div>
    <div style={{padding:"7px 10px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div style={{display:"flex",alignItems:"center",gap:4,overflow:"hidden",flex:1}}>
        {onSortChange&&<select value={tpl.sortOrder??999} onChange={e=>onSortChange(tpl.id,parseInt(e.target.value))} style={{width:34,padding:"2px 1px",borderRadius:4,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:10,textAlign:"center",outline:"none",cursor:"pointer",fontWeight:600,flexShrink:0}} title="Sort order">{Array.from({length:20},(_,i)=>i+1).map(n=><option key={n} value={n}>{n}</option>)}<option value={999}>—</option></select>}
        {tpl.noDesign&&<span style={{background:"#f59e0b",color:"#0f0f1a",borderRadius:4,padding:"1px 5px",fontSize:7,fontWeight:700,flexShrink:0}}>Standalone</span>}
        <span className="pb-text" style={{color:c.text,fontSize:9,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tpl.name}</span>
      </div>
      <div style={{display:"flex",gap:3,alignItems:"center",flexShrink:0}}>
        {onEditDC&&!tpl.noDesign&&<button
          disabled={busy}
          onClick={async()=>{setBusy(true);try{await onEditDC()}finally{setBusy(false)}}}
          style={{background:"rgba(99,102,241,0.08)",border:"none",color:"#a5b4fc",borderRadius:5,padding:"3px 6px",cursor:busy?"wait":"pointer",fontSize:8,fontWeight:600,opacity:busy?0.6:1}}
          title={busy?"Preparing edit layers…":"Design controls"}
        >{busy?"…":"🎛"}</button>}
        {onEditSO&&!tpl.noDesign&&<button onClick={()=>onEditSO(tpl)} style={{background:"rgba(99,102,241,0.15)",border:"none",color:"#a5b4fc",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Edit smart objects">✎</button>}
        {onEditFG&&!tpl.noDesign&&<button onClick={()=>onEditFG(tpl)} style={{background:"rgba(251,146,60,0.15)",border:"none",color:"#fb923c",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Edit foreground">✂</button>}
        {onEditCrop&&!tpl.noDesign&&<button onClick={()=>onEditCrop(tpl)} style={{background:"rgba(45,212,191,0.15)",border:"none",color:"#2dd4bf",borderRadius:5,padding:"3px 6px",cursor:"pointer",fontSize:8,fontWeight:600}} title="Crop design">⛶</button>}
        <button onClick={()=>{
          // Direct download of the cached full JPEG. Skips compositeWithDC
          // because we don't have the editing layers in state — what gets
          // downloaded is exactly what was generated. This is consistent
          // with what ships to Etsy via the pipeline.
          const a=document.createElement("a");a.href=fullSrc;a.download=`mockup-${tpl.name.replace(/\.[^.]+$/,"")}.jpg`;document.body.appendChild(a);a.click();document.body.removeChild(a);
        }} style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",borderRadius:6,padding:"3px 8px",fontSize:9,fontWeight:700,border:"none",cursor:"pointer"}}>↓</button>
      </div>
    </div>
  </div>;
});

/* Lightweight placeholder for mockups not yet admitted to active
   generation. Just a fixed-aspect div with a label. Cheap to mount in
   bulk — 500 of these consume basically nothing. */
const QueuedMockupTile=React.memo(function QueuedMockupTile({tpl,c,isDark}){
  return <div style={{borderRadius:10,overflow:"hidden",background:c.bgCard,border:`1px dashed ${c.border}`,opacity:0.6}}>
    <div style={{position:"relative",paddingTop:"100%",background:isDark?"#0a0a14":"#f1f5f9"}}>
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center"}}>
        <div style={{color:c.textMuted,fontSize:11,fontWeight:600,marginBottom:2}}>Queued</div>
      </div>
    </div>
    <div style={{padding:"7px 10px"}}>
      <span className="pb-text" style={{color:c.textMuted,fontSize:9,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"block"}}>{tpl.name}</span>
    </div>
  </div>;
});

/* Standalone regenerate helper for static tiles' 🎛 click. Same canvas
   pipeline as GenMockup's regenerateLayers but takes tpl + gdm directly
   so it can run without a mounted component. */
function regenerateLayersStandalone(tpl,gdm,cropByGroup){
  return new Promise((resolve)=>{
    const m=migrate(tpl);
    if(m.noDesign){resolve({baseOut:m.src,designOut:null,fgOut:null});return}
    // Optional in-progress crop (group→rect) for the live crop preview.
    // Absent cropByGroup → original smart objects (byte-identical to before).
    const sos=cropByGroup?m.smartObjects.map(so=>{
      if(!(so.group in cropByGroup))return so;
      const cr=cropByGroup[so.group];
      if(isFullCrop(cr)){const{crop,...rest}=so;return rest;}
      return{...so,crop:cr};
    }):m.smartObjects;
    if(!sos.length){resolve({baseOut:null,designOut:null,fgOut:null});return}
    const cvB=document.createElement("canvas");const ctxB=cvB.getContext("2d");
    const cvD=document.createElement("canvas");const ctxD=cvD.getContext("2d");
    const mi=new Image();const fi=m.foregroundMask?new Image():null;
    const dis={};let ld=0;
    const uds=[...new Set(sos.map(s=>gdm[s.group]).filter(Boolean))];
    const tot=1+(fi?1:0)+uds.length;
    function go(){
      ld++;if(ld<tot)return;
      const W=mi.naturalWidth,H=mi.naturalHeight;
      cvB.width=W;cvB.height=H;cvD.width=W;cvD.height=H;
      ctxB.drawImage(mi,0,0);
      ctxD.clearRect(0,0,W,H);
      for(const so of sos){const ds=gdm[so.group];const di=dis[ds];if(!di)continue;
        paintDesignSO(ctxD,di,so,W,H);
      }
      const baseDu=cvB.toDataURL("image/png");
      const desDu=cvD.toDataURL("image/png");
      // Foreground layer = base re-covered where the foregroundMask is opaque.
      let fgDu=null;
      if(fi){
        const cvFg=document.createElement("canvas");cvFg.width=W;cvFg.height=H;
        const ctxFg=cvFg.getContext("2d");
        ctxFg.clearRect(0,0,W,H);ctxFg.drawImage(mi,0,0);ctxFg.globalCompositeOperation="destination-in";ctxFg.drawImage(fi,0,0);
        fgDu=cvFg.toDataURL("image/png");
        cvFg.width=0;cvFg.height=0;
      }
      cvB.width=0;cvB.height=0;cvD.width=0;cvD.height=0;
      resolve({baseOut:baseDu,designOut:desDu,fgOut:fgDu});
    }
    mi.onload=go;mi.onerror=()=>resolve({baseOut:null,designOut:null,fgOut:null});mi.src=m.src;
    if(fi){fi.onload=go;fi.onerror=()=>{};fi.src=m.foregroundMask}
    uds.forEach(src=>{const img=new Image();img.onload=go;img.onerror=()=>{};img.src=src;dis[src]=img});
  });
}

function dataUrlToBlob(dataUrl){
  const[header,b64]=dataUrl.split(",");
  const mime=header.match(/:(.*?);/)[1];
  const bin=atob(b64);
  const arr=new Uint8Array(bin.length);
  for(let i=0;i<bin.length;i++)arr[i]=bin.charCodeAt(i);
  return new Blob([arr],{type:mime});
}
async function dlAll(o){
  for(let i=0;i<o.length;i++){
    try{
      const blob=dataUrlToBlob(o[i].dataUrl);
      const blobUrl=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=blobUrl;a.download=o[i].name;
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(blobUrl),1000);
    }catch(e){console.error("Download failed for",o[i].name,e)}
    if(i<o.length-1)await new Promise(r=>setTimeout(r,300));
  }
}
// Bundle the same {name,dataUrl}[] doDownload/downloadSet build into ONE .zip.
// Lazy-loads JSZip (code-split — zero bytes until someone downloads a set).
// De-dupes any colliding names so no file silently overwrites another.
async function dlZip(o,zipName){
  const{default:JSZip}=await import("jszip");
  const zip=new JSZip();
  const used=new Set();
  for(const it of o){
    let name=it.name;
    if(used.has(name)){const dot=name.lastIndexOf(".");const stem=dot>0?name.slice(0,dot):name;const ext=dot>0?name.slice(dot):"";let n=2;while(used.has(`${stem}_${n}${ext}`))n++;name=`${stem}_${n}${ext}`}
    used.add(name);
    zip.file(name,dataUrlToBlob(it.dataUrl));
  }
  const blob=await zip.generateAsync({type:"blob"});
  const blobUrl=URL.createObjectURL(blob);
  const a=document.createElement("a");a.href=blobUrl;a.download=zipName;
  document.body.appendChild(a);a.click();document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(blobUrl),2000);
}

/* Composite layers with design controls applied for final download */
function compositeWithDC(layers,dc){
  return new Promise((resolve,reject)=>{
    const timeout=setTimeout(()=>resolve(layers?.full||layers),15000); // 15s safety net
    try{
    if(!layers||typeof layers==="string"){clearTimeout(timeout);resolve(layers);return}
    if(!layers.design){clearTimeout(timeout);resolve(layers.full);return}
    const opac=(dc.opacity||89)/100;
    const sLift=dc.shadowLift||0;
    const isDefault=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&opac===0.89&&sLift===0;
    if(isDefault){clearTimeout(timeout);resolve(layers.full);return}
    const baseImg=new Image();
    baseImg.onerror=()=>{clearTimeout(timeout);resolve(layers.full)};
    baseImg.onload=()=>{
      const W=baseImg.naturalWidth,H=baseImg.naturalHeight;
      const cv=document.createElement("canvas");cv.width=W;cv.height=H;const ctx=cv.getContext("2d");
      ctx.drawImage(baseImg,0,0);
      const desImg=new Image();
      desImg.onerror=()=>{clearTimeout(timeout);resolve(cv.toDataURL("image/png"))};
      desImg.onload=()=>{
        const tmpFiltered=document.createElement("canvas");tmpFiltered.width=W;tmpFiltered.height=H;
        const tfc=tmpFiltered.getContext("2d");
        tfc.filter=`brightness(${dc.brightness}%) contrast(${dc.contrast}%) saturate(${dc.saturation}%)`;
        tfc.globalAlpha=opac;
        tfc.drawImage(desImg,0,0);tfc.filter="none";
        if(sLift>0)applyShadowLift(tmpFiltered,sLift);
        if(dc.shadow>0){
          const ang=(dc.shadowAngle||135)*Math.PI/180;
          const offset=dc.shadowOffset||5;
          const blur=dc.shadowBlurAmt||8;
          const scale=W/800;
          ctx.save();
          ctx.shadowColor=`rgba(0,0,0,${Math.min(1,dc.shadow/100*0.6)})`;
          ctx.shadowBlur=blur*scale;
          ctx.shadowOffsetX=Math.round(Math.cos(ang)*offset*scale);
          ctx.shadowOffsetY=Math.round(Math.sin(ang)*offset*scale);
          ctx.drawImage(tmpFiltered,0,0);
          ctx.restore();
        }else{
          ctx.drawImage(tmpFiltered,0,0);
        }
        if(layers.fg){const fgImg=new Image();fgImg.onerror=()=>{clearTimeout(timeout);resolve(cv.toDataURL("image/png"))};fgImg.onload=()=>{ctx.drawImage(fgImg,0,0);clearTimeout(timeout);resolve(cv.toDataURL("image/png"))};fgImg.src=layers.fg}
        else{clearTimeout(timeout);resolve(cv.toDataURL("image/png"))}
      };desImg.src=layers.design;
    };baseImg.src=layers.base;
    }catch(e){clearTimeout(timeout);resolve(layers?.full||layers)}
  });
}

// Headless compositor for Download All / Download Set. A tile only fills
// genOut[k] when it actually RENDERS (mount-limited by __msGenCounter + scroll);
// mockups never scrolled into view were silently skipped from the ZIP
// (`if(!layers)continue`). This bakes the SAME `full` JPEG a rendered tile would
// produce — from (tpl, gdm, dc) alone, no DOM tile, no scroll — so the ZIP can
// contain EVERY expected mockup regardless of render state. Slow (regenerates
// each layer) but correct; the caller shows a "Preparing X/Y…" progress label.
// Byte-for-byte mirror of GenMockup's go() bake (App.jsx: base → DC-baked design
// → foreground → canvasToCompressedJpeg).
function bakeFullHeadless(baseDu,designDu,fgDu,dc){
  return new Promise((resolve)=>{
    const baseImg=new Image();
    baseImg.onerror=()=>resolve(baseDu);
    baseImg.onload=()=>{
      const W=baseImg.naturalWidth,H=baseImg.naturalHeight;
      const cvFull=document.createElement("canvas");cvFull.width=W;cvFull.height=H;const ctxF=cvFull.getContext("2d");
      ctxF.drawImage(baseImg,0,0);
      const done=()=>{const du=canvasToCompressedJpeg(cvFull);cvFull.width=0;cvFull.height=0;resolve(du)};
      if(!designDu){done();return}
      const desImg=new Image();
      desImg.onerror=done;
      desImg.onload=()=>{
        const cvD=document.createElement("canvas");cvD.width=W;cvD.height=H;cvD.getContext("2d").drawImage(desImg,0,0);
        const opac=(dc.opacity||89)/100;
        const sLift=dc.shadowLift||0;
        const dcIsDefault=dc.brightness===100&&dc.contrast===100&&dc.saturation===100&&dc.shadow===0&&opac===0.89&&sLift===0;
        if(dcIsDefault){
          ctxF.globalAlpha=0.89;ctxF.drawImage(cvD,0,0);ctxF.globalAlpha=1;
        }else{
          const cvFiltered=document.createElement("canvas");cvFiltered.width=W;cvFiltered.height=H;
          const ctxFil=cvFiltered.getContext("2d");
          ctxFil.filter=`brightness(${dc.brightness}%) contrast(${dc.contrast}%) saturate(${dc.saturation}%)`;
          ctxFil.globalAlpha=opac;
          ctxFil.drawImage(cvD,0,0);
          ctxFil.filter="none";
          if(sLift>0)applyShadowLift(cvFiltered,sLift);
          if(dc.shadow>0){
            const ang=(dc.shadowAngle||135)*Math.PI/180;
            const offset=dc.shadowOffset||5;
            const blur=dc.shadowBlurAmt||8;
            const scale=W/800;
            ctxF.save();
            ctxF.shadowColor=`rgba(0,0,0,${Math.min(1,dc.shadow/100*0.6)})`;
            ctxF.shadowBlur=blur*scale;
            ctxF.shadowOffsetX=Math.round(Math.cos(ang)*offset*scale);
            ctxF.shadowOffsetY=Math.round(Math.sin(ang)*offset*scale);
            ctxF.drawImage(cvFiltered,0,0);
            ctxF.restore();
          }else{
            ctxF.drawImage(cvFiltered,0,0);
          }
          cvFiltered.width=0;cvFiltered.height=0;
        }
        cvD.width=0;cvD.height=0;
        if(!fgDu){done();return}
        const fgImg=new Image();
        fgImg.onerror=done;
        fgImg.onload=()=>{ctxF.drawImage(fgImg,0,0);done()};
        fgImg.src=fgDu;
      };
      desImg.src=designDu;
    };
    baseImg.src=baseDu;
  });
}

// Produce the final download data URL for ONE mockup from scratch (no rendered
// tile needed). Mirrors what a StaticMockupTile carries in genOut[k].full.
async function compositeMockupHeadless(tpl,gdm,dc){
  const m=migrate(tpl);
  if(m.noDesign)return m.src;
  const layers=await regenerateLayersStandalone(tpl,gdm);
  if(!layers)return m.src;
  if(!layers.designOut)return layers.baseOut||m.src;
  return await bakeFullHeadless(layers.baseOut,layers.designOut,layers.fgOut,dc||DEFAULT_DC);
}

/* Composite the {baseOut,designOut,fgOut} layers from
   regenerateLayersStandalone into a single flattened mockup, mirroring
   GenMockup's DEFAULT-design-controls fast path (design drawn at 0.89
   alpha, foreground on top) and compressing with the SAME helper the
   manual path uses (canvasToCompressedJpeg → 2000px JPEG q0.92). This is
   the F1 batch render's stand-in for the `full` layer that GenMockup
   builds inline; it bakes NO per-mockup DC (pure rendering — the F1
   message carries no DC), so its output matches a manual mockup whose DC
   is left at defaults. Resolves null only when there's no base at all. */
function compositeLayersToFull(layers){
  return new Promise((resolve)=>{
    if(!layers||!layers.baseOut){resolve(null);return}
    // noDesign / standalone templates come back base-only — nothing to composite.
    if(!layers.designOut){resolve(layers.baseOut);return}
    const baseImg=new Image();
    baseImg.onerror=()=>resolve(layers.baseOut);
    baseImg.onload=()=>{
      const W=baseImg.naturalWidth,H=baseImg.naturalHeight;
      const cv=document.createElement("canvas");cv.width=W;cv.height=H;const ctx=cv.getContext("2d");
      ctx.drawImage(baseImg,0,0);
      const finish=()=>{const du=canvasToCompressedJpeg(cv);cv.width=0;cv.height=0;resolve(du)};
      const desImg=new Image();
      desImg.onerror=finish;
      desImg.onload=()=>{
        ctx.globalAlpha=0.89;ctx.drawImage(desImg,0,0);ctx.globalAlpha=1;
        if(layers.fgOut){const fg=new Image();fg.onerror=finish;fg.onload=()=>{ctx.drawImage(fg,0,0);finish()};fg.src=layers.fgOut}
        else finish();
      };
      desImg.src=layers.designOut;
    };
    baseImg.src=layers.baseOut;
  });
}

/* ═══════ MOCKUP BANK (community proven-mockup bank) ═══════
   A self-contained section, separate from the mockup-editing wizard.
   Browse (any member) · Submit (any member) · Review (admin only). */

function bankIsUrl(s){try{const u=new URL(String(s||"").trim());return u.protocol==="http:"||u.protocol==="https:"}catch{return false}}

const EMPTY_PROOF={listingLink:"",niche:"",img:null,preview:"",loading:false};

function BankSubmitForm({onSubmitted,openTip,setOpenTip}){
  const{isDark}=useTheme();const c=t(isDark);
  const[title,setTitle]=useState("");
  const[nickname,setNickname]=useState("");
  const[purchase,setPurchase]=useState("");
  const[instructions,setInstructions]=useState("");
  const[proofs,setProofs]=useState([{...EMPTY_PROOF},{...EMPTY_PROOF},{...EMPTY_PROOF}]);
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");

  const setProof=(i,patch)=>setProofs(ps=>ps.map((p,j)=>j===i?{...p,...patch}:p));

  async function pickFile(i,file){
    if(!file)return;
    setErr("");
    setProof(i,{loading:true});
    try{
      const img=await prepProofImages(file);
      if(!img){setProof(i,{loading:false});setErr(`Proof ${i+1}: couldn't read that image.`);return}
      setProof(i,{img,preview:img.thumb,loading:false});
    }catch(e){
      setProof(i,{loading:false});setErr(`Proof ${i+1}: ${e.message||"image failed"}`);
    }
  }

  // Drag-and-drop onto a proof zone. Takes the first dropped file, accepts
  // only images, and routes through the SAME pickFile path as click-upload.
  function dropFile(i,e){
    e.preventDefault();
    setProof(i,{dragOver:false});
    const f=e.dataTransfer&&e.dataTransfer.files&&e.dataTransfer.files[0];
    if(!f)return;
    if(f.type&&f.type.startsWith("image/"))pickFile(i,f);
    else setErr(`Proof ${i+1}: please drop an image file.`);
  }

  // Required: title + each proof's screenshot and niche. The purchase link
  // and listing links are OPTIONAL (only format-checked when provided).
  function validate(){
    if(!title.trim())return"Please add a title.";
    if(purchase.trim()&&!bankIsUrl(purchase))return"The mockup purchase link doesn't look like a valid URL — fix it or leave it blank.";
    for(let i=0;i<3;i++){
      const p=proofs[i];
      if(!p.img)return`Proof ${i+1}: add a screenshot.`;
      if(!p.niche.trim())return`Proof ${i+1}: add a niche.`;
      if(p.listingLink.trim()&&!bankIsUrl(p.listingLink))return`Proof ${i+1}: the listing link doesn't look like a valid URL — fix it or leave it blank.`;
    }
    return"";
  }

  async function submit(){
    const v=validate();
    if(v){setErr(v);return}
    setErr("");setBusy(true);
    try{
      await bankSubmit({
        title:title.trim(),
        nickname:nickname.trim(),
        mockupPurchaseLink:purchase.trim(),
        instructions:instructions.trim(),
        proofs:proofs.map(p=>({listingLink:p.listingLink.trim(),niche:p.niche.trim(),image:p.img.full,thumb:p.img.thumb})),
      });
      setTitle("");setNickname("");setPurchase("");setInstructions("");
      setProofs([{...EMPTY_PROOF},{...EMPTY_PROOF},{...EMPTY_PROOF}]);
      if(onSubmitted)onSubmitted();
    }catch(e){
      setErr(e.message||"Submission failed.");
    }finally{
      setBusy(false);
    }
  }

  const inputStyle={width:"100%",padding:"9px 11px",borderRadius:8,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:12,fontFamily:"inherit",outline:"none"};
  const labelStyle={display:"block",fontSize:11,fontWeight:700,color:c.textSub,marginBottom:5};

  return(
    <div className="mg-fadeUp" style={{maxWidth:760}}>
      <div style={{marginBottom:16}}>
        <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>Submit a proven mockup<Tip id="bank-submit" text="A mockup is 'proven' when 3 different shops in 3 different niches use the same blank mockup. Only a screenshot + niche for each of the 3 proofs, plus a title, are required — the purchase link and listing links are optional. Submissions are reviewed before appearing in the bank." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
        <p style={{fontSize:12,color:c.textSub,marginTop:6,lineHeight:1.5}}>
          A mockup is "proven" when <strong>3 different shops in 3 different niches</strong> use the same blank mockup.
          Add a screenshot and niche for each of the 3 proofs — links are optional. Your submission is reviewed before it appears in the bank.
        </p>
      </div>

      <div style={{marginBottom:14}}>
        <label style={labelStyle}>Title <span style={{color:"#ef4444"}}>*</span></label>
        <input style={inputStyle} value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Bella Canvas 3001 — front-flat folded tee" maxLength={200}/>
      </div>

      <div style={{marginBottom:14}}>
        <label style={{...labelStyle,display:"inline-flex",alignItems:"center"}}>Your name / nickname <span style={{color:c.textMuted,fontWeight:400,marginLeft:3}}>(optional)</span><Tip id="bank-nickname" text="Shown as the uploader on your submission. Your email is never shown to other members. Leave blank to appear as 'Anonymous'." openTip={openTip} setOpenTip={setOpenTip} align="left"/></label>
        <input style={inputStyle} value={nickname} onChange={e=>setNickname(e.target.value)} placeholder="How you want to be credited (e.g. Sam, ShopName)" maxLength={60}/>
      </div>

      <div style={{marginBottom:14}}>
        <label style={labelStyle}>Mockup purchase link <span style={{color:c.textMuted,fontWeight:400}}>(optional)</span></label>
        <input style={inputStyle} value={purchase} onChange={e=>setPurchase(e.target.value)} placeholder="https://… where you buy the blank mockup" maxLength={1000}/>
      </div>

      <div style={{marginBottom:8,fontSize:11,fontWeight:700,color:c.textSub,display:"inline-flex",alignItems:"center"}}>Proof — 3 shops, 3 niches <span style={{color:"#ef4444",marginLeft:3}}>*</span><Tip id="bank-proof" text="The 3-shops-3-niches proof. For each proof: a screenshot and a niche are required; the listing link is optional. This is what makes a mockup 'proven' — the same blank used by different shops across different niches." openTip={openTip} setOpenTip={setOpenTip} align="left"/></div>
      {proofs.map((p,i)=>(
        <div key={i} style={{display:"flex",gap:12,padding:12,marginBottom:10,borderRadius:10,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}>
          <label
            onDragOver={e=>{e.preventDefault();if(!p.dragOver)setProof(i,{dragOver:true})}}
            onDragLeave={e=>{e.preventDefault();if(p.dragOver)setProof(i,{dragOver:false})}}
            onDrop={e=>dropFile(i,e)}
            title="Click or drag an image here"
            style={{flexShrink:0,width:90,height:90,borderRadius:8,border:p.dragOver?`2px solid ${c.uploadBorderHover}`:`1px dashed ${c.uploadBorder}`,background:p.dragOver?c.uploadBgHover:c.uploadBg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",overflow:"hidden",position:"relative",transition:"background 0.15s,border-color 0.15s"}}>
            {p.dragOver?<span style={{fontSize:10,color:"#818cf8",fontWeight:700,textAlign:"center",padding:4,pointerEvents:"none"}}>Drop here</span>
              :p.loading?<span style={{fontSize:10,color:c.textSub}}>…</span>
              :p.preview?<img className="pb-img" src={p.preview} alt="" style={{width:"100%",height:"100%",objectFit:"cover",pointerEvents:"none"}}/>
              :<span style={{fontSize:10,color:c.textSub,textAlign:"center",padding:4,pointerEvents:"none"}}>📷<br/>Screenshot<br/><span style={{fontSize:8,opacity:0.7}}>or drag</span></span>}
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>pickFile(i,e.target.files&&e.target.files[0])}/>
          </label>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:700,color:c.textSub,marginBottom:6}}>Proof {i+1}</div>
            <input style={{...inputStyle,marginBottom:8}} value={p.listingLink} onChange={e=>setProof(i,{listingLink:e.target.value})} placeholder="Live listing link (optional)" maxLength={1000}/>
            <input style={inputStyle} value={p.niche} onChange={e=>setProof(i,{niche:e.target.value})} placeholder="Niche — required (e.g. wedding, fishing, nurse…)" maxLength={200}/>
          </div>
        </div>
      ))}

      <div style={{marginTop:6,marginBottom:16}}>
        <label style={labelStyle}>Instructions / notes <span style={{color:c.textMuted,fontWeight:400}}>(optional)</span></label>
        <textarea style={{...inputStyle,minHeight:70,resize:"vertical"}} value={instructions} onChange={e=>setInstructions(e.target.value)} placeholder="Anything useful about using this mockup…" maxLength={2000}/>
      </div>

      {err&&<div style={{padding:"9px 12px",borderRadius:8,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}

      <button onClick={submit} disabled={busy} style={{padding:"11px 28px",borderRadius:11,border:"none",background:busy?c.btnSecBg:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:busy?c.textSub:"white",fontSize:13,fontWeight:700,cursor:busy?"default":"pointer",boxShadow:busy?"none":"0 4px 16px rgba(99,102,241,0.4)"}}>
        {busy?"Submitting…":"Submit for review"}
      </button>
    </div>
  );
}

function BankDetail({rec,c,onClose,isAdmin,onDelete}){
  const isPending=rec.status!=="approved";
  const scope=isPending?"pending":"published";
  const isCommunity=rec.source==="community-sheet";
  const links=Array.isArray(rec.links)?rec.links:[];
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",zIndex:200,overflowY:"auto"}}>
      <div onClick={e=>e.stopPropagation()} className="mg-fadeUp" style={{maxWidth:760,width:"100%",background:c.modalBg,borderRadius:14,border:`1px solid ${isPending?"rgba(245,158,11,0.5)":c.border}`,padding:22}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:12,marginBottom:6}}>
          <h2 className="pb-text" style={{fontSize:19,fontWeight:700,color:c.text,margin:0}}>{rec.title}</h2>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            {isAdmin&&<button onClick={()=>onDelete&&onDelete(rec)} title="Permanently delete this proven entry + its images" style={{padding:"0 12px",height:30,borderRadius:8,border:"1px solid rgba(239,68,68,0.4)",background:"transparent",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer"}}>Delete</button>}
            <button onClick={onClose} style={{width:30,height:30,borderRadius:8,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:14,cursor:"pointer"}}>✕</button>
          </div>
        </div>
        <div style={{fontSize:11,color:c.textMuted,marginBottom:14}}>by {rec.nickname||"Anonymous"} · {bankFormatDate(rec.approvedAt||rec.createdAt)}</div>

        {isPending&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:9,background:"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.4)",color:"#f59e0b",fontSize:12,fontWeight:700,marginBottom:16}}>⏳ Pending approval — not yet verified. This submission hasn't been confirmed as proven yet.</div>}
        {isCommunity&&<div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:20,background:"rgba(20,184,166,0.15)",border:"1px solid rgba(20,184,166,0.4)",color:"#14b8a6",fontSize:11,fontWeight:800}}>📋 From community sheet</span>
          {rec.category&&<span style={{padding:"6px 12px",borderRadius:20,background:"#0ea5e9",color:"#0a0a14",fontSize:11,fontWeight:800,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rec.category}</span>}
        </div>}

        {isCommunity?(
          // Community-sheet entry: link list (purchase blank + proving listings), no niche chips.
          links.length>0&&<div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:c.textSub,marginBottom:6}}>Links</div>
            {links.map((u,i)=><div key={i} style={{marginBottom:3}}><a href={u} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:"#818cf8",textDecoration:"none",wordBreak:"break-all"}}>{i===0?"Purchase blank":"Proving listing"}: {u} ↗</a></div>)}
          </div>
        ):(
          rec.mockupPurchaseLink&&<a href={rec.mockupPurchaseLink} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",padding:"9px 18px",borderRadius:9,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:12,fontWeight:700,textDecoration:"none",marginBottom:16}}>Buy this mockup ↗</a>
        )}

        <div style={{fontSize:12,fontWeight:700,color:c.textSub,marginBottom:8}}>{isCommunity?"Proof — same blank used by different shops":"Proof — 3 shops, 3 niches"}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:rec.instructions?16:0}}>
          {(rec.proofs||[]).map((p,i)=>(
            <div key={i} style={{borderRadius:10,overflow:"hidden",border:`1px solid ${c.border}`,background:c.savedBg}}>
              <a href={bankImageUrl(scope,rec.id,"full",i)} target="_blank" rel="noopener noreferrer">
                <img className="pb-img" src={bankImageUrl(scope,rec.id,"full",i)} alt={`Proof ${i+1}`} loading="lazy" style={{width:"100%",display:"block",aspectRatio:"1/1",objectFit:"cover"}}/>
              </a>
              {!isCommunity&&<div style={{padding:"8px 10px"}}>
                <div style={{fontSize:12,fontWeight:700,color:c.text,marginBottom:3}}>{p.niche||"—"}</div>
                {p.listingLink&&<a href={p.listingLink} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#818cf8",textDecoration:"none",wordBreak:"break-all"}}>View listing ↗</a>}
              </div>}
            </div>
          ))}
        </div>

        {rec.instructions&&<div style={{fontSize:12,color:c.textSub,lineHeight:1.5,padding:"10px 12px",borderRadius:8,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}><strong style={{color:c.text}}>Notes:</strong> {rec.instructions}</div>}
      </div>
    </div>
  );
}

function PersonalDetail({item,c,onClose}){
  const pub=item.visibility==="public";
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",zIndex:200,overflowY:"auto"}}>
      <div onClick={e=>e.stopPropagation()} className="mg-fadeUp" style={{maxWidth:620,width:"100%",background:c.modalBg,borderRadius:14,border:`1px solid ${c.border}`,padding:22}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:12,marginBottom:6}}>
          <h2 className="pb-text" style={{fontSize:19,fontWeight:700,color:c.text,margin:0}}>{item.title}</h2>
          <button onClick={onClose} style={{flexShrink:0,width:30,height:30,borderRadius:8,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:11,color:c.textMuted,marginBottom:12}}>by {item.ownerNickname||"Anonymous"} · {bankFormatDate(item.createdAt)} · {item.mine&&!pub?"🔒 Private (only you)":pub?"🌐 Public":"Personal"}</div>
        <div style={{borderRadius:10,overflow:"hidden",border:`1px solid ${c.border}`,background:c.savedBg,marginBottom:12}}>
          <a href={bankPersonalImageUrl(item.id,"full")} target="_blank" rel="noopener noreferrer"><img className="pb-img" src={bankPersonalImageUrl(item.id,"full")} alt={item.title} loading="lazy" style={{width:"100%",display:"block"}}/></a>
        </div>
        {item.link&&<div style={{marginBottom:10}}><a href={item.link} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:"#818cf8",textDecoration:"none",wordBreak:"break-all"}}>{item.link} ↗</a></div>}
        {item.note&&<div style={{fontSize:12,color:c.textSub,lineHeight:1.5,padding:"10px 12px",borderRadius:8,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}><strong style={{color:c.text}}>Note:</strong> {item.note}</div>}
      </div>
    </div>
  );
}

function ArchiveDetail({item,c,onClose}){
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",zIndex:200,overflowY:"auto"}}>
      <div onClick={e=>e.stopPropagation()} className="mg-fadeUp" style={{maxWidth:620,width:"100%",background:c.modalBg,borderRadius:14,border:`1px solid ${c.border}`,padding:22}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:12,marginBottom:6}}>
          <h2 className="pb-text" style={{fontSize:19,fontWeight:700,color:c.text,margin:0}}>{item.title}</h2>
          <button onClick={onClose} style={{flexShrink:0,width:30,height:30,borderRadius:8,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:11,color:c.textMuted,marginBottom:12}}>Community archive{item.category?` · ${item.category}`:""} · not individually verified</div>
        {(() => {
          // imageCount + links[] normalized by the backend (legacy items → 1 image, [link]).
          const n = item.imageCount && item.imageCount > 0 ? item.imageCount : 1;
          const links = Array.isArray(item.links) ? item.links : (item.link ? [item.link] : []);
          return (<>
            <div style={{display:"grid",gridTemplateColumns:n>1?"repeat(auto-fit,minmax(180px,1fr))":"1fr",gap:8,marginBottom:12}}>
              {Array.from({length:n}).map((_,i)=>(
                <a key={i} href={bankArchiveImageUrl(item.id,i,"full")} target="_blank" rel="noopener noreferrer" style={{borderRadius:10,overflow:"hidden",border:`1px solid ${c.border}`,background:c.savedBg}}>
                  <img className="pb-img" src={bankArchiveImageUrl(item.id,i,"full")} alt={`${item.title} ${i+1}`} loading="lazy" style={{width:"100%",display:"block",aspectRatio:n>1?"1/1":undefined,objectFit:"cover"}}/>
                </a>
              ))}
            </div>
            {links.length>0&&<div style={{marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,color:c.textSub,marginBottom:4}}>Listings</div>
              {links.map((u,i)=><div key={i}><a href={u} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:"#818cf8",textDecoration:"none",wordBreak:"break-all"}}>{u} ↗</a></div>)}
            </div>}
          </>);
        })()}
        {item.note&&<div style={{fontSize:12,color:c.textSub,lineHeight:1.5,padding:"10px 12px",borderRadius:8,background:c.savedBg,border:`1px solid ${c.savedBorder}`,whiteSpace:"pre-wrap"}}><strong style={{color:c.text}}>Note:</strong> {item.note}</div>}
      </div>
    </div>
  );
}

function BankBrowse({openTip,setOpenTip,isAdmin}){
  const{isDark}=useTheme();const c=t(isDark);
  const[records,setRecords]=useState(null);
  const[personal,setPersonal]=useState([]);
  const[archive,setArchive]=useState([]);
  const[err,setErr]=useState("");
  const[selected,setSelected]=useState(null);
  const[selPersonal,setSelPersonal]=useState(null);
  const[selArchive,setSelArchive]=useState(null);
  const[archiveShown,setArchiveShown]=useState(60); // cap render; "show more" for big archives
  const[archiveCat,setArchiveCat]=useState("All"); // category filter (derived from items)
  const[provenCat,setProvenCat]=useState("All"); // proven category filter
  const[provenSource,setProvenSource]=useState("all"); // "all" | "member" | "community"

  useEffect(()=>{
    let alive=true;
    bankListBrowse().then(d=>{if(alive)setRecords(d.records||[])}).catch(e=>{if(alive){setErr(e.message||"Couldn't load the bank.");setRecords([])}});
    // Personal: the viewer's OWN items (public+private) + everyone's PUBLIC items,
    // deduped by id (the viewer's own copy wins so private items stay marked).
    Promise.all([bankPersonalListMine().catch(()=>({records:[]})),bankPersonalListPublic().catch(()=>({records:[]}))]).then(([mine,pub])=>{
      if(!alive)return;
      const byId=new Map();
      for(const it of (pub.records||[]))byId.set(it.id,it);
      for(const it of (mine.records||[]))byId.set(it.id,it); // own copy overrides (keeps private flag)
      setPersonal(Array.from(byId.values()).sort((a,b)=>(b.createdAt||0)-(a.createdAt||0)));
    });
    // Community archive: shared, admin-curated library (read-only for members).
    bankArchiveList().then(d=>{if(alive)setArchive(d.records||[])}).catch(()=>{});
    return()=>{alive=false};
  },[]);

  if(records===null)return<div className="mg-fadeUp" style={{color:c.textSub,fontSize:13,padding:"40px 0",textAlign:"center"}}>Loading the bank…</div>;

  // Proven (approved) entries are the default focus; pending entries are shown
  // after them, visibly secondary (grayed + badged).
  const approved=records.filter(r=>r.status==="approved").sort((a,b)=>(b.approvedAt||b.createdAt||0)-(a.approvedAt||a.createdAt||0));
  const pending=records.filter(r=>r.status!=="approved").sort((a,b)=>(b.createdAt||0)-(a.createdAt||0));
  // Proven filters: source (member vs community-sheet) AND category (community entries carry one).
  const provenCats=Array.from(new Set([...approved,...pending].map(r=>r.category).filter(Boolean))).sort();
  const matchProven=r=>(
    (provenSource==="all" || (provenSource==="community" ? r.source==="community-sheet" : r.source!=="community-sheet"))
    && (provenCat==="All" || r.category===provenCat)
  );
  const approvedF=approved.filter(matchProven);
  const pendingF=pending.filter(matchProven);
  const provenFiltering=provenSource!=="all"||provenCat!=="All";

  // Admin-only hard delete of a proven entry (approved or pending) + its images.
  async function deleteProven(rec){
    if(!window.confirm("Permanently delete this proven mockup and its images? This can't be undone."))return;
    const scope=rec.status==="approved"?"published":"pending";
    try{
      await bankProvenDelete(rec.id,scope);
      setRecords(rs=>(rs||[]).filter(r=>r.id!==rec.id));
      setSelected(s=>s&&s.id===rec.id?null:s);
    }catch(e){setErr(e.message||"Couldn't delete.")}
  }

  const card=(rec)=>{
    const isPending=rec.status!=="approved";
    const scope=isPending?"pending":"published";
    const isCommunity=rec.source==="community-sheet";
    const nProofs=(rec.proofs||[]).length||1;
    const baseBorder=isPending?"rgba(245,158,11,0.4)":c.border;
    const hoverBorder=isPending?"rgba(245,158,11,0.8)":"rgba(99,102,241,0.5)";
    return(
      <div key={rec.id} onClick={()=>setSelected(rec)} style={{position:"relative",padding:14,borderRadius:12,background:c.bgCard,border:`1px solid ${baseBorder}`,cursor:"pointer",transition:"border-color 0.15s,transform 0.15s",opacity:isPending?0.72:1}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=hoverBorder;e.currentTarget.style.transform="translateY(-2px)"}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=baseBorder;e.currentTarget.style.transform="none"}}>
        {isAdmin&&<button onClick={e=>{e.stopPropagation();deleteProven(rec)}} title="Delete (admin)" style={{position:"absolute",top:8,right:8,zIndex:2,width:24,height:24,borderRadius:6,border:"1px solid rgba(239,68,68,0.5)",background:"rgba(10,10,20,0.55)",color:"#ef4444",fontSize:12,fontWeight:700,cursor:"pointer",lineHeight:1}}>✕</button>}
        {isPending&&<div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#0a0a14",background:"#f59e0b",padding:"3px 10px",borderRadius:20,marginBottom:10,letterSpacing:0.3}}>⏳ Pending approval</div>}
        {isCommunity&&<div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#0a0a14",background:"#14b8a6",padding:"3px 10px",borderRadius:20,letterSpacing:0.3}}>📋 From community sheet</span>
          {rec.category&&<span style={{fontSize:10,fontWeight:800,color:"#0a0a14",background:"#0ea5e9",padding:"3px 10px",borderRadius:20,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rec.category}</span>}
        </div>}
        {isCommunity?(
          // one representative image + "N proofs" cue (all proofs open in the modal)
          <div style={{position:"relative",aspectRatio:"1/1",borderRadius:7,overflow:"hidden",background:c.savedBg,border:`1px solid ${c.border}`,marginBottom:10}}>
            <img className="pb-img" src={bankImageUrl(scope,rec.id,"thumb",0)} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            {nProofs>1&&<span style={{position:"absolute",bottom:7,right:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"rgba(10,10,20,0.6)",color:"#fff"}} title={`${nProofs} proof images — click to view`}>▦ {nProofs} proofs</span>}
          </div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginBottom:10,opacity:isPending?0.55:1,filter:isPending?"grayscale(0.4)":"none"}}>
            {(rec.proofs||[]).map((p,i)=>(
              <div key={i} style={{aspectRatio:"1/1",borderRadius:7,overflow:"hidden",background:c.savedBg,border:`1px solid ${c.border}`}}>
                <img className="pb-img" src={bankImageUrl(scope,rec.id,"thumb",i)} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              </div>
            ))}
          </div>
        )}
        <div className="pb-text" style={{fontSize:14,fontWeight:700,color:c.text,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={rec.title}>{rec.title}</div>
        {/* niche chips only for normal member entries (community-sheet has no niches) */}
        {!isCommunity&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
          {(rec.proofs||[]).map((p,i)=><span key={i} style={{fontSize:10,fontWeight:600,color:c.textSub,padding:"2px 8px",borderRadius:20,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}>{p.niche||"—"}</span>)}
        </div>}
        <div style={{fontSize:10,color:c.textMuted}}>by {rec.nickname||"Anonymous"}</div>
      </div>
    );
  };

  const gridStyle={display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14};

  // Personal card — single image, owner nickname, public/private marker.
  const personalCard=(it)=>{
    const pub=it.visibility==="public";
    const privateMine=it.mine&&!pub;
    return(
      <div key={it.id} onClick={()=>setSelPersonal(it)} style={{borderRadius:12,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`,cursor:"pointer",transition:"border-color 0.15s,transform 0.15s"}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(99,102,241,0.5)";e.currentTarget.style.transform="translateY(-2px)"}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=c.border;e.currentTarget.style.transform="none"}}>
        <div style={{position:"relative",aspectRatio:"1/1",background:c.savedBg}}>
          <img className="pb-img" src={bankPersonalImageUrl(it.id,"thumb")} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          {privateMine&&<span style={{position:"absolute",top:7,left:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"#64748b",color:"#0a0a14"}}>🔒 Private (only you)</span>}
          {it.mine&&pub&&<span style={{position:"absolute",top:7,left:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"#34d399",color:"#0a0a14"}}>🌐 Yours · Public</span>}
        </div>
        <div style={{padding:"10px 11px"}}>
          <div className="pb-text" style={{fontSize:13,fontWeight:700,color:c.text,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={it.title}>{it.title}</div>
          <div style={{fontSize:10,color:c.textMuted}}>by {it.ownerNickname||"Anonymous"}</div>
        </div>
      </div>
    );
  };

  // Community archive card — shows ONE representative image (index 0). A subtle
  // "N proofs" pill signals that clicking opens the full proof set; the detail
  // modal (ArchiveDetail) shows all images. Legacy single-image items: no pill.
  const archiveCard=(it)=>{
    const n=it.imageCount&&it.imageCount>0?it.imageCount:1;
    return(
      <div key={it.id} onClick={()=>setSelArchive(it)} style={{borderRadius:12,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`,cursor:"pointer",transition:"border-color 0.15s,transform 0.15s"}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(14,165,233,0.5)";e.currentTarget.style.transform="translateY(-2px)"}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=c.border;e.currentTarget.style.transform="none"}}>
        <div style={{position:"relative",aspectRatio:"1/1",overflow:"hidden",background:c.savedBg}}>
          <img className="pb-img" src={bankArchiveImageUrl(it.id,0,"thumb")} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          {it.category&&<span style={{position:"absolute",top:7,left:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"#0ea5e9",color:"#0a0a14",maxWidth:"90%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.category}</span>}
          {n>1&&<span style={{position:"absolute",bottom:7,right:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"rgba(10,10,20,0.6)",color:"#fff"}} title={`${n} proof images — click to view`}>▦ {n} proofs</span>}
        </div>
        <div style={{padding:"10px 11px"}}>
          <div className="pb-text" style={{fontSize:13,fontWeight:700,color:c.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={it.title}>{it.title}</div>
        </div>
      </div>
    );
  };

  return(
    <div className="mg-fadeUp">
      <div style={{marginBottom:16}}>
        <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>Proven mockups<Tip id="bank-browse" text="Community-proven mockups: a mockup is 'proven' when 3 different shops in 3 different niches use the same blank mockup. Proven entries show first; submissions still awaiting admin approval appear below, grayed out and badged." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
        <p style={{fontSize:12,color:c.textSub,marginTop:4}}>Mockups verified by 3 shops across 3 niches. {approved.length} proven{pending.length>0?`, ${pending.length} pending approval`:""}{provenFiltering?` · showing ${approvedF.length+pendingF.length}`:""}.</p>
      </div>

      {err&&<div style={{padding:"9px 12px",borderRadius:8,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}

      {approved.length===0&&pending.length===0&&personal.length===0&&archive.length===0&&<div style={{color:c.textSub,fontSize:13,padding:"40px 0",textAlign:"center"}}>No mockups yet. Be the first to submit one!</div>}

      {/* Proven filters: source + category (chips mirror the Community Archive's). */}
      {(approved.length>0||pending.length>0)&&(() => {
        const chip=(label,active,on)=>(
          <button key={label} onClick={on} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${active?"#0ea5e9":c.border}`,background:active?"rgba(14,165,233,0.15)":"transparent",color:active?"#0ea5e9":c.textSub,fontSize:11,fontWeight:700,cursor:"pointer"}}>{label}</button>
        );
        return (<div style={{marginBottom:14}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:provenCats.length>0?8:0}}>
            {chip("All sources", provenSource==="all", ()=>setProvenSource("all"))}
            {chip("Member submissions", provenSource==="member", ()=>setProvenSource("member"))}
            {chip("From community sheet", provenSource==="community", ()=>setProvenSource("community"))}
          </div>
          {provenCats.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {chip("All categories", provenCat==="All", ()=>setProvenCat("All"))}
            {provenCats.map(cat=>chip(cat, provenCat===cat, ()=>setProvenCat(cat)))}
          </div>}
        </div>);
      })()}

      {approvedF.length>0&&<div style={gridStyle}>{approvedF.map(card)}</div>}

      {pendingF.length>0&&<div style={{marginTop:approvedF.length>0?28:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,paddingBottom:8,borderBottom:`1px solid ${c.border}`}}>
          <span style={{fontSize:14,fontWeight:700,color:"#f59e0b",display:"inline-flex",alignItems:"center"}}>⏳ Pending approval<Tip id="bank-pending" text="These submissions haven't been verified by an admin yet — treat them as unconfirmed, not proven. Once an admin approves one, it moves up into the proven mockups above." openTip={openTip} setOpenTip={setOpenTip} align="left"/></span>
          <span style={{fontSize:11,color:c.textMuted}}>not yet verified — awaiting admin review</span>
        </div>
        <div style={gridStyle}>{pendingF.map(card)}</div>
      </div>}

      {(approved.length>0||pending.length>0)&&approvedF.length===0&&pendingF.length===0&&<div style={{color:c.textSub,fontSize:12,padding:"20px 0",textAlign:"center"}}>No proven entries match this filter.</div>}

      {personal.length>0&&<div style={{marginTop:(approved.length>0||pending.length>0)?28:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,paddingBottom:8,borderBottom:`1px solid ${c.border}`}}>
          <span style={{fontSize:14,fontWeight:700,color:c.text,display:"inline-flex",alignItems:"center"}}>Personal collection<Tip id="bank-personal-browse" text="Members' own mockups — your items (including your private ones, marked 'only you') plus other members' public items. These are NOT vetted as proven; they're personal shares. Manage your own in the My Collection tab." openTip={openTip} setOpenTip={setOpenTip} align="left"/></span>
          <span style={{fontSize:11,color:c.textMuted}}>personal shares — not vetted as proven</span>
        </div>
        <p style={{fontSize:11,color:c.textMuted,margin:"0 0 12px"}}>Your items (private ones visible only to you) and other members' public items.</p>
        <div style={gridStyle}>{personal.map(personalCard)}</div>
      </div>}

      {archive.length>0&&<div style={{marginTop:(approved.length>0||pending.length>0||personal.length>0)?28:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,paddingBottom:8,borderBottom:`1px solid ${c.border}`}}>
          <span style={{fontSize:14,fontWeight:700,color:"#0ea5e9",display:"inline-flex",alignItems:"center"}}>📚 Community Archive<Tip id="bank-archive-browse" text="A shared library of community mockups, curated by admins. These are NOT individually verified as 'proven' — they're a broad reference collection (including the legacy mockup sheet). Only admins can add or edit these." openTip={openTip} setOpenTip={setOpenTip} align="left"/></span>
          <span style={{fontSize:11,color:c.textMuted}}>shared library — not individually verified</span>
        </div>
        {(() => {
          const cats = Array.from(new Set(archive.map(a => a.category).filter(Boolean))).sort();
          const filtered = archiveCat === "All" ? archive : archive.filter(a => a.category === archiveCat);
          const chip = (label, active, on) => (
            <button key={label} onClick={on} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${active?"#0ea5e9":c.border}`,background:active?"rgba(14,165,233,0.15)":"transparent",color:active?"#0ea5e9":c.textSub,fontSize:11,fontWeight:700,cursor:"pointer"}}>{label}</button>
          );
          return (<>
            <p style={{fontSize:11,color:c.textMuted,margin:"0 0 10px"}}>{archive.length} item{archive.length===1?"":"s"} in the shared community archive{archiveCat!=="All"?` · showing ${filtered.length} in "${archiveCat}"`:""}.</p>
            {cats.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
              {chip("All", archiveCat==="All", ()=>{setArchiveCat("All");setArchiveShown(60)})}
              {cats.map(cat=>chip(cat, archiveCat===cat, ()=>{setArchiveCat(cat);setArchiveShown(60)}))}
            </div>}
            <div style={gridStyle}>{filtered.slice(0,archiveShown).map(archiveCard)}</div>
            {filtered.length>archiveShown&&<div style={{textAlign:"center",marginTop:14}}><button onClick={()=>setArchiveShown(n=>n+60)} style={{padding:"8px 20px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:700,cursor:"pointer"}}>Show more ({filtered.length-archiveShown} left)</button></div>}
          </>);
        })()}
      </div>}

      {selected&&<BankDetail rec={selected} c={c} onClose={()=>setSelected(null)} isAdmin={isAdmin} onDelete={deleteProven}/>}
      {selPersonal&&<PersonalDetail item={selPersonal} c={c} onClose={()=>setSelPersonal(null)}/>}
      {selArchive&&<ArchiveDetail item={selArchive} c={c} onClose={()=>setSelArchive(null)}/>}
    </div>
  );
}

function bankFormatDate(ts){
  if(!ts)return"";
  try{return new Date(ts).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"})}catch{return""}
}

/* A single proof's thumbnail + links. scope decides which store the gated
   image endpoint reads from. Clicking the thumb opens the full image. */
function BankProofThumb({scope,id,index,proof,c,showLink=true}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:4,width:120}}>
      <a href={bankImageUrl(scope,id,"full",index)} target="_blank" rel="noopener noreferrer" style={{display:"block",width:120,height:120,borderRadius:8,overflow:"hidden",border:`1px solid ${c.border}`,background:c.savedBg}}>
        <img className="pb-img" src={bankImageUrl(scope,id,"thumb",index)} alt={`Proof ${index+1}`} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
      </a>
      <div style={{fontSize:10,fontWeight:700,color:c.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}} title={proof.niche}>{proof.niche||"—"}</div>
      {showLink&&proof.listingLink&&<a href={proof.listingLink} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"#818cf8",textDecoration:"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}} title={proof.listingLink}>View listing ↗</a>}
    </div>
  );
}

function BankReview({openTip,setOpenTip}){
  const{isDark}=useTheme();const c=t(isDark);
  const[records,setRecords]=useState(null);
  const[err,setErr]=useState("");
  const[busyId,setBusyId]=useState("");
  // Ids we've already approved/rejected this session — so a poll that races
  // Netlify Blobs' eventually-consistent list() doesn't resurrect them.
  const actedRef=useRef(null);
  if(actedRef.current===null)actedRef.current=new Set();

  async function load(){
    try{
      const data=await bankListPending();
      setErr("");
      setRecords((data.records||[]).filter(r=>!actedRef.current.has(r.id)));
    }catch(e){setErr(e.message||"Couldn't load pending submissions.");setRecords(r=>r||[])}
  }
  // The tab remounts this component each time it's opened, so this fetches on
  // open. We ALSO poll while the tab stays open: Netlify Blobs list() is
  // eventually consistent (a just-submitted record can take a few seconds to
  // appear), and an admin watching this tab should see new submissions land
  // without manually hitting Refresh. Cheap — admin-only view.
  useEffect(()=>{
    load();
    const iv=setInterval(load,10000);
    return()=>clearInterval(iv);
  },[]);

  async function act(id,fn){
    setBusyId(id);setErr("");
    try{
      await fn(id);
      actedRef.current.add(id);
      setRecords(rs=>(rs||[]).filter(r=>r.id!==id));
    }catch(e){setErr(e.message||"Action failed.")}
    finally{setBusyId("")}
  }

  if(records===null)return<div className="mg-fadeUp" style={{color:c.textSub,fontSize:13,padding:"40px 0",textAlign:"center"}}>Loading pending submissions…</div>;

  return(
    <div className="mg-fadeUp">
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>Pending review<Tip id="bank-review" text="Approve moves a submission into the public proven bank (visible to all members). Reject permanently deletes it and its screenshots. This list refreshes on its own every few seconds." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
          <p style={{fontSize:12,color:c.textSub,marginTop:4}}>{records.length} submission{records.length===1?"":"s"} awaiting approval.</p>
        </div>
        <button onClick={load} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:11,fontWeight:600,cursor:"pointer"}}>↻ Refresh</button>
      </div>

      {err&&<div style={{padding:"9px 12px",borderRadius:8,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}

      {records.length===0&&<div style={{color:c.textSub,fontSize:13,padding:"40px 0",textAlign:"center"}}>No pending submissions right now. 🎉</div>}

      {records.map(rec=>(
        <div key={rec.id} style={{padding:16,marginBottom:14,borderRadius:12,background:c.bgCard,border:`1px solid ${c.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:10}}>
            <div style={{minWidth:0}}>
              <div className="pb-text" style={{fontSize:15,fontWeight:700,color:c.text}}>{rec.title}{rec.source==="community-sheet"&&<span style={{marginLeft:8,fontSize:9,fontWeight:800,color:"#0a0a14",background:"#14b8a6",padding:"2px 8px",borderRadius:20}}>📋 community sheet</span>}</div>
              <div style={{fontSize:11,color:c.textMuted,marginTop:3}}>by {rec.nickname||"Anonymous"}{rec.submittedBy?<span style={{opacity:0.7}}> · {rec.submittedBy}</span>:""} · {bankFormatDate(rec.createdAt)}</div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <button disabled={busyId===rec.id} onClick={()=>act(rec.id,bankApprove)} style={{padding:"8px 18px",borderRadius:9,border:"none",background:busyId===rec.id?c.btnSecBg:"linear-gradient(135deg,#34d399,#059669)",color:busyId===rec.id?c.textSub:"white",fontSize:12,fontWeight:700,cursor:busyId===rec.id?"default":"pointer"}}>Approve</button>
              <button disabled={busyId===rec.id} onClick={()=>{if(window.confirm("Reject this submission? It declines the pending entry and removes it from the queue."))act(rec.id,bankReject)}} title="Decline this pending submission" style={{padding:"8px 18px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:700,cursor:busyId===rec.id?"default":"pointer"}}>Reject</button>
              <button disabled={busyId===rec.id} onClick={()=>{if(window.confirm("Permanently delete this entry and its images? This can't be undone."))act(rec.id,(id)=>bankProvenDelete(id,"pending"))}} title="Permanently delete this entry + its images" style={{padding:"8px 18px",borderRadius:9,border:"none",background:"#ef4444",color:"white",fontSize:12,fontWeight:700,cursor:busyId===rec.id?"default":"pointer"}}>Delete</button>
            </div>
          </div>
          {rec.mockupPurchaseLink&&<div style={{fontSize:11,marginBottom:10}}>
            <a href={rec.mockupPurchaseLink} target="_blank" rel="noopener noreferrer" style={{color:"#818cf8",textDecoration:"none"}}>Mockup purchase link ↗</a>
          </div>}
          <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:rec.instructions?10:0}}>
            {(rec.proofs||[]).map((p,i)=><BankProofThumb key={i} scope="pending" id={rec.id} index={i} proof={p} c={c}/>)}
          </div>
          {rec.instructions&&<div style={{fontSize:12,color:c.textSub,lineHeight:1.5,padding:"8px 10px",borderRadius:8,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}><strong style={{color:c.text}}>Notes:</strong> {rec.instructions}</div>}
        </div>
      ))}
    </div>
  );
}

// Legacy community "proven mockups" Google Sheet (shared view-only). Embedded
// read-only via the /preview path (frames cleanly; the raw /edit URL does not).
const BANK_SHEET_ID="19Pt909Zwk5J24843fNsni5GnJoAJidHC-DnjjKjpH1k";

function BankSheet({openTip,setOpenTip}){
  const{isDark}=useTheme();const c=t(isDark);
  const embedSrc=`https://docs.google.com/spreadsheets/d/${BANK_SHEET_ID}/preview`;
  const fullUrl=`https://docs.google.com/spreadsheets/d/${BANK_SHEET_ID}/edit?usp=sharing`;
  const linkStyle={color:"#818cf8",textDecoration:"none",fontWeight:600};
  return(
    <div className="mg-fadeUp">
      <div style={{marginBottom:12}}>
        <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>Community Sheet <span style={{fontSize:11,fontWeight:600,color:c.textMuted,marginLeft:6}}>(legacy archive)</span><Tip id="bank-sheet" text="The community's shared Google Sheet of proven mockups — the original archive, embedded view-only. To add new mockups, use the Submit tab: the native bank is where new entries live and grow." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
        <p style={{fontSize:12,color:c.textSub,marginTop:6,lineHeight:1.5}}>
          The original shared spreadsheet of proven mockups, embedded view-only. New mockups should be added through the <strong>Submit</strong> tab — the native bank above is where new entries live and grow. <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>Open the full sheet in a new tab ↗</a>
        </p>
      </div>
      <div style={{borderRadius:12,overflow:"hidden",border:`1px solid ${c.border}`,background:c.bgCard}}>
        <iframe title="Community proven-mockups sheet" src={embedSrc} loading="lazy" style={{width:"100%",height:"75vh",minHeight:560,border:"none",display:"block"}}/>
      </div>
      <div style={{fontSize:11,color:c.textMuted,marginTop:8}}>Sheet not loading? <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>Open it in a new tab ↗</a> — some browsers block embedded Google docs.</div>
    </div>
  );
}

function MyCollection({openTip,setOpenTip,onPromoted}){
  const{isDark}=useTheme();const c=t(isDark);
  // Add-form state
  const[title,setTitle]=useState("");
  const[note,setNote]=useState("");
  const[link,setLink]=useState("");
  const[nickname,setNickname]=useState("");
  const[isPublic,setIsPublic]=useState(false);
  const[img,setImg]=useState(null);
  const[preview,setPreview]=useState("");
  const[imgLoading,setImgLoading]=useState(false);
  const[dragOver,setDragOver]=useState(false);
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  // List state
  const[items,setItems]=useState(null);
  const[actId,setActId]=useState("");

  async function load(){
    try{const d=await bankPersonalListMine();setItems(d.records||[])}
    catch(e){setErr(e.message||"Couldn't load your collection.");setItems([])}
  }
  useEffect(()=>{load()},[]);

  async function pickImage(file){
    if(!file)return;setErr("");setImgLoading(true);
    try{const r=await prepProofImages(file);if(!r){setImgLoading(false);setErr("Couldn't read that image.");return}setImg(r);setPreview(r.thumb);setImgLoading(false)}
    catch(e){setImgLoading(false);setErr(e.message||"Image failed.")}
  }
  function dropImage(e){
    e.preventDefault();setDragOver(false);
    const f=e.dataTransfer&&e.dataTransfer.files&&e.dataTransfer.files[0];
    if(!f)return;
    if(f.type&&f.type.startsWith("image/"))pickImage(f);else setErr("Please drop an image file.");
  }

  async function add(){
    if(!title.trim()){setErr("Please add a title.");return}
    if(!img){setErr("Please add an image.");return}
    if(link.trim()&&!bankIsUrl(link)){setErr("Link must be a valid URL, or leave it blank.");return}
    setBusy(true);setErr("");
    try{
      await bankPersonalCreate({title:title.trim(),note:note.trim(),link:link.trim(),ownerNickname:nickname.trim(),visibility:isPublic?"public":"private",image:img.full,thumb:img.thumb});
      setTitle("");setNote("");setLink("");setImg(null);setPreview("");setIsPublic(false);
      await load();
    }catch(e){setErr(e.message||"Couldn't add.")}finally{setBusy(false)}
  }

  async function toggleVis(it){
    setActId(it.id);setErr("");
    try{await bankPersonalSetVisibility(it.id,it.visibility==="public"?"private":"public");await load()}
    catch(e){setErr(e.message||"Couldn't update.")}finally{setActId("")}
  }
  async function del(it){
    if(!window.confirm("Delete this personal mockup? This can't be undone."))return;
    setActId(it.id);setErr("");
    try{await bankPersonalDelete(it.id);setItems(xs=>(xs||[]).filter(x=>x.id!==it.id))}
    catch(e){setErr(e.message||"Couldn't delete.")}finally{setActId("")}
  }
  async function promote(it){
    if(!window.confirm("Promote to proven? A copy is sent to admin review to become a verified proven mockup (you'll keep your personal copy)."))return;
    setActId(it.id);setErr("");
    try{await bankPersonalPromote(it.id);if(onPromoted)onPromoted()}
    catch(e){setErr(e.message||"Couldn't promote.")}finally{setActId("")}
  }

  const inputStyle={width:"100%",padding:"9px 11px",borderRadius:8,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:12,fontFamily:"inherit",outline:"none"};
  const labelStyle={display:"block",fontSize:11,fontWeight:700,color:c.textSub,marginBottom:5};
  const btn=(bg,col,extra)=>({padding:"5px 11px",borderRadius:7,border:"none",background:bg,color:col,fontSize:10,fontWeight:700,cursor:"pointer",...extra});

  return(
    <div className="mg-fadeUp">
      <div style={{marginBottom:14}}>
        <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>My Collection<Tip id="bank-personal" text="Your own loose bank of mockups — just an image and a title. Private by default (only you see it); flip a item to Public to share it in Browse. 'Promote to proven' sends a copy to admin review to become a verified proven mockup." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
        <p style={{fontSize:12,color:c.textSub,marginTop:4,lineHeight:1.5}}>Your personal mockups. <strong>Private</strong> items are visible only to you; <strong>Public</strong> ones show in everyone's Browse. Promote a standout to the proven bank for admin review.</p>
      </div>

      {/* Add form */}
      <div style={{display:"flex",gap:12,padding:12,marginBottom:18,borderRadius:12,background:c.savedBg,border:`1px solid ${c.savedBorder}`,flexWrap:"wrap"}}>
        <label
          onDragOver={e=>{e.preventDefault();if(!dragOver)setDragOver(true)}}
          onDragLeave={e=>{e.preventDefault();if(dragOver)setDragOver(false)}}
          onDrop={dropImage}
          title="Click or drag an image here"
          style={{flexShrink:0,width:110,height:110,borderRadius:10,border:dragOver?`2px solid ${c.uploadBorderHover}`:`1px dashed ${c.uploadBorder}`,background:dragOver?c.uploadBgHover:c.uploadBg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",overflow:"hidden",position:"relative",transition:"background 0.15s,border-color 0.15s"}}>
          {dragOver?<span style={{fontSize:10,color:"#818cf8",fontWeight:700,pointerEvents:"none"}}>Drop here</span>
            :imgLoading?<span style={{fontSize:10,color:c.textSub}}>…</span>
            :preview?<img className="pb-img" src={preview} alt="" style={{width:"100%",height:"100%",objectFit:"cover",pointerEvents:"none"}}/>
            :<span style={{fontSize:10,color:c.textSub,textAlign:"center",padding:4,pointerEvents:"none"}}>📷<br/>Image<br/><span style={{fontSize:8,opacity:0.7}}>click or drag</span></span>}
          <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>pickImage(e.target.files&&e.target.files[0])}/>
        </label>
        <div style={{flex:1,minWidth:220}}>
          <div style={{marginBottom:8}}><label style={labelStyle}>Title <span style={{color:"#ef4444"}}>*</span></label><input style={inputStyle} value={title} onChange={e=>setTitle(e.target.value)} placeholder="What is this mockup?" maxLength={200}/></div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            <input style={{...inputStyle,flex:2,minWidth:140}} value={link} onChange={e=>setLink(e.target.value)} placeholder="Link (optional)" maxLength={1000}/>
            <input style={{...inputStyle,flex:1,minWidth:110}} value={nickname} onChange={e=>setNickname(e.target.value)} placeholder="Your nickname (optional)" maxLength={60}/>
          </div>
          <input style={{...inputStyle,marginBottom:8}} value={note} onChange={e=>setNote(e.target.value)} placeholder="Note (optional)" maxLength={2000}/>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <button onClick={()=>setIsPublic(p=>!p)} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${isPublic?"rgba(52,211,153,0.5)":c.border}`,background:isPublic?"rgba(52,211,153,0.12)":"transparent",color:isPublic?"#34d399":c.textSub,fontSize:11,fontWeight:700,cursor:"pointer"}}>{isPublic?"🌐 Public":"🔒 Private (only you)"}</button>
            <button onClick={add} disabled={busy} style={{padding:"8px 20px",borderRadius:9,border:"none",background:busy?c.btnSecBg:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:busy?c.textSub:"white",fontSize:12,fontWeight:700,cursor:busy?"default":"pointer"}}>{busy?"Adding…":"Add to my collection"}</button>
          </div>
        </div>
      </div>

      {err&&<div style={{padding:"9px 12px",borderRadius:8,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}

      {items===null?<div style={{color:c.textSub,fontSize:13,padding:"30px 0",textAlign:"center"}}>Loading your collection…</div>
        :items.length===0?<div style={{color:c.textSub,fontSize:13,padding:"30px 0",textAlign:"center"}}>No personal mockups yet. Add your first above!</div>
        :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
          {items.map(it=>{const pub=it.visibility==="public";return(
            <div key={it.id} style={{borderRadius:12,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`}}>
              <div style={{position:"relative",aspectRatio:"1/1",background:c.savedBg}}>
                <img className="pb-img" src={bankPersonalImageUrl(it.id,"thumb")} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                <span style={{position:"absolute",top:7,left:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:pub?"#34d399":"#64748b",color:"#0a0a14"}}>{pub?"🌐 Public":"🔒 Private"}</span>
              </div>
              <div style={{padding:"10px 11px"}}>
                <div className="pb-text" style={{fontSize:13,fontWeight:700,color:c.text,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={it.title}>{it.title}</div>
                {it.link&&<a href={it.link} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"#818cf8",textDecoration:"none",display:"block",marginBottom:8,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.link} ↗</a>}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button disabled={actId===it.id} onClick={()=>toggleVis(it)} style={btn(c.btnSecBg,c.text)}>{pub?"Make private":"Make public"}</button>
                  <button disabled={actId===it.id} onClick={()=>promote(it)} style={btn("linear-gradient(135deg,#6366f1,#8b5cf6)","white")}>Promote</button>
                  <button disabled={actId===it.id} onClick={()=>del(it)} style={btn("transparent","#ef4444",{border:"1px solid rgba(239,68,68,0.4)"})}>Delete</button>
                </div>
              </div>
            </div>
          )})}
        </div>}
    </div>
  );
}

function ArchiveAdmin({openTip,setOpenTip}){
  const{isDark}=useTheme();const c=t(isDark);
  const[title,setTitle]=useState("");
  const[linksText,setLinksText]=useState("");      // one URL per line
  const[note,setNote]=useState("");
  const[category,setCategory]=useState("");
  const[imgs,setImgs]=useState([]);                // [{full,thumb,preview}] up to 4
  const[imgLoading,setImgLoading]=useState(false);
  const[dragOver,setDragOver]=useState(false);
  const[busy,setBusy]=useState(false);
  const[err,setErr]=useState("");
  const[items,setItems]=useState(null);
  const[actId,setActId]=useState("");
  const[editing,setEditing]=useState(null);        // {id,title,linksText,note,category}
  const[filterCat,setFilterCat]=useState("All");
  const MAXIMG=4;

  async function load(){
    try{const d=await bankArchiveList();setItems(d.records||[])}
    catch(e){setErr(e.message||"Couldn't load the archive.");setItems([])}
  }
  useEffect(()=>{load()},[]);

  function parseLinks(text){return (text||"").split(/\n+/).map(s=>s.trim()).filter(Boolean)}

  async function addFiles(fileList){
    const files=Array.from(fileList||[]).filter(f=>f.type&&f.type.startsWith("image/"));
    if(!files.length){setErr("Please choose image files.");return}
    setErr("");setImgLoading(true);
    try{
      for(const f of files){
        if(imgsRef.current.length>=MAXIMG){setErr(`Up to ${MAXIMG} images per item.`);break}
        const r=await prepProofImages(f);
        if(r){imgsRef.current=[...imgsRef.current,{full:r.full,thumb:r.thumb,preview:r.thumb}];setImgs(imgsRef.current)}
      }
    }catch(e){setErr(e.message||"Image failed.")}
    finally{setImgLoading(false)}
  }
  // ref mirror so sequential async appends don't clobber each other
  const imgsRef=useRef([]);
  useEffect(()=>{imgsRef.current=imgs},[imgs]);
  function removeImg(i){const n=imgs.filter((_,j)=>j!==i);imgsRef.current=n;setImgs(n)}
  function dropImage(e){e.preventDefault();setDragOver(false);addFiles(e.dataTransfer&&e.dataTransfer.files)}

  async function add(){
    if(!title.trim()){setErr("Please add a title.");return}
    if(!imgs.length){setErr("Please add at least one image.");return}
    const links=parseLinks(linksText);
    for(const u of links)if(!bankIsUrl(u)){setErr(`Not a valid URL: ${u.slice(0,40)}`);return}
    setBusy(true);setErr("");
    try{
      await bankArchiveCreate({title:title.trim(),note:note.trim(),category:category.trim(),
        images:imgs.map(x=>({image:x.full,thumb:x.thumb})),links});
      setTitle("");setLinksText("");setNote("");setCategory("");imgsRef.current=[];setImgs([]);
      await load();
    }catch(e){setErr(e.message||"Couldn't add.")}finally{setBusy(false)}
  }
  async function saveEdit(){
    if(!editing.title.trim()){setErr("Title cannot be empty.");return}
    const links=parseLinks(editing.linksText);
    for(const u of links)if(!bankIsUrl(u)){setErr(`Not a valid URL: ${u.slice(0,40)}`);return}
    setBusy(true);setErr("");
    try{
      await bankArchiveUpdate(editing.id,{title:editing.title.trim(),note:editing.note.trim(),category:editing.category.trim(),links});
      setEditing(null);await load();
    }catch(e){setErr(e.message||"Couldn't save.")}finally{setBusy(false)}
  }
  async function del(it){
    if(!window.confirm("Delete this archive item and all its images? This can't be undone."))return;
    setActId(it.id);setErr("");
    try{await bankArchiveDelete(it.id);setItems(xs=>(xs||[]).filter(x=>x.id!==it.id))}
    catch(e){setErr(e.message||"Couldn't delete.")}finally{setActId("")}
  }

  const inputStyle={width:"100%",padding:"9px 11px",borderRadius:8,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:12,fontFamily:"inherit",outline:"none"};
  const labelStyle={display:"block",fontSize:11,fontWeight:700,color:c.textSub,marginBottom:5};
  const cats=items?Array.from(new Set(items.map(i=>i.category).filter(Boolean))).sort():[];
  const shownItems=items?(filterCat==="All"?items:items.filter(i=>i.category===filterCat)):null;

  return(
    <div className="mg-fadeUp">
      <div style={{marginBottom:14}}>
        <h2 style={{fontSize:18,fontWeight:700,color:c.text,margin:0,display:"inline-flex",alignItems:"center"}}>Community Archive <span style={{fontSize:11,fontWeight:600,color:"#0ea5e9",marginLeft:6}}>admin</span><Tip id="bank-archive-admin" text="The shared community library — visible to all members but only admins can add/edit/delete. Add up to 4 images per item (the proof set) and one link per line. Use category for the legacy tab name (e.g. 'PROVEN CC FOLDED')." openTip={openTip} setOpenTip={setOpenTip} align="left"/></h2>
        <p style={{fontSize:12,color:c.textSub,marginTop:4,lineHeight:1.5}}>Add mockups (up to {MAXIMG} images each) to the shared archive. Put one listing link per line.</p>
      </div>

      <div style={{display:"flex",gap:12,padding:12,marginBottom:18,borderRadius:12,background:c.savedBg,border:`1px solid ${c.savedBorder}`,flexWrap:"wrap"}}>
        <div style={{flexShrink:0,display:"flex",flexDirection:"column",gap:6}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",maxWidth:240}}>
            {imgs.map((im,i)=>(
              <div key={i} style={{position:"relative",width:72,height:72,borderRadius:8,overflow:"hidden",border:`1px solid ${c.border}`}}>
                <img className="pb-img" src={im.preview} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                <button onClick={()=>removeImg(i)} title="Remove" style={{position:"absolute",top:2,right:2,width:18,height:18,borderRadius:5,border:"none",background:"rgba(10,10,20,0.7)",color:"#fff",fontSize:11,cursor:"pointer",lineHeight:1}}>×</button>
              </div>
            ))}
            {imgs.length<MAXIMG&&<label
              onDragOver={e=>{e.preventDefault();if(!dragOver)setDragOver(true)}}
              onDragLeave={e=>{e.preventDefault();if(dragOver)setDragOver(false)}}
              onDrop={dropImage}
              title="Click or drag images here"
              style={{width:72,height:72,borderRadius:8,border:dragOver?`2px solid ${c.uploadBorderHover}`:`1px dashed ${c.uploadBorder}`,background:dragOver?c.uploadBgHover:c.uploadBg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",textAlign:"center"}}>
              <span style={{fontSize:9,color:c.textSub,pointerEvents:"none"}}>{imgLoading?"…":"＋ image"}</span>
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>addFiles(e.target.files)}/>
            </label>}
          </div>
          <span style={{fontSize:9,color:c.textMuted}}>{imgs.length}/{MAXIMG} images</span>
        </div>
        <div style={{flex:1,minWidth:240}}>
          <div style={{marginBottom:8}}><label style={labelStyle}>Title <span style={{color:"#ef4444"}}>*</span></label><input style={inputStyle} value={title} onChange={e=>setTitle(e.target.value)} placeholder="Mockup name" maxLength={200}/></div>
          <div style={{marginBottom:8}}><label style={labelStyle}>Category</label><input style={inputStyle} value={category} onChange={e=>setCategory(e.target.value)} placeholder="e.g. PROVEN CC FOLDED" maxLength={120}/></div>
          <div style={{marginBottom:8}}><label style={labelStyle}>Links <span style={{color:c.textMuted,fontWeight:400}}>(one URL per line)</span></label><textarea style={{...inputStyle,minHeight:54,resize:"vertical"}} value={linksText} onChange={e=>setLinksText(e.target.value)} placeholder={"https://www.etsy.com/listing/…\nhttps://www.etsy.com/listing/…"}/></div>
          <input style={{...inputStyle,marginBottom:8}} value={note} onChange={e=>setNote(e.target.value)} placeholder="Note (optional)" maxLength={2000}/>
          <button onClick={add} disabled={busy} style={{padding:"8px 20px",borderRadius:9,border:"none",background:busy?c.btnSecBg:"linear-gradient(135deg,#0ea5e9,#6366f1)",color:busy?c.textSub:"white",fontSize:12,fontWeight:700,cursor:busy?"default":"pointer"}}>{busy?"Working…":"Add to archive"}</button>
        </div>
      </div>

      {err&&<div style={{padding:"9px 12px",borderRadius:8,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}

      {cats.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
        {["All",...cats].map(cat=>(
          <button key={cat} onClick={()=>setFilterCat(cat)} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${filterCat===cat?"#0ea5e9":c.border}`,background:filterCat===cat?"rgba(14,165,233,0.15)":"transparent",color:filterCat===cat?"#0ea5e9":c.textSub,fontSize:11,fontWeight:700,cursor:"pointer"}}>{cat}</button>
        ))}
      </div>}

      {items===null?<div style={{color:c.textSub,fontSize:13,padding:"30px 0",textAlign:"center"}}>Loading archive…</div>
        :items.length===0?<div style={{color:c.textSub,fontSize:13,padding:"30px 0",textAlign:"center"}}>No archive items yet. Add the first above.</div>
        :<><div style={{fontSize:11,color:c.textMuted,marginBottom:10}}>{shownItems.length} item{shownItems.length===1?"":"s"}{filterCat!=="All"?` in "${filterCat}"`:" in the archive"}.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
          {shownItems.map(it=>{const n=it.imageCount&&it.imageCount>0?it.imageCount:1;return(
            <div key={it.id} style={{borderRadius:12,overflow:"hidden",background:c.bgCard,border:`1px solid ${c.border}`}}>
              <div style={{position:"relative",aspectRatio:"1/1",background:c.savedBg}}>
                <img className="pb-img" src={bankArchiveImageUrl(it.id,0,"thumb")} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                {it.category&&<span style={{position:"absolute",top:7,left:7,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"#0ea5e9",color:"#0a0a14",maxWidth:"90%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.category}</span>}
                {n>1&&<span style={{position:"absolute",top:7,right:7,fontSize:9,fontWeight:800,padding:"2px 7px",borderRadius:20,background:"rgba(10,10,20,0.6)",color:"#fff"}}>{n} imgs</span>}
              </div>
              <div style={{padding:"10px 11px"}}>
                <div className="pb-text" style={{fontSize:13,fontWeight:700,color:c.text,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={it.title}>{it.title}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setEditing({id:it.id,title:it.title||"",linksText:(it.links||[]).join("\n"),note:it.note||"",category:it.category||""})} style={{padding:"5px 11px",borderRadius:7,border:"none",background:c.btnSecBg,color:c.text,fontSize:10,fontWeight:700,cursor:"pointer"}}>Edit</button>
                  <button disabled={actId===it.id} onClick={()=>del(it)} style={{padding:"5px 11px",borderRadius:7,border:"1px solid rgba(239,68,68,0.4)",background:"transparent",color:"#ef4444",fontSize:10,fontWeight:700,cursor:"pointer"}}>Delete</button>
                </div>
              </div>
            </div>
          )})}
        </div></>}

      {editing&&<div onClick={()=>setEditing(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",zIndex:200,overflowY:"auto"}}>
        <div onClick={e=>e.stopPropagation()} className="mg-fadeUp" style={{maxWidth:520,width:"100%",background:c.modalBg,borderRadius:14,border:`1px solid ${c.border}`,padding:22}}>
          <h2 style={{fontSize:16,fontWeight:700,color:c.text,margin:"0 0 14px"}}>Edit archive item <span style={{fontSize:11,fontWeight:400,color:c.textMuted}}>(text/links; images unchanged)</span></h2>
          <div style={{marginBottom:8}}><label style={labelStyle}>Title <span style={{color:"#ef4444"}}>*</span></label><input style={inputStyle} value={editing.title} onChange={e=>setEditing(d=>({...d,title:e.target.value}))} maxLength={200}/></div>
          <div style={{marginBottom:8}}><label style={labelStyle}>Category</label><input style={inputStyle} value={editing.category} onChange={e=>setEditing(d=>({...d,category:e.target.value}))} maxLength={120}/></div>
          <div style={{marginBottom:8}}><label style={labelStyle}>Links <span style={{color:c.textMuted,fontWeight:400}}>(one URL per line)</span></label><textarea style={{...inputStyle,minHeight:70,resize:"vertical"}} value={editing.linksText} onChange={e=>setEditing(d=>({...d,linksText:e.target.value}))}/></div>
          <div style={{marginBottom:14}}><label style={labelStyle}>Note</label><textarea style={{...inputStyle,minHeight:60,resize:"vertical"}} value={editing.note} onChange={e=>setEditing(d=>({...d,note:e.target.value}))} maxLength={2000}/></div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveEdit} disabled={busy} style={{padding:"8px 20px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#0ea5e9,#6366f1)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>{busy?"Saving…":"Save"}</button>
            <button onClick={()=>setEditing(null)} style={{padding:"8px 18px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:12,fontWeight:700,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      </div>}
    </div>
  );
}

function MockupBank({isAdmin}){
  const{isDark}=useTheme();const c=t(isDark);
  const[tab,setTab]=useState("browse");
  const[flash,setFlash]=useState("");
  // Own tooltip open-state + outside-click close, mirroring the Studio's
  // sub-components, so the shared Tip component works inside the bank.
  const[openTip,setOpenTip]=useState(null);
  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);

  const tabs=[{id:"browse",label:"Browse"},{id:"submit",label:"Submit"},{id:"mine",label:"My Collection"},{id:"sheet",label:"Community Sheet"}];
  if(isAdmin){tabs.push({id:"review",label:"Review"});tabs.push({id:"archive",label:"Archive"});}

  function onSubmitted(){
    setFlash("✓ Submitted for review. Andreas will approve it before it appears in the bank.");
    setTab("browse");
    setTimeout(()=>setFlash(""),6000);
  }
  function onPromoted(){
    setFlash("✓ Sent to admin review. If approved, it'll appear as a proven mockup. Your personal copy stays in My Collection.");
    setTimeout(()=>setFlash(""),6000);
  }

  return(
    <div className="mg-fadeUp">
      <div style={{display:"flex",gap:4,marginBottom:18,borderBottom:`1px solid ${c.border}`,paddingBottom:0}}>
        {tabs.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{padding:"8px 16px",border:"none",borderBottom:tab===tb.id?"2px solid #6366f1":"2px solid transparent",background:"transparent",color:tab===tb.id?c.text:c.textSub,fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:-1}}>
            {tb.label}{(tb.id==="review"||tb.id==="archive")&&<span style={{marginLeft:5,fontSize:9,opacity:0.7}}>admin</span>}
          </button>
        ))}
      </div>

      {flash&&<div style={{padding:"10px 14px",borderRadius:10,background:"rgba(52,211,153,0.12)",border:"1px solid rgba(52,211,153,0.35)",color:"#34d399",fontSize:12,fontWeight:600,marginBottom:16}}>{flash}</div>}

      {tab==="browse"&&<BankBrowse openTip={openTip} setOpenTip={setOpenTip} isAdmin={isAdmin}/>}
      {tab==="submit"&&<BankSubmitForm onSubmitted={onSubmitted} openTip={openTip} setOpenTip={setOpenTip}/>}
      {tab==="mine"&&<MyCollection openTip={openTip} setOpenTip={setOpenTip} onPromoted={onPromoted}/>}
      {tab==="sheet"&&<BankSheet openTip={openTip} setOpenTip={setOpenTip}/>}
      {tab==="review"&&isAdmin&&<BankReview openTip={openTip} setOpenTip={setOpenTip}/>}
      {tab==="archive"&&isAdmin&&<ArchiveAdmin openTip={openTip} setOpenTip={setOpenTip}/>}
    </div>
  );
}

/* ═══════ COLOUR-LINK MODAL ═══════
   Per-set "🎨 Link colours": bind a variation template's colour-property options
   to mockups IN this set. Templates are fetched from the studio-side read proxy
   (/api/variation-templates → the Listing Editor's mirror). Two steps: pick a
   template + its colour property, then assign a mockup to each colour option.
   Anchored on the set; publish rides colorLinks on the first pipeline chunk. */
/* `set` provided  → single-set mode (the per-chip 🎨): behaviour is byte-for-byte
   today's — no step 0, no set picker. `set` null → picker mode (the labeled
   "Link photos to variations" button): a new Step 0 lists every saved set; picking
   one flows into the SAME steps below via `activeSet`. `sets`/`mockupsForSet`/
   `onSaveForSet` are used only in picker mode. */
function ColorLinkModal({ set, mockups, sets, mockupsForSet, colors:c, isDark, onSave, onSaveForSet, onClose }){
  const pickerMode = !set;
  const [pickedSet,setPickedSet] = useState(null);
  const activeSet = set || pickedSet;                 // the set the steps operate on
  const seed0 = (set && set.colorLinks) || null;      // single-mode initial seeding only
  const [templates,setTemplates] = useState(null); // null = loading; [] = loaded/none
  const [error,setError] = useState(null);          // 'auth' | 'load' | null
  const [step,setStep] = useState(set?(seed0?2:1):0); // picker mode starts on step 0
  const [chosenTemplateId,setChosenTemplateId] = useState(seed0?.templateId || null);
  const [propertyKey,setPropertyKey] = useState(seed0?.propertyKey || null);
  const [draftMap,setDraftMap] = useState(()=>({ ...(seed0?.map || {}) }));
  const [pendingTpl,setPendingTpl] = useState(null); // template awaiting replace-confirm
  const existing = activeSet?.colorLinks || null;
  const activeMockups = pickerMode ? (activeSet ? (mockupsForSet?.(activeSet) || []) : []) : mockups;

  // Picker → editor: seed the exact same state the per-chip path seeds (incl. the
  // "existing links open at step 2" behaviour and stale-template handling below).
  const chooseSet = (s)=>{ const ex=s?.colorLinks||null; setPickedSet(s); setChosenTemplateId(ex?.templateId||null); setPropertyKey(ex?.propertyKey||null); setDraftMap({ ...(ex?.map||{}) }); setPendingTpl(null); setStep(ex?2:1); };
  const backToPicker = ()=>{ setPickedSet(null); setPendingTpl(null); setStep(0); };

  const load = useCallback(async()=>{
    setTemplates(null); setError(null);
    try{
      const r = await fetch('/api/variation-templates',{ headers:{ Authorization:`Bearer ${_suiteToken}` } });
      if(!r.ok){ setError(r.status===403?'auth':'load'); setTemplates([]); return; }
      const j = await r.json();
      setTemplates(parseTemplatesResponse(j));
    }catch{ setError('load'); setTemplates([]); }
  },[]);
  useEffect(()=>{ load(); },[load]);

  const chosen = (templates||[]).find(t=>t.id===chosenTemplateId) || null;
  const stale = !!(chosenTemplateId && templates && templates.length>=0 && !chosen && !error);
  const options = chosen && propertyKey ? optionsForProperty(chosen, propertyKey) : [];
  const assignedCount = Object.keys(draftMap).filter(k=>draftMap[k]).length;

  const commitTemplate = (t)=>{ setChosenTemplateId(t.id); setPropertyKey(detectColorPropertyKey(t.property_names)); setDraftMap({}); setPendingTpl(null); };
  const pickTemplate = (t)=>{ if(chosenTemplateId && chosenTemplateId!==t.id && assignedCount>0){ setPendingTpl(t); return; } commitTemplate(t); };
  const assign = (opt,mid)=> setDraftMap(m=>{ const n={...m}; if(n[opt]===mid) delete n[opt]; else n[opt]=mid; return n; });
  const clearRow = (opt)=> setDraftMap(m=>{ const n={...m}; delete n[opt]; return n; });
  const doSave = ()=>{ const t = chosen || { id:chosenTemplateId, name:existing?.templateName || '' }; const cl = makeColorLinks({ template:t, propertyKey, map:draftMap, savedAt:Date.now() }); if(set) onSave(cl); else onSaveForSet(activeSet.id, cl); };

  const btn = (bg,col,extra)=>({ padding:"7px 14px", borderRadius:8, border:"none", background:bg, color:col, fontSize:11, fontWeight:700, cursor:"pointer", ...extra });
  const ghost = { padding:"7px 12px", borderRadius:8, border:`1px solid ${c.border}`, background:"transparent", color:c.textSub, fontSize:11, fontWeight:600, cursor:"pointer" };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:9000,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"min(680px,96vw)",maxHeight:"90vh",overflow:"auto",background:c.bg,border:`1px solid ${c.border}`,borderRadius:14,boxShadow:"0 20px 60px rgba(0,0,0,0.5)",padding:18}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
            {pickerMode&&activeSet&&<button onClick={backToPicker} style={{...ghost,padding:"4px 10px"}}>← Sets</button>}
            <div style={{color:c.text,fontSize:14,fontWeight:800}}>{activeSet?<>🎨 Link colours — <span className="pb-text" style={{color:"#a5b4fc"}}>{activeSet.name}</span></>:"🎨 Link photos to variations"}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:c.textSub,fontSize:16,cursor:"pointer"}}>✕</button>
        </div>

        {/* STEP 0 — pick which set to work on (picker mode only; per-chip path never sees this) */}
        {pickerMode && step===0 && (
          (sets||[]).length===0 ? (
            <div style={{padding:"22px 16px",borderRadius:10,background:isDark?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",color:c.textSub,fontSize:12,lineHeight:1.6,textAlign:"center"}}>
              No mockup sets yet.<br/>Save a mockup set first — select templates and hit <b>💾 Save these as a set</b>, then come back to link your colours.
              <div style={{marginTop:12}}><button onClick={onClose} style={btn("linear-gradient(135deg,#6366f1,#8b5cf6)","white")}>Got it</button></div>
            </div>
          ) : (
            <div>
              <div style={{color:c.textMuted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Choose a set to link</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {buildSetPickerRows(sets, s=>(mockupsForSet?.(s)||[]).length).map((row,i)=>{ const s=sets[i]; return (
                  <button key={row.id??i} onClick={()=>chooseSet(s)} style={{textAlign:"left",padding:"11px 13px",borderRadius:10,border:`1px solid ${c.border}`,background:isDark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                    <span style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                      <span className="pb-text" style={{color:c.text,fontSize:12,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{row.name||"(untitled set)"}</span>
                      <span style={{color:c.textMuted,fontSize:10}}>{row.mockupCount} mockup{row.mockupCount===1?"":"s"}{row.hasVideo?" · 🎥 video":""}</span>
                    </span>
                    {row.linked
                      ? <span style={{flex:"0 0 auto",padding:"3px 9px",borderRadius:20,background:"rgba(99,102,241,0.16)",border:"1px solid rgba(99,102,241,0.4)",color:"#a5b4fc",fontSize:10,fontWeight:700}}>🎨 {row.linkedCount} linked</span>
                      : <span style={{flex:"0 0 auto",color:c.textMuted,fontSize:15,opacity:0.5}}>→</span>}
                  </button>
                );})}
              </div>
            </div>
          )
        )}

        {templates===null && step!==0 && <div style={{color:c.textSub,fontSize:12,padding:"24px 0",textAlign:"center"}}>Loading your variation templates…</div>}

        {error==='auth' && step!==0 && <div style={{padding:"12px 14px",borderRadius:10,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#f87171",fontSize:12}}>Couldn’t read your variation templates (not authorised). Try reopening the Studio from the Suite, then <button onClick={load} style={{...ghost,padding:"3px 9px",marginLeft:6,color:"#f87171"}}>Refresh</button></div>}
        {error==='load' && step!==0 && <div style={{padding:"12px 14px",borderRadius:10,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#fbbf24",fontSize:12}}>Couldn’t load your variation templates right now. <button onClick={load} style={{...ghost,padding:"3px 9px",marginLeft:6,color:"#fbbf24"}}>Try again</button></div>}

        {/* STEP 1 — pick template + colour property */}
        {templates!==null && !error && step===1 && (
          templates.length===0 ? (
            <div style={{padding:"20px 16px",borderRadius:10,background:isDark?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",color:c.textSub,fontSize:12,lineHeight:1.5,textAlign:"center"}}>
              No variation templates found.<br/>Create a variation template in the <b>Listing Editor</b> first, then come back.
              <div style={{marginTop:12}}><button onClick={load} style={btn("linear-gradient(135deg,#6366f1,#8b5cf6)","white")}>↻ Refresh</button></div>
            </div>
          ) : (
            <div>
              {stale && <div style={{marginBottom:10,padding:"10px 12px",borderRadius:9,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#fbbf24",fontSize:11}}>The previously linked template “{existing?.templateName||'—'}” no longer exists. Pick another below — your saved links are kept until you change them.</div>}
              <div style={{color:c.textMuted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>1 · Choose a variation template</div>
              <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:14}}>
                {templates.map(t=>{ const sel=t.id===chosenTemplateId; return (
                  <button key={t.id} onClick={()=>pickTemplate(t)} style={{textAlign:"left",padding:"9px 12px",borderRadius:9,border:`1px solid ${sel?"rgba(99,102,241,0.5)":c.border}`,background:sel?"rgba(99,102,241,0.14)":(isDark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)"),cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span className="pb-text" style={{color:sel?"#a5b4fc":c.text,fontSize:12,fontWeight:600}}>{t.name||"(untitled)"}</span>
                    <span style={{color:c.textMuted,fontSize:9}}>{(t.property_names||[]).map(p=>p.property_name).filter(Boolean).join(" · ")||"no properties"}</span>
                  </button>
                );})}
              </div>
              {chosen && (
                <div style={{marginBottom:14}}>
                  <div style={{color:c.textMuted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>2 · Which property is the colour?</div>
                  {(chosen.property_names||[]).length===0 ? (
                    <div style={{color:"#fbbf24",fontSize:11}}>This template has no properties to link.</div>
                  ) : (
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {chosen.property_names.map(p=>{ const sel=p.property_name===propertyKey; return (
                        <button key={p.property_name} onClick={()=>setPropertyKey(p.property_name)} style={{padding:"6px 12px",borderRadius:20,border:`1px solid ${sel?"rgba(99,102,241,0.5)":c.border}`,background:sel?"rgba(99,102,241,0.18)":"transparent",color:sel?"#a5b4fc":c.textSub,fontSize:11,fontWeight:600,cursor:"pointer"}}>{p.property_name||"(unnamed)"} <span style={{color:c.textMuted,fontSize:9}}>({(p.options||[]).length})</span></button>
                      );})}
                    </div>
                  )}
                  {!propertyKey && (chosen.property_names||[]).length>0 && <div style={{marginTop:6,color:c.textMuted,fontSize:10}}>Pick the property that holds your colours.</div>}
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                <button onClick={onClose} style={ghost}>Cancel</button>
                <button onClick={()=>setStep(2)} disabled={!chosen||!propertyKey} style={btn(chosen&&propertyKey?"linear-gradient(135deg,#6366f1,#8b5cf6)":c.btnSecBg,chosen&&propertyKey?"white":c.textDim,{cursor:chosen&&propertyKey?"pointer":"not-allowed"})}>Next →</button>
              </div>
            </div>
          )
        )}

        {/* STEP 2 — assign a mockup to each colour option */}
        {templates!==null && !error && step===2 && (
          stale ? (
            <div style={{padding:"18px 16px",borderRadius:10,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#fbbf24",fontSize:12,lineHeight:1.5}}>
              The linked template “{existing?.templateName||'—'}” is no longer available.
              <div style={{marginTop:12}}><button onClick={()=>setStep(1)} style={btn("linear-gradient(135deg,#6366f1,#8b5cf6)","white")}>Pick another template</button></div>
            </div>
          ) : (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{color:c.textSub,fontSize:11}}><b className="pb-text" style={{color:c.text}}>{chosen?.name}</b> · colour = <b style={{color:"#a5b4fc"}}>{propertyKey}</b></div>
                <button onClick={()=>setStep(1)} style={{...ghost,padding:"4px 10px"}}>← Change template</button>
              </div>
              {activeMockups.length===0 ? (
                <div style={{color:"#fbbf24",fontSize:11,padding:"14px 0"}}>This set has no mockups to link yet.</div>
              ) : options.length===0 ? (
                <div style={{color:c.textMuted,fontSize:11,padding:"14px 0"}}>This property has no colour options.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                  {options.map(opt=>{ const mid=draftMap[opt]; return (
                    <div key={opt} style={{padding:"8px 10px",borderRadius:9,border:`1px solid ${c.border}`,background:isDark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                        <span style={{color:c.text,fontSize:11,fontWeight:700}}>{opt}</span>
                        {mid ? <button onClick={()=>clearRow(opt)} style={{background:"none",border:"none",color:"#f87171",fontSize:10,cursor:"pointer"}}>clear</button>
                             : <span style={{color:c.textMuted,fontSize:9}}>uses the primary photo</span>}
                      </div>
                      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                        {activeMockups.map(mk=>{ const sel=mid===mk.id; return (
                          <button key={mk.id} onClick={()=>assign(opt,mk.id)} title={mk.name} style={{flex:"0 0 auto",width:52,height:52,borderRadius:7,overflow:"hidden",padding:0,border:`2px solid ${sel?"#6366f1":c.border}`,boxShadow:sel?"0 0 0 2px rgba(99,102,241,0.35)":"none",cursor:"pointer",background:c.bgCard}}>
                            <img className="pb-img" src={mk.src} alt={mk.name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                          </button>
                        );})}
                      </div>
                    </div>
                  );})}
                </div>
              )}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div>{assignedCount>0 && <button onClick={()=>setDraftMap({})} style={{...ghost,color:"#f87171",borderColor:"rgba(239,68,68,0.3)"}}>Clear all</button>}</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={onClose} style={ghost}>Cancel</button>
                  <button onClick={doSave} style={btn("linear-gradient(135deg,#34d399,#059669)","white")}>Save links</button>
                </div>
              </div>
              <div style={{marginTop:8,color:c.textMuted,fontSize:9}}>{assignedCount} of {options.length} colour{options.length===1?"":"s"} linked · one mockup can serve several colours · unlinked colours show the primary photo.</div>
            </div>
          )
        )}

        {/* Replace-confirm when switching templates with existing assignments */}
        {pendingTpl && (
          <div onClick={()=>setPendingTpl(null)} style={{position:"absolute",inset:0}}>
            <div onClick={e=>e.stopPropagation()} style={{position:"fixed",inset:0,display:"flex",alignItems:"center",justifyContent:"center",zIndex:9100}}>
              <div style={{background:c.bg,border:`1px solid ${c.border}`,borderRadius:12,padding:18,width:"min(400px,92vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.55)"}}>
                <div style={{color:c.text,fontSize:12,fontWeight:700,marginBottom:6}}>Change the template?</div>
                <div className="pb-text" style={{color:c.textSub,fontSize:11,lineHeight:1.5,marginBottom:14}}>Switching to “{pendingTpl.name||'this template'}” clears the {assignedCount} colour link{assignedCount===1?"":"s"} you’ve set.</div>
                <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                  <button onClick={()=>setPendingTpl(null)} style={ghost}>Keep current</button>
                  <button onClick={()=>commitTemplate(pendingTpl)} style={btn("linear-gradient(135deg,#f59e0b,#d97706)","white")}>Change &amp; clear</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════ MAIN APP ═══════ */
export default function App({suiteToken,suiteUser}){
  const[isDark,setIsDark]=useState(()=>getTheme()==="dark");

  /* Privacy mode v2 — embedded-only, driven by the Suite.
     Mirror the current theme onto <html data-theme> so the covering CSS can
     pick a theme-aware opaque cover (dark → white, light → black). */
  useEffect(()=>{
    try{document.documentElement.setAttribute("data-theme",isDark?"dark":"light")}catch(e){}
  },[isDark]);

  /* Inbound privacy listener — hardened. Only when embedded. Trust chain:
     e.origin === suite → d.source === "creative-seller-suite" →
     d.type === "SUITE_PRIVACY_STATE" → boolean d.privacyOn. */
  useEffect(()=>{
    if(window.self===window.top)return;
    const onMsg=(e)=>{
      if(e.origin!=="https://suite.wtfamievendoing.com")return;
      const d=e.data;
      if(!d||typeof d!=="object")return;
      if(d.source!=="creative-seller-suite"||d.type!=="SUITE_PRIVACY_STATE")return;
      if(typeof d.privacyOn!=="boolean")return;
      if(d.privacyOn)document.documentElement.classList.add("privacy-mode");
      else document.documentElement.classList.remove("privacy-mode");
    };
    window.addEventListener("message",onMsg);
    return()=>window.removeEventListener("message",onMsg);
  },[]);

  /* Global dark mode v2 — embedded-only, driven by the Suite. Same hardening as the
     privacy listener above: suite origin, exact source/type, then apply the theme. */
  useEffect(()=>{
    if(!IS_EMBEDDED)return;
    const onMsg=(e)=>{
      if(e.origin!=="https://suite.wtfamievendoing.com")return;
      const d=e.data;
      if(!d||typeof d!=="object")return;
      if(d.source!=="creative-seller-suite"||d.type!=="SUITE_THEME_STATE")return;
      if(d.theme==="dark"||d.theme==="light")setIsDark(d.theme==="dark");
    };
    window.addEventListener("message",onMsg);
    return()=>window.removeEventListener("message",onMsg);
  },[]);

  const[step,setStep]=useState(0);
  // Top-level section: the mockup-editing wizard ("studio") or the community
  // proven-mockup bank ("bank"). The bank is fully self-contained.
  const[section,setSection]=useState("studio");
  const isAdmin=((suiteUser?.app_metadata?.roles)||[]).map(r=>String(r).toLowerCase()).includes("admin");
  const[mockups,setMockups]=useState([]);
  const[editMock,setEditMock]=useState(null);
  const[editSavedSO,setEditSavedSO]=useState(null);
  const[editMask,setEditMask]=useState(null); // the ONE foreground editor (mask + imported overlay)
  const[editResultSO,setEditResultSO]=useState(null); // editing SO from results with design preview
  const[bulkZoneEditor,setBulkZoneEditor]=useState(null); // {set} — post-gen bulk zone edit across a generated set
  const[bulkZoneScope,setBulkZoneScope]=useState("session"); // "session" (default — safe, experimental, doesn't touch saved templates) | "permanent" (writes templates)
  const[editResultFG,setEditResultFG]=useState(null);
  // O25b — scoped design crop. Three SESSION stores (reset on reload, like
  // the SO editor's session scopes + DC). Each maps to a per-group crop
  // {[groupId]:{sx,sy,sw,sh}}. Resolved at tile-render into so.crop.
  const[mockupCrop,setMockupCrop]=useState({});     // scope (a): {[mk]:cropByGroup}
  const[templateCrop,setTemplateCrop]=useState({}); // scope (c): {[tplId]:cropByGroup}
  const[batchCrop,setBatchCrop]=useState(null);     // scope (b): cropByGroup | null
  const[cropModal,setCropModal]=useState(null);     // {tpl,gdm,mk} for CropScopeModal
  const[tempOverrides,setTempOverrides]=useState({});
  const[globalDC,setGlobalDC]=useState({...DEFAULT_DC}); // global design controls
  const[setDCs,setSetDCs]=useState({}); // per-set overrides: {setId: {...}}
  const[mockupDCs,setMockupDCs]=useState({}); // per-mockup overrides: {"setId_tplId": {...}}
  const[dcModal,setDcModal]=useState(null);
  // Bulk DC editor state. null = closed. {scope:"global"} or {scope:"set",setId}
  // when open. Modal pulls dc from the matching state slot, applies on
  // confirm by clearing matching genOut entries.
  const[bulkDcEditor,setBulkDcEditor]=useState(null);
  // ── Listing Video LIBRARY (server-side, chunked, cross-device) ──────────
  // A listing video is now a library item like any mockup: uploaded through the
  // Step-1 bar, stored server-side in the mockup-studio-videos Blobs store (NO
  // compression — validate only), and selectable INTO a set exactly like a
  // template. `libVideos` = the user's manifests ({videoId,file_name,sizeBytes,
  // durationSec,parts,partSize,thumb,createdAt}); bytes are fetched on demand.
  const[libVideos,setLibVideos]=useState([]);
  const[videoLoading,setVideoLoading]=useState(false); // library list fetch in flight
  const[videoLoadErr,setVideoLoadErr]=useState(false); // list fetch failed after retries → show "couldn't load", NOT the empty "no video" state
  const[videoUsage,setVideoUsage]=useState({count:0,bytes:0,maxBytes:MAX_TOTAL_VIDEO_BYTES});
  // Per-file upload rows: ONE row per picked file, each with its own bar + state
  // ("queued"→"uploading"→"done"|"error"). The queue still SERIALIZES the actual
  // uploads (one at a time — concurrent uploads raced the shared per-user index
  // blob, which has no CAS in @netlify/blobs); the rows are presentation only.
  // Clean "done" rows auto-clear after a few seconds; errors stay until dismissed.
  const[videoRows,setVideoRows]=useState([]); // [{rowId,file_name,state,pct,error,warn}]
  const videoRowSeqRef=useRef(0);             // stable monotonic row ids
  const[videoNotice,setVideoNotice]=useState(null); // one-off {error} (e.g. a delete that failed)
  const videoQueueRef=useRef([]);          // pending {rowId,file} (FIFO)
  const videoQueueRunningRef=useRef(false); // true while the queue drains
  // Step-3 "Generate & Send" manual path selects ONE library video by id. On
  // send it rides the single-message PIPELINE_VIDEO the suite consumes today —
  // gated at the proven ~4.5MB ceiling (larger clips publish via SETS once the
  // suite attach arc ships).
  const[manualVideoId,setManualVideoId]=useState(null);
  const[manualVideoNote,setManualVideoNote]=useState("");
  // Working-selection videos (mirrors selSaved for templates): ONE multi-select
  // set, toggled by the card-body click and shown by the indigo ✓ — identical
  // feel to template selection. It drives BOTH bulk delete (header "🗑 Delete N")
  // and set membership (save enforces exactly one selected → one video per set).
  const[selVideoIds,setSelVideoIds]=useState(new Set());
  // Object-URL cache for inline <video> previews + manual-send reassembly, keyed
  // by videoId. Reassembled from server parts on demand; at most a couple live.
  const videoUrlCacheRef=useRef({});

  // Read video duration in seconds. Robust against common browser quirks:
  //   - Some browsers won't fire metadata events on detached <video>
  //     elements, so we attach to a hidden DOM container.
  //   - Some MP4 containers report duration as Infinity initially and only
  //     resolve to a real number after seeking (Chrome bug since forever).
  //     We seek to a large time, then re-read duration on `seeked` /
  //     `durationchange`.
  //   - 10s timeout — if metadata hasn't arrived by then, the file is
  //     genuinely broken or in an unsupported codec.
  function readVideoDuration(file){
    return new Promise((resolve,reject)=>{
      const url=URL.createObjectURL(file);
      const v=document.createElement("video");
      // Hidden container — required for some browsers to actually load
      // metadata. position:fixed + opacity:0 keeps it visually invisible
      // without using `display:none` (which also blocks metadata loading).
      const container=document.createElement("div");
      container.style.cssText="position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
      container.appendChild(v);
      document.body.appendChild(container);

      let settled=false;
      const cleanup=()=>{
        try{URL.revokeObjectURL(url)}catch{}
        try{document.body.removeChild(container)}catch{}
      };
      const finish=(err,dur)=>{
        if(settled)return;
        settled=true;
        clearTimeout(timer);
        cleanup();
        if(err)reject(err);else resolve(dur);
      };
      const timer=setTimeout(()=>finish(new Error("Timed out reading video metadata (10s)")),10000);

      // Try to resolve with a sensible duration. The common path: metadata
      // loads, duration is a finite number, done. Less common: duration
      // is Infinity (Chrome-only quirk on some MP4s) — seek to a huge time
      // and let the browser re-read.
      const tryResolve=()=>{
        const d=v.duration;
        if(typeof d==="number"&&isFinite(d)&&d>0){
          finish(null,d);
        }
      };

      v.preload="auto";
      v.muted=true;
      v.playsInline=true;
      v.onloadedmetadata=()=>{
        if(typeof v.duration==="number"&&isFinite(v.duration)&&v.duration>0){
          finish(null,v.duration);
        }else{
          // Duration: Infinity — try the seek-to-huge-time trick
          v.currentTime=1e9;
        }
      };
      v.ondurationchange=tryResolve;
      v.onseeked=tryResolve;
      v.onloadeddata=tryResolve;
      v.onerror=()=>{
        const code=v.error?.code;
        const msg=v.error?.message;
        console.warn("Video metadata load failed:",{code,msg,fileType:file.type,fileName:file.name});
        finish(new Error(`Video element error (${code||"unknown"})`));
      };
      v.src=url;
    });
  }

  const[setNames,setSetNames]=useState({}); // custom names for sets: {setId: "Custom Name"} // {mk, tpl, baseOut, designOut} for per-mockup DC modal
  // Per-pair routing OVERRIDES: pairId → { groupId: "dark"|"light"|"both" }. A member
  // click on the pair card's [Dark|Light|Both] control stores one here; it wins over the
  // auto (direct-semantics) routing in designSets. Session-persisted; cleared on ✕ Break
  // and by the per-pair Auto reset.
  const[pairRouting,setPairRouting]=useState({}); // pairId → { memberId: "auto"|"all"|groupId } (missing = auto)
  // Resolve design controls for a specific mockup: mockup > set > global
  const getDC=(setId,tplId)=>{const mk=`${setId}_${tplId}`;return mockupDCs[mk]||setDCs[setId]||globalDC}; // temporary SO overrides for this session only // editing foreground from results
  const[designs,setDesigns]=useState([]);
  // Pair-tone cache: pairId → {lightSrc,darkSrc,combined,key}. Computed async
  // (luminance sample + side-by-side combine) from the pair's two members so the
  // synchronous designSets builder can F8-route each group without re-classing on
  // every render. Keyed by member ids+sizes so it recomputes only on real change.
  const[pairArt,setPairArt]=useState({});
  const pairArtKeysRef=useRef({});
  // Latest pipeline pair metadata (id → {pairId,pairPrimary}), accumulated across
  // every PIPELINE_STATE. Lets the Resume path re-hydrate pairing onto a session
  // restored from a PRE-pairing build even though PIPELINE_STATE fired before the
  // user clicked Resume (so it can't heal the not-yet-applied stale designs itself).
  const pipelinePairRef=useRef(new Map());
  // Classify each pipeline pair's two members by luminance (light-inked → DARK
  // garments, dark-inked → LIGHT garments — the SAME rule the headless
  // RENDER_BATCH pair path uses, role flag ignored) and pre-render the never-blank
  // side-by-side combined image. Async (Image decode) so it can't run inside the
  // synchronous designSets build; results land in pairArt and re-drive the build.
  useEffect(()=>{
    const byPair={};
    designs.forEach(d=>{if(d.pairId){(byPair[d.pairId]||(byPair[d.pairId]=[])).push(d)}});
    Object.entries(byPair).forEach(([pid,members])=>{
      if(members.length!==2)return; // orphan half / malformed → stays legacy-single
      const[a,b]=members;
      const key=`${a.id}:${(a.src||"").length}|${b.id}:${(b.src||"").length}`;
      if(pairArtKeysRef.current[pid]===key)return; // unchanged → skip recompute
      pairArtKeysRef.current[pid]=key;
      (async()=>{
        const[la,lb]=await Promise.all([artLuminance(a.src),artLuminance(b.src)]);
        const aLighter=la>=lb;
        const lightSrc=aLighter?a.src:b.src; // light-inked → DARK garments
        const darkSrc=aLighter?b.src:a.src;  // dark-inked → LIGHT garments
        const combined=await buildPairCombinedImage(darkSrc,lightSrc);
        setPairArt(p=>({...p,[pid]:{lightSrc,darkSrc,combined,key}}));
      })();
    });
  },[designs]);
  const[showRes,setShowRes]=useState(false);
  const[saved,setSaved]=useState([]);
  const[selSaved,setSelSaved]=useState(new Set());
  const[failedSaves,setFailedSaves]=useState({}); // {String(id): reason} for templates whose last server save was rejected → drives the per-card badge AND its wording (reason ∈ 'oversize' | 'cap' | 'transient'; derived from _failedServerSaves)
  const[dbOk,setDbOk]=useState(false);
  const[authExpired,setAuthExpired]=useState(false); // Suite session expired → show "reopen" card instead of a blank grid
  const[tplLimit,setTplLimit]=useState({max:150,used:0}); // pre-load fallback; the real cap is read from the server on load (serverData.usage.limitTemplates) so a future cap change needs no client edit. Kept in sync with mockup-store.js MAX_TEMPLATES_PER_USER.
  const[genOut,setGenOut]=useState({});
  const renderBatchStateRef=useRef({allReady:[]}); // live templates for the headless F1 RENDER_BATCH handler
  const[dling,setDling]=useState(false);
  // Download progress: while a ZIP is being prepared, {done,total} counts up on
  // the button ("Preparing 14/32…") so a slow headless composite of offscreen
  // mockups never looks frozen.
  const[dlProg,setDlProg]=useState({done:0,total:0});
  const[openTip,setOpenTip]=useState(null);
  const[profs,setProfs]=useState([]);
  const[showProfSave,setShowProfSave]=useState(false);
  const[profName,setProfName]=useState("");
  const[activeProfId,setActiveProfId]=useState(null);
  const[linkedPlacementOpen,setLinkedPlacementOpen]=useState(false); // linked multi-mockup placement editor (Model B) open?
  const[colorLinkProfId,setColorLinkProfId]=useState(null); // set whose "Link colours" modal is open (null=closed)
  const[colorLinkPickerOpen,setColorLinkPickerOpen]=useState(false); // labeled "Link photos to variations" button → set-picker entry (null set → step 0)
  const[globalGroups,setGlobalGroups]=useState([{id:"g1",name:"Group 1",color:gc(0)}]);
  // PSD import (lazy-loads ag-psd on first use — zero bytes for non-importers)
  const[psdImp,setPsdImp]=useState(null); // {busy?,msg?,summary?,warnings?,error?}
  const[psdExp,setPsdExp]=useState(null); // {id?} exporting a template to .psd, or {error?}/{warn?} result
  const[mockDrag,setMockDrag]=useState(false); // Step-1 upload box drag-over state
  const[designDrag,setDesignDrag]=useState(false); // Step-3 design upload box drag-over state

  // Resume-session banner state. When the studio mounts and finds a saved
  // session in IndexedDB (< 24h old), we surface a banner offering to
  // restore it instead of starting from scratch. The user said reopening
  // the studio always forces them to re-pick templates and re-assign
  // designs — this fixes that without auto-jumping the user somewhere
  // they didn't expect.
  const[pendingSession,setPendingSession]=useState(null); // session payload waiting for user choice
  const[sessionDismissed,setSessionDismissed]=useState(false);
  // Has the session-restore lifecycle finished? True when EITHER:
  //   - loadSession resolved with no recoverable session (nothing to ask)
  //   - user clicked Resume (state populated from saved session)
  //   - user clicked Start fresh (clearSession ran)
  // Until this flag flips, autosave is suppressed entirely. This closes the
  // race where the autosave timer fires during the brief window between
  // page mount (empty state) and pendingSession being set, which would
  // otherwise overwrite the saved session metadata with empties.
  const[sessionDecided,setSessionDecided]=useState(false);
  const fRef=useRef(null);const dRef=useRef(null);const vidRef=useRef(null);

  useEffect(()=>{const h=e=>{if(!e.target.closest(".mg-tip-trigger"))setOpenTip(null)};document.addEventListener("click",h);return()=>document.removeEventListener("click",h)},[]);
  // Surface swallowed INTERACTIVE server-save rejections (library-full 400 / other) to the
  // member. Oversize (413) no longer routes here — it's a permanent state shown as a per-card
  // badge instead of a modal (see setFailedSavesHandler below), so this alert stays for the
  // action-triggered "library full" case only, where a modal at the moment of the click is fine.
  useEffect(()=>{setSaveErrorHandler(msg=>alert(msg));return()=>setSaveErrorHandler(null)},[]);
  // Mirror the module-level _failedServerSaves set into React so the saved-mockup grid can render
  // a "Not backed up" badge on any mockup the server rejected (oversize or otherwise). Seed once
  // in case a rejection already landed before this effect ran (the load-time sync is async).
  useEffect(()=>{const toMap=entries=>Object.fromEntries(entries.map(([k,v])=>[String(k),v]));setFailedSavesHandler(entries=>setFailedSaves(toMap(entries)));setFailedSaves(toMap([..._failedServerSaves.entries()]));return()=>setFailedSavesHandler(null)},[]);
  // Best-effort flush of any debounced template/profile sync when the tab is hidden or unloaded,
  // so a member who closes the studio inside the debounce window still pushes their last edit.
  // If the in-flight fetch doesn't complete (page gone), the load-time resync re-pushes it next
  // time — this just shrinks that window; it is not the correctness guarantee.
  useEffect(()=>{
    const flush=()=>{flushTemplateSync({surface:false});flushProfilesSync();};
    const onHide=()=>{if(document.visibilityState==="hidden")flush();};
    window.addEventListener("pagehide",flush);
    document.addEventListener("visibilitychange",onHide);
    return()=>{window.removeEventListener("pagehide",flush);document.removeEventListener("visibilitychange",onHide);};
  },[]);
  useEffect(()=>{
    if(suiteToken)setSyncToken(suiteToken);
    if(suiteToken)refreshLibVideos(); // load the server-side listing-video library
    Promise.all([loadTpls(),loadProfs()]).then(async([localTpls,localProfs])=>{
      const localGG=loadGlobalGroups();
      // Always try to get limits from server. Response nests them under `usage`
      // (mockup-store.js: usage.limitTemplates / usage.templateCount). NOTE: the
      // `const serverData` line below is load-bearing — every reader in this .then
      // depends on it (a prior edit dropping it → ReferenceError → blank Studio).
      const serverData=await serverLoad();
      if(serverData?.usage)setTplLimit({max:serverData.usage.limitTemplates||150,used:serverData.usage.templateCount||0});
      // §2 — reconcile the SERVER-authoritative delete set (deletedIds) into THIS browser's
      // per-browser tombstones, so a set/mockup deleted on ANOTHER browser stays gone here
      // too. The resurrection class was exactly this: per-browser tombstones a second browser
      // couldn't see, so its stale local copy re-saved the row. Split by id prefix (prof_… =
      // a set) into the two tombstone lists; the merges below then exclude them and
      // serverSaveAll can't push them back. No-op when the server has no deletedIds.
      const serverDeleted=Array.isArray(serverData?.deletedIds)?serverData.deletedIds.map(String):[];
      if(serverDeleted.length){
        const tSet=new Set(loadTombstones()),pSet=new Set(loadProfileTombstones());
        serverDeleted.forEach(id=>{(id.startsWith("prof_")?pSet:tSet).add(id)});
        saveTombstones([...tSet]);saveProfileTombstones([...pSet]);
      }
      if(localTpls.length>0||localProfs.length>0){
        // brief 5.9: MERGE in any server-only templates/profiles. This browser's
        // IndexedDB is the local source, but a set can exist ONLY on the server —
        // e.g. one the Suite's Speedrun wizard appended to the server blob but never to
        // this IndexedDB. The old code used local XOR server, so those sets never showed
        // (and serverSaveAll then pushed local back over them). Local wins on id
        // conflicts; server-only items are added + persisted to IndexedDB so they render.
        const srvT=serverData?.templates||[],srvP=serverData?.profiles||[];
        // Tombstone-aware template merge (brief 5.15 §1 — pure, tested). A locally-deleted
        // template never resurrects from the server; tombstones are pruned/retried ONLY
        // when the server was actually read (serverData!=null), so a no-token/failed load
        // can't drop the guard and let the row come back on the next authed load.
        const {merged:mergedT,addT,keepTombstones,retryDeletes}=mergeTemplatesWithTombstones({local:localTpls,server:srvT,tombstones:loadTombstones(),serverReadOk:serverData!=null});
        if(retryDeletes.length)serverDeleteTemplatesMany(retryDeletes); // batched: a backlog of un-synced deletes drains in ONE request, not one-per-id
        saveTombstones(keepTombstones);
        // Same tombstone discipline for PROFILES (brief 5.17 §2) — reuses the pure merge.
        // Without this a deleted set that survived on the server (delete never acked) came
        // back here and was re-persisted, which is the profile-resurrection mechanism.
        const {merged:mergedP,addT:addP,keepTombstones:keepPTomb,retryDeletes:retryPDel}=mergeTemplatesWithTombstones({local:localProfs,server:srvP,tombstones:loadProfileTombstones(),serverReadOk:serverData!=null});
        if(retryPDel.length)serverDeleteProfilesMany(retryPDel); // batched (mirrors the template retry)
        saveProfileTombstones(keepPTomb);
        for(const t of addT){dbPut(TPL,t).catch(()=>{})}
        for(const p of addP){dbPut(PROF,p).catch(()=>{})}
        setSaved(mergedT.map(migrate));setProfs(mergedP);
        // brief 5.14 §2: MERGE server-only globalGroups too (union by id, local wins).
        // The old code used LOCAL globalGroups only and then clobbered the server copy —
        // so groups the Suite's Speedrun wizard registered on the server (save-groups)
        // never reached the editor, leaving its zones' group ids unresolved (= zones
        // showed but "not assigned to a group"). Merging surfaces them.
        const srvGG=serverData?.globalGroups||[];
        const lgIds=new Set(localGG.map(g=>g&&g.id));
        const mergedGG=[...localGG,...srvGG.filter(g=>g&&g.id&&!lgIds.has(g.id))];
        if(mergedGG.length>0){setGlobalGroups(mergedGG);saveGlobalGroups(mergedGG)}
        setDbOk(true);
        setTplLimit(p=>({...p,used:mergedT.length}));
        serverSaveAll(mergedT,mergedP,mergedGG,serverData);
      }else if(serverData&&(serverData.templates?.length>0||serverData.profiles?.length>0)){
        const gg=serverData.globalGroups||[];
        // Tombstones apply here too (brief 5.15 §1 — pure, tested): if the user deleted
        // every local template but the server delete didn't take, don't re-hydrate them.
        // serverReadOk is true here (this branch requires a successful serverLoad).
        const {merged:tpls,keepTombstones,retryDeletes}=mergeTemplatesWithTombstones({local:[],server:serverData.templates||[],tombstones:loadTombstones(),serverReadOk:true});
        if(retryDeletes.length)serverDeleteTemplatesMany(retryDeletes); // batched: a backlog of un-synced deletes drains in ONE request, not one-per-id
        saveTombstones(keepTombstones);
        // Profiles get the same tombstone guard here (brief 5.17 §2) — a deleted set must
        // not re-hydrate from the server just because this browser has no local copy.
        const {merged:profs,keepTombstones:keepPTomb,retryDeletes:retryPDel}=mergeTemplatesWithTombstones({local:[],server:serverData.profiles||[],tombstones:loadProfileTombstones(),serverReadOk:true});
        if(retryPDel.length)serverDeleteProfilesMany(retryPDel); // batched (mirrors the template retry)
        saveProfileTombstones(keepPTomb);
        for(const t of tpls){await dbPut(TPL,t).catch(()=>{})}
        for(const p of profs){await dbPut(PROF,p).catch(()=>{})}
        if(gg.length>0)saveGlobalGroups(gg);
        setSaved(tpls.map(migrate));setProfs(profs);
        if(gg.length>0)setGlobalGroups(gg);
        // twin of the 3982 read (no-local-templates branch): carry the server cap here too
        setTplLimit(p=>({max:serverData?.usage?.limitTemplates||p.max,used:tpls.length}));
        setDbOk(true);
      }else{setDbOk(true)}
    }).catch(()=>setDbOk(true));
  },[]);

  // Suite-session expiry watch. Register the setter so a server 401 (serverLoad)
  // flips the UI to a clear "session expired" card, and poll the token's own exp so
  // a session that ages out mid-edit is caught without waiting for a call to fail.
  // Latched: once expired we keep the card up until the member reopens (a real
  // expiry means the Suite session is gone, so reopening — and re-auth if needed —
  // is the only reliable recovery; a stale in-place token can't be self-healed here).
  useEffect(()=>{
    setAuthExpiredHandler(()=>setAuthExpired(true));
    const check=()=>{if(isAuthExpired()||suiteTokenExpired())setAuthExpired(true)};
    check();
    const id=setInterval(check,60000);
    return()=>{setAuthExpiredHandler(null);clearInterval(id)};
  },[]);
  // Keep the module token current when the Suite pushes a fresh one (SUITE_TOKEN_REFRESH
  // re-mounts SuiteGate's token → new prop here), so any later call uses the live token.
  useEffect(()=>{if(suiteToken)setSyncToken(suiteToken)},[suiteToken]);

  const updateGlobalGroups=grps=>{
    setGlobalGroups(grps);
    saveGlobalGroups(grps);
    serverSaveGroups(grps);
  };

  /* Pipeline */
  useEffect(()=>{
    // ── F1 Phase 1: headless RENDER_BATCH ────────────────────────────────
    // Render design×template mockups driven by a suite message instead of a
    // human clicking "Generate All", then ship them through the SAME outbound
    // the manual flow uses (PIPELINE_MOCKUPS_CHUNK — 8-batched, compressed,
    // designId/broadcast tagged). Pure rendering: NO Etsy write, NO publish,
    // NO Blobs-store write. Memory-safe by construction — every render goes
    // through the same acquireGenSlot/releaseGenSlot semaphore (cap
    // MAX_CONCURRENT_GEN) that bounds the manual grid, so at most that many
    // canvas pipelines are ever in flight no matter how many combos a batch
    // contains (a batch can be 50 designs × N templates).
    const postToSuite=payload=>{try{window.parent&&window.parent.postMessage({source:"creative-seller-pipeline",...payload},"*")}catch(ex){}};
    const runRenderBatch=async(msg)=>{
      const batchId=msg.batchId||null;
      try{
        const designsIn=Array.isArray(msg.designs)?msg.designs:[];
        // F1 set-resolution: if a saved-set (profile) id is supplied, resolve
        // the template list DIRECTLY from in-memory profile/library data —
        // bypassing the manual selSaved/UI-click path so the set renders
        // unattended. This mirrors what applyProf + the normal allReady build
        // produce (migrate → tempOverrides → sortMap sortOrder → sort), but
        // touches neither selSaved nor any UI state. READ-ONLY: in-memory
        // profs only, no Blobs write.
        let tpls;
        let profColorLinks=null; // set's colour→mockup links, rides the FIRST chunk (part 1; no suite consumer yet)
        if(msg.profileId){
          const st=renderBatchStateRef.current;
          const prof=(st.profs||[]).find(p=>p.id===msg.profileId);
          if(!prof){
            postToSuite({type:"PIPELINE_MOCKUPS_ERROR",batchId,designId:null,message:st.profsLoaded?"profile not found":"profiles still loading, retry"});
            return;
          }
          const sm=prof.sortMap||{};
          tpls=(st.saved||[])
            .filter(s=>(prof.templateIds||[]).includes(s.id))
            .map(migrate)
            .map(tpl=>{const ov=(st.tempOverrides||{})[tpl.id];const base=ov?{...tpl,...ov,smartObject:(ov.smartObjects||[])[0]||null}:tpl;return sm[tpl.id]!=null?{...base,sortOrder:sm[tpl.id]}:base;})
            .sort((a,b)=>(a.sortOrder??999)-(b.sortOrder??999));
          profColorLinks=colorLinksForPayload(prof.colorLinks,new Set(tpls.map(t=>t.id)));
        }else{
          const sel=Array.isArray(msg.templateSelection)?msg.templateSelection:null;
          const all=renderBatchStateRef.current.allReady||[];
          // No templateSelection (or empty) → render every currently-resolved
          // template; otherwise restrict to the requested template ids.
          tpls=sel&&sel.length>0?all.filter(t=>sel.includes(t.id)):all;
        }
        const designTpls=tpls.filter(t=>!t.noDesign);
        const standaloneTpls=tpls.filter(t=>t.noDesign);

        // Build the render list. Each design binds its src to EVERY group the
        // template covers (the "one design → one listing" model, mirroring
        // designSets' "all groups" path) and is tagged with that design's id
        // so the suite routes it to one listing. Standalone (noDesign)
        // templates render once and broadcast to every listing — exactly the
        // designId/broadcast split sendToPipeline emits.
        // Dual-design chart (pairing arc D): for every design that arrives with
        // a pair partner (the optional inbound `pairPartnerImage` data URL),
        // resolve the two per-cell design roles ONCE and reuse them across every
        // chart-slot template below. The chart mirrors the print-file inversion
        // law per garment cell — DARK garment ← LIGHT design, LIGHT garment ←
        // DARK design — so we must know which artwork is the light one. The
        // suite sends no role flag, so we infer it: the higher mean-luminance
        // (light-inked) artwork is the LIGHT design, the darker one the DARK
        // design. A side-by-side `combined` image is kept as the never-blank
        // fallback for chart cells whose garment color can't be classed (e.g. a
        // single-zone size guide). Absent partner → nothing built → today's
        // exact single-design path (byte-identical). A combine failure falls
        // back to the single design inside buildPairCombinedImage.
        const pairArtByIndex={};
        await Promise.all(designsIn.map(async(d,di)=>{
          const partner=d.pairPartnerImage;
          const src=d.image||d.src;
          if(!partner||!src)return;
          const[lumSrc,lumPartner]=await Promise.all([artLuminance(src),artLuminance(partner)]);
          const srcIsLighter=lumSrc>=lumPartner;
          const lightArt=srcIsLighter?src:partner;   // light-inked → for DARK garments
          const darkArt=srcIsLighter?partner:src;     // dark-inked → for LIGHT garments
          const combined=await buildPairCombinedImage(src,partner);
          pairArtByIndex[di]={lightArt,darkArt,combined};
        }));

        const combos=[];
        designsIn.forEach((d,di)=>{
          const src=d.image||d.src;if(!src)return;
          const designId=d.id||null;
          const setLabel=d.name||`Design ${di+1}`;
          // Additive per-design group routing. Absent / null / "all" → bind into
          // EVERY group (today's exact behavior). A specific group id → bind ONLY
          // into that group's zones (the manual d.group routing). Unknown id →
          // matches no zone → template skipped below (design gets fewer/zero
          // mockups, which the suite's zero-mockups halt is designed to catch).
          const grp=d.group;                       // undefined | null | "all" | groupId
          const routeAll=(grp==null||grp==="all");
          for(const t of designTpls){
            // On a chart / size-guide slot, a PAIRED design routes per cell to
            // mirror the print-file inversion: each cell's garment is classed
            // from its group name — DARK garment ← LIGHT design, LIGHT garment ←
            // DARK design. Cells whose garment can't be classed (e.g. a single
            // unnamed size-guide zone) get the never-blank both-designs image.
            // A non-chart template, or any unpaired design, binds the single
            // design src exactly as before — byte-identical.
            const isChart=isChartSlot(t.name);
            const pair=isChart?pairArtByIndex[di]:null; // {lightArt,darkArt,combined} on a paired chart only
            const grpDefs=(t.groups&&t.groups.length)?t.groups:(renderBatchStateRef.current.globalGroups||[]);
            const gdm={};
            // F7: a chart is a whole-range composite by definition, so a PAIRED
            // chart binds EVERY grouped cell, bypassing the design's own-group
            // (`so.group===grp`) restriction. The owner's chart groups cells into
            // TWO routing groups ("Light Design" / "Dark Design"), so one design's
            // own-group filter would reach only HALF the cells and leave the other
            // group structurally blank. Tone routing still decides which artwork
            // lands where. Because all-groups binding is design-agnostic, both
            // pair members' chart combos become equivalent — the once-per-listing
            // dedup keeps one and the result is correct regardless of which wins.
            // Non-chart templates and any UNPAIRED design (incl. a single's chart,
            // where `pair` is null) keep the own-group filter, byte-identical.
            (t.smartObjects||[]).forEach(so=>{
              const g=(grpDefs||[]).find(x=>x.id===so.group);
              const gname=(g&&g.name)||"";
              // NEUTRAL-BINDS LAW (renderRouting.shouldBindZone): a design routed to a
              // tone group also binds every NEUTRAL group's zones (name matches neither
              // /dark/i nor /light/i) — the tone-independent chart/banner/flat-lay slots
              // that pair tone-routing used to skip → the AUTO 8-photo loss. A paired
              // chart still binds every cell (pair); route-all still binds everything.
              // Covers pairs, singles, auto and review at this single gate. An explicit
              // group tone TAG (g.tone) wins over the name regex here; untagged groups
              // fall back to the regex → byte-identical to the pre-tag behaviour.
              if(!shouldBindZone({soGroup:so.group,grp,routeAll,pair,groupName:gname,groupResolved:!!g,groupTone:g&&g.tone}))return;
              if(pair){
                // F8: two group-naming conventions. A group named after the
                // DESIGN ("Light Design" / "Dark Design") names WHICH ARTWORK its
                // cells show → bind that design directly. A group named after the
                // GARMENT ("Black" / "Ivory") names the shirt tone → bind the
                // inverse (print-file law: dark garment ← light art). The same
                // light/dark word detection (classifyGarmentTone) drives both;
                // only the artwork mapping flips. Unclassable → never-blank
                // combined either way. In short: a group named after the design
                // gets that design; a group named after the garment gets the
                // inverse.
                const tone=classifyGarmentTone(gname);
                gdm[so.group]=/design/i.test(gname)
                  ?(tone==="dark"?pair.darkArt:tone==="light"?pair.lightArt:pair.combined)
                  :(tone==="dark"?pair.lightArt:tone==="light"?pair.darkArt:pair.combined);
              }else{
                gdm[so.group]=src;
              }
            });
            if(!(t.smartObjects||[]).some(so=>gdm[so.group]))continue; // no matching zone → skip
            combos.push({tpl:t,gdm,designId,broadcast:false,noDesign:false,setLabel,setIndex:di,oncePerListing:isChart});
          }
        });
        standaloneTpls.forEach(t=>combos.push({tpl:t,gdm:{},designId:null,broadcast:true,noDesign:true,setLabel:"Standalone",setIndex:-1}));

        if(combos.length===0){postToSuite({type:"PIPELINE_MOCKUPS_DONE",batchId,count:0});return}

        // Additive per-mockup progress. The full render plan is known here:
        // combos.length is the exact number of mockups this batch WILL render
        // after group filtering. done counts completed renders across all
        // chunks (renderOne runs concurrently under the gen semaphore, so this
        // ticks in real completion order). Purely a UI signal for the suite's
        // 1-click bar — the suite (v7.5) falls back gracefully if it never
        // arrives. Emits ZERO change to render/chunk/payload behavior below.
        const planTotal=combos.length;
        let renderedDone=0;

        const CHUNK_SIZE=8;      // mirror sendToPipeline's batching
        const CHUNK_DELAY_MS=50; // let the receiver drain between chunks
        const totalChunks=Math.ceil(combos.length/CHUNK_SIZE);

        // Render ONE combo, gated by the shared gen semaphore so the batch
        // can never add more than MAX_CONCURRENT_GEN canvas pipelines on top
        // of whatever the UI is already running. regenerateLayersStandalone
        // is the bare render core (it does NOT acquire a slot itself — the
        // manual GenMockup wraps it the same way), so the wrap here is what
        // makes the batch memory-safe.
        const renderOne=async(combo)=>{
          await acquireGenSlot();
          try{
            const layers=await regenerateLayersStandalone(combo.tpl,combo.gdm);
            const full=await compositeLayersToFull(layers);
            if(!full)throw new Error("empty render (template/design failed to load)");
            const imageDataUrl=await compressMockupForPipeline(full);
            // Per-mockup completion point: this combo is fully rendered. Tick
            // the batch-wide counter and ping the suite so its 1-click bar can
            // fill "N of M" in real time (mirrors PIPELINE_MOCKUPS_CHUNK's
            // source via postToSuite).
            renderedDone++;
            postToSuite({type:"PIPELINE_RENDER_PROGRESS",done:renderedDone,total:planTotal});
            return{templateId:combo.tpl.id,templateName:combo.tpl.name,setLabel:combo.setLabel,setIndex:combo.setIndex,designId:combo.designId,broadcast:combo.broadcast,noDesign:combo.noDesign,oncePerListing:combo.oncePerListing||false,sortOrder:combo.tpl.sortOrder??0,imageDataUrl};
          }finally{releaseGenSlot()}
        };

        // Process in chunks of CHUNK_SIZE; within a chunk the per-combo
        // acquireGenSlot keeps actual concurrency at the semaphore cap, so at
        // most ~CHUNK_SIZE compressed JPEGs are buffered at once (never the
        // whole batch). Ship each chunk as it completes, exactly like
        // sendToPipeline, so memory stays flat for 50×N.
        for(let ci=0;ci<totalChunks;ci++){
          const sliceCombos=combos.slice(ci*CHUNK_SIZE,(ci+1)*CHUNK_SIZE);
          let mockups;
          try{
            mockups=await Promise.all(sliceCombos.map(renderOne));
          }catch(err){
            // Surface the first failure and HALT the batch so the suite
            // orchestrator can decide whether to retry. designId is best-effort.
            postToSuite({type:"PIPELINE_MOCKUPS_ERROR",batchId,designId:sliceCombos[0]&&sliceCombos[0].designId||null,message:String(err&&err.message||err)});
            return;
          }
          const isLast=ci===totalChunks-1;
          postToSuite({type:"PIPELINE_MOCKUPS_CHUNK",batchId,mockups,chunkIndex:ci,totalChunks,isFirst:ci===0,isLast,...(ci===0&&profColorLinks?{colorLinks:profColorLinks}:{})});
          if(!isLast)await new Promise(r=>setTimeout(r,CHUNK_DELAY_MS));
        }
        postToSuite({type:"PIPELINE_MOCKUPS_DONE",batchId,count:combos.length});
        // Per-set video: if this batch resolved a saved set that carries a
        // listing video, deliver its bytes now over the PIPELINE_VIDEO channel
        // (single message for small clips, PIPELINE_VIDEO_CHUNK parts for big).
        // Lightweight ref already travels in MOCKUP_PROFILES. Absent → video:null.
        if(msg.profileId){ await emitProfileVideo(msg.profileId,batchId); }
      }catch(err){
        postToSuite({type:"PIPELINE_MOCKUPS_ERROR",batchId,designId:null,message:String(err&&err.message||err)});
      }
    };

    const h=e=>{const d=e.data;if(!d||typeof d!=="object"||d.source!=="creative-seller-suite")return;
      if(d.type==="RENDER_BATCH"){runRenderBatch(d);return}
      if(d.type==="REQUEST_PROFILE_VIDEO"){
        // On-demand per-set video bytes. The Suite knows a set hasVideo from the
        // lightweight MOCKUP_PROFILES ref; it asks here for the actual clip when
        // it's about to publish. Reply on the PIPELINE_VIDEO / PIPELINE_VIDEO_CHUNK
        // channel (video:null when the set has none). Bytes come from the server
        // store, so this works cross-device now.
        emitProfileVideo(d.profileId,d.batchId||null);
        return;
      }
      if(d.type==="REQUEST_ONECLICK_DESIGNS"){
        // One-click entry point (step 4/4): the suite's ⚡ button asks for ALL
        // current designs. Read them via the ref (CURRENT state — the closed-over
        // `designs` is frozen at mount []). Map src→image (reverse of the inbound
        // image→src at PIPELINE_STATE); src is already a data-URL, so this is sync.
        // Post even when empty so the suite shows an accurate "no designs" message
        // instead of the misleading timeout.
        const cur=renderBatchStateRef.current.designs||[];
        const designs=cur.map(x=>({id:x.id,name:x.name,image:x.src}));
        postToSuite({type:"PIPELINE_DESIGNS",designs,startOneClick:true});
        return;
      }
      if(d.type==="PIPELINE_STATE"){
        const inc=d.designs||[];
        if(inc.length===0)return;
        // Incoming pair metadata, keyed by design id. A session restored from IDB may
        // have been seeded by a PRE-pairing build (no pairId), and the first-seed guard
        // below then discarded every fresh PIPELINE_STATE forever — so a stale session
        // never learned it was paired. Heal instead of ignore.
        const pairById=new Map();
        inc.forEach(pd=>{if(pd&&pd.id!=null&&pd.pairId!=null)pairById.set(pd.id,{pairId:pd.pairId,pairPrimary:pd.pairPrimary})});
        // Accumulate into the ref so a later Resume can heal too (see pipelinePairRef).
        pairById.forEach((v,k)=>pipelinePairRef.current.set(k,v));
        setDesigns(p=>{
          if(p.length>0){
            // EXISTING / restored session — never re-seed or duplicate, and never touch
            // the member's group assignments or src. Just RE-HYDRATE pair metadata onto
            // matching ids so a pre-fix session (seeded with no pairId) heals itself on
            // the next suite load. Non-clearing, identity-stable when nothing changes.
            return healPairMeta(p,pairById);
          }
          // Fresh (empty) — seed from the pipeline, carrying pairId/pairPrimary.
          // Seed with the originals first so the UI updates immediately,
          // then downsample in the background and replace each one as it
          // completes. Without downsampling, 50 designs from the pipeline
          // (often 3000-6000px each at 5-20MB) consume 700MB-1GB just
          // sitting in state — same OOM trigger as the upload path.
          // Preserve the suite's pairing (pipeline pairs = ONE product). The
          // manual PIPELINE_STATE channel carries pairId/pairPrimary on each
          // design (suite forwards pipeline.designs verbatim), so keep them —
          // the pair-aware designSets builder collapses same-pairId designs into
          // one set instead of the pair-blind design×template explosion. Absent
          // on direct uploads / unpaired designs → undefined → legacy path.
          // Single-design convenience (mirror of the upload path): a lone incoming design
          // (exactly one, not a pair) pre-selects ✦ All Groups; a pair or batch keeps the
          // manual group=null default, since the group choice matters there.
          const seeded=inc.map(pd=>({id:pd.id||`p${Date.now()}${Math.random()}`,name:pd.name||"Pipeline",src:pd.image,fromPipeline:true,group:(inc.length===1&&!pd.pairId)?"all":null,pairId:pd.pairId,pairPrimary:pd.pairPrimary}));
          // Fire downsampling for each design async; replace state per
          // design as each finishes. No-op for already-small designs.
          seeded.forEach(d=>{
            downsampleDataUrl(d.src,{maxDim:2000,jpegQuality:0.92}).then(small=>{
              if(small&&small!==d.src){
                setDesigns(curr=>curr.map(x=>x.id===d.id?{...x,src:small}:x));
              }
            });
          });
          return seeded;
        });
      }
    };
    window.addEventListener("message",h);
    try{window.parent.postMessage({type:"PIPELINE_REQUEST",source:"creative-seller-pipeline"},"*")}catch(ex){}
    return()=>window.removeEventListener("message",h);
  },[]);

  /* F1 set-resolution — broadcast the user's saved sets (profiles) to the
     suite so a future picker can offer them for a one-click RENDER_BATCH.
     Metadata ONLY ({id,name,count}) — no template internals. Mirrors the
     suite's VARIATION_TEMPLATES shape (source:"creative-seller-pipeline").
     Fires on mount and whenever profs changes (load / save / delete) so the
     suite stays current. READ-ONLY: just reads in-memory profs. */
  useEffect(()=>{
    if(typeof window==="undefined"||!window.parent||window.parent===window)return;
    // Union of so.group across the profile's templates, resolved to group NAMES
    // via globalGroups (the same names the step-2 assign dropdown shows). The
    // suite ignores groups until its follow-up ships; additive only. Reads deps
    // saved/globalGroups directly, so they're in the effect deps below to keep
    // the broadcast current when templates/groups change without profs changing.
    const groupsForProfile=(p)=>{
      const ids=new Set(p.templateIds||[]);
      const tpls=(saved||[]).filter(t=>ids.has(t.id));
      const gids=new Set();
      tpls.forEach(t=>(t.smartObjects||[]).forEach(so=>{if(so.group)gids.add(so.group)}));
      // Resolve each id to its NAME. Saved templates carry their OWN `groups`
      // (id+name+color) captured when they were configured, so those hold the
      // true names ("light design"/"dark design") even when this session's
      // globalGroups was reset and no longer contains the id. Order: (a) a real
      // name from any of the profile's templates' own groups, (b) globalGroups,
      // (c) the raw id as a last resort. On disagreement, prefer a non-empty,
      // non-"Default"/non-id name so the suite can class light/dark.
      const isReal=(n,id)=>!!n&&n!==id&&!/^default$/i.test(String(n).trim());
      return [...gids].map(id=>{
        // `tone` = the explicit group tone TAG (dark/light/all), resolved with the
        // same template-groups-first precedence as name/color so the suite (Phase 2)
        // can route by tag instead of the /dark|light/ name regex. null when untagged
        // (additive — the suite keeps its regex fallback for untagged groups).
        let name=null,color=null,tone=null;
        tpls.forEach(t=>(t.groups||[]).forEach(g=>{
          if(g.id!==id)return;
          if(color==null&&g.color)color=g.color;
          if(tone==null&&g.tone)tone=g.tone;
          if(g.name&&(isReal(g.name,id)||name==null))name=g.name;
        }));
        if(!isReal(name,id)||tone==null||color==null){
          const gg=(globalGroups||[]).find(x=>x.id===id);
          if(gg){if(!isReal(name,id)&&isReal(gg.name,id))name=gg.name;if(color==null)color=gg.color||null;if(tone==null&&gg.tone)tone=gg.tone;}
        }
        return{id,name:isReal(name,id)?name:(name||id),color:color||null,tone:tone||null};
      });
    };
    // Per-set video: broadcast a LIGHTWEIGHT reference only — hasVideo + name/
    // size/duration — DERIVED from set membership (profile.videoId → library
    // manifest), NEVER the base64. The Suite localStorage-caches this
    // MOCKUP_PROFILES payload; a multi-MB clip would blow that quota. Bytes are
    // fetched on demand (REQUEST_PROFILE_VIDEO → PIPELINE_VIDEO/PIPELINE_VIDEO_CHUNK,
    // and the headless RENDER_BATCH{profileId} path emits them after mockups).
    try{window.parent.postMessage({type:"MOCKUP_PROFILES",source:"creative-seller-pipeline",profiles:profs.map(p=>({id:p.id,name:p.name,count:(p.templateIds||[]).length,groups:groupsForProfile(p),video:deriveProfileVideo(p,libVideos)}))},"*")}catch(ex){}
  },[profs,saved,globalGroups,libVideos]);

  /* Session restore — check IndexedDB for a recent working session and
     surface a banner asking the user if they want to resume. We only set
     pendingSession; the user clicks "Resume" to actually apply it.

     The banner only fires for sessions saved BEFORE the page first loaded —
     anything later is the user's own autosave from this page-load instance,
     which we don't want to retroactively offer to "resume". The user
     reported the banner showing up referencing a stale session even while
     mid-flow; this gate fixes that by anchoring the comparison to load
     time rather than 24h. */
  const pageLoadTimeRef=useRef(Date.now());
  useEffect(()=>{
    if(!dbOk)return; // wait until templates are loaded so selSaved IDs are valid
    let cancelled=false;
    loadSession().then(s=>{
      if(cancelled)return;
      // Resolve sessionDecided: if there's nothing to offer, the user's
      // implicit decision is "start fresh from current empty state",
      // which means autosave can begin running. The early-return paths
      // below all reach this same conclusion.
      if(!s){setSessionDecided(true);return}
      if((s.ts||0)>=pageLoadTimeRef.current-5000){
        // Skip sessions newer than this page load — those are this instance's
        // own autosaves. Add a small grace window (5s) to avoid misclassifying
        // the very first autosave on first render.
        setSessionDecided(true);return;
      }
      const hasGenerated=s.genOut&&Object.keys(s.genOut).length>0;
      const hasSetup=(s.selSavedIds?.length>0)||(s.designs?.length>0)||(s.step>0);
      if(hasGenerated||hasSetup){
        setPendingSession(s);
        // sessionDecided stays false until the user clicks Resume or Start fresh
      }else{
        setSessionDecided(true);
      }
    }).catch(()=>setSessionDecided(true));
    return()=>{cancelled=true};
  },[dbOk]);

  /* DC re-apply helpers.

     With DC baked at generation time, applying DC changes to existing
     mockups requires regenerating them. We expose this as an explicit
     user action ("Apply" button on each DC panel) rather than auto-
     triggering on slider change. Two reasons:

     1. UX clarity. Auto-regeneration on every slider change makes the
        sliders feel laggy ("I moved it, nothing happened") and risks
        triggering a 1-2 minute regen of 600 mockups for a single
        accidental tweak.
     2. Experimentation. The user can drag sliders to compare values
        side-by-side via the per-mockup 🎛 modal (which has live
        preview using regenerated layers) WITHOUT triggering bulk
        regen. Then commit via Apply when they like a setting.

     applyGlobalDC() clears all genOut entries that resolve to globalDC.
     applySetDC(setId) clears entries in that set that resolve to setDC.
     applyMockupDC(mk) clears just that one mockup's entry.

     The wave system handles the regen seamlessly — only ACTIVE_BATCH_SIZE
     mockups regen at a time. */
  const applyGlobalDC=useCallback(()=>{
    setGenOut(prev=>{
      const next={...prev};
      for(const mk of Object.keys(prev)){
        // Resolve: does this mockup use globalDC (vs set or per-mockup
        // override)?
        const usesMockup=mockupDCs[mk]!==undefined;
        // Find which set this mockup belongs to. mk is "setId_tplId"
        // or "setId_sa_tplId" or "tplId" (no-set standalone). setIds
        // contain underscores ("set_0"), so we can't simply split on
        // "_". Match against the actual set ids we know.
        let setId=null;
        for(const sid of Object.keys(setDCs)){
          if(mk.startsWith(sid+"_")){setId=sid;break}
        }
        const usesSet=!usesMockup&&setId!=null;
        const usesGlobal=!usesMockup&&!usesSet;
        if(usesGlobal)delete next[mk];
      }
      return next;
    });
  },[mockupDCs,setDCs]);
  const applySetDC=useCallback((setIdToApply)=>{
    setGenOut(prev=>{
      const next={...prev};
      for(const mk of Object.keys(prev)){
        if(!mk.startsWith(setIdToApply+"_"))continue;
        const usesMockup=mockupDCs[mk]!==undefined;
        if(!usesMockup)delete next[mk];
      }
      return next;
    });
  },[mockupDCs]);
  const applyMockupDC=useCallback((mkToApply)=>{
    setGenOut(prev=>{
      const next={...prev};
      delete next[mkToApply];
      return next;
    });
  },[]);

  /* Autosave — debounced to avoid hammering IndexedDB on every keystroke.
     Splits into two writes: lightweight metadata (always) and heavy genOut
     (only when populated). This addresses three problems:
       1. Empty genOut={} no longer overwrites a previously-saved genOut.
          Earlier this caused Resume to land at step 2 with no mockups —
          the user clicks Generate which sets genOut={}, autosave fires
          before any mockups arrive, wipes the saved output, and on
          resume there's nothing to restore.
       2. A failed genOut write (size, quota) no longer takes down the
          metadata save. The user's setup is preserved either way.
       3. Skip autosave entirely while a pendingSession is awaiting user
          choice. Otherwise a fresh page load (which has empty state) would
          clobber the saved session's metadata before the user has clicked
          Resume. The autosave effect fires whenever its deps change —
          including the initial mount — so without this gate, the empty
          initial state gets persisted over the real saved state during
          the brief window before the user reacts to the banner.

     We still log a size warning if genOut would exceed a sane budget, but
     don't block — IndexedDB will reject if it can't fit. */
  // O26 — the mockup-deletion suppression sets. Declared HERE, ABOVE the two
  // autosave effects (and their dependency arrays) that reference them — NOT
  // down with the other result-view state where they naturally belong. useState
  // is a `const` binding: listing removedMks/removedSets in a dep array that
  // executes before their declaration is a temporal-dead-zone ReferenceError
  // that throws on mount and blanks the whole app for every member (the O26
  // regression, 2026-09-20). See removeMockup/removeSetMockups below for use.
  const[removedMks,setRemovedMks]=useState(()=>new Set());   // per-mockup deletions
  const[removedSets,setRemovedSets]=useState(()=>new Set()); // whole-set deletions
  useEffect(()=>{
    if(!dbOk)return;
    if(!sessionDecided)return; // suppress autosave until the user has resolved the resume banner (or there was nothing to restore)
    const handle=setTimeout(()=>{
      // Check genOut size first. For large batches (50+ designs typical)
      // we skip saving entirely AND wipe any prior session — the user
      // explicitly opted into "no resume for big batches" because:
      //   - genOut can be 500MB-1GB which fails JSON.stringify or hits
      //     IndexedDB per-record limits
      //   - 50 design dataURLs in metadata can also exceed IndexedDB
      //     record limits, leaving a stale older session as the most
      //     recent successful save (the bug the user reported)
      //   - Even if both saved, blocking the main thread for seconds
      //     during write is bad UX
      // 100MB threshold is comfortably below typical limits but above
      // realistic small-batch sizes (15-30 mockups).
      const GENOUT_SAVE_LIMIT_BYTES=100*1024*1024;
      const genCount=Object.keys(genOut||{}).length;
      let genSize=0;
      if(genCount>0){
        try{genSize=JSON.stringify(genOut).length}catch(e){genSize=Infinity}
      }
      const tooBig=genSize>GENOUT_SAVE_LIMIT_BYTES;
      if(tooBig){
        // Wipe both records — once per too-big-detected event, not
        // every autosave tick.
        if(!window.__msGenOutCleared){
          console.warn(`Mockup batch too large for resume (${isFinite(genSize)?(genSize/1024/1024).toFixed(0)+"MB":"unmeasurable"}). Resume disabled for this batch.`);
          dbDel(SESS,GENOUT_ID).catch(()=>{});
          dbDel(SESS,SESSION_ID).catch(()=>{});
          window.__msGenOutCleared=true;
        }
        return;
      }
      // Normal-size batch — save metadata, and genOut if non-empty.
      window.__msGenOutCleared=false;
      saveSessionMeta({
        selSavedIds:[...selSaved],
        designs:designs.map(d=>({id:d.id,name:d.name,src:d.src,group:d.group,fromPipeline:!!d.fromPipeline})),
        step,
        setNames,
        pairRouting,
        showRes,
        // O26 — persist the mockup-deletion suppression sets alongside genOut, so a
        // member's deletions survive a reload. Without these, Resume restores genOut
        // full but the (in-memory) suppression sets come back EMPTY, re-deriving every
        // deleted tile. Session-scoped tombstones (die with the 24h session), mirroring
        // the ms_deletedTemplates discipline at the session's scope, not localStorage.
        removedMks:[...removedMks],
        removedSets:[...removedSets],
      });
      if(genCount>0)saveSessionGenOut(genOut);
    },800);
    return()=>clearTimeout(handle);
  },[selSaved,designs,step,setNames,pairRouting,showRes,genOut,removedMks,removedSets,dbOk,sessionDecided]);

  /* Flush the autosave on tab close. The 800ms debounce can swallow the
     last write if the user closes the tab quickly after generation
     finishes. beforeunload fires synchronously; dbPut returns a promise
     but the write is queued before the page unloads as long as we don't
     await anything. This catches the common "I just closed the tab" case
     so genOut is persisted.

     Note: this can't catch hard tab kills (browser crash, force-close)
     but addresses normal close behavior. */
  useEffect(()=>{
    if(!dbOk)return;
    const flush=()=>{
      // Same gate as the debounced autosave — don't write over a session
      // the user hasn't yet decided to resume or discard. The user closing
      // the tab while the banner is up means "leave my saved state alone".
      if(!sessionDecided)return;
      saveSessionMeta({
        selSavedIds:[...selSaved],
        designs:designs.map(d=>({id:d.id,name:d.name,src:d.src,group:d.group,fromPipeline:!!d.fromPipeline})),
        step,
        setNames,
        pairRouting,
        showRes,
        removedMks:[...removedMks],   // O26 — persist deletions on tab close too (see debounced autosave)
        removedSets:[...removedSets],
      });
      // Same size gate as the debounced autosave — don't try to flush a
      // huge genOut on tab close, both because it'll fail and because
      // beforeunload only has milliseconds before the page is gone.
      if(Object.keys(genOut||{}).length>0){
        let size=0;
        try{size=JSON.stringify(genOut).length}catch(e){size=Infinity}
        if(size<=100*1024*1024)saveSessionGenOut(genOut);
      }
    };
    window.addEventListener("beforeunload",flush);
    window.addEventListener("pagehide",flush);
    return()=>{
      window.removeEventListener("beforeunload",flush);
      window.removeEventListener("pagehide",flush);
    };
  },[selSaved,designs,step,setNames,pairRouting,showRes,genOut,removedMks,removedSets,dbOk,sessionDecided]);

  const c=t(isDark);
  const cfgd=mockups.filter(m=>(m.smartObjects||[]).length>0||m.smartObject||m.noDesign);
  const canStep2=mockups.length>0;
  const allReady=[...cfgd,...saved.filter(s=>selSaved.has(s.id))].map(migrate).map(t=>{
    const ov=tempOverrides[t.id];
    return ov?{...t,...ov,smartObject:(ov.smartObjects||[])[0]||null}:t;
  }).sort((a,b)=>(a.sortOrder??999)-(b.sortOrder??999));
  const canStep3=allReady.length>0;

  // Groups from all ready templates (design-needing ones only)
  const allGroups=(()=>{const gm=new Map();allReady.filter(t=>!t.noDesign).forEach(t=>(t.groups||[]).forEach(g=>{if(!gm.has(g.id))gm.set(g.id,g)}));return[...gm.values()]})();
  const designNeeded=allReady.filter(t=>!t.noDesign);

  // Build design sets — only require designs for groups that have zones on design-needing templates
  const usedGroupIds=new Set();
  designNeeded.forEach(t=>(t.smartObjects||[]).forEach(so=>{if(so.group)usedGroupIds.add(so.group)}));
  const activeGroups=allGroups.filter(g=>usedGroupIds.has(g.id));
  // AUTO tone of a group under DIRECT semantics (tag wins, else name lexicon):
  // "dark"/"light"/null(neutral). Feeds both the pair set gdm and the card's default
  // [Dark|Light|Both] selection, so the control and the render agree.
  const pairAutoTone=g=>g.tone==="dark"?"dark":g.tone==="light"?"light":g.tone==="all"?null:classifyGarmentTone(g.name);

  // Live snapshot of the resolved templates for the headless RENDER_BATCH
  // (F1) handler. The inbound message listener below is registered once
  // (empty-deps useEffect) so it would otherwise close over the FIRST
  // render's `allReady`; mutating this ref every render keeps the handler
  // reading the CURRENT template set without re-registering the listener.
  renderBatchStateRef.current.allReady=allReady;
  // F1 set-resolution: bridge the live profile/library state across the
  // empty-deps listener too, so a RENDER_BATCH{profileId} can resolve a saved
  // set from CURRENT data (not the mount-time empties the closure would see).
  // profsLoaded (=dbOk) lets the handler tell "still loading" from "not found".
  renderBatchStateRef.current.profs=profs;
  renderBatchStateRef.current.saved=saved;
  renderBatchStateRef.current.tempOverrides=tempOverrides;
  renderBatchStateRef.current.profsLoaded=dbOk;
  // Pairing arc D: bridge live global groups across the same empty-deps closure
  // so a paired chart's per-cell garment-tone classing reads CURRENT group
  // names (the SO-editor color labels), not the mount-time defaults. Used only
  // as a fallback when a template carries no local `groups` copy.
  renderBatchStateRef.current.globalGroups=globalGroups;
  // F1 one-click responder: same closure fix. The message listener (empty-deps)
  // would close over the mount-time `designs` ([]); mirror the CURRENT designs
  // here so REQUEST_ONECLICK_DESIGNS reads live state, not the frozen empty.
  renderBatchStateRef.current.designs=designs;

  const designSets=(()=>{
    if(activeGroups.length===0&&designNeeded.length>0)return[];
    if(activeGroups.length===0)return[{id:"set_0",gdm:{},label:"Standalone only",designs:{}}];

    // PAIR SETS — a pipeline pair (two designs sharing a pairId, art classified)
    // is ONE product = ONE set. DIRECT group semantics (owner law): a group's NAME
    // names WHICH DESIGN goes there — "Black"/"Dark Design" → the dark design,
    // "White"/"Light Design" → the light design, directly (no garment inversion on
    // GROUPS, matching the suite's 1-click classify). A NEUTRAL group (name classifies
    // to no tone) gets the never-blank combined image here and is split once-per-member
    // by setDesignItems for non-chart neutral templates. Garment inversion exists ONLY
    // on chart CELLS (setDesignItems chart branch), where names denote real garments.
    const pairSets=[];
    const pairedIds=new Set();
    const byPair={};
    designs.forEach(d=>{if(d.pairId){(byPair[d.pairId]||(byPair[d.pairId]=[])).push(d)}});
    Object.entries(byPair).forEach(([pid,members])=>{
      const pa=pairArt[pid];
      if(members.length!==2||!pa)return; // orphan half / not-yet-classified → legacy path
      members.forEach(m=>pairedIds.add(m.id));
      // Member-keyed routing: each member's dropdown choice ("auto"|"all"|groupId).
      const darkM=members.find(m=>m.src===pa.darkSrc)||members[0];
      const lightM=members.find(m=>m.src===pa.lightSrc)||members[1];
      const rt=pairRouting[pid]||{};
      const darkChoice=rt[darkM.id]||"auto";
      const lightChoice=rt[lightM.id]||"auto";
      const gdm={};const combo={};
      activeGroups.forEach(g=>{
        // Explicit tone TAG wins (direct: the tag names WHICH ARTWORK binds). An
        // UNTAGGED legacy group falls back to the name inference — byte-identical
        // to the pre-tag behaviour. Chart-cell groups are typically untagged, so
        // they keep their lexicon classification (owner: cells are a separate axis).
        // Resolve this group from BOTH members' choices. AUTO = direct semantics (group
        // NAME names WHICH DESIGN: "Black"/"Dark Design"→dark, "White"/"Light Design"→
        // light, neutral→both). Both members claim → combined (neutral → per-member for
        // non-chart in setDesignItems). Neither claims → UNCOVERED (skip). Chart cells
        // ignore all of this and invert per-cell.
        const art=pairGroupArt({groupId:g.id,autoTone:pairAutoTone(g),darkChoice,lightChoice},pa);
        if(art==null)return; // uncovered → gdm[g.id] left unset → templates needing only it skip
        gdm[g.id]=art;
        combo[g.id]=members.find(m=>m.src===art)||members[0];
      });
      pairSets.push({
        id:`set_pair_${pid}`,
        gdm,
        label:members.map(m=>(m.name||"").replace(/\.[^.]+$/,"")).join(" + "),
        designs:combo,
        pair:{lightSrc:pa.lightSrc,darkSrc:pa.darkSrc,combined:pa.combined,members:members.map(m=>({id:m.id,src:m.src,name:m.name}))},
      });
    });

    // Legacy sets are built ONLY from unpaired designs (singles, direct uploads,
    // orphan pair halves, and pairs whose art hasn't classified yet). Paired
    // designs never enter the all-groups / cartesian paths → no cross-pair blow-up.
    const unpaired=designs.filter(d=>!pairedIds.has(d.id));
    // Separate "all groups" designs from group-specific ones
    const allGroupDesigns=unpaired.filter(d=>d.group==="all");
    const specificDesigns=unpaired.filter(d=>d.group!=="all");

    const sets=[];
    const seen=new Set();

    // 1. Each "all groups" design creates one set with that design on every covered group
    allGroupDesigns.forEach(d=>{
      const gdm={};const combo={};
      activeGroups.forEach(g=>{gdm[g.id]=d.src;combo[g.id]=d});
      const key=activeGroups.map(g=>d.id).join("|");
      if(!seen.has(key)){seen.add(key);sets.push({gdm,combo,label:d.name.replace(/\.[^.]+$/,"")})}
    });

    // 2. Build combos from group-specific designs only
    if(specificDesigns.length>0){
      const perGroup={};
      activeGroups.forEach(g=>{
        perGroup[g.id]=specificDesigns.filter(d=>d.group===g.id);
      });
      const coveredGroups=activeGroups.filter(g=>perGroup[g.id].length>0);
      if(coveredGroups.length>0){
        const groupIds=coveredGroups.map(g=>g.id);
        let combos=[{}];
        for(const gid of groupIds){
          const ds=perGroup[gid];
          const next=[];
          for(const combo of combos){for(const d of ds){next.push({...combo,[gid]:d})}}
          combos=next;
        }
        combos.forEach(combo=>{
          const key=coveredGroups.map(g=>combo[g.id]?.id).join("|");
          if(!seen.has(key)){
            seen.add(key);
            const gdm={};const names=new Set();
            for(const[gid,d]of Object.entries(combo)){gdm[gid]=d.src;names.add(d.name.replace(/\.[^.]+$/,""))}
            sets.push({gdm,combo,label:[...names].join(" + ")});
          }
        });
      }
    }

    const legacySets=sets.map((s,i)=>({id:`set_${i}`,gdm:s.gdm,label:s.label,designs:s.combo}));
    return[...pairSets,...legacySets];
  })();

  const canGen=allReady.length>0&&(designNeeded.length===0||designSets.length>0);

  // Helper: check if a template has at least one zone with a design in the given gdm
  const tplHasDesign=(tpl,gdm)=>(tpl.smartObjects||[]).some(so=>gdm[so.group]);


  // The design-mockup render plan for a set: [{tpl, k, gdm}]. Single source of
  // truth for every consumer (grid, downloads, pipeline push, warmup, count math)
  // so pair semantics live in ONE place.
  //   • Non-pair set → one item per covered design-template, gdm = set.gdm,
  //     key `${set.id}_${tpl.id}` (byte-identical to the pre-pairing behaviour).
  //   • Pair set, CHART template → one item with a per-CELL gdm (F8): each cell is
  //     classed by its OWN group name — garment-named cells INVERT (dark cell ← light
  //     design), design-named cells bind direct, neutral cells combined. Independent
  //     of the group's DIRECT semantics, so charts stay garment-correct even when a
  //     garment mockup group and a chart cell share a "Black"/"White" name.
  //   • Pair set, non-chart tone template → one item, gdm = set.gdm (direct).
  //   • Pair set, NON-chart template touching a NEUTRAL zone → once PER MEMBER
  //     (1-click neutral-binds law): each member binds its OWN tone zones plus the
  //     neutral zones with its OWN art, key `${set.id}_${tpl.id}_m${member.id}`.
  const setDesignItems=(set)=>{
    const items=[];
    const covered=allReady.filter(t=>!t.noDesign&&tplHasDesign(t,set.gdm));
    for(const t of covered){
      if(set.pair&&isChartSlot(t.name)){
        // Chart cells name GARMENTS → F8 per cell, NOT the group's direct gdm.
        const gdm={};
        (t.smartObjects||[]).forEach(so=>{
          if(set.gdm[so.group]===undefined)return; // only cells this set covers
          const g=allGroups.find(x=>x.id===so.group);
          const gname=(g&&g.name)||"";
          gdm[so.group]=chartCellArt(classifyGarmentTone(gname),/design/i.test(gname),{darkSrc:set.pair.darkSrc,lightSrc:set.pair.lightSrc,combined:set.pair.combined});
        });
        if((t.smartObjects||[]).some(so=>gdm[so.group]))items.push({tpl:t,k:`${set.id}_${t.id}`,gdm});
        continue;
      }
      // Effective neutrality = the group's resolved art IS the combined image (a
      // name-neutral group OR a "both" override). Keying on the gdm value (not the
      // group name) means a member's "Both" override also renders per-member here.
      const touchesNeutral=set.pair&&(t.smartObjects||[]).some(so=>set.gdm[so.group]===set.pair.combined);
      if(touchesNeutral){
        for(const m of set.pair.members){
          const gdm={};
          (t.smartObjects||[]).forEach(so=>{
            const art=set.gdm[so.group];
            if(art===undefined)return;
            if(art===set.pair.combined)gdm[so.group]=m.src;   // neutral zone → THIS member's art
            else if(art===m.src)gdm[so.group]=art;            // this member's own tone zone
            // else: the other member's tone zone → member m doesn't bind it
          });
          if(!(t.smartObjects||[]).some(so=>gdm[so.group]))continue;
          items.push({tpl:t,k:`${set.id}_${t.id}_m${m.id}`,gdm});
        }
      }else{
        items.push({tpl:t,k:`${set.id}_${t.id}`,gdm:set.gdm});
      }
    }
    return items;
  };

  // Hard size ceilings (rejects, never silent). PSDs parse fully in memory via
  // ag-psd; images persist as dataURLs in IndexedDB. Both caps are shown in the
  // upload-box copy so members see the limit before hitting a wall.
  const MAX_IMG_MB=60, MAX_PSD_MB=200;

  // Add one product photo as a new-mockup card (unchanged per-file logic).
  const addMockFile=f=>{const r=new FileReader();r.onload=ev=>setMockups(p=>[...p,{id:`n${Date.now()}${Math.random()}`,name:f.name,src:ev.target.result,smartObjects:[],groups:globalGroups,foregroundMask:null,noDesign:false,sortOrder:999}]);r.readAsDataURL(f)};

  // ── Listing Video library ────────────────────────────────────────────────
  // Refresh the user's server-side video library (manifests + usage).
  const refreshLibVideos=useCallback(async(attempt=0)=>{
    setVideoLoading(true);
    try{const d=await videoList();setLibVideos(d.videos||[]);if(d.usage)setVideoUsage(d.usage);setVideoLoadErr(false);setVideoLoading(false);return d.videos||[];}
    catch(e){
      console.warn("Video list failed:",e);
      // Auto-retry transient blips (edge cold-start, flaky moment) before surfacing
      // an error. The empty-state must only ever mean "you have no videos" — never
      // "we couldn't load them", which silently hid present videos before.
      if(attempt<2){setTimeout(()=>{refreshLibVideos(attempt+1)},600*(attempt+1));return null;}
      setVideoLoadErr(true);setVideoLoading(false);return null;
    }
  },[]);

  // Grab a small first-frame JPEG thumbnail for the grid card. Best-effort —
  // returns null if the browser can't decode the clip (we still upload it).
  const generateVideoThumb=(file)=>new Promise((resolve)=>{
    try{
      const url=URL.createObjectURL(file);
      const v=document.createElement("video");
      const cont=document.createElement("div");
      cont.style.cssText="position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
      cont.appendChild(v);document.body.appendChild(cont);
      let done=false;const finish=(val)=>{if(done)return;done=true;try{URL.revokeObjectURL(url)}catch{}try{document.body.removeChild(cont)}catch{}resolve(val)};
      v.muted=true;v.playsInline=true;v.preload="metadata";
      v.onloadeddata=()=>{try{
        const w=v.videoWidth,h=v.videoHeight;if(!w||!h){finish(null);return;}
        const scale=Math.min(1,256/Math.max(w,h));const cw=Math.max(1,Math.round(w*scale)),ch=Math.max(1,Math.round(h*scale));
        const cv=document.createElement("canvas");cv.width=cw;cv.height=ch;cv.getContext("2d").drawImage(v,0,0,cw,ch);
        finish(cv.toDataURL("image/jpeg",0.6));
      }catch{finish(null)}};
      v.onseeked=v.onloadeddata;
      v.onerror=()=>finish(null);
      v.src=url;
      // Nudge to ~1s (or start) so we skip a black first frame when possible.
      const seek=()=>{try{v.currentTime=Math.min(1,(v.duration||1)/2)}catch{}};
      v.onloadedmetadata=seek;
      setTimeout(()=>finish(null),8000);
    }catch{resolve(null)}
  });

  // ── Per-file progress rows (F8) ──────────────────────────────────────────
  // patch a single row by id; auto-clear a clean "done" row after a few seconds
  // (errors + warns stay until the member dismisses them).
  const patchVideoRow=(rowId,patch)=>setVideoRows(rows=>rows.map(r=>r.rowId===rowId?{...r,...patch}:r));
  const dismissVideoRow=rowId=>setVideoRows(rows=>rows.filter(r=>r.rowId!==rowId));

  // Upload one video file into the library: validate (format/duration/size) →
  // budget guard → thumbnail → slice into ~3.5MB parts → begin/upload/finalize.
  // NO compression — Etsy takes the file as-is. `rowId` = the progress row this
  // file owns; all state lands on that row (never a shared single panel).
  const uploadLibraryVideo=async(file,rowId)=>{
    if(!file)return;
    patchVideoRow(rowId,{state:"uploading",pct:0});
    // Duration (best-effort — a codec the browser can't read isn't a dead-end).
    let durationSec=null,metaFailed=false;
    try{durationSec=await readVideoDuration(file);}catch{metaFailed=true;}
    const val=validateVideoFile({name:file.name,sizeBytes:file.size,durationSec,metaFailed});
    if(!val.ok){patchVideoRow(rowId,{state:"error",error:val.error});return;}
    // Read the CURRENT library from the ref (state is stale across queued items —
    // the ref updates on every setLibVideos re-render). Server re-checks anyway.
    const budget=checkVideoBudget(libVideosRef.current,file.size);
    if(!budget.ok){patchVideoRow(rowId,{state:"error",error:budget.error});return;}
    const videoId=`vid_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    try{
      await videoBegin({videoId,file_name:file.name,sizeBytes:file.size,durationSec,parts:0});
      patchVideoRow(rowId,{pct:0.02});
      const thumb=await generateVideoThumb(file);
      const ranges=partByteRanges(file.size);
      for(const rg of ranges){
        patchVideoRow(rowId,{pct:rg.index/ranges.length});
        await videoUploadPart(videoId,rg.index,file.slice(rg.start,rg.end));
      }
      patchVideoRow(rowId,{pct:0.98});
      const fin=await videoFinalize({videoId,file_name:file.name,sizeBytes:file.size,durationSec,parts:ranges.length,partSize:PART_SIZE,thumb,createdAt:Date.now()});
      if(fin.videos)setLibVideos(fin.videos);else await refreshLibVideos();
      if(fin.usage)setVideoUsage(fin.usage);
      patchVideoRow(rowId,{state:"done",pct:1,warn:val.warn||null});
      // Clean done → auto-clear; a warn carries info the seller should read → keep.
      if(!val.warn)setTimeout(()=>dismissVideoRow(rowId),4000);
    }catch(e){
      // Best-effort cleanup of any parts already written on a mid-upload failure.
      try{await videoDelete(videoId)}catch{}
      patchVideoRow(rowId,{state:"error",error:`Upload failed: ${e.message}`});
    }
  };

  // Serialized upload queue. Concurrent uploads raced on the shared per-user
  // index blob (@netlify/blobs has no compare-and-swap), so we drain one file at
  // a time, in pick order. Each file gets its own progress ROW up-front (state
  // "queued") so a multi-file pick shows a row per file immediately. Called by
  // BOTH pickers (Step-1 bar + the empty-frame placeholder) and drag-drop.
  const enqueueVideos=(files)=>{
    const list=Array.from(files||[]).filter(Boolean);
    if(!list.length)return;
    const jobs=list.map(file=>({rowId:`vr_${++videoRowSeqRef.current}`,file}));
    setVideoRows(rows=>[...rows,...jobs.map(j=>({rowId:j.rowId,file_name:j.file.name,state:"queued",pct:0}))]);
    videoQueueRef.current.push(...jobs);
    if(videoQueueRunningRef.current)return; // a drain is already in flight
    videoQueueRunningRef.current=true;
    (async()=>{
      try{
        while(videoQueueRef.current.length){
          const job=videoQueueRef.current.shift();
          await uploadLibraryVideo(job.file,job.rowId);
        }
      }finally{videoQueueRunningRef.current=false;}
    })();
  };

  // Shared post-delete local cleanup for one-or-many removed video ids: unbind
  // from every set that referenced any of them, drop from the working/manual
  // selections, and revoke cached preview URLs. `ids` = the removed videoIds.
  const cleanupDeletedVideos=(ids)=>{
    const idset=ids instanceof Set?ids:new Set(ids);
    setProfs(prev=>{
      if(!prev.some(p=>idset.has(p.videoId)))return prev;
      const next=prev.map(p=>idset.has(p.videoId)?setProfileVideo(p,null):p);
      next.forEach((p,i)=>{if(p!==prev[i])dbPut(PROF,p).catch(()=>{})});
      scheduleProfilesSync(next);
      return next;
    });
    setSelVideoIds(prev=>{if(![...prev].some(id=>idset.has(id)))return prev;const n=new Set(prev);idset.forEach(id=>n.delete(id));return n;});
    if(idset.has(manualVideoId))setManualVideoId(null);
    idset.forEach(id=>{const cached=videoUrlCacheRef.current[id];if(cached){try{URL.revokeObjectURL(cached)}catch{}delete videoUrlCacheRef.current[id];}});
  };

  // Delete ONE video — its own per-card affordance, deliberately DECOUPLED from
  // the set-attachment selection (selVideoIds). Deleting a library video is
  // permanent (parts reclaimed server-side, no backup), so it ALWAYS confirms and
  // NAMES any set that uses the video first — the prior design overloaded the
  // set-pick selection as the delete target with no confirm, so picking a video to
  // use it and then hitting delete destroyed it (and left sets dangling). We only
  // commit local state AFTER the server confirms; cleanupDeletedVideos then clears
  // the videoId off every loaded set + the selection so nothing is left dangling.
  const deleteOneVideo=async(videoId)=>{
    const v=(libVideosRef.current||[]).find(x=>x.videoId===videoId);
    const name=v?v.file_name:"this video";
    const usingSets=(profs||[]).filter(p=>p.videoId===videoId);
    const warn=usingSets.length?`\n\n⚠️ It's attached to ${usingSets.length} set${usingSets.length===1?"":"s"} — ${usingSets.map(p=>`“${p.name}”`).join(", ")}. Deleting removes it from ${usingSets.length===1?"that set":"those sets"} too.`:"";
    if(!window.confirm(`Delete “${name}” permanently?\n\nThis can't be undone — the video is removed from our servers with no backup.${warn}`))return;
    try{
      const d=await videoDelete(videoId);
      setLibVideos(d.videos!=null?d.videos:(libVideosRef.current||[]).filter(x=>x.videoId!==videoId));
      if(d.usage)setVideoUsage(d.usage);
      cleanupDeletedVideos(new Set([videoId])); // clear set links + selection after the confirmed delete
    }catch(e){
      console.warn("Video delete failed:",e);
      setVideoNotice({error:`Couldn't delete “${name}”: ${e.message}`});
    }
  };

  // Get a playable object-URL for a library video, reassembling from server
  // parts on first request and caching the result.
  const getVideoUrl=useCallback(async(videoId)=>{
    if(videoUrlCacheRef.current[videoId])return videoUrlCacheRef.current[videoId];
    const manifest=(libVideos||[]).find(v=>v.videoId===videoId);
    if(!manifest)return null;
    const blob=await videoReassembleBlob(manifest);
    const url=URL.createObjectURL(blob);
    videoUrlCacheRef.current[videoId]=url;
    return url;
  },[libVideos]);

  // Live mirrors so the mount-only pipeline message handler reads CURRENT sets +
  // library (the handler closes over first-render state otherwise).
  const profsRef=useRef(profs);profsRef.current=profs;
  const libVideosRef=useRef(libVideos);libVideosRef.current=libVideos;

  // Deliver a set's listing-video BYTES to the suite on demand. Reassembles from
  // the server store, then emits on the PIPELINE_VIDEO channel: a single message
  // for small clips (the form the suite consumes today), or PIPELINE_VIDEO_CHUNK
  // parts + isLast for big clips (10–80MB) — the suite-side attach arc consumes
  // this contract verbatim. video:null when the set has no video.
  const emitProfileVideo=async(profileId,batchId)=>{
    const post=payload=>{try{window.parent&&window.parent.postMessage({source:"creative-seller-pipeline",...payload},"*")}catch(ex){}};
    try{
      const prof=(profsRef.current||[]).find(p=>p.id===profileId);
      const manifest=prof&&prof.videoId?(libVideosRef.current||[]).find(v=>v.videoId===prof.videoId):null;
      if(!manifest){post({type:"PIPELINE_VIDEO",batchId,profileId,video:null});return;}
      const meta={file_name:manifest.file_name,sizeBytes:manifest.sizeBytes,durationSec:manifest.durationSec!=null?manifest.durationSec:null};
      const dataUrl=await blobToDataUrl(await videoReassembleBlob(manifest));
      if(fitsManualSend(manifest.sizeBytes)){
        // Small clip → single-message form.
        console.log("[Studio] emitting set video (1 message, small)",{profileId,file_name:manifest.file_name,sizeBytes:manifest.sizeBytes});
        post({type:"PIPELINE_VIDEO",batchId,profileId,video:{base64:dataUrl,...meta}});
        return;
      }
      // Big clip → chunk the base64 (same shape as PIPELINE_MOCKUPS_CHUNK).
      const CH=3_500_000; // base64 chars per chunk (~3.5MB)
      const totalChunks=Math.ceil(dataUrl.length/CH);
      console.log("[Studio] emitting set video",{profileId,file_name:manifest.file_name,sizeBytes:manifest.sizeBytes,totalChunks});
      for(let i=0;i<totalChunks;i++){
        post({type:"PIPELINE_VIDEO_CHUNK",batchId,profileId,videoId:manifest.videoId,chunkIndex:i,totalChunks,isFirst:i===0,isLast:i===totalChunks-1,data:dataUrl.slice(i*CH,(i+1)*CH),...(i===0?meta:{})});
      }
    }catch(ex){post({type:"PIPELINE_VIDEO",batchId,profileId,video:null});}
  };

  // Unified Step-1 intake: route each file by type. Images become cards
  // instantly (as before, size-capped); every .psd is queued and imported
  // SEQUENTIALLY (one ag-psd parse at a time — no parallel-parse memory spikes,
  // but all of them processed). A mixed batch adds the images AND imports every
  // PSD. No silent failures — each reject names its reason and limit. Called by
  // BOTH the file-input onChange AND the drag-drop handler so a dropped batch
  // routes identically to a picked one.
  const handleIntake=fileList=>{
    const files=Array.from(fileList||[]);
    if(!files.length)return;
    const isPsd=f=>/\.psd$/i.test(f.name)||f.type==="image/vnd.adobe.photoshop";
    const isVid=f=>isVideoFile(f.name,f.type);
    const vids=files.filter(isVid);
    const rest=files.filter(f=>!isVid(f));
    const psds=rest.filter(isPsd), imgs=rest.filter(f=>!isPsd(f));
    const imgRejects=[];
    imgs.forEach(f=>{ if(f.size>MAX_IMG_MB*1048576){imgRejects.push(`"${f.name}" (${(f.size/1048576).toFixed(0)} MB) is over the ${MAX_IMG_MB} MB image limit`);return;} addMockFile(f); });
    // A video is a library item; queue them so they upload one at a time, in
    // order (concurrent uploads race the shared server index — see enqueueVideos).
    if(vids.length)enqueueVideos(vids);
    if(psds.length)importPsdQueue(psds,imgRejects);
    else if(imgRejects.length)setPsdImp({error:imgRejects.join(" · ")});
  };
  // Snapshot the picked files to a real ARRAY *before* clearing the input —
  // e.target.files is a LIVE FileList that value="" empties, so capturing the
  // reference first (then clearing) would hand handleIntake an empty list. This
  // is why click-to-pick imported nothing while drag-drop (dataTransfer.files,
  // never cleared) worked. Array first, then clear, then route.
  const upMock=e=>{ const files=Array.from(e.target.files||[]); e.target.value=""; handleIntake(files); };

  // Parse + build ONE .psd into a saved template. Throws a friendly message on
  // any reject/failure so the queue runner can catch per-file (one bad PSD can't
  // sink the batch). Owns no UX (psdImp/editor) — the runner drives that. `gg`
  // threads the accumulating global groups so a batch dedupes group names.
  const importOnePsd=async(file,gg,onProgress)=>{
    if(!/\.psd$/i.test(file.name)&&file.type!=="image/vnd.adobe.photoshop")throw new Error(`"${file.name}" isn't a .psd file.`);
    if(file.size>MAX_PSD_MB*1048576)throw new Error(`"${file.name}" is ${(file.size/1048576).toFixed(0)} MB — over the ${MAX_PSD_MB} MB PSD limit. Flatten unused layers in Photoshop and re-export.`);
    let importPsd;
    try{ ({importPsd}=await import("./psdImport")); }
    catch(e){ const m=`${(e&&(e.message||e.name))||""}`;
      if(/loading chunk|chunkloaderror|dynamically imported module|failed to fetch/i.test(m)) throw new Error("The app updated in the background — please refresh the page and try again.");
      throw new Error("Couldn't load the PSD reader — check your connection and try again."); }
    const r=await importPsd(file,{globalGroups:gg,onProgress});
    const tpl={id:`s${Date.now()}${Math.random()}`,name:r.name,src:r.photo,smartObjects:r.smartObjects,groups:r.groups,smartObject:r.smartObjects[0]||null,foregroundMask:r.foregroundMask||null,noDesign:false,sortOrder:999,savedAt:Date.now()};
    saveTplSync(tpl);
    setSaved(p=>[...p,tpl]);
    setTplLimit(p=>({...p,used:p.used+1}));
    return {tpl,warnings:r.warnings||[],summary:r.summary,globalGroups:r.globalGroups};
  };

  // Sequential PSD queue. Imports up to the remaining template-limit room,
  // reports batch progress, then EITHER lands in the editor (a single clean
  // import — the familiar flow) OR leaves everything in the saved list with a
  // roll-up summary (a batch — cleaner than yanking the member into one editor).
  const importPsdQueue=async(psds,imgRejects=[])=>{
    if(psdImp?.busy)return; // never run two queues concurrently
    const room=Math.max(0, tplLimit.max - saved.length);
    const toDo=psds.slice(0,room), overflow=psds.length-toDo.length;
    const imported=[], errors=[...imgRejects];
    let gg=globalGroups;
    for(let i=0;i<toDo.length;i++){
      setPsdImp({busy:true,msg:toDo.length>1?`Importing PSD ${i+1} of ${toDo.length}…`:"Starting…"});
      try{
        const res=await importOnePsd(toDo[i],gg,msg=>setPsdImp({busy:true,msg:toDo.length>1?`PSD ${i+1}/${toDo.length}: ${msg}`:msg}));
        if(res.globalGroups&&res.globalGroups.length)gg=res.globalGroups;
        imported.push(res);
      }catch(e){ errors.push(e&&e.message?e.message:`Couldn't import "${toDo[i].name}".`); }
    }
    if(gg!==globalGroups)updateGlobalGroups(gg);
    if(overflow>0)errors.push(`${overflow} PSD${overflow>1?"s":""} not imported — template limit (${tplLimit.max}) reached. Delete some saved templates and re-import.`);
    if(!imported.length){ setPsdImp({error:errors.length?errors.join(" · "):"No PSDs were imported."}); return; }
    if(imported.length===1&&psds.length===1&&!errors.length){
      // single clean import → land in the editor, zones pre-drawn (unchanged flow)
      setPsdImp({summary:imported[0].summary,warnings:imported[0].warnings});
      setEditSavedSO(imported[0].tpl);
      return;
    }
    const warn=[...errors];
    imported.forEach(r=>r.warnings.forEach(w=>warn.push(`${r.tpl.name}: ${w}`)));
    setPsdImp({summary:`${imported.length} template${imported.length>1?"s":""} imported — open any below to edit its zones.`,warnings:warn});
  };

  const rmMock=id=>setMockups(p=>p.filter(m=>m.id!==id));
  const setSortOrder=(id,val)=>{
    setMockups(p=>p.map(m=>m.id===id?{...m,sortOrder:val}:m));
    setSaved(p=>{const u=p.map(s=>s.id===id?{...s,sortOrder:val}:s);const f=u.find(s=>s.id===id);if(f)saveTplSync(f);return u});
  };

  const saveSO=({smartObjects:sos,groups:grps,noDesign:nd})=>{
    if(saved.length>=tplLimit.max){alert(`Template limit reached (${tplLimit.max}). Delete some saved templates to add new ones.`);setEditMock(null);return}
    const u={...editMock,smartObjects:sos,groups:grps,smartObject:sos[0]||null,noDesign:nd};
    setMockups(p=>p.map(m=>m.id===editMock.id?u:m));
    const tpl={id:`s${Date.now()}${Math.random()}`,name:u.name,src:u.src,smartObjects:sos,groups:grps,smartObject:sos[0]||null,foregroundMask:u.foregroundMask,noDesign:nd,sortOrder:u.sortOrder??999,savedAt:Date.now()};
    saveTplSync(tpl);setSaved(p=>[...p,tpl]);setTplLimit(p=>({...p,used:p.used+1}));
    setEditMock(null);
  };
  const saveSavedSO=({smartObjects:sos,groups:grps,noDesign:nd})=>{
    const id=editSavedSO.id;
    setSaved(p=>{const u=p.map(s=>s.id===id?{...s,smartObjects:sos,groups:grps,smartObject:sos[0]||null,noDesign:nd}:s);const f=u.find(s=>s.id===id);if(f)saveTplSync(f);return u});
    setEditSavedSO(null);
  };
  // Linked multi-mockup placement — writes the new zone geometry/rotation into
  // every mockup the member positioned at once. updates = {id: smartObjects}.
  // Mirrors saveSavedSO's persist path per template, loops it over the set (same
  // setSaved+saveTplSync pattern used by the bulk convert at line ~5600), keeps
  // the in-memory `mockups` copies in step, and drops any generated output for
  // the touched templates so results re-render with the new placement.
  const saveLinkedPlacement=(updates)=>{
    const ids=Object.keys(updates||{});
    if(ids.length===0){setLinkedPlacementOpen(false);return}
    setSaved(p=>{const u=p.map(s=>updates[s.id]?{...s,smartObjects:updates[s.id],smartObject:updates[s.id][0]||null}:s);u.forEach(s=>{if(updates[s.id])saveTplSync(s)});return u;});
    setMockups(p=>p.map(m=>updates[m.id]?{...m,smartObjects:updates[m.id],smartObject:updates[m.id][0]||null}:m));
    setGenOut(p=>{const n={...p};Object.keys(n).forEach(k=>{if(ids.some(id=>k.includes(id)))delete n[k]});return n;});
    setLinkedPlacementOpen(false);
  };
  const[resultEditMode,setResultEditMode]=useState("permanent"); // "permanent" | "session" | "thisonly"
  const[mockupSOOverrides,setMockupSOOverrides]=useState({}); // per-mockup-key SO overrides
  // O26 — member-driven deletion of generated mockups. Tiles are DERIVED each
  // render from templates×designSets (not a static list), so a mockup can't be
  // dropped by clearing genOut alone — that only forces a regenerate. Removal is
  // a suppression set applied IDENTICALLY in the render loop AND sendToPipeline,
  // so what a member removes is gone from the view and never reaches Etsy. We
  // also drop the genOut bytes to free memory. removedSets hides a whole set.
  // NOTE: removedMks/removedSets are declared far above (just before the autosave
  // effects) so their dep-array references don't hit a temporal dead zone — see
  // the O26 comment there. Only the handlers that mutate them live here.
  const removeMockup=(mk)=>{
    setRemovedMks(p=>{const n=new Set(p);n.add(mk);return n;});
    setGenOut(p=>{const n={...p};delete n[mk];return n;});
  };
  const removeSetMockups=(set)=>{
    const items=setDesignItems(set);
    const count=items.length;
    if(!window.confirm(`Remove this entire set — all ${count} mockup${count===1?"":"s"}? This can't be undone.`))return;
    const keys=items.map(it=>it.k);
    setRemovedSets(p=>{const s=new Set(p);s.add(set.id);return s;});
    setRemovedMks(p=>{const s=new Set(p);keys.forEach(k=>s.add(k));return s;});
    // Drop EVERY genOut key for this set — design items, member variants AND this
    // set's standalone copies (all keyed `${set.id}_…`) — so totGen stays in step
    // with the now-lower totExp (removedSets excludes the whole set from both).
    setGenOut(p=>{const n={...p};Object.keys(n).forEach(k=>{if(k.startsWith(`${set.id}_`))delete n[k];});return n;});
  };
  // The floating ✕ overlaid on a generated mockup tile (top-right). Rendered by
  // the tile-wrapping <div> in the results grid, not inside the tile components.
  const removeBadge=(mk)=>(<button onClick={()=>removeMockup(mk)} title="Remove this mockup" style={{position:"absolute",top:5,right:5,zIndex:6,width:22,height:22,borderRadius:6,border:"none",background:"rgba(239,68,68,0.92)",color:"#fff",fontSize:12,fontWeight:800,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 1px 5px rgba(0,0,0,0.35)"}}>✕</button>);
  // O26b — the design map (group→art) belonging to a SPECIFIC mockup key, for
  // the SO editor's live design preview. Was hardcoded to designSets[0].gdm,
  // which showed the FIRST set's design on every mockup regardless of which one
  // was opened (the §2 bug). Resolve by matching the mockup's own key instead.
  const gdmForMk=(mk)=>{
    for(const set of designSets){
      if(removedSets.has(set.id))continue;
      for(const it of setDesignItems(set)){ if(it.k===mk)return it.gdm; }
    }
    return {};
  };
  const saveResultSO=({smartObjects:sos,groups:grps,noDesign:nd})=>{
    const id=editResultSO.id;
    const mk=editResultSO._mk;
    const prev=mockups.find(m=>m.id===id);
    const changed=!prev||JSON.stringify(prev.smartObjects)!==JSON.stringify(sos)||JSON.stringify(prev.groups)!==JSON.stringify(grps)||prev.noDesign!==nd;
    if(resultEditMode==="permanent"){
      setSaved(p=>{const u=p.map(s=>s.id===id?{...s,smartObjects:sos,groups:grps,smartObject:sos[0]||null,noDesign:nd}:s);const f=u.find(s=>s.id===id);if(f)saveTplSync(f);return u});
      setMockups(p=>p.map(m=>m.id===id?{...m,smartObjects:sos,groups:grps,smartObject:sos[0]||null,noDesign:nd}:m));
      if(changed)setGenOut(p=>{const n={...p};Object.keys(n).forEach(k=>{if(k.includes(id))delete n[k]});return n});
    }else if(resultEditMode==="session"){
      setTempOverrides(p=>({...p,[id]:{smartObjects:sos,groups:grps,noDesign:nd}}));
      if(changed)setGenOut(p=>{const n={...p};Object.keys(n).forEach(k=>{if(k.includes(id))delete n[k]});return n});
    }else{
      // "thisonly" — only override this specific mockup instance
      setMockupSOOverrides(p=>({...p,[mk]:{smartObjects:sos,groups:grps,noDesign:nd}}));
      if(changed&&mk)setGenOut(p=>{const n={...p};delete n[mk];return n});
    }
    setEditResultSO(null);
  };
  // O25b — resolve the effective crop for one (mk, tplId, group) across the
  // three session scopes. Precedence: this-mockup ▸ this-template ▸ batch ▸
  // none. Returns a rect or null.
  const resolveCropForGroup=(mk,tplId,g)=>{
    const a=mockupCrop[mk]&&mockupCrop[mk][g];
    if(a&&!isFullCrop(a))return a;
    const cc=templateCrop[tplId]&&templateCrop[tplId][g];
    if(cc&&!isFullCrop(cc))return cc;
    const b=batchCrop&&batchCrop[g];
    if(b&&!isFullCrop(b))return b;
    return null;
  };
  // Populate so.crop on an effective template's smart objects from the
  // resolver, just before bake. If nothing resolves, returns eff UNCHANGED
  // (same reference) so the uncropped path stays byte-identical.
  const applyCropToTpl=(eff,mk)=>{
    const sos=eff.smartObjects||[];
    let any=false;
    const newSos=sos.map(so=>{
      const cr=resolveCropForGroup(mk,eff.id,so.group);
      if(cr){any=true;return{...so,crop:cr};}
      return so;
    });
    return any?{...eff,smartObjects:newSos}:eff;
  };
  // O25b — crop save from CropScopeModal. Writes the per-group crop to the
  // chosen SESSION store and flushes genOut with the SAME pattern the SO
  // editor uses for its parallel scope (one tile / k.includes(tplId) / all).
  // Full-frame crops are dropped (reset → uncropped). NO persistence.
  const saveCropScoped=(cropByGroup,scope)=>{
    if(!cropModal)return;
    const mk=cropModal.mk;const tplId=cropModal.tpl.id;
    const clean={};
    for(const[g,cr]of Object.entries(cropByGroup||{})){if(!isFullCrop(cr))clean[g]={sx:cr.sx,sy:cr.sy,sw:cr.sw,sh:cr.sh};}
    if(scope==="thisonly"){
      setMockupCrop(p=>({...p,[mk]:clean}));
      setGenOut(p=>{const n={...p};delete n[mk];return n});
    }else if(scope==="template"){
      setTemplateCrop(p=>({...p,[tplId]:clean}));
      setGenOut(p=>{const n={...p};Object.keys(n).forEach(k=>{if(k.includes(tplId))delete n[k]});return n});
    }else{ // batch — applies to every tile
      setBatchCrop(clean);
      setGenOut({});
    }
    setCropModal(null);
  };
  // The ONE foreground editor always saves back a foregroundMask.
  const saveResultFG=data=>{
    const id=editResultFG.id;
    setSaved(p=>{const u=p.map(s=>s.id===id?{...s,foregroundMask:data}:s);const f=u.find(s=>s.id===id);if(f)saveTplSync(f);return u});
    setMockups(p=>p.map(m=>m.id===id?{...m,foregroundMask:data}:m));
    setGenOut(p=>{const n={...p};delete n[id];return n});
    setEditResultFG(null);
  };
  const saveFG=data=>{const id=editMask.id;setMockups(p=>p.map(m=>m.id===id?{...m,foregroundMask:data}:m));setSaved(p=>{const u=p.map(s=>s.id===id?{...s,foregroundMask:data}:s);const f=u.find(s=>s.id===id);if(f)saveTplSync(f);return u});setEditSavedSO(p=>p&&p.id===id?{...p,foregroundMask:data}:p);setEditMask(null)};
  const delSaved=id=>{delTplSync(id);setSaved(p=>p.filter(s=>s.id!==id));setSelSaved(p=>{const n=new Set(p);n.delete(id);return n});setTplLimit(p=>({...p,used:Math.max(0,p.used-1)}));pruneColorLinksForDeleted(new Set([id]))};
  // Bulk-delete the CURRENTLY selected saved templates (reuses selSaved — the same
  // selection that saves a set). Local removal (tombstone + IndexedDB) per id, then
  // ONE batched server delete for the whole selection — never a per-id fan-out (N
  // concurrent whole-doc deletes 502'd and could clobber the tombstone set →
  // resurrection). Set-selection semantics for saving are untouched.
  const deleteSelectedTemplates=()=>{
    const ids=[...selSaved];
    if(!ids.length)return;
    if(!window.confirm(`Delete ${ids.length} selected template${ids.length===1?"":"s"}? This can't be undone.`))return;
    ids.forEach(id=>{addTombstone(id);dbDel(TPL,id).catch(()=>{})}); // local: tombstone + IndexedDB (the per-id server call is replaced by the batch below)
    serverDeleteTemplatesMany(ids);
    const idset=new Set(ids);
    setSaved(p=>p.filter(s=>!idset.has(s.id)));
    setSelSaved(new Set());
    setActiveProfId(null);
    setTplLimit(p=>({...p,used:Math.max(0,p.used-ids.length)}));
    pruneColorLinksForDeleted(idset);
  };

  // Export a saved template back out as a .psd (lazy-loads the ag-psd writer so
  // non-exporters pay zero bytes). Base photo → raster; each zone → a true
  // Smart Object; foreground → real garment/hair pixels on top. Downloads named
  // from the template. Round-trips back through the importer.
  const doExportPsd=async tpl=>{
    if(psdExp?.id)return;
    setPsdExp({id:tpl.id});
    try{
      const{exportTemplateToPsd}=await import("./psdExport");
      const{buffer,filename,warnings}=await exportTemplateToPsd(migrate(tpl));
      const url=URL.createObjectURL(new Blob([buffer],{type:"image/vnd.adobe.photoshop"}));
      const a=document.createElement("a");a.href=url;a.download=filename;document.body.appendChild(a);a.click();document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url),2000);
      setPsdExp(warnings&&warnings.length?{warn:`Exported "${filename}" · ${warnings.join(" · ")}`}:{done:`Exported "${filename}"`});
    }catch(e){
      setPsdExp({error:e&&e.message?e.message:"Couldn't export this template."});
    }
  };

  // One-time migration: earlier PSD imports stored above-design layers as a
  // separate `foregroundImage` (drawn on top). That path is gone — every
  // foreground is now a foregroundMask. A lingering foregroundImage's alpha IS
  // the hair's shape, and the base photo already contains the hair, so convert
  // it to a foregroundMask (unioned with any existing clip mask), drop the old
  // field, and persist. Keeps already-imported templates rendering their hair.
  const fgMigRef=useRef(false);
  useEffect(()=>{
    if(fgMigRef.current)return;
    const needy=saved.filter(s=>s.foregroundImage);
    if(!needy.length)return;
    fgMigRef.current=true;
    Promise.all(needy.map(s=>new Promise(res=>{
      const im=new Image();
      im.onload=()=>{
        const cv=document.createElement("canvas");cv.width=im.naturalWidth;cv.height=im.naturalHeight;const cx=cv.getContext("2d");
        cx.drawImage(im,0,0); // alpha = foreground (hair) shape
        const finish=()=>{const d=cx.getImageData(0,0,cv.width,cv.height);const dd=d.data;for(let i=0;i<dd.length;i+=4){dd[i]=dd[i+1]=dd[i+2]=0;}cx.putImageData(d,0,0);res({id:s.id,mask:cv.toDataURL("image/png")});};
        if(s.foregroundMask){const em=new Image();em.onload=()=>{cx.drawImage(em,0,0);finish()};em.onerror=finish;em.src=s.foregroundMask;}else finish();
      };
      im.onerror=()=>res({id:s.id,mask:s.foregroundMask||null});
      im.src=s.foregroundImage;
    }))).then(results=>{
      const map=new Map(results.map(r=>[r.id,r.mask]));
      const conv=s=>{const{foregroundImage,...rest}=s;return{...rest,foregroundMask:map.get(s.id)};};
      setSaved(p=>{const u=p.map(s=>map.has(s.id)?conv(s):s);u.forEach(s=>{if(map.has(s.id))saveTplSync(s)});return u;});
      setMockups(p=>p.map(m=>map.has(m.id)?conv(m):m));
    });
  },[saved]);

  /* Profiles - now save sortOrder too */
  // A set carries its selected templates AND (optionally) ONE library video.
  // Membership rides the working selection (selSaved + selVideoIds), exactly like
  // templates — whatever's selected joins the set on save. A set holds ONE video,
  // so save is blocked (UI shows the message) when >1 video is selected; exactly
  // 1 joins the set, 0 saves without a video.
  const saveProfi=async()=>{if(!profName.trim()||(selSaved.size===0&&selVideoIds.size===0)||selVideoIds.size>1)return;
    // Flush any pending (debounced) template syncs FIRST, so the gate below reads the real
    // server state — a mockup still queued in scheduleTemplateSync has been saved locally but
    // not yet attempted on the server, and must not slip past the gate as if it were backed up.
    // surface:false — the gate is the single voice here, not the flush's own alert.
    await flushTemplateSync({surface:false});
    // GATE: never write a set that references a mockup the server didn't store. That dangling id
    // is invisible when the set is reused elsewhere (Speedrun picker resolves only server-persisted
    // templates) — the silent loss this closes. The message is now REASON-ACCURATE: a transient
    // 502/500 is a hiccup to retry, NOT a full library (the old hard-coded "library is full" string
    // mis-reported contention as fullness and cost two diagnoses). See _failedServerSaves reasons.
    const _unsaved=[...selSaved].filter(id=>_failedServerSaves.has(String(id)));
    if(_unsaved.length){
      const reasons=_unsaved.map(id=>_failedServerSaves.get(String(id)));
      const nOver=reasons.filter(r=>r==="oversize").length;
      const nCap=reasons.filter(r=>r==="cap").length;
      const nTrans=_unsaved.length-nOver-nCap;
      const bits=[];
      if(nTrans)bits.push(`${nTrans} ${nTrans>1?"haven't":"hasn't"} finished saving — a temporary server hiccup, not a full library. Reload the studio to let ${nTrans>1?"them":"it"} retry, then save this set again.`);
      if(nOver)bits.push(`${nOver} ${nOver>1?"are":"is"} too large to back up — re-save ${nOver>1?"them":"it"} at a smaller size and upload again.`);
      if(nCap)bits.push(`your mockup library is full — delete some unused mockups to make room.`);
      alert(`This set can't be saved yet: ${bits.join(" ")} Otherwise ${_unsaved.length>1?"those mockups":"that mockup"} won't appear when you reuse the set.`);
      return;
    }
    const tplIds=[...selSaved];const sortMap={};saved.forEach(s=>{if(selSaved.has(s.id))sortMap[s.id]=s.sortOrder??999});const p={id:`prof_${Date.now()}`,name:profName.trim(),templateIds:tplIds,sortMap,createdAt:Date.now()};if(selVideoIds.size===1)p.videoId=[...selVideoIds][0];removeProfileTombstone(p.id);dbPut(PROF,p).then(()=>{const newProfs=[...profs,p];setProfs(newProfs);setProfName("");setShowProfSave(false);setActiveProfId(p.id);scheduleProfilesSync(newProfs)}).catch(()=>{})};
  const delProfi=id=>{
    addProfileTombstone(id); // queue + block resurrection; retried on the next authed load
    dbDel(PROF,id).then(()=>{
      const newProfs=profs.filter(p=>p.id!==id);
      setProfs(newProfs);
      if(activeProfId===id)setActiveProfId(null);
      // Authoritative, honest server delete (brief 5.17 §2). No token → stays queued via the
      // tombstone. Token present but the server refused → tell the user rather than silently
      // implying it synced (the 5.8 lying-green lesson).
      if(hasSyncToken())serverDeleteProfile(id).then(ok=>{if(!ok)window.alert("This set was removed here, but couldn't be removed from the cloud backup just now — it'll retry automatically next time the studio loads.")});
    }).catch(()=>{})
  };
  const applyProf=prof=>{setSelSaved(new Set(prof.templateIds.filter(id=>saved.some(s=>s.id===id))));setSelVideoIds(prof.videoId?new Set([prof.videoId]):new Set());if(prof.sortMap){setSaved(p=>p.map(s=>prof.sortMap[s.id]!=null?{...s,sortOrder:prof.sortMap[s.id]}:s))}setActiveProfId(prof.id)};
  // Set the active set's video directly (the one-click path): persists to the
  // profile immediately. videoId null clears it. Enforces one video per set.
  const setActiveSetVideo=(profileId,videoId)=>{setProfs(prev=>{const next=prev.map(p=>p.id===profileId?setProfileVideo(p,videoId):p);const tgt=next.find(p=>p.id===profileId);if(tgt)dbPut(PROF,tgt).catch(()=>{});scheduleProfilesSync(next);return next;});if(activeProfId===profileId)setSelVideoIds(videoId?new Set([videoId]):new Set());};

  // Save the colour→mockup links onto a set (mirrors setActiveSetVideo's persist
  // path: IndexedDB + server). colorLinks null/empty clears the field.
  const saveColorLinks=(profileId,colorLinks)=>{
    setProfs(prev=>{
      const next=prev.map(p=>p.id===profileId?{...p,colorLinks:(colorLinks&&(hasAssignments(colorLinks)||colorLinks.templateId))?colorLinks:undefined}:p);
      const tgt=next.find(p=>p.id===profileId);
      if(tgt)dbPut(PROF,tgt).catch(()=>{});
      scheduleProfilesSync(next);
      return next;
    });
    setColorLinkProfId(null);
  };

  // Prune colour links referencing now-deleted mockups from EVERY set (never a
  // dangling ref). Called after a template is deleted. Persists only the sets
  // that actually changed.
  const pruneColorLinksForDeleted=(deletedIdSet)=>{
    setProfs(prev=>{
      let touched=false;
      const next=prev.map(p=>{
        if(!p.colorLinks)return p;
        const pruned=pruneColorLinks(p.colorLinks,new Set((p.templateIds||[]).filter(id=>!deletedIdSet.has(id))));
        if(pruned===p.colorLinks)return p;
        touched=true;dbPut(PROF,{...p,colorLinks:pruned}).catch(()=>{});
        return {...p,colorLinks:pruned};
      });
      if(touched)scheduleProfilesSync(next);
      return touched?next:prev;
    });
  };

  const togSaved=id=>{setSelSaved(p=>{const n=new Set(p);if(n.has(id))n.delete(id);else n.add(id);return n});setActiveProfId(null)};
  // Toggle a video in the working multi-select (like togSaved for templates).
  // Clears the active set pointer so the selection reflects a fresh, unsaved set.
  const togVideo=id=>{setSelVideoIds(p=>{const n=new Set(p);if(n.has(id))n.delete(id);else n.add(id);return n});setActiveProfId(null)};
  const selAll=()=>{if(selSaved.size===saved.length){setSelSaved(new Set());setActiveProfId(null)}else setSelSaved(new Set(saved.map(s=>s.id)))};

  // Shared by the file-picker (upDesigns) and the drag-and-drop box. Filters to
  // images (the picker uses accept=image/*, but a drop can carry anything) then
  // downsamples on upload — keeping 50 native-res designs (3000-6000px, 5-20MB
  // each) in React state alone consumed 700MB-1GB before the user even clicked
  // Generate, crashing Chrome at scale. 2000px is Etsy's recommended target so
  // this is no quality loss.
  const addDesignFiles=async files=>{
    const list=Array.from(files||[]).filter(f=>/^image\//.test(f.type)||/\.(png|jpe?g|webp|gif|avif)$/i.test(f.name||""));
    for(const f of list){
      const src=await readAndDownsample(f,{maxDim:2000,jpegQuality:0.92});
      if(!src)continue;
      setDesigns(p=>{
        // Single-design convenience: a LONE first design (empty set + this upload is a
        // single file) pre-selects ✦ All Groups, so one design lands on every mockup with
        // no manual pick. A multi-file upload, or adding to an existing set, keeps the manual
        // "— Assign —" default (group=null) — a pair/batch's group choice matters there. We
        // key on list.length (the upload batch), not the live count, so an incremental
        // multi-file add never briefly looks solo mid-loop.
        const solo=p.length===0&&list.length===1;
        return[...p,{id:Date.now()+Math.random(),name:f.name,src,group:solo?"all":null}];
      });
    }
  };
  const upDesigns=async e=>{
    // Snapshot to an array BEFORE clearing the input: e.target.files is a LIVE FileList
    // bound to the input, so e.target.value="" empties it — reading it after the reset
    // (addDesignFiles runs async) would see zero files. Copy first (matches upMock).
    const files=Array.from(e.target.files||[]);
    e.target.value="";
    await addDesignFiles(files);
  };
  const rmDesign=id=>setDesigns(p=>p.filter(d=>d.id!==id));
  const assignDG=(did,gid)=>setDesigns(p=>p.map(d=>d.id===did?{...d,group:gid}:d));
  // Break a pair the detection got wrong: clear pairId/pairPrimary on BOTH members
  // (mirror of the suite's unpair) so they drop back to two independent designs on
  // the legacy per-design path. Manual escape hatch for the auto-detected pipeline pair.
  const unpairDesign=did=>setDesigns(p=>{const d=p.find(x=>x.id===did);const pid=d&&d.pairId;if(!pid)return p;setPairRouting(prev=>{if(!prev[pid])return prev;const n={...prev};delete n[pid];return n;});return p.map(x=>x.pairId===pid?{...x,pairId:undefined,pairPrimary:undefined}:x)});

  // Bulk-selection of designs for fast group assignment. The user said:
  // "I should be able to just click and mark the ones I want and then assign
  // all at once to a single group". A Set of design IDs that are checked.
  const[selectedDesignIds,setSelectedDesignIds]=useState(()=>new Set());
  const toggleDesignSelected=did=>setSelectedDesignIds(prev=>{
    const next=new Set(prev);
    if(next.has(did))next.delete(did);else next.add(did);
    return next;
  });
  const selectAllDesigns=()=>setSelectedDesignIds(new Set(designs.map(d=>d.id)));
  const clearDesignSelection=()=>setSelectedDesignIds(new Set());
  const bulkAssignGroup=gid=>{
    if(selectedDesignIds.size===0)return;
    setDesigns(p=>p.map(d=>selectedDesignIds.has(d.id)?{...d,group:gid}:d));
    // Keep the selection so user can quickly reassign or clear with one click
  };

  const gen=()=>{
    // Reset in-memory genOut, hide it from the UI, and explicitly clear
    // the persisted genOut record so resume after this fresh generation
    // doesn't surface mockups from the previous batch.
    setGenOut({});
    // A fresh "Generate All" is a clean slate — clear any prior per-mockup/per-set
    // removals so the full batch renders (and never gets stuck behind a stale
    // suppression whose genOut was just wiped).
    setRemovedMks(new Set());
    setRemovedSets(new Set());
    setShowRes(true);
    dbDel(SESS,GENOUT_ID).catch(()=>{});
  };
  const onReady=useCallback((k,data)=>{setGenOut(p=>({...p,[k]:data}))},[]);
  // Rebuild the effective template a tile renders for key `mk` (SO overrides +
  // per-group crops), so a headless composite matches the on-screen tile exactly.
  const effTplForKey=(tpl,mk,withCrop)=>{
    const mkov=mockupSOOverrides[mk];
    const eff0=mkov?{...tpl,...mkov,smartObject:(mkov.smartObjects||[])[0]||null}:tpl;
    return withCrop?applyCropToTpl(eff0,mk):eff0;
  };
  // The COMPLETE list of mockups a download should contain — one entry per
  // expected tile whether or not it ever rendered. `si<0` = standalone-only mode
  // (no sets). Returns {t,k,gdm,dc,effTpl,name} sorted + numbered like the grid.
  const buildDownloadItems=(set,si)=>{
    if(!set){ // no design sets — standalones only
      const sorted=[...standalones].filter(t=>!removedMks.has(t.id)).sort((a,b)=>(a.sortOrder??999)-(b.sortOrder??999));
      return sorted.map((t,i)=>{
        const num=t.sortOrder&&t.sortOrder<999?String(t.sortOrder).padStart(2,"0"):String(i+1).padStart(2,"0");
        return{t,k:t.id,gdm:{},dc:mockupDCs[t.id]||globalDC,effTpl:t,name:`${num}.png`};
      });
    }
    if(removedSets.has(set.id))return []; // a member-removed set contributes nothing to the ZIP
    const sdc=setDCs[set.id];
    const sName=(setNames[set.id]||`set${si+1}`).replace(/[^a-zA-Z0-9_-]/g,"_");
    const all=[
      ...setDesignItems(set).filter(({k})=>!removedMks.has(k)).map(({tpl,k,gdm})=>({t:tpl,k,gdm,withCrop:true})),
      ...standalones.filter(t=>!removedMks.has(`${set.id}_sa_${t.id}`)).map(t=>({t,k:`${set.id}_sa_${t.id}`,gdm:{},withCrop:false})),
    ].sort((a,b)=>(a.t.sortOrder??999)-(b.t.sortOrder??999));
    return all.map((item,idx)=>{
      const num=item.t.sortOrder&&item.t.sortOrder<999?String(item.t.sortOrder).padStart(2,"0"):String(idx+1).padStart(2,"0");
      return{...item,dc:mockupDCs[item.k]||sdc||globalDC,effTpl:effTplForKey(item.t,item.k,item.withCrop),name:`${sName}_${num}.png`};
    });
  };
  // Resolve one item's final data URL — rendered tile's genOut cache when present,
  // else composited HEADLESS so scroll/render state can never drop it from the ZIP.
  const resolveItemDataUrl=async(item)=>{
    const layers=genOut[item.k];
    if(layers)return await compositeWithDC(layers,item.dc);
    return await compositeMockupHeadless(item.effTpl,item.gdm,item.dc);
  };
  // Composite EVERY item (with progress), returning the ZIP payload. Any offscreen
  // mockup missing from genOut is regenerated headless rather than silently skipped.
  const buildZipPayload=async(items,onStep)=>{
    const o=[];
    for(let i=0;i<items.length;i++){
      try{ const dataUrl=await resolveItemDataUrl(items[i]); if(dataUrl)o.push({name:items[i].name,dataUrl}); }
      catch(e){ console.error("[download] compose failed for",items[i].k,e); }
      onStep(i+1);
    }
    return o;
  };
  const doDownload=async()=>{
    setDling(true);
    const items=designSets.length===0
      ? buildDownloadItems(null,-1)
      : designSets.flatMap((set,si)=>buildDownloadItems(set,si));
    const expected=items.length;
    setDlProg({done:0,total:expected});
    const o=await buildZipPayload(items,(done)=>setDlProg({done,total:expected}));
    console.log(`[download] Download All — expected ${expected}, zipped ${o.length}`);
    if(o.length<expected)console.warn(`[download] ${expected-o.length} mockup(s) could not be composited and were omitted`);
    // ZIP is the only download mode; dlAll stays purely as a ZIP-failure fallback.
    try{ await dlZip(o,"mockups.zip"); }
    catch(e){ console.error("ZIP failed — falling back to per-file download",e); await dlAll(o); }
    setDling(false);setDlProg({done:0,total:0});
  };
  const downloadSet=async(set,si)=>{
    setDling(true);
    const sName=(setNames[set.id]||`set${si+1}`).replace(/[^a-zA-Z0-9_-]/g,"_");
    const items=buildDownloadItems(set,si);
    const expected=items.length;
    setDlProg({done:0,total:expected});
    const o=await buildZipPayload(items,(done)=>setDlProg({done,total:expected}));
    console.log(`[download] Download Set "${sName}" — expected ${expected}, zipped ${o.length}`);
    if(o.length<expected)console.warn(`[download] ${expected-o.length} mockup(s) in set "${sName}" could not be composited and were omitted`);
    try{ await dlZip(o,`${sName}.zip`); }
    catch(e){ console.error("ZIP failed — falling back to per-file download",e); await dlAll(o); }
    setDling(false);setDlProg({done:0,total:0});
  };

  // Pipeline push
  //
  // Sends generated mockups to the parent Suite via postMessage. With many
  // mockups (e.g. 7 designs × 10 templates = 70) the naive approach of putting
  // all base64 PNG data URLs into a single array and posting it crashed the tab
  // — postMessage structure-clones the entire payload, so the source AND the
  // destination need a copy in memory simultaneously. 70 × ~3MB PNGs ≈ 200MB,
  // doubled during clone ≈ 400MB → OOM (Chrome "Aw, Snap! error code 5").
  //
  // Two changes to make this safe at any scale:
  //   1. Pre-compress each mockup to 2000px max + JPEG q=0.92 BEFORE sending.
  //      The Suite already compresses again to 2000px JPEG q=0.85 before
  //      uploading to Etsy, so doing this earlier just moves a step left
  //      and saves an order of magnitude of memory.
  //   2. Send mockups in chunks of CHUNK_SIZE via PIPELINE_MOCKUPS_CHUNK
  //      messages, with a small await between chunks so the receiver's main
  //      thread can drain the queue. The first chunk replaces the receiver's
  //      mockup array; subsequent chunks append; the last chunk's isLast
  //      flag triggers Suite navigation to /pipeline.
  const sendToPipeline=async()=>{
    // No parent Suite to receive the handoff (Studio opened as its own tab, not
    // embedded in the Suite). THROW rather than silently resolving — a silent
    // return let the caller report "✓ Sent N" while nothing left the tab. Mirrors
    // the auth-expiry guard below: the send button's catch surfaces this message.
    if(!window.parent||window.parent===window){throw new Error("Open the Mockup Studio from inside the Suite to send to the pipeline — a standalone browser tab has no Suite to send to.");}
    // Never ship an EMPTY/partial handoff on a dead session — that's what rendered a
    // blank box in the Suite. If the token expired, surface it and abort (thrown
    // errors are shown by the send button's catch → alert).
    if(isAuthExpired()||suiteTokenExpired()){markAuthExpired();setAuthExpired(true);throw new Error("Your Suite session has expired — reopen the Mockup Studio from the Suite (sign in again if needed), then send.");}
    const CHUNK_SIZE=8;     // mockups per postMessage batch
    const CHUNK_DELAY_MS=50; // gap between chunks to let receiver process

    // Build the list of mockups. Each mockup carries EITHER a `designId`
    // (single string — the design this mockup is FOR) OR `broadcast: true`
    // (standalone templates like size charts that go on every listing).
    //
    // The earlier `designIds: [array]` model was ambiguous: empty array
    // meant "broadcast" but that was easy to misinterpret. This split is
    // explicit. PublishPage filters with: `m.broadcast || m.designId === design.id`.
    const items=[];
    const standalonesPushed=new Set();
    let pipelineDesignCount=0;     // diagnostic
    let pipelineStandaloneCount=0; // diagnostic

    // The human-readable ZONE/GROUP name a template belongs to (e.g. "light design"),
    // resolved from the template's own captured `groups` via its smart objects — the
    // same source groupsForProfile uses. Sent per mockup so the suite Pipeline page can
    // LABEL each thumb by zone instead of showing it unlabelled. null → no label.
    const groupNameForTemplate=(t)=>{
      const groups=(t&&t.groups)||[];
      for(const so of ((t&&t.smartObjects)||[])){
        if(!so||!so.group)continue;
        const g=groups.find(x=>x.id===so.group);
        if(g&&g.name&&g.name!==so.group)return g.name;
      }
      return null;
    };

    designSets.forEach((set,si)=>{
      if(removedSets.has(set.id))return; // O26 — a member-removed set never ships
      // For the typical "one design per set" case, set.designs has exactly one
      // entry. Multi-group templates (front + back of the same shirt) would
      // have multiple entries — but those still represent ONE listing, not
      // multiple. Pick the first design id as the canonical owner; in
      // practice users put one design per group anyway.
      //
      // NOTE: the public set object exposes the design map as `designs`
      // (line ~1069: `sets.map(s => ({...,designs:s.combo}))`). An earlier
      // version of this file read `set.combo` which is undefined on the
      // exposed object, dropping every design mockup silently.
      const setDesignIds=Object.values(set.designs||{}).map(d=>d?.id).filter(Boolean);
      const primaryDesignId=setDesignIds[0]||null;

      setDesignItems(set).forEach((item,ti)=>{
        const t=item.tpl;const k=item.k;
        if(removedMks.has(k))return; // O26 — a member-removed mockup never ships
        const layers=genOut[k];
        if(layers){
          // Always push design mockups so they make it into the pipeline.
          // designId may be null if we can't resolve it — PublishPage's
          // legacy setIndex fallback will route by position in that case.
          items.push({
            templateId:t.id,
            templateName:t.name,
            setLabel:set.label,
            setIndex:si,
            designId:primaryDesignId,
            broadcast:false,
            _raw:typeof layers==="string"?layers:layers.full,
            sortOrder:t.sortOrder??ti,
            noDesign:false,
            groupName:groupNameForTemplate(t),
          });
          pipelineDesignCount++;
        }
      });
      // Standalones broadcast to every listing — pushed once
      standalones.forEach((t,ti)=>{
        if(standalonesPushed.has(t.id))return;
        const k=`${set.id}_sa_${t.id}`;
        if(removedMks.has(k))return; // O26 — a member-removed standalone instance never ships
        const layers=genOut[k];
        if(layers){
          standalonesPushed.add(t.id);
          items.push({
            templateId:t.id,
            templateName:t.name,
            setLabel:"Standalone",
            setIndex:-1,
            designId:null,
            broadcast:true,                     // ← appears on every listing
            _raw:typeof layers==="string"?layers:layers.full,
            sortOrder:t.sortOrder??ti+100,
            noDesign:true,
            groupName:groupNameForTemplate(t),
          });
          pipelineStandaloneCount++;
        }
      });
    });

    // Diagnostic: log what we're about to send so we can debug routing on the
    // Suite side. Easy to spot if designs aren't mapping correctly.
    console.log("[Mockup Studio → Pipeline] sending", {
      totalMockups: items.length,
      perDesign: pipelineDesignCount,
      standalones: pipelineStandaloneCount,
      designIdsSeen: Array.from(new Set(items.map(i=>i.designId).filter(Boolean))),
      sampleMockup: items[0]?{
        templateName:items[0].templateName,
        designId:items[0].designId,
        broadcast:items[0].broadcast,
      }:null,
    });

    // Nothing to send. If the session died this is the expiry case (handled above);
    // otherwise it's a genuine "no generated mockups" — say so instead of silently
    // posting nothing (which navigated the Suite to a blank pipeline).
    if(items.length===0){throw new Error("No mockups to send — generate your mockups first, and check that none of your sets were removed. (If your templates didn't load, reopen the Studio from the Suite.)");}

    // B — push the ACTIVE SET's listing video FIRST, and AWAIT it, BEFORE the mockup
    // chunks. The mockups' LAST chunk makes the suite navigate to /pipeline, which
    // UNMOUNTS this studio iframe — so emitting the video after the mockups (as it was)
    // let navigation kill the async reassembly mid-flight and the bytes never reached
    // the suite (the manual "video didn't arrive" timeout). Emitting here, while the
    // iframe is still mounted and no navigation has fired, guarantees the suite receives
    // and stores the chunks (keyed by profileId) before publish. No-op without a set
    // video. Chunks it for big clips; the suite holds the bytes in IndexedDB and uploads
    // them once at publish — parity with 1-Click.
    if(activeProfId){
      const ap=(profs||[]).find(p=>p.id===activeProfId);
      if(ap&&ap.videoId){
        setPipelineProgress({done:0,total:items.length});
        await emitProfileVideo(activeProfId,null);
      }
    }

    const totalChunks=Math.ceil(items.length/CHUNK_SIZE);
    setPipelineProgress({done:0,total:items.length});

    // The active set's colour→mockup links ride the FIRST chunk (alongside
    // profileId), pruned to the mockups actually sent so no ref dangles. Part 1:
    // additive only — today's suite ignores it (no consumer until part 2).
    const activeProf=(profs||[]).find(p=>p.id===activeProfId);
    const firstChunkColorLinks=activeProf?colorLinksForPayload(activeProf.colorLinks,new Set(items.map(i=>i.templateId))):null;

    for(let ci=0;ci<totalChunks;ci++){
      const slice=items.slice(ci*CHUNK_SIZE,(ci+1)*CHUNK_SIZE);
      // Compress just this chunk's mockups, in parallel within the chunk.
      const compressed=await Promise.all(slice.map(async(it)=>{
        const imageDataUrl=await compressMockupForPipeline(it._raw);
        const{_raw,...rest}=it;
        return{...rest,imageDataUrl};
      }));
      const isFirst=ci===0;
      const isLast=ci===totalChunks-1;
      window.parent.postMessage({
        type:"PIPELINE_MOCKUPS_CHUNK",
        source:"creative-seller-pipeline",
        mockups:compressed,
        chunkIndex:ci,
        totalChunks,
        isFirst,
        isLast,
        // B — the ACTIVE SET/profile these mockups came from (null for an ad-hoc
        // selection). Lets the suite resolve + attach this set's listing video on the
        // MANUAL publish path. Additive; the suite ignores it when absent.
        profileId:activeProfId||null,
        // Colour→mockup links, FIRST chunk only. Additive; no suite consumer yet.
        ...(isFirst&&firstChunkColorLinks?{colorLinks:firstChunkColorLinks}:{}),
      },"*");
      setPipelineProgress({done:Math.min(items.length,(ci+1)*CHUNK_SIZE),total:items.length});
      // Yield so the parent thread can clone and process before next chunk.
      if(!isLast)await new Promise(r=>setTimeout(r,CHUNK_DELAY_MS));
    }

    // After all mockup chunks land, send the manually-selected listing video
    // (or clear it if none). The manual path rides the SINGLE-message
    // PIPELINE_VIDEO the suite consumes today, whose Etsy leg is body-limited to
    // ~4.5MB binary — so we gate here: a bigger library clip is left to publish
    // via a SET once the suite attach arc ships (honest note surfaced in the UI).
    let manualVideo=null;
    const mv=manualVideoId?(libVideos||[]).find(v=>v.videoId===manualVideoId):null;
    if(mv&&fitsManualSend(mv.sizeBytes)){
      try{
        const dataUrl=await blobToDataUrl(await videoReassembleBlob(mv));
        manualVideo={base64:dataUrl,file_name:mv.file_name,sizeBytes:mv.sizeBytes,durationSec:mv.durationSec!=null?mv.durationSec:null};
      }catch(e){console.warn("Manual video reassembly failed:",e);}
    }
    window.parent.postMessage({
      type:"PIPELINE_VIDEO",
      source:"creative-seller-pipeline",
      video:manualVideo,
    },"*");
    // Return what ACTUALLY left the tab so the button confirms the real count,
    // never the expected tile count (totExp).
    return items.length;
  };

  /* Re-encode a base64 PNG data URL into a smaller JPEG data URL.
     - Caps at 2000px on the longest edge (matches Etsy's expectations)
     - JPEG quality 0.92 (visually lossless for mockups, ~5x smaller than PNG)
     Returns the original data URL on any failure so generation isn't lost. */
  async function compressMockupForPipeline(dataUrl){
    if(!dataUrl||typeof dataUrl!=="string")return dataUrl;
    try{
      const img=await new Promise((res,rej)=>{
        const i=new Image();i.onload=()=>res(i);i.onerror=rej;i.src=dataUrl;
      });
      const MAX=2000;
      let{naturalWidth:w,naturalHeight:h}=img;
      if(w<=MAX&&h<=MAX&&dataUrl.startsWith("data:image/jpeg")){
        return dataUrl; // already small enough and JPEG
      }
      if(w>MAX||h>MAX){
        const r=Math.min(MAX/w,MAX/h);
        w=Math.round(w*r);h=Math.round(h*r);
      }
      const cv=document.createElement("canvas");
      cv.width=w;cv.height=h;
      const ctx=cv.getContext("2d");
      // White background since JPEG doesn't support transparency
      ctx.fillStyle="#ffffff";ctx.fillRect(0,0,w,h);
      ctx.drawImage(img,0,0,w,h);
      return cv.toDataURL("image/jpeg",0.92);
    }catch(e){
      console.warn("compressMockupForPipeline failed, sending raw:",e);
      return dataUrl;
    }
  }

  const standalones=allReady.filter(t=>t.noDesign);
  // Post-generation bulk zone edit — the EXACT mockup keys a bulk-zone apply will clear+rebuild, honoring the
  // editor's scope: a GLOBAL edit fans across every non-removed set, a PER-SET
  // edit stays inside its one set. The regenerate COUNT and the genOut CLEAR both
  // read this one list, so the number shown is the number that actually rebuilds
  // (exact tpl.id match — no substring over-fire). Standalones are never bulk-
  // edited (the editor is fed setDesignItems templates only), so they're excluded.
  const regenKeysFor=(ids)=>{
    const idset=new Set(ids);const keys=[];
    const sets=bulkZoneEditor?.global?designSets:(bulkZoneEditor?.set?[bulkZoneEditor.set]:designSets);
    for(const set of sets){
      if(removedSets.has(set.id))continue;
      for(const it of setDesignItems(set)){if(idset.has(it.tpl.id)&&!removedMks.has(it.k))keys.push(it.k);}
    }
    return keys;
  };
  const countRegenForTemplates=(ids)=>regenKeysFor(ids).length;
  // Apply a bulk zone edit from the results view.
  //  • PER-SET (session only): write one per-INSTANCE override keyed by the full
  //    `${set}_${tpl}` mockup key (member variants included), so ONLY this set's
  //    tiles move — other sets keep their placement. Mirrors saveResultSO's "This
  //    mockup only" path, fanned across the set. A per-set override layers OVER any
  //    global tempOverride for the same template (render: {...tpl,...mkov}), i.e.
  //    per-set silently refines global — matching expectation.
  //  • GLOBAL + session: a template-wide tempOverride (every set, by design).
  //  • GLOBAL + permanent: writes saved templates (+in-memory mockups) so it sticks.
  // Permanent is DISALLOWED per-set (templates are shared → can't be one-set) —
  // hidden in the editor and guarded here. Either way, clear EXACTLY regenKeysFor
  // (the keys the count reported) so shown-count == rebuilt-count.
  const saveBulkResultPlacement=(updates,scope)=>{
    const ids=Object.keys(updates||{});
    if(!ids.length){setBulkZoneEditor(null);return;}
    const perSet=!bulkZoneEditor?.global&&bulkZoneEditor?.set;
    const keys=regenKeysFor(ids);
    if(perSet){
      const set=bulkZoneEditor.set;
      setMockupSOOverrides(p=>{const n={...p};for(const it of setDesignItems(set)){const up=updates[it.tpl.id];if(!up||removedMks.has(it.k))continue;const base=it.tpl;n[it.k]={smartObjects:up,groups:base.groups,noDesign:base.noDesign};}return n;});
    }else if(scope==="session"){
      setTempOverrides(p=>{const n={...p};ids.forEach(id=>{const base=saved.find(s=>s.id===id)||mockups.find(m=>m.id===id)||{};n[id]={smartObjects:updates[id],groups:base.groups,noDesign:base.noDesign};});return n;});
    }else{
      setSaved(p=>{const u=p.map(s=>updates[s.id]?{...s,smartObjects:updates[s.id],smartObject:updates[s.id][0]||null}:s);u.forEach(s=>{if(updates[s.id])saveTplSync(s)});return u;});
      setMockups(p=>p.map(m=>updates[m.id]?{...m,smartObjects:updates[m.id],smartObject:updates[m.id][0]||null}:m));
    }
    setGenOut(p=>{const n={...p};keys.forEach(k=>{delete n[k]});return n;});
    setBulkZoneEditor(null);
  };
  // O26 — expected-tile counts MUST exclude member-removed mockups/sets. Removal
  // deletes the genOut bytes (so totGen drops) and suppresses the tile; if totExp
  // still counted the removed mockup, the progress gate (totGen<totExp) would be
  // unsatisfiable and the Studio would stick at "Rendering 9/10" forever. Mirror
  // the render loop's removedSets/removedMks suppression exactly.
  const liveSets=designSets.filter(s=>!removedSets.has(s.id));
  const totNoDesign=designSets.length>0
    ? liveSets.reduce((sum,set)=>sum+standalones.filter(t=>!removedMks.has(`${set.id}_sa_${t.id}`)).length,0)
    : standalones.filter(t=>!removedMks.has(t.id)).length;
  const totDesignMockups=liveSets.reduce((sum,set)=>sum+setDesignItems(set).filter(it=>!removedMks.has(it.k)).length,0);
  const totExp=totNoDesign+totDesignMockups;
  const totGen=Object.keys(genOut).length;
  // Display-only derivations (read-only; do NOT feed send/download logic).
  // sendToPipeline dedups standalones (broadcast:true → one copy fans out to
  // every listing), so the pipeline receives FEWER items than the per-set
  // physical tile count (totExp). totSent mirrors sendToPipeline's items.length
  // in the only state where the Send button renders (totGen>=totExp, i.e. all
  // generated): design mockups one-per-set + standalones once. standaloneDupes
  // is the per-set standalone copies that are downloaded but not re-sent — the
  // exact gap between "generated" and "sent" that would otherwise read as loss.
  const totSent=totDesignMockups+standalones.length;
  const standaloneDupes=standalones.length*Math.max(0,designSets.length-1);
  const ib=(bg,col)=>({background:bg,border:"none",color:col,borderRadius:5,width:20,height:20,cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",opacity:0.75,transition:"opacity 0.15s"});
  const[pipelineSent,setPipelineSent]=useState(false);
  const[pipelineSending,setPipelineSending]=useState(false);
  const[sentCount,setSentCount]=useState(0); // actual mockups posted (from sendToPipeline) — the confirmation reflects THIS, not totExp
  const[pipelineProgress,setPipelineProgress]=useState({done:0,total:0});

  return<ThemeContext.Provider value={{isDark,toggle:()=>setIsDark(d=>{const n=!d;saveTheme(n?"dark":"light");return n;})}}>
    <div style={{minHeight:"100vh",background:c.bg,fontFamily:"'DM Sans','Segoe UI',sans-serif",color:c.text,transition:"background 0.3s,color 0.3s"}}>
      {authExpired&&<div style={{position:"fixed",inset:0,zIndex:5000,background:"rgba(10,10,20,0.86)",backdropFilter:"blur(6px)",display:"grid",placeItems:"center",padding:16}}>
        <div style={{textAlign:"center",maxWidth:480,padding:"34px 30px",borderRadius:16,background:c.bgCard,border:`1px solid ${c.border}`,boxShadow:"0 20px 60px rgba(0,0,0,0.45)"}}>
          <div style={{fontSize:34,marginBottom:10}}>⏱️</div>
          <h2 style={{margin:"0 0 8px",color:c.text,fontSize:19,fontWeight:700}}>Session expired</h2>
          <p style={{color:c.textSub,fontSize:13,lineHeight:1.55,margin:0}}>Your Suite session timed out, so your mockups and templates can't load or be sent to the pipeline. Please go back to the Suite and <strong>reopen the Mockup Studio</strong> — sign in again first if the Suite also asks you to.</p>
          <p style={{color:c.textMuted,fontSize:11,marginTop:12,marginBottom:0}}>Nothing you saved is lost — your templates are safe on the server and will reappear once you reopen with a fresh session.</p>
        </div>
      </div>}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,700;1,9..40,400&display=swap');@keyframes mg-spin{to{transform:rotate(360deg)}}@keyframes mg-fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.mg-spinner{width:22px;height:22px;border:3px solid rgba(99,102,241,0.3);border-top-color:#6366f1;border-radius:50%;animation:mg-spin 0.8s linear infinite}.mg-fadeUp{animation:mg-fadeUp 0.3s ease}*{box-sizing:border-box}::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${c.scrollThumb};border-radius:3px}`}</style>

      <div style={{borderBottom:`1px solid ${c.border}`,padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",background:c.bgHeader,backdropFilter:"blur(20px)",position:"sticky",top:0,zIndex:100,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:9}}>
          <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#6366f1,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"white"}}>CS</div>
          <div><div style={{fontWeight:700,fontSize:14,color:c.text}}>Mockup Studio</div><div style={{fontSize:9,color:c.textMuted}}>Product photo generator</div></div>
        </div>
        <div style={{display:"flex",gap:2,alignItems:"center"}}>
          {section==="studio"&&STEPS.map((s,i)=>{const en=i===0||(i===1&&canStep2)||(i===2&&canStep3);return<React.Fragment key={s.id}><button onClick={()=>{if(en){setStep(i);if(i!==2){setShowRes(false)/* keep genOut so navigating back to step 2 still shows the previously-generated mockups without forcing a regenerate */}}}} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:7,background:step===i?"rgba(99,102,241,0.15)":"transparent",border:step===i?`1px solid ${c.borderActive}`:"1px solid transparent",color:step===i?"#a5b4fc":step>i?"#34d399":c.textDim,fontSize:10,fontWeight:600,cursor:en?"pointer":"default",opacity:en?1:0.4}}><span style={{fontSize:11}}>{step>i?"✓":s.icon}</span><span>{s.label}</span></button>{i<2&&<div style={{width:14,height:1,background:step>i?"#34d399":c.border,margin:"0 1px"}}/>}</React.Fragment>})}
          <div style={{width:1,height:18,background:c.border,margin:"0 5px"}}/>
          {/* Primary mode switch (Studio ↔ Mockup Bank) — a prominent segmented
              control so the Bank is obviously a distinct destination. */}
          <div style={{display:"inline-flex",padding:3,borderRadius:11,background:isDark?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)",border:`1px solid ${c.border}`,gap:3}}>
            {[{id:"studio",label:"Studio",icon:"🎨"},{id:"bank",label:"Mockup Bank",icon:"🏦"}].map(sec=>{
              const active=section===sec.id;
              return <button key={sec.id} onClick={()=>setSection(sec.id)} title={sec.id==="bank"?"Community proven-mockup bank":"Mockup generator"} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,whiteSpace:"nowrap",transition:"all 0.15s",background:active?"linear-gradient(135deg,#6366f1,#ec4899)":"transparent",color:active?"#fff":c.textSub,boxShadow:active?"0 2px 10px rgba(99,102,241,0.4)":"none"}}><span style={{fontSize:14}}>{sec.icon}</span><span>{sec.label}</span></button>;
            })}
          </div>
          {/* Embedded: the suite's header toggle is the ONLY theme switch — hide this
              local one + its divider (standalone keeps them). */}
          {!IS_EMBEDDED && <><div style={{width:1,height:18,background:c.border,margin:"0 5px"}}/><ThemeToggle/></>}
        </div>
      </div>

      <div style={{maxWidth:1360,margin:"0 auto",padding:"22px 18px"}}>

        {section==="bank"&&<MockupBank isAdmin={isAdmin}/>}

        {/* Resume previous session banner. Surfaces when a saved session is
            found in IndexedDB and the user hasn't dismissed it. Restoring
            applies selSavedIds → selSaved, designs → designs (with their
            group assignments), step → step, setNames → setNames. The user
            still has to click Generate again — output isn't persisted, but
            the tedious setup is. */}
        {section==="studio" && pendingSession && !sessionDismissed && (() => {
          const tplCount = pendingSession.selSavedIds?.length || 0;
          const designCount = pendingSession.designs?.length || 0;
          const genCount = pendingSession.genOut ? Object.keys(pendingSession.genOut).length : 0;
          const ageMin = Math.round((Date.now() - (pendingSession.ts || 0)) / 60000);
          const ageLabel = ageMin < 1 ? "just now"
            : ageMin < 60 ? `${ageMin}m ago`
            : ageMin < 1440 ? `${Math.round(ageMin / 60)}h ago`
            : `${Math.round(ageMin / 1440)}d ago`;
          // If we have generated mockups saved, lead with that — it's the
          // most valuable thing the user is being offered to recover.
          const summary = genCount > 0
            ? `${genCount} generated mockup${genCount === 1 ? "" : "s"} ready to view, plus ${tplCount} template${tplCount === 1 ? "" : "s"} and ${designCount} design${designCount === 1 ? "" : "s"} from your setup.`
            : `${tplCount} template${tplCount === 1 ? "" : "s"} selected, ${designCount} design${designCount === 1 ? "" : "s"} assigned. Picks up where you left off so you don't have to redo your selections.`;
          return (
            <div style={{
              display:"flex",alignItems:"center",gap:14,padding:"12px 16px",marginBottom:18,
              borderRadius:12,
              background:isDark?"rgba(99,102,241,0.10)":"rgba(99,102,241,0.08)",
              border:`1px solid ${isDark?"rgba(99,102,241,0.35)":"rgba(99,102,241,0.30)"}`,
              flexWrap:"wrap"
            }}>
              <span style={{fontSize:20}}>↩️</span>
              <div style={{flex:1,minWidth:200}}>
                <div style={{color:c.text,fontSize:13,fontWeight:700,marginBottom:2}}>
                  Resume your previous session?
                </div>
                <div style={{color:c.textSub,fontSize:11,lineHeight:1.4}}>
                  Last edited {ageLabel} — {summary}
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button
                  onClick={()=>{
                    const ids=new Set(pendingSession.selSavedIds||[]);
                    setSelSaved(ids);
                    // Re-hydrate pairing onto the restored designs: a session saved by a
                    // PRE-pairing build has no pairId, and PIPELINE_STATE already fired
                    // (before this click) so it can't heal these not-yet-applied designs.
                    // pipelinePairRef holds the pipeline's pair metadata → merge it on now.
                    if(Array.isArray(pendingSession.designs)) setDesigns(healPairMeta(pendingSession.designs,pipelinePairRef.current));
                    if(pendingSession.setNames) setSetNames(pendingSession.setNames);
                    if(pendingSession.pairRouting) setPairRouting(pendingSession.pairRouting);
                    // O26 — restore the mockup-deletion suppression sets so deletions survive the
                    // reload. Without this the derivation (designSets×templates) re-renders every
                    // deleted tile and onReady refills genOut — the "deleted mockups come back" bug.
                    if(Array.isArray(pendingSession.removedMks)) setRemovedMks(new Set(pendingSession.removedMks));
                    if(Array.isArray(pendingSession.removedSets)) setRemovedSets(new Set(pendingSession.removedSets));
                    const hasGen=pendingSession.genOut&&Object.keys(pendingSession.genOut).length>0;
                    if(hasGen){
                      setGenOut(pendingSession.genOut);
                      setStep(2);
                      setShowRes(true);
                    }else{
                      if(typeof pendingSession.step==="number") setStep(pendingSession.step);
                    }
                    setPendingSession(null);
                    setSessionDecided(true); // unblock autosave now that state is populated
                  }}
                  style={{
                    padding:"7px 16px",borderRadius:8,border:"none",
                    background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
                    color:"white",fontSize:11,fontWeight:700,cursor:"pointer",
                    boxShadow:"0 2px 10px rgba(99,102,241,0.3)"
                  }}
                >
                  ↻ Resume
                </button>
                <button
                  onClick={()=>{
                    // User wants a clean slate. Clear the session from DB
                    // immediately — autosave will re-create one as soon as
                    // they pick a template or upload a design, but starting
                    // from empty matches their explicit intent.
                    clearSession();
                    setPendingSession(null);
                    setSessionDismissed(true);
                    setSessionDecided(true); // unblock autosave (cleared session means autosave can start fresh)
                  }}
                  style={{
                    padding:"7px 14px",borderRadius:8,
                    border:`1px solid ${c.border}`,
                    background:"transparent",
                    color:c.textSub,fontSize:11,fontWeight:600,cursor:"pointer"
                  }}
                >
                  Start fresh
                </button>
              </div>
            </div>
          );
        })()}

        {/* STEP 1 */}
        {section==="studio"&&step===0&&<div className="mg-fadeUp">
          <div style={{textAlign:"center",marginBottom:24}}><h1 style={{fontSize:24,fontWeight:700,margin:0,color:c.text,display:"inline-flex",alignItems:"center"}}>Upload Your Mockup Templates<Tip id="s1" text="Upload your PRODUCT PHOTOS here — these are the mockup templates (t-shirt photos, flat lays, etc.) that your designs will be placed onto. Do NOT upload your designs here — designs are added in Step 3 after you configure where they go. You can also select from previously saved templates below." openTip={openTip} setOpenTip={setOpenTip}/></h1><p style={{color:c.textMuted,fontSize:12,marginTop:5}}>Upload product photos (not designs). Set sort order numbers for Etsy listing order.</p></div>
          {/* One upload box for photos, Photoshop files AND listing videos.
              images/* + .psd + video/* route by type in handleIntake; ag-psd
              lazy-loads only when a .psd is picked; a video uploads to the
              library and appears as a card below. Busy while a PSD parses. */}
          <input ref={fRef} type="file" accept={`image/*,.psd,${VIDEO_ACCEPT}`} multiple style={{display:"none"}} onChange={upMock}/>
          {/* Video-only picker fired by the empty listing-video placeholder (F2).
              Same intake path as the main box — videos route through the queue. */}
          <input ref={vidRef} type="file" accept={VIDEO_ACCEPT} multiple style={{display:"none"}} onChange={upMock}/>
          <div onClick={()=>{if(!psdImp?.busy)fRef.current?.click()}}
            onDragOver={e=>{e.preventDefault();if(!psdImp?.busy&&!mockDrag)setMockDrag(true)}}
            onDragLeave={e=>{e.preventDefault();if(mockDrag)setMockDrag(false)}}
            onDrop={e=>{e.preventDefault();setMockDrag(false);if(!psdImp?.busy)handleIntake(e.dataTransfer&&e.dataTransfer.files)}}
            style={{border:`2px dashed ${mockDrag?c.uploadBorderHover:c.uploadBorder}`,borderRadius:14,padding:"32px 20px",textAlign:"center",cursor:psdImp?.busy?"wait":"pointer",background:mockDrag?c.uploadBgHover:c.uploadBg,transition:"all 0.3s",marginBottom:18}} onMouseEnter={e=>{e.currentTarget.style.borderColor=c.uploadBorderHover;e.currentTarget.style.background=c.uploadBgHover}} onMouseLeave={e=>{if(!mockDrag){e.currentTarget.style.borderColor=c.uploadBorder;e.currentTarget.style.background=c.uploadBg}}}>
            <div style={{fontSize:26,marginBottom:6}}>📸</div><div style={{color:"#a5b4fc",fontSize:13,fontWeight:600,display:"inline-flex",alignItems:"center"}}>Click or drop to upload mockup templates or a listing video<Tip id="s1-upload" text="Upload product photos (PNG/JPG), a Photoshop .psd, or an Etsy listing video (MP4/MOV/AVI/WEBM/MPEG, 5–15s). A .psd imports as a template — each Smart Object becomes a design zone. Photos become new-mockup cards. A video becomes a library card you can add to a set — your design is NOT applied to it; it publishes as-is alongside your mockups. Not your designs — those go in Step 3." openTip={openTip} setOpenTip={setOpenTip}/></div><div style={{color:c.textMuted,fontSize:10,marginTop:2}}>PNG, JPG (up to {MAX_IMG_MB}MB), PSD (up to {MAX_PSD_MB}MB), or a listing video (up to {MAX_VIDEO_BYTES/1048576}MB, 5–15s) • Your product photos + optional listing video, not your designs</div>
          </div>
          {/* PSD import status (spinner / ✅ summary / ⚠️ error) — sits under the shared box. */}
          {(psdImp?.busy||psdImp?.error||psdImp?.summary)&&<div style={{marginBottom:18}}>
            {psdImp?.busy&&<div style={{marginTop:8,display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:9,background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.25)"}}><div style={{width:14,height:14,border:"2px solid rgba(99,102,241,0.3)",borderTopColor:"#6366f1",borderRadius:"50%",animation:"mg-spin 0.8s linear infinite"}}/><span style={{color:"#a5b4fc",fontSize:11,fontWeight:600}}>{psdImp.msg||"Importing…"}</span></div>}
            {psdImp?.error&&<div style={{marginTop:8,padding:"8px 12px",borderRadius:9,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.35)",display:"flex",alignItems:"flex-start",gap:8}}><span style={{fontSize:13}}>⚠️</span><span style={{color:"#f87171",fontSize:11,fontWeight:600,flex:1}}>{psdImp.error}</span><button onClick={()=>setPsdImp(null)} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:12}}>✕</button></div>}
            {psdImp?.summary&&<div style={{marginTop:8,padding:"8px 12px",borderRadius:9,background:"rgba(52,211,153,0.1)",border:"1px solid rgba(52,211,153,0.35)"}}><div style={{display:"flex",alignItems:"flex-start",gap:8}}><span style={{fontSize:13}}>✅</span><span style={{color:"#34d399",fontSize:11,fontWeight:600,flex:1}}>{psdImp.summary}</span><button onClick={()=>setPsdImp(null)} style={{background:"none",border:"none",color:"#34d399",cursor:"pointer",fontSize:12}}>✕</button></div>{psdImp.warnings&&psdImp.warnings.length>0&&<ul style={{margin:"6px 0 0",paddingLeft:26,color:c.textMuted,fontSize:10,lineHeight:1.5}}>{psdImp.warnings.map((w,i)=><li key={i}>{w}</li>)}</ul>}</div>}
          </div>}
          {mockups.length>0&&<div style={{marginBottom:18}}><span style={{color:c.textSub,fontSize:11,fontWeight:600}}>{mockups.length} new mockup{mockups.length!==1?"s":""}</span><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,marginTop:8}}>{mockups.map(m=><MockupCard key={m.id} mockup={m} onRemove={rmMock} isConfigured={(m.smartObjects||[]).length>0||m.noDesign} sortOrder={m.sortOrder} onSortChange={setSortOrder}/>)}</div></div>}
          {dbOk&&saved.length===0&&<div style={{padding:"14px 16px",borderRadius:14,background:c.savedBg,border:`1px solid ${c.savedBorder}`,marginBottom:18}}>
            <span style={{color:c.textSub,fontSize:11}}>No saved templates yet.</span> <span style={{color:c.textMuted,fontSize:10}}>Templates are saved automatically when you configure smart objects.</span>
          </div>}
          {dbOk&&saved.length>0&&<div style={{padding:16,borderRadius:14,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
              <h3 style={{color:c.text,fontSize:13,fontWeight:700,margin:0}}>📁 Saved Templates</h3>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{color:saved.length>=tplLimit.max?"#f87171":c.textMuted,fontSize:9,fontWeight:600}}>{saved.length}/{tplLimit.max} used</span>
                <div style={{width:60,height:4,borderRadius:2,background:c.border,overflow:"hidden"}}><div style={{width:`${Math.min(100,saved.length/tplLimit.max*100)}%`,height:"100%",borderRadius:2,background:saved.length>=tplLimit.max?"#ef4444":saved.length>tplLimit.max*0.8?"#f59e0b":"#6366f1",transition:"width 0.3s"}}/></div>
              </div>
              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                <button onClick={selAll} style={{padding:"4px 10px",borderRadius:7,border:"1px solid rgba(99,102,241,0.3)",background:selSaved.size===saved.length?"rgba(99,102,241,0.2)":"transparent",color:"#a5b4fc",fontSize:9,fontWeight:600,cursor:"pointer"}}>{selSaved.size===saved.length?"Deselect All":"Select All"}</button>
                {selSaved.size>0&&<button onClick={deleteSelectedTemplates} style={{padding:"4px 10px",borderRadius:7,border:"1px solid rgba(239,68,68,0.4)",background:"rgba(239,68,68,0.15)",color:"#f87171",fontSize:9,fontWeight:700,cursor:"pointer"}}>🗑 Delete {selSaved.size}</button>}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:9}}>
              {saved.map(s=><MockupCard key={s.id} mockup={s} isConfigured selectable selected={selSaved.has(s.id)} onToggleSelect={()=>togSaved(s.id)} sortOrder={s.sortOrder} onSortChange={setSortOrder} syncFailed={failedSaves[String(s.id)]} extraButtons={<div style={{display:"flex",gap:2}} onClick={e=>e.stopPropagation()}><button onClick={()=>setEditSavedSO(s)} title="Edit zones" style={ib("rgba(99,102,241,0.8)","white")} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.75"}>◻</button><button onClick={()=>setEditMask(s)} title="Foreground (mask / imported overlay)" style={ib("rgba(251,146,60,0.8)","white")} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.75"}>✂</button><button onClick={()=>doExportPsd(s)} disabled={!!psdExp?.id} title="Export as .psd (Smart Object zones)" style={{...ib("rgba(16,185,129,0.8)","white"),cursor:psdExp?.id?"wait":"pointer",opacity:psdExp?.id===s.id?1:0.75}} onMouseEnter={e=>{if(!psdExp?.id)e.currentTarget.style.opacity="1"}} onMouseLeave={e=>{if(psdExp?.id!==s.id)e.currentTarget.style.opacity="0.75"}}>{psdExp?.id===s.id?"…":"⬇"}</button><button onClick={()=>delSaved(s.id)} title="Delete" style={ib("rgba(239,68,68,0.8)","white")} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.75"}>✕</button></div>}/>)}
            </div>
          </div>}
          {/* Listing Videos — a video is a library item like any mockup: uploaded
              through the box above, stored server-side (cross-device, no
              compression), and selectable into a set. Shown whenever the studio
              has loaded so the empty-state placeholder is discoverable. */}
          {dbOk&&<div style={{marginTop:14,padding:16,borderRadius:14,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
              <h3 style={{color:c.text,fontSize:13,fontWeight:700,margin:0,display:"inline-flex",alignItems:"center"}}>🎥 Listing Videos<Tip id="lib-video" text="Upload an Etsy listing video (MP4/MOV/AVI/WEBM/MPEG, 5–15s, up to 80MB) through the box above and it becomes a library card here. Your design is NOT applied to it — it uploads to the Etsy listing as-is, alongside your mockups. Add it to a mockup set (select it, then save/attach) so it publishes with every listing from that set. Stored on our servers, so it's available on every device." openTip={openTip} setOpenTip={setOpenTip}/></h3>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{color:videoUsage.bytes>=videoUsage.maxBytes?"#f87171":c.textMuted,fontSize:9,fontWeight:600}}>{videoUsage.count} video{videoUsage.count===1?"":"s"} · {fmtMB(videoUsage.bytes)} / {Math.round(videoUsage.maxBytes/1048576)} MB</span>
              </div>
            </div>
            {videoNotice&&videoNotice.error&&<div style={{marginBottom:10,padding:"8px 12px",borderRadius:9,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.35)",display:"flex",alignItems:"flex-start",gap:8}}><span style={{fontSize:13}}>⚠️</span><span style={{color:"#f87171",fontSize:11,fontWeight:600,flex:1}}>{videoNotice.error}</span><button onClick={()=>setVideoNotice(null)} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:12}}>✕</button></div>}
            {/* Per-file upload rows — one row per picked file with its own bar +
                state. The queue still serializes the actual uploads (race fix);
                this is presentation only. Clean "✓ Done" rows auto-clear. */}
            {videoRows.length>0&&<div style={{marginBottom:10,display:"flex",flexDirection:"column",gap:6}}>
              {videoRows.map(r=>{
                const pct=Math.round((r.pct||0)*100);
                const label=r.state==="queued"?"Queued":r.state==="done"?"✓ Done":r.state==="error"?"Failed":"Uploading";
                const barColor=r.state==="error"?"#ef4444":r.state==="done"?"#34d399":"linear-gradient(90deg,#8b5cf6,#ec4899)";
                const barPct=r.state==="done"?100:r.state==="error"?100:pct;
                return <div key={r.rowId} style={{padding:"8px 12px",borderRadius:9,background:r.state==="error"?"rgba(239,68,68,0.08)":r.state==="done"?"rgba(52,211,153,0.08)":"rgba(139,92,246,0.08)",border:`1px solid ${r.state==="error"?"rgba(239,68,68,0.3)":r.state==="done"?"rgba(52,211,153,0.3)":"rgba(139,92,246,0.25)"}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:r.state==="error"?0:6}}>
                    <span className="pb-text" style={{color:c.text,fontSize:10,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{r.file_name}</span>
                    <span style={{color:r.state==="error"?"#f87171":r.state==="done"?"#34d399":c.textMuted,fontSize:10,fontWeight:600,flexShrink:0}}>{r.state==="uploading"?`${pct}%`:label}</span>
                    {(r.state==="error"||r.warn)&&<button onClick={()=>dismissVideoRow(r.rowId)} style={{background:"none",border:"none",color:r.state==="error"?"#f87171":"#fbbf24",cursor:"pointer",fontSize:12,flexShrink:0}}>✕</button>}
                  </div>
                  {r.state==="error"
                    ? <div style={{color:"#f87171",fontSize:10,fontWeight:600,marginTop:4}}>{r.error}</div>
                    : <><div style={{width:"100%",height:4,borderRadius:2,background:c.border,overflow:"hidden"}}><div style={{width:`${barPct}%`,height:"100%",background:barColor,transition:"width 0.2s"}}/></div>
                       {r.warn&&<div style={{color:"#fbbf24",fontSize:9,fontWeight:600,marginTop:5}}>{r.warn}</div>}</>}
                </div>;
              })}
            </div>}
            {libVideos.length>0
              ? <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:9}}>{libVideos.map(v=><VideoCard key={v.videoId} v={v} selected={selVideoIds.has(v.videoId)} onToggle={()=>togVideo(v.videoId)} onDelete={()=>deleteOneVideo(v.videoId)} getUrl={getVideoUrl}/>)}</div>
              : videoLoading
              ? <div style={{padding:"22px 16px",borderRadius:12,border:`1px dashed ${c.border}`,background:c.uploadBg,textAlign:"center"}}><div style={{width:18,height:18,margin:"0 auto 8px",border:"2px solid rgba(99,102,241,0.3)",borderTopColor:"#6366f1",borderRadius:"50%",animation:"mg-spin 0.8s linear infinite"}}/><div style={{color:c.textSub,fontSize:12,fontWeight:600}}>Loading your videos…</div></div>
              : videoLoadErr
              ? <div style={{padding:"22px 16px",borderRadius:12,border:"1px solid rgba(245,158,11,0.4)",background:"rgba(245,158,11,0.08)",textAlign:"center"}}><div style={{fontSize:22,marginBottom:6}}>⚠️</div><div style={{color:"#fbbf24",fontSize:12,fontWeight:600,marginBottom:8}}>Couldn't load your videos. They're safe on our servers — this was just a hiccup loading the list.</div><button onClick={()=>refreshLibVideos()} style={{padding:"6px 14px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>↻ Retry</button></div>
              : <div onClick={()=>vidRef.current?.click()} role="button" tabIndex={0}
                  onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();vidRef.current?.click()}}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=c.uploadBorderHover;e.currentTarget.style.background=c.uploadBgHover}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=c.uploadBorder;e.currentTarget.style.background=c.uploadBg}}
                  style={{padding:"22px 16px",borderRadius:12,border:`2px dashed ${c.uploadBorder}`,background:c.uploadBg,textAlign:"center",cursor:"pointer",transition:"background 0.15s,border-color 0.15s"}}>
                  <div style={{fontSize:24,marginBottom:6}}>🎥</div>
                  <div style={{color:c.textSub,fontSize:12,fontWeight:600}}>No video added — click to upload, or drop one in the box above.</div>
                  <div style={{color:c.textMuted,fontSize:10,marginTop:5,maxWidth:460,marginLeft:"auto",marginRight:"auto",lineHeight:1.5}}>Your design is <strong>not</strong> applied to the video — it uploads to the Etsy listing as-is, alongside your mockups. Add it to a mockup set so it publishes with every listing from that set.</div>
                </div>}
          </div>}
          {dbOk&&saved.length>0&&<div style={{marginTop:14,padding:"14px 16px",borderRadius:14,background:c.savedBg,border:`1px solid ${c.savedBorder}`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
              <div style={{display:"flex",alignItems:"center",gap:2}}>
                <h3 style={{color:c.text,fontSize:13,fontWeight:700,margin:0,display:"inline-flex",alignItems:"center"}}>📁 Mockup Sets</h3>
                {/* Same Tip component used throughout the studio (smart object
                    editor, design assignment, etc.). Click the ? to toggle —
                    works on touch devices, matches the rest of the UI. */}
                <Tip id="mset" align="left" text="A reusable group of mockup templates. Pick the ones you use together (e.g. all your Comfort Colors tee mockups, or your full mug set), save them with a name, and one click reloads that exact selection on your next project — no more hunting through the library." openTip={openTip} setOpenTip={setOpenTip}/>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {selSaved.size>0&&!showProfSave&&<button onClick={()=>setShowProfSave(true)} style={{padding:"5px 12px",borderRadius:7,border:"1px solid rgba(99,102,241,0.3)",background:"rgba(99,102,241,0.1)",color:"#a5b4fc",fontSize:10,fontWeight:600,cursor:"pointer"}}>💾 Save these {selSaved.size} as a set</button>}
                {selSaved.size>=2&&<button onClick={()=>setLinkedPlacementOpen(true)} title="Move, resize and rotate the design once and have every selected mockup update together — same spot on every shirt." style={{padding:"5px 12px",borderRadius:7,border:"1px solid rgba(99,102,241,0.3)",background:"rgba(99,102,241,0.1)",color:"#a5b4fc",fontSize:10,fontWeight:600,cursor:"pointer"}}>📐 Bulk edit zones ({selSaved.size})</button>}
                <button onClick={()=>setColorLinkPickerOpen(true)} title="Pick a set, then link its variation colours to specific mockup photos" style={{padding:"5px 12px",borderRadius:7,border:"1px solid rgba(99,102,241,0.3)",background:"rgba(99,102,241,0.1)",color:"#a5b4fc",fontSize:10,fontWeight:600,cursor:"pointer"}}>🎨 Link photos to variations</button>
              </div>
            </div>
            {showProfSave&&(()=>{const tooManyVideos=selVideoIds.size>1;const canSave=!!profName.trim()&&!tooManyVideos;return<div style={{marginBottom:10}}><div style={{display:"flex",gap:6,alignItems:"center",padding:"8px 10px",borderRadius:9,background:isDark?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)"}}><input value={profName} onChange={e=>setProfName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveProfi()} placeholder='Set name (e.g. "Comfort Colors Tee Set")' style={{flex:1,padding:"6px 10px",borderRadius:7,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:11,outline:"none"}} autoFocus/><button onClick={saveProfi} disabled={!canSave} style={{padding:"6px 14px",borderRadius:7,border:"none",background:canSave?"linear-gradient(135deg,#6366f1,#8b5cf6)":c.btnSecBg,color:canSave?"white":c.textDim,fontSize:10,fontWeight:700,cursor:canSave?"pointer":"not-allowed"}}>Save</button><button onClick={()=>{setShowProfSave(false);setProfName("")}} style={{padding:"6px 10px",borderRadius:7,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>Cancel</button></div>{tooManyVideos&&<div style={{marginTop:6,padding:"7px 10px",borderRadius:8,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#fbbf24",fontSize:10,fontWeight:600}}>A set can include one video — keep just one selected. ({selVideoIds.size} selected)</div>}</div>;})()}
            {profs.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{profs.map(p=><div key={p.id} title={`${p.templateIds.length} template${p.templateIds.length===1?"":"s"} — click to load`} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",borderRadius:8,background:activeProfId===p.id?"rgba(99,102,241,0.15)":(isDark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)"),border:`1px solid ${activeProfId===p.id?"rgba(99,102,241,0.4)":c.border}`,cursor:"pointer"}} onClick={()=>applyProf(p)}><span className="pb-text" style={{fontSize:10,fontWeight:600,color:activeProfId===p.id?"#a5b4fc":c.text}}>{p.name}</span><span style={{fontSize:8,color:c.textMuted}}>{p.templateIds.length}</span>{p.videoId&&<span title="This set has a listing video" style={{fontSize:9}}>🎥</span>}<button onClick={e=>{e.stopPropagation();setColorLinkProfId(p.id)}} title={hasAssignments(p.colorLinks)?`${Object.keys(p.colorLinks.map).length} colour${Object.keys(p.colorLinks.map).length===1?"":"s"} linked — edit`:"Link colours to mockups"} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,padding:0,opacity:hasAssignments(p.colorLinks)?1:0.55,filter:hasAssignments(p.colorLinks)?"none":"grayscale(1)"}}>🎨{hasAssignments(p.colorLinks)&&<span style={{fontSize:8,color:"#a5b4fc",fontWeight:700,marginLeft:1}}>{Object.keys(p.colorLinks.map).length}</span>}</button><button onClick={e=>{e.stopPropagation();delProfi(p.id)}} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:10,padding:0}}>✕</button></div>)}</div>}
            {colorLinkProfId&&(()=>{const cp=profs.find(p=>p.id===colorLinkProfId);if(!cp)return null;const cpMocks=saved.filter(s=>(cp.templateIds||[]).includes(s.id));return<ColorLinkModal set={cp} mockups={cpMocks} colors={c} isDark={isDark} onSave={cl=>saveColorLinks(cp.id,cl)} onClose={()=>setColorLinkProfId(null)}/>;})()}
            {colorLinkPickerOpen&&<ColorLinkModal set={null} sets={profs} mockupsForSet={s=>saved.filter(m=>(s.templateIds||[]).includes(m.id))} colors={c} isDark={isDark} onSaveForSet={(id,cl)=>{saveColorLinks(id,cl);setColorLinkPickerOpen(false)}} onClose={()=>setColorLinkPickerOpen(false)}/>}
            {/* Per-set video — attach ONE library video to the ACTIVE set (the
                one-click path; persists to the profile immediately). One video
                per set. Bytes live server-side, so it is cross-device. */}
            {(()=>{
              const ap=(profs||[]).find(p=>p.id===activeProfId);
              if(!ap)return null;
              const cur=ap.videoId?(libVideos||[]).find(v=>v.videoId===ap.videoId):null;
              return <div style={{marginTop:12,padding:"10px 12px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                  <span style={{color:c.text,fontSize:11,fontWeight:700,display:"inline-flex",alignItems:"center"}}>🎥 Set Video — “{ap.name}” <span style={{color:c.textMuted,fontWeight:500,fontSize:10,marginLeft:6}}>(optional — one listing video for every listing in this set)</span><Tip id="set-video" text="Attach one video from your library to this set and it publishes as the Etsy listing video on every listing from this set. Your design is NOT applied to it — it uploads as-is. Upload videos through the box in Step 1; they are stored on our servers, so this works on every device." openTip={openTip} setOpenTip={setOpenTip}/></span>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {libVideos.length>0
                      ? <select className="pb-text" value={ap.videoId||""} onChange={e=>setActiveSetVideo(ap.id,e.target.value||null)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:10,fontWeight:600,outline:"none",cursor:"pointer",maxWidth:220}}><option value="">— No video —</option>{libVideos.map(v=><option key={v.videoId} value={v.videoId}>{v.file_name} ({fmtMB(v.sizeBytes)} MB)</option>)}</select>
                      : <span style={{color:c.textMuted,fontSize:10}}>Upload a video in Step 1 to attach one.</span>}
                    {ap.videoId&&<button onClick={()=>setActiveSetVideo(ap.id,null)} style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>Remove</button>}
                  </div>
                </div>
                {cur&&<div style={{marginTop:8,color:c.textMuted,fontSize:10}}>Attached: <strong className="pb-text" style={{color:c.text}}>{cur.file_name}</strong> · {fmtMB(cur.sizeBytes)} MB · {cur.durationSec!=null?`${Number(cur.durationSec).toFixed(1)}s`:"length unverified"} — publishes as-is on every listing from this set (your design is not applied to it).</div>}
                {ap.videoId&&!cur&&<div style={{marginTop:8,padding:"8px 10px",borderRadius:6,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.25)",color:"#fbbf24",fontSize:10}}>The video attached to this set was deleted — pick another above or clear it.</div>}
              </div>;
            })()}
          </div>}
          <div style={{textAlign:"center",marginTop:22,display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            {canStep2&&<button onClick={()=>setStep(1)} style={{padding:"10px 28px",borderRadius:11,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 16px rgba(99,102,241,0.4)"}}>Next: Set Smart Objects →</button>}
            {selSaved.size>0&&<button onClick={()=>setStep(2)} style={{padding:"10px 28px",borderRadius:11,border:"none",background:"linear-gradient(135deg,#34d399,#059669)",color:"white",fontSize:12,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 16px rgba(52,211,153,0.4)"}}>Skip to Generate ({selSaved.size}) ⚡</button>}
          </div>
        </div>}

        {/* STEP 2 */}
        {section==="studio"&&step===1&&<div className="mg-fadeUp">
          <div style={{textAlign:"center",marginBottom:24}}><h1 style={{fontSize:24,fontWeight:700,margin:0,color:c.text}}>Define Smart Object Areas</h1><p style={{color:c.textMuted,fontSize:12,marginTop:5}}>Click each mockup to set zones. Toggle "No Design" for standalone images like size charts.</p></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
            {mockups.map(m=><div key={m.id} style={{position:"relative"}}><MockupCard mockup={m} onConfigure={()=>setEditMock(m)} isConfigured={(m.smartObjects||[]).length>0||m.noDesign}/>{((m.smartObjects||[]).length>0||m.smartObject)&&!m.noDesign&&<button onClick={()=>setEditMask(m)} title="Foreground" style={{position:"absolute",bottom:42,right:7,background:"rgba(99,102,241,0.9)",border:"none",color:"white",borderRadius:6,padding:"2px 8px",cursor:"pointer",fontSize:9,fontWeight:600}}>✂</button>}</div>)}
          </div>
          <div style={{marginTop:16,padding:"10px 14px",borderRadius:11,background:c.statusBarBg,border:`1px solid ${c.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <button onClick={()=>setStep(0)} style={{padding:"7px 16px",borderRadius:8,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:11,fontWeight:600,cursor:"pointer"}}>← Back</button>
              <span style={{color:c.textSub,fontSize:11}}>{cfgd.length}/{mockups.length} configured</span>
            </div>
            {cfgd.length>0&&<button onClick={()=>setStep(2)} style={{padding:"8px 22px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>Next: Generate →</button>}
          </div>
          {editMock&&<MultiSOEditor mockup={editMock} onSave={saveSO} onCancel={()=>setEditMock(null)} globalGroups={globalGroups} onUpdateGlobalGroups={updateGlobalGroups}/>}
          {editMask&&step===1&&<FGEditor mockup={editMask} onSave={saveFG} onCancel={()=>setEditMask(null)}/>}
        </div>}

        {/* STEP 3 */}
        {section==="studio"&&step===2&&<div className="mg-fadeUp">
          {!showRes?<>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:8}}>
              <div><h1 style={{fontSize:24,fontWeight:700,margin:0,color:c.text,display:"inline-flex",alignItems:"center"}}>Assign Designs to Groups<Tip id="s3a" text="Upload your actual designs here and assign each one to a group. Each group's design will be placed into the matching zones on all templates. You can assign multiple designs to the same group to generate multiple sets of mockups. Use 'All Groups' to apply one design across every group." openTip={openTip} setOpenTip={setOpenTip}/></h1><p style={{color:c.textMuted,fontSize:12,marginTop:5}}>{designNeeded.length} templates need designs • {allReady.length-designNeeded.length} standalone</p></div>
              <button onClick={()=>setStep(canStep2?1:0)} style={{padding:"7px 16px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>← Back</button>
            </div>

            {activeGroups.length>0&&<div style={{marginBottom:18}}>
              <h3 style={{color:c.text,fontSize:13,fontWeight:700,marginBottom:10}}>Design Groups ({activeGroups.length})</h3>
              {activeGroups.map(g=>{const specific=designs.filter(d=>d.group===g.id);const allGrp=designs.filter(d=>d.group==="all");const assigned=[...specific,...allGrp];return<div key={g.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"10px 14px",borderRadius:10,background:c.statusBarBg,border:`2px solid ${assigned.length>0?g.color+"60":c.border}`}}>
                <div style={{width:14,height:14,borderRadius:4,background:g.color,flexShrink:0}}/>
                <span className="pb-text" style={{color:c.text,fontSize:12,fontWeight:600,minWidth:80}}>{g.name}</span>
                {assigned.length>0?<div style={{display:"flex",alignItems:"center",gap:4,flex:1,flexWrap:"wrap"}}>
                  {assigned.map(d=><div key={d.id} style={{display:"flex",alignItems:"center",gap:4,padding:"2px 6px",borderRadius:5,background:d.group==="all"?"rgba(99,102,241,0.1)":`${g.color}15`,border:`1px solid ${d.group==="all"?"rgba(99,102,241,0.3)":g.color+"40"}`}}>
                    <img className="pb-img" src={d.src} alt="" style={{width:24,height:24,borderRadius:4,objectFit:"cover"}}/>
                    <span className="pb-text" style={{color:c.textSub,fontSize:9,maxWidth:60,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</span>
                    {d.group==="all"&&<span style={{color:"#a5b4fc",fontSize:7,fontWeight:700}}>ALL</span>}
                    {d.group!=="all"&&<button onClick={()=>assignDG(d.id,null)} style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:9,padding:0}}>✕</button>}
                  </div>)}
                  <span style={{color:c.textMuted,fontSize:9}}>{assigned.length} design{assigned.length!==1?"s":""}</span>
                </div>
                :<span style={{color:c.textMuted,fontSize:10,fontStyle:"italic"}}>No designs — assign below</span>}
              </div>})}
            </div>}

            <div style={{marginBottom:18}}>
              <h3 style={{color:"#a5b4fc",fontSize:11,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>Upload & Assign Designs</h3>
              <input ref={dRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={upDesigns}/>
              <div onClick={()=>dRef.current?.click()} onDragOver={e=>{e.preventDefault();if(!designDrag)setDesignDrag(true)}} onDragLeave={e=>{e.preventDefault();if(designDrag)setDesignDrag(false)}} onDrop={e=>{e.preventDefault();setDesignDrag(false);addDesignFiles(e.dataTransfer&&e.dataTransfer.files)}} style={{border:`2px dashed ${designDrag?c.uploadBorderHover:c.designUploadBorder}`,borderRadius:11,padding:"20px 14px",textAlign:"center",cursor:"pointer",background:designDrag?c.uploadBgHover:c.designUploadBg,marginBottom:10,transition:"background 0.15s,border-color 0.15s"}}><div style={{fontSize:16,marginBottom:3}}>🎨</div><div style={{color:"#f9a8d4",fontSize:11,fontWeight:600}}>{designDrag?"Drop to upload":"Upload designs — click or drag & drop"}</div></div>

              {/* Bulk-assign toolbar — appears when there are designs to manage.
                  Lets the user check multiple designs and assign them all to one
                  group with a single click, instead of opening each design's
                  dropdown one by one. Big speedup when uploading 12+ designs. */}
              {designs.length>0&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.25)",borderRadius:8,marginBottom:8,flexWrap:"wrap"}}>
                <span style={{color:c.text,fontSize:10,fontWeight:600}}>
                  {selectedDesignIds.size===0
                    ? `Select designs to bulk-assign:`
                    : `${selectedDesignIds.size} of ${designs.length} selected`}
                </span>
                <div style={{display:"flex",gap:4,marginLeft:"auto",flexWrap:"wrap"}}>
                  {selectedDesignIds.size<designs.length?(
                    <button onClick={selectAllDesigns} style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${c.border}`,background:"transparent",color:c.text,fontSize:9,fontWeight:600,cursor:"pointer"}}>Select all</button>
                  ):(
                    <button onClick={clearDesignSelection} style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${c.border}`,background:"transparent",color:c.text,fontSize:9,fontWeight:600,cursor:"pointer"}}>Clear</button>
                  )}
                  {selectedDesignIds.size>0&&<>
                    <button
                      onClick={()=>{bulkAssignGroup("all");}}
                      style={{padding:"3px 8px",borderRadius:5,border:"1px solid #6366f1",background:"#6366f1",color:"white",fontSize:9,fontWeight:700,cursor:"pointer"}}
                      title="Assign selected designs to All Groups"
                    >→ All Groups</button>
                    {activeGroups.map(g=>(
                      <button
                        key={g.id}
                        onClick={()=>bulkAssignGroup(g.id)}
                        style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${g.color}`,background:g.color,color:"white",fontSize:9,fontWeight:700,cursor:"pointer"}}
                        title={`Assign selected designs to ${g.name}`}
                      >→ {g.name}</button>
                    ))}
                    <button
                      onClick={()=>bulkAssignGroup(null)}
                      style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${c.border}`,background:"transparent",color:"#f87171",fontSize:9,fontWeight:600,cursor:"pointer"}}
                      title="Unassign selected designs"
                    >Unassign</button>
                  </>}
                </div>
              </div>}

              {designs.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8}}>{designs.map(d=>{const partnerCount=d.pairId?designs.filter(x=>x.pairId===d.pairId).length:0;const pa=d.pairId?pairArt[d.pairId]:null;
                // A pair only FORMS as a set when both members are present AND their
                // tone is classified (designSets: members.length===2 && pairArt ready).
                // pairId present but not formed = orphan half or pairArt still resolving
                // → surface it as PENDING so the member sees pairing state, not counts.
                const isPaired=!!d.pairId&&partnerCount===2&&!!pa;const pairPending=!!d.pairId&&!isPaired;const pairRole=pa?(d.src===pa.lightSrc?"Light":d.src===pa.darkSrc?"Dark":null):null;
                // JOINED PAIR CARD (display only — no designSets/routing change). A FORMED
                // pair collapses to ONE 2-col card at its FIRST member's slot; the second
                // member renders nothing (folded in). Dark thumb left, Light right (from
                // pairArt). Click toggles BOTH; ✕ removes BOTH; ✕ Break splits them back
                // into two normal cards (unpairDesign). Pending/unpaired fall through to the
                // single-card path below, byte-identical to before.
                if(isPaired){
                  const members=designs.filter(x=>x.pairId===d.pairId);
                  if(members[0]?.id!==d.id)return null; // second member is folded into the first's card
                  const darkM=members.find(m=>m.src===pa.darkSrc)||members[0];
                  const lightM=members.find(m=>m.src===pa.lightSrc)||members[1];
                  const pairSel=members.every(m=>selectedDesignIds.has(m.id));
                  const togglePair=()=>setSelectedDesignIds(prev=>{const n=new Set(prev);const all=members.every(m=>n.has(m.id));members.forEach(m=>all?n.delete(m.id):n.add(m.id));return n;});
                  const removePair=e=>{e.stopPropagation();members.forEach(m=>rmDesign(m.id));setSelectedDesignIds(prev=>{const n=new Set(prev);members.forEach(m=>n.delete(m.id));return n;});};
                  const label=`${darkM.name} + ${lightM.name}`;
                  // The groups a member effectively binds in THIS pair set (reuse the set
                  // gdm): a group whose art is the member's own src (its tone group) or the
                  // combined image (neutral / same-group conflict → per-member). Drives the
                  // "Auto — <resolved>" option label so the dropdown states the live routing.
                  const psGdm=(designSets.find(s=>s.id===`set_pair_${d.pairId}`)||{}).gdm||{};
                  const boundGroups=m=>activeGroups.filter(g=>{const a=psGdm[g.id];return a===m.src||a===pa.combined;});
                  const autoLabel=m=>{const gs=boundGroups(m);if(gs.length===0)return "none";if(gs.every(g=>psGdm[g.id]===pa.combined))return "All Groups";const ns=gs.map(g=>g.name);return ns.length===1?ns[0]:`${ns[0]} +${ns.length-1}`;};
                  // Member-keyed routing: each member's dropdown value ("auto"|"all"|groupId).
                  // Selecting "auto" removes the key (missing = auto); an empty pair entry is
                  // dropped so Break/session stay clean.
                  const rt=pairRouting[d.pairId]||{};
                  const routeVal=m=>rt[m.id]||"auto";
                  const setMemberRoute=(mid,val)=>setPairRouting(prev=>{const po={...(prev[d.pairId]||{})};if(val==="auto")delete po[mid];else po[mid]=val;const next={...prev};if(Object.keys(po).length)next[d.pairId]=po;else delete next[d.pairId];return next;});
                  return<div key={`pair-${d.pairId}`} style={{gridColumn:"span 2",borderRadius:8,overflow:"hidden",border:`2px solid ${pairSel?"#10b981":"#f59e0b"}`,background:c.bgCard,boxShadow:pairSel?"0 0 0 2px rgba(16,185,129,0.2)":"none"}}>
                    {/* TWO distinct designs, each in its own framed cell with a gap. */}
                    <div style={{position:"relative"}}>
                      <div style={{display:"flex",gap:6,padding:6,cursor:"pointer"}} onClick={togglePair}>
                        {[{m:darkM,cap:"Dark"},{m:lightM,cap:"Light"}].map(({m,cap})=>{return<div key={cap} style={{flex:1,minWidth:0,border:`1px solid ${c.border}`,borderRadius:6,overflow:"hidden",background:isDark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)"}}>
                          <div style={{aspectRatio:"1"}}><img className="pb-img" src={m.src} alt={m.name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/></div>
                          <div style={{padding:"3px 4px"}}>
                            <div className="pb-text" style={{fontSize:8,fontWeight:600,color:c.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={m.name}>{m.name}</div>
                            <div style={{fontSize:8,fontWeight:700,color:"#f59e0b"}}>{cap}</div>
                            {/* Same dropdown as an unpaired card — "Auto" replaces "— Assign —" and, while selected, states what it resolved to. stopPropagation so picking a group doesn't toggle the card's selection. */}
                            <select className="pb-text" value={routeVal(m)} onClick={e=>e.stopPropagation()} onChange={e=>setMemberRoute(m.id,e.target.value)} style={{width:"100%",marginTop:3,padding:"3px 5px",borderRadius:4,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:9,outline:"none",cursor:"pointer"}}><option value="auto">{routeVal(m)==="auto"?`Auto — ${autoLabel(m)}`:"Auto"}</option><option value="all">✦ All Groups</option>{activeGroups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}</select>
                          </div>
                        </div>;})}
                      </div>
                      <div style={{position:"absolute",top:9,left:9,width:18,height:18,borderRadius:4,background:pairSel?"#10b981":"rgba(0,0,0,0.55)",border:pairSel?"2px solid #10b981":"2px solid rgba(255,255,255,0.5)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"white",fontWeight:900,pointerEvents:"none"}}>{pairSel?"✓":""}</div>
                      <button onClick={removePair} title="Remove both designs of this pair" style={{position:"absolute",top:9,right:9,background:"rgba(0,0,0,0.7)",border:"none",color:"#f87171",borderRadius:4,width:16,height:16,cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                    </div>
                    <div style={{padding:"0 8px 6px"}}>
                      <div style={{color:c.text,fontSize:9,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:3}} title={label}>🔗 {label}</div>
                      <button onClick={()=>unpairDesign(d.id)} title="Break this pair — the two designs become independent and are assigned to groups manually" style={{width:"100%",padding:"3px 5px",borderRadius:4,border:"1px solid rgba(245,158,11,0.5)",background:"rgba(245,158,11,0.12)",color:"#f59e0b",fontSize:9,fontWeight:700,outline:"none",cursor:"pointer"}}>🔗 Paired — ✕ Break</button>
                    </div>
                  </div>;
                }
                const isAll=d.group==="all";const ag=isAll?null:allGroups.find(g=>g.id===d.group);const borderCol=(isPaired||pairPending)?"#f59e0b":isAll?"#a5b4fc":ag?ag.color:c.border;const isSelected=selectedDesignIds.has(d.id);return<div key={d.id} style={{borderRadius:8,overflow:"hidden",border:`2px solid ${isSelected?"#10b981":borderCol}`,background:c.bgCard,boxShadow:isSelected?"0 0 0 2px rgba(16,185,129,0.2)":"none"}}>
                <div style={{position:"relative",aspectRatio:"1",cursor:"pointer"}} onClick={()=>toggleDesignSelected(d.id)}>
                  <img className="pb-img" src={d.src} alt={d.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  {/* Selection checkbox — clicking image area toggles it */}
                  <div style={{position:"absolute",top:3,left:3,width:18,height:18,borderRadius:4,background:isSelected?"#10b981":"rgba(0,0,0,0.55)",border:isSelected?"2px solid #10b981":"2px solid rgba(255,255,255,0.5)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"white",fontWeight:900,pointerEvents:"none"}}>{isSelected?"✓":""}</div>
                  <button onClick={e=>{e.stopPropagation();rmDesign(d.id);setSelectedDesignIds(prev=>{const n=new Set(prev);n.delete(d.id);return n;});}} style={{position:"absolute",top:3,right:3,background:"rgba(0,0,0,0.7)",border:"none",color:"#f87171",borderRadius:4,width:16,height:16,cursor:"pointer",fontSize:9,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                  {isPaired&&<div style={{position:"absolute",bottom:3,left:3,background:"#f59e0b",color:"#0f0f1a",borderRadius:4,padding:"1px 6px",fontSize:8,fontWeight:700,pointerEvents:"none"}}>🔗 {pairRole?`${pairRole} of pair`:"Paired"}</div>}
                  {pairPending&&<div title="This design is paired in the pipeline but its pair set hasn't formed yet — its partner isn't here yet, or the light/dark classification is still resolving." style={{position:"absolute",bottom:3,left:3,background:"rgba(245,158,11,0.9)",color:"#0f0f1a",borderRadius:4,padding:"1px 6px",fontSize:8,fontWeight:700,pointerEvents:"none"}}>🔗 pairing…</div>}
                  {!isPaired&&!pairPending&&isAll&&<div style={{position:"absolute",bottom:3,left:3,background:"#6366f1",color:"white",borderRadius:4,padding:"1px 6px",fontSize:8,fontWeight:700,pointerEvents:"none"}}>All Groups</div>}
                  {!isPaired&&!pairPending&&!isAll&&ag&&<div className="pb-text" style={{position:"absolute",bottom:3,left:3,background:ag.color,color:"white",borderRadius:4,padding:"1px 6px",fontSize:8,fontWeight:700,pointerEvents:"none"}}>{ag.name}</div>}
                </div>
                <div style={{padding:"6px 8px"}}><div className="pb-text" style={{color:c.text,fontSize:9,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:3}}>{d.name}</div>{isPaired
                  ?<button onClick={()=>unpairDesign(d.id)} title="Break this pair — the two designs become independent and are assigned to groups manually" style={{width:"100%",padding:"3px 5px",borderRadius:4,border:"1px solid rgba(245,158,11,0.5)",background:"rgba(245,158,11,0.12)",color:"#f59e0b",fontSize:9,fontWeight:700,outline:"none",cursor:"pointer"}}>🔗 Paired · auto-routed — ✕ Break</button>
                  :<>{pairPending&&<div style={{marginBottom:3,padding:"2px 5px",borderRadius:4,background:"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.4)",color:"#f59e0b",fontSize:8,lineHeight:1.3}}>🔗 Pair detected — waiting on partner / classification</div>}<select className="pb-text" value={d.group||""} onChange={e=>assignDG(d.id,e.target.value||null)} style={{width:"100%",padding:"3px 5px",borderRadius:4,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:9,outline:"none",cursor:"pointer"}}><option value="">— Assign —</option><option value="all">✦ All Groups</option>{activeGroups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}</select></>}</div>
              </div>})}</div>}
            </div>

            <div style={{marginBottom:18}}><h3 style={{color:"#34d399",fontSize:11,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>Templates — sorted by order ({allReady.length})</h3><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(70px,1fr))",gap:5}}>{allReady.map((m,i)=><div key={m.id} style={{borderRadius:6,overflow:"hidden",border:`2px solid ${m.noDesign?"rgba(245,158,11,0.4)":"rgba(52,211,153,0.3)"}`,position:"relative"}}><img className="pb-img" src={m.src} alt={m.name} style={{width:"100%",aspectRatio:"1",objectFit:"cover"}}/><div style={{position:"absolute",top:2,left:2,background:"rgba(0,0,0,0.7)",color:"white",borderRadius:3,padding:"0 4px",fontSize:8,fontWeight:700}}>{m.sortOrder??i+1}</div>{m.noDesign&&<div style={{position:"absolute",bottom:2,left:2,background:"#f59e0b",color:"#0f0f1a",borderRadius:3,padding:"0 4px",fontSize:7,fontWeight:700}}>No Design</div>}</div>)}</div></div>

            {canGen&&<div style={{textAlign:"center",padding:"18px 14px",background:c.statusBarBg,borderRadius:14,border:`1px solid ${c.border}`}}><div style={{color:c.textSub,fontSize:11,marginBottom:10}}>Ready: <strong style={{color:c.text}}>{totExp} mockups</strong> ({designSets.length} design set{designSets.length!==1?"s":""}{totNoDesign>0?` + ${standalones.length} standalone`:""})</div><button onClick={gen} style={{padding:"11px 40px",borderRadius:11,border:"none",background:"linear-gradient(135deg,#6366f1,#ec4899)",color:"white",fontSize:13,fontWeight:700,cursor:"pointer",boxShadow:"0 6px 24px rgba(99,102,241,0.4)"}}>⚡ Generate All Mockups</button></div>}
            {!canGen&&designs.length>0&&activeGroups.length>0&&<div style={{textAlign:"center",padding:"12px",borderRadius:10,background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)"}}><span style={{color:"#f59e0b",fontSize:11}}>Assign at least one design to at least one group to generate mockups.</span></div>}
            {canGen&&activeGroups.some(g=>{const specific=designs.filter(d=>d.group===g.id);const allGrp=designs.filter(d=>d.group==="all");return[...specific,...allGrp].length===0})&&<div style={{textAlign:"center",padding:"12px",borderRadius:10,background:"rgba(99,102,241,0.06)",border:"1px solid rgba(99,102,241,0.15)",marginBottom:12}}><span style={{color:"#a5b4fc",fontSize:11}}>ℹ️ Some groups have no designs assigned — templates using only those groups will be skipped. Assign designs to all groups to include all templates.</span></div>}
          </>:<>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:8}}>
              <div><h1 style={{fontSize:20,fontWeight:700,margin:0,color:c.text,display:"inline-flex",alignItems:"center"}}>Your Mockups Are Ready ✨<Tip id="s3r" text="Your generated mockups are shown below organized by design set. You can: change sort order using the dropdowns on each card, edit smart objects (✎) or foreground layers (✂) directly, adjust design brightness/contrast/saturation per mockup (🎛), rename sets by clicking the set name, and download all mockups at once. Generated mockups are NOT saved — download before leaving!" openTip={openTip} setOpenTip={setOpenTip}/></h1><p style={{color:c.textMuted,fontSize:11,marginTop:2}}>{totExp} mockups • {designSets.length} set{designSets.length!==1?"s":""}</p>{standalones.length>0&&designSets.length>1&&<p style={{color:c.textMuted,fontSize:10,marginTop:4,maxWidth:560,lineHeight:1.5}}>ℹ️ <strong style={{color:c.textSub}}>{totExp} mockups generated → all {totExp} appear across your {designSets.length} listings.</strong> Your {standalones.length} standalone{standalones.length!==1?"s":""} {standalones.length!==1?"ride":"rides"} every listing but {standalones.length!==1?"are":"is"} transmitted once — so {totSent} unique files are sent, not {totExp}, and nothing is duplicated or lost. Download All still saves all {totExp} files (a copy in each set’s folder).</p>}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>setShowRes(false)} style={{padding:"7px 16px",borderRadius:9,border:`1px solid ${c.border}`,background:"transparent",color:c.textSub,fontSize:10,fontWeight:600,cursor:"pointer"}}>← Back</button>
                {designSets.some(s=>!removedSets.has(s.id))&&<button onClick={()=>{setBulkZoneScope("session");setBulkZoneEditor({global:true});}} title="Move, resize or rotate the design across EVERY set at once — see the change, then apply. It tells you how many mockups will re-render before you commit." style={{padding:"7px 16px",borderRadius:9,border:"1px solid rgba(99,102,241,0.4)",background:"rgba(99,102,241,0.12)",color:"#a5b4fc",fontSize:10,fontWeight:700,cursor:"pointer"}}>📐 Bulk edit zones — all sets</button>}
                {totGen>=totExp&&totGen>0&&<button onClick={async()=>{
                  if(pipelineSending||pipelineSent)return;
                  setPipelineSending(true);
                  try{const sent=await sendToPipeline();setSentCount(sent);setPipelineSent(true);}
                  catch(e){console.error("sendToPipeline failed:",e);alert("Failed to send to pipeline: "+e.message);}
                  finally{setPipelineSending(false);}
                }} disabled={pipelineSent||pipelineSending} title="Standalones are sent once and broadcast to every listing" style={{padding:"7px 16px",borderRadius:9,border:"none",background:pipelineSent?"linear-gradient(135deg,#059669,#10b981)":pipelineSending?"linear-gradient(135deg,#6366f1,#8b5cf6)":"linear-gradient(135deg,#8b5cf6,#ec4899)",color:"white",fontSize:10,fontWeight:700,cursor:(pipelineSent||pipelineSending)?"default":"pointer"}}>{pipelineSent?`✓ Sent ${sentCount} to Pipeline`:pipelineSending?(pipelineProgress.total>0?`Sending… ${pipelineProgress.done}/${pipelineProgress.total}`:"Sending…"):"🚀 Send to Pipeline"}</button>}
                <button onClick={()=>doDownload()} disabled={dling||totExp===0||totGen<totExp} style={{padding:"7px 16px",borderRadius:9,border:"none",background:totExp>0?"linear-gradient(135deg,#6366f1,#ec4899)":c.btnSecBg,color:totExp>0?"white":c.textDim,fontSize:10,fontWeight:700,cursor:(dling||totGen<totExp)?"wait":totExp>0?"pointer":"not-allowed"}}>{dling?(dlProg.done<dlProg.total?`Preparing ${dlProg.done}/${dlProg.total}…`:"Zipping…"):totGen<totExp?`⏳ Rendering ${Math.min(totGen,totExp)}/${totExp}…`:`↓ Download All — ZIP (${totExp})`}</button>
              </div>
            </div>

            {/* Global Design Controls */}
            <DCPanel dc={globalDC} onChange={setGlobalDC} label="🎛 Global Design Controls — applies to all designs on the mockups" c={c} onReset={()=>setGlobalDC({...DEFAULT_DC})} onApply={applyGlobalDC} onEdit={()=>setBulkDcEditor({scope:"global"})} tipId="dc-global" tipText="Click Edit with preview to open a modal with a live-updating preview tile — drag sliders and see results in real time. Click Apply to bake the values into all affected mockups in the bulk grid." openTip={openTip} setOpenTip={setOpenTip}/>

            {/* Listing Video (manual send) — pick ONE video from your
                library. It rides the single-message PIPELINE_VIDEO the suite
                consumes today, whose Etsy leg is body-limited (~4.5MB), so we
                GATE here: this manual single-send leg is body-limited (~4.5MB).
                Bigger clips go through a SET — the studio emits PIPELINE_VIDEO_CHUNK,
                which the SUITE consumes (videoChunks/videoHold → upload-once Blobs →
                Etsy), so sets publish listing videos of any size. No compression here. */}
            <div style={{padding:"10px 14px",borderRadius:10,background:c.statusBarBg,border:`1px solid ${c.border}`,marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                <span style={{color:c.text,fontSize:11,fontWeight:700,display:"inline-flex",alignItems:"center"}}>🎥 Listing Video <span style={{color:c.textMuted,fontWeight:500,fontSize:10,marginLeft:6}}>(optional — applied to every listing in this batch)</span><Tip id="bcast-video" text="Attach one video from your library to this batch and it uploads as the Etsy listing video on every listing here. Your design is NOT applied to it — it publishes as-is. Upload videos through the box in Step 1. Big clips (over ~4.5MB) can't go through this manual send — attach them to a saved SET instead, which publishes listing videos of any size." openTip={openTip} setOpenTip={setOpenTip}/></span>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  {libVideos.length>0
                    ? <select className="pb-text" value={manualVideoId||""} onChange={e=>setManualVideoId(e.target.value||null)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${c.border}`,background:c.bgCard,color:c.text,fontSize:10,fontWeight:600,outline:"none",cursor:"pointer",maxWidth:240}}><option value="">— No video —</option>{libVideos.map(v=><option key={v.videoId} value={v.videoId}>{v.file_name} ({fmtMB(v.sizeBytes)} MB)</option>)}</select>
                    : <span style={{color:c.textMuted,fontSize:10}}>Upload a video in Step 1 to attach one.</span>}
                </div>
              </div>
              {(()=>{const mv=manualVideoId?(libVideos||[]).find(v=>v.videoId===manualVideoId):null;if(!mv)return null;const ok=fitsManualSend(mv.sizeBytes);return <div style={{marginTop:8}}>
                <div className="pb-text" style={{color:c.textMuted,fontSize:10,marginBottom:6}}>{mv.file_name} · {fmtMB(mv.sizeBytes)} MB · {mv.durationSec!=null?`${Number(mv.durationSec).toFixed(1)}s`:"length unverified"}</div>
                {ok
                  ? <div style={{color:c.textMuted,fontSize:10}}>Attached to every listing in this batch — publishes as-is (your design is not applied to it).</div>
                  : <div style={{padding:"8px 10px",borderRadius:6,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.25)",color:"#fbbf24",fontSize:10}}>This clip is {fmtMB(mv.sizeBytes)} MB — the manual send can only carry ~4.5 MB, so it will be skipped here. Attach it to a saved SET instead — sets publish listing videos of any size.</div>}
              </div>;})()}
            </div>

            {/* Wave-based mounting counter. Reset on each render, mutated as
                we walk the grids in render order. Limits how many GenMockup
                components are simultaneously mounted — anything past the
                limit renders as a Queued placeholder until earlier ones
                complete (populating genOut, becoming static tiles, freeing
                their slot). Without this gate, mounting 600 GenMockup
                components in one render pass crashes Chrome at MOUNT time
                with STATUS_OUT_OF_MEMORY, before any canvas work runs. */}
            {(()=>{window.__msGenCounter={active:0,limit:30};return null})()}

            {/* Each design set includes standalone images */}
            {designSets.map((set,si)=>{
              if(removedSets.has(set.id))return null;
              const sdc=setDCs[set.id];
              const customName=setNames[set.id];
              const displayName=customName||`Set ${si+1}`;
              return<div key={set.id} style={{marginBottom:28}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"8px 12px",borderRadius:9,background:c.statusBarBg,border:`1px solid ${c.border}`}}>
                <input value={displayName} onChange={e=>setSetNames(p=>({...p,[set.id]:e.target.value}))} style={{background:"transparent",border:"none",color:"#a5b4fc",fontSize:12,fontWeight:700,outline:"none",width:Math.max(50,displayName.length*8),padding:0,minWidth:50}} title="Click to rename set"/>
                <span style={{color:c.textMuted,fontSize:9}}>({set.label})</span>
                <div style={{display:"flex",gap:4,marginLeft:"auto",alignItems:"center"}}>
                  {allGroups.map(g=>{const d=set.designs[g.id];return d?<div key={g.id} style={{display:"flex",alignItems:"center",gap:3}}><div style={{width:8,height:8,borderRadius:2,background:g.color}}/><img className="pb-img" src={d.src} alt="" style={{width:20,height:20,borderRadius:3,objectFit:"cover",border:`1px solid ${g.color}`}}/></div>:null})}
                  <button onClick={()=>setSetDCs(p=>({...p,[set.id]:sdc?undefined:{...globalDC}}))} style={{padding:"3px 8px",borderRadius:5,border:"none",background:sdc?"rgba(99,102,241,0.15)":c.btnSecBg,color:sdc?"#a5b4fc":c.textMuted,fontSize:8,fontWeight:600,cursor:"pointer",marginLeft:6}}>{sdc?"🎛 ▾":"🎛"}</button>
                  <button onClick={()=>{setBulkZoneScope("session");setBulkZoneEditor({set});}} title="Move, resize or rotate the design across every mockup in this set at once — see the result, then apply (the affected mockups re-render)." style={{padding:"3px 10px",borderRadius:5,border:"none",background:c.btnSecBg,color:c.textSub,fontSize:8,fontWeight:700,cursor:"pointer"}}>📐 Bulk edit zones</button>
                  <button onClick={()=>downloadSet(set,si)} disabled={dling} style={{padding:"3px 10px",borderRadius:5,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:8,fontWeight:700,cursor:dling?"wait":"pointer"}}>{dling?(dlProg.total>0&&dlProg.done<dlProg.total?`Preparing ${dlProg.done}/${dlProg.total}…`:"Zipping…"):"↓ Download Set (ZIP)"}</button>
                  <button onClick={()=>removeSetMockups(set)} title="Remove this entire set" style={{padding:"3px 9px",borderRadius:5,border:"none",background:"rgba(239,68,68,0.9)",color:"white",fontSize:10,fontWeight:800,lineHeight:1,cursor:"pointer"}}>✕</button>
                </div>
              </div>
              {sdc&&<DCPanel dc={sdc} onChange={v=>setSetDCs(p=>({...p,[set.id]:v}))} label={`🎛 Set ${si+1} Controls`} c={c} onReset={()=>setSetDCs(p=>{const n={...p};delete n[set.id];return n})} onApply={()=>applySetDC(set.id)} onEdit={()=>setBulkDcEditor({scope:"set",setId:set.id})} tipId={`dc-set-${si}`} tipText="Click Edit with preview to open a modal with a live-updating preview tile — drag sliders and see results in real time. Click Apply to bake the values into this set's mockups." openTip={openTip} setOpenTip={setOpenTip}/>}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                {setDesignItems(set).map(({tpl,k:mk,gdm})=>{
                  if(removedMks.has(mk))return null;
                  const mdc=mockupDCs[mk]||sdc||globalDC;
                  const cached=genOut[mk];
                  const counter=window.__msGenCounter;
                  // §2b — the per-mockup SO override must ride into the editor on
                  // reopen too, not only into the generated bytes. StaticMockupTile
                  // used to get the ORIGINAL tpl, so reopening "edit smart objects"
                  // showed the design back at its pre-move position even though the
                  // render was correct. Merge the override here so onEditSO carries it.
                  const mkov=mockupSOOverrides[mk];
                  const effBase=mkov?{...tpl,...mkov,smartObject:(mkov.smartObjects||[])[0]||null}:tpl;
                  let tile;
                  if(cached){
                    tile=<StaticMockupTile tpl={effBase} cached={cached} dc={mdc} c={c} isDark={isDark} onSortChange={setSortOrder} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)} onEditCrop={()=>setCropModal({tpl:effBase,gdm,mk})} onEditDC={async()=>{const r=await regenerateLayersStandalone(effBase,gdm);setDcModal({mk,tpl:effBase,baseOut:r.baseOut,designOut:r.designOut,fgOut:r.fgOut})}}/>;
                  }else if(counter.active<counter.limit){
                    counter.active++;
                    const effTpl=applyCropToTpl(effBase,mk);
                    tile=<GenMockup designCtrl={mdc} key={mk+JSON.stringify(mkov||"")} tpl={effTpl} gdm={gdm} cachedResult={undefined} onReady={(k,du)=>onReady(mk,du)} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)} onEditCrop={()=>setCropModal({tpl,gdm,mk})} onSortChange={setSortOrder} onEditDC={({tpl:tp,baseOut:bo,designOut:dout,fgOut:fg})=>setDcModal({mk,tpl:tp,baseOut:bo,designOut:dout,fgOut:fg})}/>;
                  }else{
                    tile=<QueuedMockupTile tpl={tpl} c={c} isDark={isDark}/>;
                  }
                  return <div key={mk} style={{position:"relative"}}>{tile}{removeBadge(mk)}</div>;
                })}
                {standalones.map(tpl=>{
                  const mk=`${set.id}_sa_${tpl.id}`;
                  if(removedMks.has(mk))return null;
                  const mdc=mockupDCs[mk]||sdc||globalDC;
                  const cached=genOut[mk];
                  const counter=window.__msGenCounter;
                  const mkov=mockupSOOverrides[mk];
                  const effBase=mkov?{...tpl,...mkov,smartObject:(mkov.smartObjects||[])[0]||null}:tpl;
                  let tile;
                  if(cached){
                    tile=<StaticMockupTile tpl={effBase} cached={cached} dc={mdc} c={c} isDark={isDark} onSortChange={setSortOrder} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)}/>;
                  }else if(counter.active<counter.limit){
                    counter.active++;
                    tile=<GenMockup designCtrl={mdc} key={mk+JSON.stringify(mkov||"")} tpl={effBase} gdm={{}} cachedResult={undefined} onReady={(k,du)=>onReady(mk,du)} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)} onSortChange={setSortOrder}/>;
                  }else{
                    tile=<QueuedMockupTile tpl={tpl} c={c} isDark={isDark}/>;
                  }
                  return <div key={mk} style={{position:"relative"}}>{tile}{removeBadge(mk)}</div>;
                })}
              </div>
            </div>})}

            {/* If only standalone, no sets */}
            {designSets.length===0&&standalones.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
              {standalones.map(tpl=>{
                const mk=tpl.id;
                if(removedMks.has(mk))return null;
                const cached=genOut[mk];
                const counter=window.__msGenCounter;
                const mkov=mockupSOOverrides[mk];
                const effBase=mkov?{...tpl,...mkov,smartObject:(mkov.smartObjects||[])[0]||null}:tpl;
                let tile;
                if(cached){
                  tile=<StaticMockupTile tpl={effBase} cached={cached} dc={mockupDCs[mk]||globalDC} c={c} isDark={isDark} onSortChange={setSortOrder} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)}/>;
                }else if(counter.active<counter.limit){
                  counter.active++;
                  tile=<GenMockup designCtrl={globalDC} key={mk+JSON.stringify(mkov||"")} tpl={effBase} gdm={{}} cachedResult={undefined} onReady={(k,du)=>onReady(mk,du)} onEditSO={t=>setEditResultSO({...t,_mk:mk})} onEditFG={t=>setEditResultFG(t)} onSortChange={setSortOrder}/>;
                }else{
                  tile=<QueuedMockupTile tpl={tpl} c={c} isDark={isDark}/>;
                }
                return <div key={mk} style={{position:"relative"}}>{tile}{removeBadge(mk)}</div>;
              })}
            </div>}
          </>}
        </div>}
      </div>

      <div style={{textAlign:"center",padding:"24px 16px 18px",color:c.textMuted,fontSize:10}}>© {new Date().getFullYear()} Wtfamievendoing AB. All rights reserved.</div>

      {/* Scroll to top */}
      <button onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} style={{position:"fixed",bottom:24,right:24,width:40,height:40,borderRadius:12,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"white",fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 16px rgba(99,102,241,0.4)",zIndex:90,opacity:0.85,transition:"opacity 0.2s"}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.85"} title="Scroll to top">↑</button>

      {editSavedSO&&<MultiSOEditor mockup={editSavedSO} onSave={saveSavedSO} onCancel={()=>setEditSavedSO(null)} globalGroups={globalGroups} onUpdateGlobalGroups={updateGlobalGroups} onEditForeground={setEditMask}/>}
      {linkedPlacementOpen&&<LinkedPlacementEditor mockups={saved.filter(s=>selSaved.has(s.id))} globalGroups={globalGroups} onApply={saveLinkedPlacement} onCancel={()=>setLinkedPlacementOpen(false)}/>}
      {/* PSD export status toast (busy / ✅ done / ⚠️ note / error) */}
      {psdExp&&<div style={{position:"fixed",bottom:18,left:"50%",transform:"translateX(-50%)",zIndex:1200,maxWidth:520,display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:11,background:psdExp.error?"rgba(239,68,68,0.12)":psdExp.warn?"rgba(245,158,11,0.12)":psdExp.id?"rgba(99,102,241,0.12)":"rgba(16,185,129,0.12)",border:`1px solid ${psdExp.error?"rgba(239,68,68,0.4)":psdExp.warn?"rgba(245,158,11,0.4)":psdExp.id?"rgba(99,102,241,0.3)":"rgba(16,185,129,0.4)"}`,backdropFilter:"blur(8px)",boxShadow:"0 8px 30px rgba(0,0,0,0.25)"}}>
        {psdExp.id&&<div style={{width:15,height:15,border:"2px solid rgba(99,102,241,0.3)",borderTopColor:"#6366f1",borderRadius:"50%",animation:"mg-spin 0.8s linear infinite",flexShrink:0}}/>}
        <span style={{fontSize:13,flexShrink:0}}>{psdExp.error?"⚠️":psdExp.warn?"⚠️":psdExp.id?"":"✅"}</span>
        <span style={{color:psdExp.error?"#f87171":psdExp.warn?"#fbbf24":psdExp.id?"#a5b4fc":"#34d399",fontSize:12,fontWeight:600}}>{psdExp.id?"Building your .psd…":psdExp.error||psdExp.warn||psdExp.done}</span>
        {!psdExp.id&&<button onClick={()=>setPsdExp(null)} style={{background:"none",border:"none",color:psdExp.error?"#f87171":psdExp.warn?"#fbbf24":"#34d399",cursor:"pointer",fontSize:13,marginLeft:4}}>✕</button>}
      </div>}
      {editResultSO&&<>
        <MultiSOEditor mockup={editResultSO} onSave={saveResultSO} onCancel={()=>setEditResultSO(null)} globalGroups={globalGroups} onUpdateGlobalGroups={updateGlobalGroups} designPreview={gdmForMk(editResultSO._mk)} extraFooter={
          <div style={{display:"flex",alignItems:"center",gap:4,marginRight:"auto",flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
            {[{v:"permanent",label:"All mockups (permanent)",color:"#34d399"},{v:"session",label:"All instances (session)",color:"#f59e0b"},{v:"thisonly",label:"This mockup only",color:"#60a5fa"}].map(o=>
              <button key={o.v} onClick={()=>setResultEditMode(o.v)} style={{padding:"3px 8px",borderRadius:5,border:resultEditMode===o.v?`1.5px solid ${o.color}`:`1px solid rgba(255,255,255,0.1)`,background:resultEditMode===o.v?`${o.color}15`:"transparent",color:resultEditMode===o.v?o.color:"#94a3b8",fontSize:9,fontWeight:600,cursor:"pointer",transition:"all 0.15s"}}>{o.label}</button>
            )}
          </div>
        }/>
      </>}
      {editResultFG&&<FGEditor mockup={editResultFG} onSave={saveResultFG} onCancel={()=>setEditResultFG(null)}/>}
      {bulkZoneEditor&&(()=>{
        // One editor for both scopes: per-set edits that set's templates; GLOBAL
        // edits the UNION across every non-removed set. Templates are shared, so
        // the union is just the unique templates (few), while the regenerate count
        // (countRegenForTemplates, across ALL sets) reports the full mockup
        // fan-out — e.g. a handful of templates, dozens of mockups rebuilding.
        const sets=bulkZoneEditor.global?designSets.filter(s=>!removedSets.has(s.id)):[bulkZoneEditor.set];
        const seen=new Set();const tpls=[];const preview={};
        sets.forEach(s=>setDesignItems(s).forEach(it=>{
          if(!seen.has(it.tpl.id)){seen.add(it.tpl.id);tpls.push(it.tpl);}
          Object.entries(it.gdm||{}).forEach(([g,src])=>{if(preview[g]===undefined)preview[g]=src;});
        }));
        if(!tpls.length){return null;}
        return <LinkedPlacementEditor
          mockups={tpls}
          globalGroups={globalGroups}
          designPreview={preview}
          scope={bulkZoneScope}
          onScopeChange={setBulkZoneScope}
          allowPermanent={!!bulkZoneEditor.global}
          regenCountFor={countRegenForTemplates}
          onApply={saveBulkResultPlacement}
          onCancel={()=>setBulkZoneEditor(null)}
        />;
      })()}
      {cropModal&&(()=>{
        const mk=cropModal.mk;const tplId=cropModal.tpl.id;
        // Merge any per-mockup placement override so the crop preview reflects
        // the current zone placement (crop reads zones, doesn't change them).
        const ov=mockupSOOverrides[mk];
        const effTpl=ov?{...cropModal.tpl,...ov,smartObject:(ov.smartObjects||[])[0]||null}:cropModal.tpl;
        return <CropScopeModal
          tpl={effTpl}
          gdm={cropModal.gdm}
          resolveCrop={g=>resolveCropForGroup(mk,tplId,g)}
          onSave={saveCropScoped}
          onCancel={()=>setCropModal(null)}
        />;
      })()}
      {dcModal&&<DCModal mockup={dcModal.tpl} baseOut={dcModal.baseOut} designOut={dcModal.designOut} fgOut={dcModal.fgOut} dc={mockupDCs[dcModal.mk]||globalDC} onChange={v=>setMockupDCs(p=>({...p,[dcModal.mk]:v}))} onSave={()=>{applyMockupDC(dcModal.mk);setDcModal(null)}} onCancel={()=>{setMockupDCs(p=>{const n={...p};delete n[dcModal.mk];return n});setDcModal(null)}}/>}
      {/* Bulk DC editor — single modal handles both global and per-set
          scope. Subject list is built fresh each render based on the
          editor's scope so the dropdown matches what Apply will affect. */}
      {bulkDcEditor&&(()=>{
        const isGlobal=bulkDcEditor.scope==="global";
        const editorSet=!isGlobal?designSets.find(s=>s.id===bulkDcEditor.setId):null;
        // Build the subjects list for the dropdown
        const subjects=[];
        if(isGlobal){
          for(const set of designSets){
            const setLabel=setNames[set.id]||set.label;
            for(const {tpl,k,gdm} of setDesignItems(set)){
              subjects.push({key:k,tpl,gdm,label:`${setLabel} · ${tpl.name}`});
            }
          }
          if(designSets.length===0){
            for(const tpl of standalones.filter(t=>!t.noDesign)){
              subjects.push({key:tpl.id,tpl,gdm:{},label:tpl.name});
            }
          }
        }else if(editorSet){
          for(const {tpl,k,gdm} of setDesignItems(editorSet)){
            subjects.push({key:k,tpl,gdm,label:tpl.name});
          }
        }
        const dc=isGlobal?globalDC:(setDCs[bulkDcEditor.setId]||{...DEFAULT_DC});
        const onChange=isGlobal?setGlobalDC:(v=>setSetDCs(p=>({...p,[bulkDcEditor.setId]:v})));
        return <BulkDCEditor
          title={isGlobal?"🎛 Global Design Controls":`🎛 ${setNames[bulkDcEditor.setId]||editorSet?.label||"Set"} Controls`}
          scopeDescription={isGlobal?"Adjust DC for all designs across every set. Changes preview live on the selected mockup — click Apply to all to bake into the bulk grid.":"Adjust DC for this set's mockups only. Changes preview live on the selected mockup — click Apply to all to bake into this set."}
          dc={dc}
          onChange={onChange}
          subjects={subjects}
          onApply={()=>{
            if(isGlobal)applyGlobalDC();
            else applySetDC(bulkDcEditor.setId);
            setBulkDcEditor(null);
          }}
          onCancel={()=>setBulkDcEditor(null)}
        />;
      })()}
      {editMask&&step===0&&<FGEditor mockup={editMask} onSave={saveFG} onCancel={()=>setEditMask(null)}/>}
    </div>
  </ThemeContext.Provider>;
}
